
/*
* @license
* Broadcast Theme (c) Presidio Creative
*
* This file is included for advanced development by
* Shopify Agencies.  Modified versions of the theme
* code are not supported by Shopify or Presidio Creative.
*
* In order to use this file you will need to change
* theme.js to theme.dev.js in /layout/theme.liquid
*
*/

(function (scrollLock) {
    'use strict';

    (function() {
        const env = {"NODE_ENV":"production"};
        try {
            if (process) {
                process.env = Object.assign({}, process.env);
                Object.assign(process.env, env);
                return;
            }
        } catch (e) {} // avoid ReferenceError: process is not defined
        globalThis.process = { env:env };
    })();

    window.theme = window.theme || {};

    window.theme.sizes = {
      mobile: 480,
      small: 750,
      large: 990,
      widescreen: 1400,
    };

    window.theme.focusable = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const PUB_SUB_EVENTS = {
      cartUpdate: 'cart-update',
      quantityUpdate: 'quantity-update',
      optionValueSelectionChange: 'option-value-selection-change',
      variantChange: 'variant-change',
      cartError: 'cart-error',
    };

    window.theme.PUB_SUB_EVENTS = PUB_SUB_EVENTS;

    let subscribers = {};

    function subscribe$1(eventName, callback) {
      if (subscribers[eventName] === undefined) {
        subscribers[eventName] = [];
      }

      subscribers[eventName] = [...subscribers[eventName], callback];

      return function unsubscribe() {
        subscribers[eventName] = subscribers[eventName].filter((cb) => {
          return cb !== callback;
        });
      };
    }

    function publish$1(eventName, data) {
      if (subscribers[eventName]) {
        const promises = subscribers[eventName].map((callback) => callback(data));
        return Promise.all(promises);
      } else {
        return Promise.resolve();
      }
    }

    window.publish = publish$1;
    window.subscribe = subscribe$1;

    /**
     * Currency Helpers
     * -----------------------------------------------------------------------------
     * A collection of useful functions that help with currency formatting
     *
     * Current contents
     * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
     *
     */

    const moneyFormat = '${{amount}}';

    /**
     * Format money values based on your shop currency settings
     * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
     * or 3.00 dollars
     * @param  {String} format - shop money_format setting
     * @return {String} value - formatted value
     */
    window.theme.formatMoney = function (cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ', '');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', ',');
          break;
        case 'amount_with_period_and_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    };

    window.theme.debounce = function (fn, time) {
      let timeout;
      return function () {
        // eslint-disable-next-line prefer-rest-params
        if (fn) {
          const functionCall = () => fn.apply(this, arguments);
          clearTimeout(timeout);
          timeout = setTimeout(functionCall, time);
        }
      };
    };

    let screenOrientation = getScreenOrientation();
    let firstLoad = true;

    window.theme.readHeights = function () {
      const h = {};
      h.windowHeight = Math.min(window.screen.height, theme.windowHeight);
      h.headerHeight = getHeight('[data-header-height]');
      h.stickyHeaderHeight = document.querySelector('[data-header-sticky]') ? h.headerHeight : 0;
      h.collectionNavHeight = getHeight('[data-collection-nav]');

      return h;
    };

    function setVars() {
      const {windowHeight, headerHeight, collectionNavHeight} = window.theme.readHeights();
      const currentScreenOrientation = getScreenOrientation();

      if (!firstLoad || currentScreenOrientation !== screenOrientation || theme.windowWidth > window.theme.sizes.mobile) {
        // Only update the heights on screen orientation change or larger than mobile devices
        document.documentElement.style.setProperty('--full-height', `${windowHeight}px`);
        document.documentElement.style.setProperty('--three-quarters', `${windowHeight * (3 / 4)}px`);
        document.documentElement.style.setProperty('--two-thirds', `${windowHeight * (2 / 3)}px`);
        document.documentElement.style.setProperty('--one-half', `${windowHeight / 2}px`);
        document.documentElement.style.setProperty('--one-third', `${windowHeight / 3}px`);

        // Update the screen orientation state
        screenOrientation = currentScreenOrientation;
        firstLoad = false;
      }

      document.documentElement.style.setProperty('--collection-nav-height', `${collectionNavHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }

    function getScreenOrientation() {
      if (window.matchMedia('(orientation: portrait)').matches) {
        return 'portrait';
      }

      if (window.matchMedia('(orientation: landscape)').matches) {
        return 'landscape';
      }
    }

    function getHeight(selector) {
      const el = document.querySelector(selector);
      if (el) {
        return el.offsetHeight;
      } else {
        return 0;
      }
    }

    setVars();

    window.addEventListener('DOMContentLoaded', setVars);
    document.addEventListener('theme:resize', setVars);
    document.addEventListener('shopify:section:load', setVars);

    function pauseAllMedia() {
      document.querySelectorAll('[data-host="youtube"]').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      document.querySelectorAll('[data-host="vimeo"]').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      document.querySelectorAll('video').forEach((video) => video.pause());
      document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    }

    window.theme.pauseAllMedia = pauseAllMedia;

    window.theme.scrollTo = (elementTop) => {
      const stickyHeaderHeight = document.querySelector('[data-header-sticky]') ? document.querySelector('[data-header-height]').offsetHeight : 0;

      window.scrollTo({
        top: elementTop + window.scrollY - stickyHeaderHeight,
        left: 0,
        behavior: 'smooth',
      });
    };

    /**
     * A11y Helpers
     * -----------------------------------------------------------------------------
     * A collection of useful functions that help make your theme more accessible
     */

    // Define trapFocusHandlers as a global variable within the module.
    const trapFocusHandlers = {};

    const a11y = {
      /**
       * Moves focus to an HTML element
       * @param {Element} element - The element to focus on.
       * @param {Object} options - Settings unique to your theme.
       * @param {string} options.className - Class name to apply to element on focus.
       */
      forceFocus(element, options) {
        options = options || {};

        var savedTabIndex = element.tabIndex;

        element.tabIndex = -1;
        element.dataset.tabIndex = savedTabIndex;
        element.focus();
        if (typeof options.className !== 'undefined') {
          element.classList.add(options.className);
        }
        element.addEventListener('blur', callback);

        function callback(event) {
          event.target.removeEventListener(event.type, callback);

          element.tabIndex = savedTabIndex;
          delete element.dataset.tabIndex;
          if (typeof options.className !== 'undefined') {
            element.classList.remove(options.className);
          }
        }
      },

      /**
       * Focus the appropriate element based on the URL hash.
       * @param {Object} options - Settings unique to your theme.
       */
      focusHash(options) {
        options = options || {};
        var hash = window.location.hash;
        var element = document.getElementById(hash.slice(1));

        if (element && options.ignore && element.matches(options.ignore)) {
          return false;
        }

        if (hash && element) {
          this.forceFocus(element, options);
        }
      },

      /**
       * Bind in-page links to focus the appropriate element.
       * @param {Object} options - Settings unique to your theme.
       */
      bindInPageLinks(options) {
        options = options || {};
        var links = Array.prototype.slice.call(document.querySelectorAll('a[href^="#"]'));

        function queryCheck(selector) {
          return document.getElementById(selector) !== null;
        }

        return links.filter((link) => {
          if (link.hash === '#' || link.hash === '') {
            return false;
          }

          if (options.ignore && link.matches(options.ignore)) {
            return false;
          }

          if (!queryCheck(link.hash.substr(1))) {
            return false;
          }

          var element = document.querySelector(link.hash);

          if (!element) {
            return false;
          }

          link.addEventListener('click', () => {
            this.forceFocus(element, options);
          });

          return true;
        });
      },

      focusable(container) {
        var elements = Array.prototype.slice.call(
          container.querySelectorAll('[tabindex],' + '[draggable],' + 'a[href],' + 'area,' + 'button:enabled,' + 'input:not([type=hidden]):enabled,' + 'object,' + 'select:enabled,' + 'textarea:enabled')
        );

        return elements.filter((element) => !!((element.offsetWidth || element.offsetHeight || element.getClientRects().length) && this.isVisible(element)));
      },

      trapFocus(container, options) {
        options = options || {};
        var elements = this.focusable(container);
        var elementToFocus = options.elementToFocus || container;
        var first = elements[0];
        var last = elements[elements.length - 1];

        this.removeTrapFocus();

        trapFocusHandlers.focusin = function (event) {
          if (container !== event.target && !container.contains(event.target) && first && first === event.target) {
            first.focus();
          }

          if (event.target !== container && event.target !== last && event.target !== first) return;
          document.addEventListener('keydown', trapFocusHandlers.keydown);
        };

        trapFocusHandlers.focusout = function () {
          document.removeEventListener('keydown', trapFocusHandlers.keydown);
        };

        trapFocusHandlers.keydown = function (event) {
          if (event.code !== 'Tab') return;

          if (event.target === last && !event.shiftKey) {
            event.preventDefault();
            first.focus();
          }

          if ((event.target === container || event.target === first) && event.shiftKey) {
            event.preventDefault();
            last.focus();
          }
        };

        document.addEventListener('focusout', trapFocusHandlers.focusout);
        document.addEventListener('focusin', trapFocusHandlers.focusin);

        this.forceFocus(elementToFocus, options);
      },

      removeTrapFocus() {
        document.removeEventListener('focusin', trapFocusHandlers.focusin);
        document.removeEventListener('focusout', trapFocusHandlers.focusout);
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
      },

      autoFocusLastElement() {
        if (window.a11y.lastElement && document.body.classList.contains('is-focused')) {
          setTimeout(() => {
            window.a11y.lastElement?.focus();
          });
        }
      },

      accessibleLinks(elements, options) {
        if (typeof elements !== 'string') {
          throw new TypeError(elements + ' is not a String.');
        }

        elements = document.querySelectorAll(elements);

        if (elements.length === 0) {
          return;
        }

        options = options || {};
        options.messages = options.messages || {};

        var messages = {
          newWindow: options.messages.newWindow || 'Opens in a new window.',
          external: options.messages.external || 'Opens external website.',
          newWindowExternal: options.messages.newWindowExternal || 'Opens external website in a new window.',
        };

        var prefix = options.prefix || 'a11y';

        var messageSelectors = {
          newWindow: prefix + '-new-window-message',
          external: prefix + '-external-message',
          newWindowExternal: prefix + '-new-window-external-message',
        };

        function generateHTML(messages) {
          var container = document.createElement('ul');
          var htmlMessages = Object.keys(messages).reduce((html, key) => {
            return (html += '<li id=' + messageSelectors[key] + '>' + messages[key] + '</li>');
          }, '');

          container.setAttribute('hidden', true);
          container.innerHTML = htmlMessages;

          document.body.appendChild(container);
        }

        function externalSite(link) {
          return link.hostname !== window.location.hostname;
        }

        elements.forEach((link) => {
          var target = link.getAttribute('target');
          var rel = link.getAttribute('rel');
          var isExternal = externalSite(link);
          var isTargetBlank = target === '_blank';
          var missingRelNoopener = rel === null || rel.indexOf('noopener') === -1;

          if (isTargetBlank && missingRelNoopener) {
            var relValue = rel === null ? 'noopener' : rel + ' noopener';
            link.setAttribute('rel', relValue);
          }

          if (isExternal && isTargetBlank) {
            link.setAttribute('aria-describedby', messageSelectors.newWindowExternal);
          } else if (isExternal) {
            link.setAttribute('aria-describedby', messageSelectors.external);
          } else if (isTargetBlank) {
            link.setAttribute('aria-describedby', messageSelectors.newWindow);
          }
        });

        generateHTML(messages);
      },

      isVisible(el) {
        var style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      },
    };

    window.theme = window.theme || {};
    window.theme.a11y = a11y;

    window.theme.throttle = (fn, wait) => {
      let prev, next;
      return function invokeFn(...args) {
        const now = Date.now();
        next = clearTimeout(next);
        if (!prev || now - prev >= wait) {
          // eslint-disable-next-line prefer-spread
          fn.apply(null, args);
          prev = now;
        } else {
          next = setTimeout(invokeFn.bind(null, ...args), wait - (now - prev));
        }
      };
    };

    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', {bubbles: true});
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(theme.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      }

      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      isCartInput() {
        return this.input.name === 'updates[]';
      }

      getCartMin() {
        return parseInt(this.input.dataset.min) || 1;
      }

      onInputChange(event) {
        if (this.isCartInput()) {
          const min = this.getCartMin();
          const value = parseInt(this.input.value);

          if (Number.isNaN(value) || value < min) {
            this.input.value = min;
            this.validateQtyRules();
            if (String(min) !== this.input.getAttribute('value')) this.updateCart();
            return;
          }

          // Kolli products sell in whole packs, like the PDP.
          const step = parseInt(this.input.step || this.input.dataset.step, 10) || 1;
          if (value % step !== 0) this.input.value = Math.ceil(value / step) * step;
        }

        this.validateQtyRules();
        if (this.input && this.isCartInput()) {
          this.updateCart();
        }
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        const button = event.target.nodeName === 'BUTTON' ? event.target : event.target.closest('button');
        const name = button ? button.name : undefined;

        const isIncrease = name === 'increase' || name === 'plus';
        const isDecrease = name === 'decrease' || name === 'minus';

        if (isDecrease && this.isCartInput() && parseInt(previousValue) <= this.getCartMin()) return;

        if (isIncrease) {
          if (parseInt(this.input?.dataset?.min) > parseInt(this.input.step) && this.input.value == 0) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.stepUp();
          }
        } else if (isDecrease) {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input?.dataset?.min === previousValue && isDecrease) {
          this.input.value = parseInt(this.input.min);
        }
      }

      validateQtyRules() {
        const value = parseInt(this.input.value);
        const min = this.isCartInput() ? this.getCartMin() : parseInt(this.input.min);
        if (!Number.isNaN(min)) {
          const buttonMinus = this.querySelector("button[name='minus'], button[name='decrease']");
          if (buttonMinus) {
            const atMin = value <= min;
            buttonMinus.classList.toggle('disabled', atMin);
            buttonMinus.setAttribute('aria-disabled', atMin);
          }
        }
        if (this.input.max) {
          const max = parseInt(this.input.max);
          const buttonPlus = this.querySelector(".quantity__button[name='plus']") || this.querySelector(".quantity__button[name='increase']");
          if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
        }
      }

      updateCart() {
        if (!this.input || this.input.value === '') return;
        this.dispatchEvent(
          new CustomEvent('theme:cart:update', {
            bubbles: true,
            detail: {
              id: this.input.dataset.id,
              quantity: this.input.value,
            },
          })
        );
      }
    }

    customElements.define('quantity-input', QuantityInput);

    class HTMLUpdateUtility {
      /**
       * Used to swap an HTML node with a new node.
       * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
       *
       * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
       */
      static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
        preProcessCallbacks?.forEach((callback) => callback(newContent));

        const newNodeWrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
        const newNode = newNodeWrapper.firstChild;

        // dedupe IDs
        const uniqueKey = Date.now();
        oldNode.querySelectorAll('[id], [form]').forEach((element) => {
          element.id && (element.id = `${element.id}-${uniqueKey}`);
          element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
        });

        oldNode.parentNode.insertBefore(newNode, oldNode);
        oldNode.style.display = 'none';

        postProcessCallbacks?.forEach((callback) => callback(newNode));

        setTimeout(() => oldNode.remove(), 500);
      }

      // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      static setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }
    }

    window.HTMLUpdateUtility = HTMLUpdateUtility;

    function appendCartItems() {
      if (document.querySelector('cart-items')) return;

      // Add cart items tag when the cart drawer section is missing so we can still run the JS associated with the error handling
      const cartItems = document.createElement('cart-items');
      document.body.appendChild(cartItems);
    }

    function floatLabels(container) {
      container.querySelectorAll('.form-field').forEach((element) => {
        const label = element.querySelector('label');
        const input = element.querySelector('input, textarea');
        if (label && input) {
          input.addEventListener('keyup', (event) => {
            if (event.target.value !== '') {
              label.classList.add('label--float');
            } else {
              label.classList.remove('label--float');
            }
          });
          if (input.value && input.value.length) {
            label.classList.add('label--float');
          }
        }
      });
    }

    window.theme.windowWidth = getWindowWidth();
    window.theme.windowHeight = getWindowHeight();
    window.theme.isMobile = isMobile();

    let lastScale = window.visualViewport ? window.visualViewport.scale : 1;

    function dispatch$1() {
      // Update isMobile state before dispatching event
      const currentMediaQuery = isMobile();
      if (window.theme.isMobile !== currentMediaQuery) {
        window.theme.isMobile = currentMediaQuery;
      }

      const currentWidth = getWindowWidth();
      const currentHeight = getWindowHeight();

      if (window.theme.windowWidth !== currentWidth) {
        // Skip width-change events caused by scroll-lock adding/removing scrollbar compensation.
        // When a drawer/modal is open, the scrollbar disappears and clientWidth changes by ~15px —
        // that is not a real viewport resize, so don't fire theme:resize:width for it.
        if (!document.documentElement.hasAttribute('data-scroll-locked')) {
          document.dispatchEvent(new CustomEvent('theme:resize:width', {bubbles: true}));
        }
        window.theme.windowWidth = currentWidth;
      }

      if (window.theme.windowHeight !== currentHeight) {
        document.dispatchEvent(new CustomEvent('theme:resize:height', {bubbles: true}));
        window.theme.windowHeight = currentHeight;
      }

      if (!document.documentElement.hasAttribute('data-scroll-locked')) {
        document.dispatchEvent(new CustomEvent('theme:resize', {bubbles: true}));
      }
    }

    function resizeListener() {
      window.addEventListener(
        'resize',
        window.theme.debounce(function () {
          // Check if viewport is zoomed (scaled)
          const currentScale = window.visualViewport ? window.visualViewport.scale : 1;
          const zooming = Math.abs(currentScale - lastScale) > 0.05;

          if (zooming) {
            // Zoom detected — ignore resize
            lastScale = currentScale;
            return;
          }

          lastScale = currentScale;

          dispatch$1();
        }, 50)
      );
    }

    function getWindowWidth() {
      return document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth;
    }

    function getWindowHeight() {
      return document.documentElement.clientHeight || document.body.clientHeight || window.innerHeight;
    }

    function isMobile() {
      return window.innerWidth < theme.sizes.small;
    }

    let prev = window.scrollY;
    let up = null;
    let down = null;
    let wasUp = null;
    let wasDown = null;
    let scrollLockTimer = 0;

    function dispatch() {
      const position = window.scrollY;
      if (position > prev) {
        down = true;
        up = false;
      } else if (position < prev) {
        down = false;
        up = true;
      } else {
        up = null;
        down = null;
      }
      prev = position;
      document.dispatchEvent(
        new CustomEvent('theme:scroll', {
          detail: {
            up,
            down,
            position,
          },
          bubbles: false,
        })
      );
      if (up && !wasUp) {
        document.dispatchEvent(
          new CustomEvent('theme:scroll:up', {
            detail: {position},
            bubbles: false,
          })
        );
      }
      if (down && !wasDown) {
        document.dispatchEvent(
          new CustomEvent('theme:scroll:down', {
            detail: {position},
            bubbles: false,
          })
        );
      }
      wasDown = down;
      wasUp = up;
    }

    function lock(e) {
      // Prevent body scroll lock race conditions
      setTimeout(() => {
        if (scrollLockTimer) {
          clearTimeout(scrollLockTimer);
        }

        // `e.detail` is the scrollable element to keep alive while the page is
        // locked (drawer body / popout list). Passing it is what actually lets
        // that area scroll on touch devices; this library matches scrollables
        // via `scrollableSelectors: ['[data-scroll-lock-scrollable]']` + the
        // registered target. (It has no `allowTouchMove` option.)
        scrollLock.disablePageScroll(e.detail);

        document.documentElement.setAttribute('data-scroll-locked', '');
      });
    }

    function unlock(e) {
      const timeout = e.detail;

      if (timeout) {
        scrollLockTimer = setTimeout(removeScrollLock, timeout);
      } else {
        removeScrollLock();
      }
    }

    function removeScrollLock() {
      scrollLock.clearQueueScrollLocks();
      scrollLock.enablePageScroll();
      document.documentElement.removeAttribute('data-scroll-locked');
    }

    function scrollListener() {
      let timeout;
      window.addEventListener(
        'scroll',
        function () {
          if (timeout) {
            window.cancelAnimationFrame(timeout);
          }
          timeout = window.requestAnimationFrame(function () {
            dispatch();
          });
        },
        {passive: true}
      );

      window.addEventListener('theme:scroll:lock', lock);
      window.addEventListener('theme:scroll:unlock', unlock);
    }

    const wrap = (toWrap, wrapperClass = '', wrapperOption) => {
      const wrapper = wrapperOption || document.createElement('div');
      wrapper.classList.add(wrapperClass);
      toWrap.parentNode.insertBefore(wrapper, toWrap);
      return wrapper.appendChild(toWrap);
    };

    function wrapElements(container) {
      // Target tables to make them scrollable
      const tableSelectors = '.rte table';
      const tables = container.querySelectorAll(tableSelectors);
      tables.forEach((table) => {
        wrap(table, 'rte__table-wrapper');
        table.setAttribute('data-scroll-lock-scrollable', '');
      });

      // Target iframes to make them responsive
      const iframeSelectors = '.rte iframe[src*="youtube.com/embed"], .rte iframe[src*="player.vimeo"], .rte iframe#admin_bar_iframe';
      const frames = container.querySelectorAll(iframeSelectors);
      frames.forEach((frame) => {
        wrap(frame, 'rte__video-wrapper');
      });
    }

    function isTouchDevice() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
    }

    function isTouch() {
      if (isTouchDevice()) {
        document.documentElement.className = document.documentElement.className.replace('no-touch', 'supports-touch');
        window.theme.touch = true;
      } else {
        window.theme.touch = false;
      }
    }

    function ariaToggle(container) {
      const toggleButtons = container.querySelectorAll('[data-aria-toggle]');
      if (toggleButtons.length) {
        toggleButtons.forEach((element) => {
          element.addEventListener('click', function (event) {
            event.preventDefault();
            const currentTarget = event.currentTarget;
            currentTarget.setAttribute('aria-expanded', currentTarget.getAttribute('aria-expanded') == 'false' ? 'true' : 'false');
            const toggleID = currentTarget.getAttribute('aria-controls');
            const toggleElement = document.querySelector(`#${toggleID}`);
            const removeExpandingClass = () => {
              toggleElement.classList.remove('expanding');
              toggleElement.removeEventListener('transitionend', removeExpandingClass);
            };
            const addExpandingClass = () => {
              toggleElement.classList.add('expanding');
              toggleElement.removeEventListener('transitionstart', addExpandingClass);
            };

            toggleElement.addEventListener('transitionstart', addExpandingClass);
            toggleElement.addEventListener('transitionend', removeExpandingClass);

            toggleElement.classList.toggle('expanded');
          });
        });
      }
    }

    function loading() {
      document.body.classList.add('is-loaded');
    }

    const classes$k = {
      loading: 'is-loading',
    };

    const selectors$n = {
      img: 'img.is-loading',
    };

    /*
      Catch images loaded events and add class "is-loaded" to them and their containers
    */
    function loadedImagesEventHook() {
      document.addEventListener(
        'load',
        (e) => {
          if (e.target.tagName.toLowerCase() == 'img' && e.target.classList.contains(classes$k.loading)) {
            e.target.classList.remove(classes$k.loading);
            e.target.parentNode.classList.remove(classes$k.loading);

            if (e.target.parentNode.parentNode.classList.contains(classes$k.loading)) {
              e.target.parentNode.parentNode.classList.remove(classes$k.loading);
            }
          }
        },
        true
      );
    }

    /*
      Remove "is-loading" class to the loaded images and their containers
    */
    function removeLoadingClassFromLoadedImages(container) {
      container.querySelectorAll(selectors$n.img).forEach((img) => {
        if (img.complete) {
          img.classList.remove(classes$k.loading);
          img.parentNode.classList.remove(classes$k.loading);

          if (img.parentNode.parentNode.classList.contains(classes$k.loading)) {
            img.parentNode.parentNode.classList.remove(classes$k.loading);
          }
        }
      });
    }

    const selectors$m = {
      aos: '[data-aos]:not(.aos-animate)',
      aosAnchor: '[data-aos-anchor]',
      aosIndividual: '[data-aos]:not([data-aos-anchor]):not(.aos-animate)',
    };

    const classes$j = {
      aosAnimate: 'aos-animate',
    };

    const observerConfig = {
      attributes: false,
      childList: true,
      subtree: true,
    };

    let anchorContainers = [];

    const mutationCallback = (mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          const element = mutation.target;
          const elementsToAnimate = element.querySelectorAll(selectors$m.aos);
          const anchors = element.querySelectorAll(selectors$m.aosAnchor);

          if (elementsToAnimate.length) {
            elementsToAnimate.forEach((element) => {
              aosItemObserver.observe(element);
            });
          }

          if (anchors.length) {
            // Get all anchors and attach observers
            initAnchorObservers(anchors);
          }
        }
      }
    };

    /*
      Observe each element that needs to be animated
    */
    const aosItemObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(classes$j.aosAnimate);

            // Stop observing element after it was animated
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    /*
      Observe anchor elements
    */
    const aosAnchorObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio) {
            const elementsToAnimate = entry.target.querySelectorAll(selectors$m.aos);

            if (elementsToAnimate.length) {
              elementsToAnimate.forEach((item) => {
                item.classList.add(classes$j.aosAnimate);
              });
            }

            // Stop observing anchor element after inner elements were animated
            observer.unobserve(entry.target);

            // Remove the container from the anchorContainers array
            const sectionIndex = anchorContainers.indexOf('#' + entry.target.id);
            if (sectionIndex !== -1) {
              anchorContainers.splice(sectionIndex, 1);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    /*
      Watch for mutations in the body and start observing the newly added animated elements and anchors
    */
    function bodyMutationObserver() {
      const bodyObserver = new MutationObserver(mutationCallback);
      bodyObserver.observe(document.body, observerConfig);
    }

    /*
      Observe animated elements that have attribute [data-aos]
    */
    function elementsIntersectionObserver() {
      const elementsToAnimate = document.querySelectorAll(selectors$m.aosIndividual);

      if (elementsToAnimate.length) {
        elementsToAnimate.forEach((element) => {
          aosItemObserver.observe(element);
        });
      }
    }

    /*
      Observe animated elements that have attribute [data-aos]
    */
    function anchorsIntersectionObserver() {
      const anchors = document.querySelectorAll(selectors$m.aosAnchor);

      if (anchors.length) {
        // Get all anchors and attach observers
        initAnchorObservers(anchors);
      }
    }

    function initAnchorObservers(anchors) {
      if (!anchors.length || Shopify.visualPreviewMode) return;

      anchors.forEach((anchor) => {
        const containerId = anchor.dataset.aosAnchor;

        // Avoid adding multiple observers to the same element
        if (containerId && anchorContainers.indexOf(containerId) === -1) {
          const container = document.querySelector(containerId);

          if (container) {
            aosAnchorObserver.observe(container);
            anchorContainers.push(containerId);
          }
        }
      });
    }

    function initAnimations() {
      elementsIntersectionObserver();
      anchorsIntersectionObserver();
      bodyMutationObserver();

      // Remove unloaded section from the anchors array on section:unload event
      document.addEventListener('shopify:section:unload', (e) => {
        if (typeof e.target !== 'object' || e.target.nodeValue === 'undefined') return;

        const sectionId = '#' + e.target.querySelector('[data-section-id]')?.id;
        const sectionIndex = anchorContainers.indexOf(sectionId);

        if (sectionIndex !== -1) {
          anchorContainers.splice(sectionIndex, 1);
        }
      });
    }

    // Safari requestIdleCallback polyfill
    window.requestIdleCallback =
      window.requestIdleCallback ||
      function (cb) {
        var start = Date.now();
        return setTimeout(function () {
          cb({
            didTimeout: false,
            timeRemaining: function () {
              return Math.max(0, 50 - (Date.now() - start));
            },
          });
        }, 1);
      };
    window.cancelIdleCallback =
      window.cancelIdleCallback ||
      function (id) {
        clearTimeout(id);
      };

    if (window.theme.settings.enableAnimations) {
      initAnimations();
    }

    resizeListener();
    scrollListener();
    isTouch();
    loadedImagesEventHook();

    window.addEventListener('DOMContentLoaded', () => {
      ariaToggle(document);
      floatLabels(document);
      wrapElements(document);
      removeLoadingClassFromLoadedImages(document);
      loading();
      appendCartItems();

      requestIdleCallback(() => {
        if (Shopify.visualPreviewMode) {
          document.documentElement.classList.add('preview-mode');
        }
      });
    });

    document.addEventListener('shopify:section:load', (e) => {
      const container = e.target;
      if (typeof container !== 'object' || container.nodeValue === 'undefined') return;

      floatLabels(container);
      wrapElements(container);
      ariaToggle(document);
    });

    const classes$i = {
      focus: 'is-focused',
    };

    const selectors$l = {
      inPageLink: '[data-skip-content]',
      linkesWithOnlyHash: 'a[href="#"]',
    };

    class Accessibility {
      constructor() {
        this.init();
      }

      init() {
        this.a11y = window.theme.a11y;

        // DOM Elements
        this.html = document.documentElement;
        this.body = document.body;
        this.inPageLink = document.querySelector(selectors$l.inPageLink);
        this.linkesWithOnlyHash = document.querySelectorAll(selectors$l.linkesWithOnlyHash);

        // A11Y init methods
        this.a11y.focusHash();
        this.a11y.bindInPageLinks();

        // Events
        this.clickEvents();
        this.focusEvents();
      }

      /**
       * Clicked events accessibility
       *
       * @return  {Void}
       */

      clickEvents() {
        if (this.inPageLink) {
          this.inPageLink.addEventListener('click', (event) => {
            event.preventDefault();
          });
        }

        if (this.linkesWithOnlyHash) {
          this.linkesWithOnlyHash.forEach((item) => {
            item.addEventListener('click', (event) => {
              event.preventDefault();
            });
          });
        }
      }

      /**
       * Focus events
       *
       * @return  {Void}
       */

      focusEvents() {
        let usingKeyboard = false;
        let lastPointerTime = 0;
        let lastPointerWasTouch = false;

        const handlePointer = (isTouch) => {
          usingKeyboard = false;
          lastPointerTime = Date.now();
          lastPointerWasTouch = isTouch;
          this.body.classList.remove(classes$i.focus);
        };

        // Prefer Pointer Events: a single `pointerdown` reports its real
        // `pointerType` and is NOT fired twice for a touch (unlike `mousedown`,
        // which browsers synthesize after a tap and which would otherwise
        // overwrite the "this was touch" flag).
        if (window.PointerEvent) {
          document.addEventListener('pointerdown', (event) => {
            handlePointer(event.pointerType !== 'mouse');
          });
        } else {
          document.addEventListener('mousedown', () => handlePointer(false));
          document.addEventListener('touchstart', () => handlePointer(true), { passive: true });
        }

        document.addEventListener('keydown', () => {
          usingKeyboard = true;
        });

        document.addEventListener('focusin', () => {
          // Show the focus ring for keyboard (Tab) users, or when focus moves
          // with no recent pointer interaction at all (desktop assistive tech).
          // Never show it for touch — including focus moved programmatically
          // after a tap (e.g. the mobile menu re-focusing its trigger), which
          // is what produced the stray blue focus underline.
          const recentPointer = Date.now() - lastPointerTime < 1000;
          if (usingKeyboard || (!recentPointer && !lastPointerWasTouch)) {
            this.body.classList.add(classes$i.focus);
          }
        });
      }
    }

    window.a11y = new Accessibility();

    /*
      Trigger event after animation completes
    */
    window.theme.waitForAnimationEnd = function (element) {
      return new Promise((resolve) => {
        if (!element) {
          resolve();
          return;
        }

        const computed = getComputedStyle(element);

        const duration = parseFloat(computed.animationDuration) * 1000;
        const delay = parseFloat(computed.animationDelay) * 1000;
        const iterations = computed.animationIterationCount;

        // Cases with no real animation
        if (computed.display === 'none' || computed.animationName === 'none' || duration === 0 || iterations === 'infinite') {
          resolve();
          return;
        }

        const totalDuration = duration + delay;

        function onAnimationEnd(e) {
          if (e.currentTarget !== element) return;

          element.removeEventListener('animationend', onAnimationEnd);
          clearTimeout(fallback);
          resolve();
        }

        element.addEventListener('animationend', onAnimationEnd);

        // Fallback to avoid stuck Promise
        const fallback = setTimeout(() => {
          element.removeEventListener('animationend', onAnimationEnd);
          resolve();
        }, totalDuration + 50);
      });
    };

    /*
      Trigger event after all animations complete in a specific section
    */
    window.theme.waitForAllAnimationsEnd = function (section) {
      return new Promise((resolve, rejected) => {
        const animatedElements = section.querySelectorAll('[data-aos]');
        let animationCount = 0;

        function onAnimationEnd(event) {
          animationCount++;

          if (animationCount === animatedElements.length) {
            // All animations have ended
            resolve();
          }

          event.target.removeEventListener('animationend', onAnimationEnd);
        }

        animatedElements.forEach((element) => {
          element.addEventListener('animationend', onAnimationEnd);
        });

        if (!animationCount) rejected();
      });
    };

    function FetchError(object) {
      this.status = object.status || null;
      this.headers = object.headers || null;
      this.json = object.json || null;
      this.body = object.body || null;
    }
    FetchError.prototype = Error.prototype;

/* utils/bundle-properties.js
 * ---------------------------------------------------------------------------
 * Single source of truth for the "single-line bundle" cart-line contract
 * consumed by the `cart-transformer` Shopify Function in theposterclub-app.
 *
 * Two theme features build bundles client-side and add them to the cart:
 *   1. <print-configurator>  — PDP size + passepartout + frame picker.
 *   2. art-wall-designer.js — saved-wall checkout (one bundle per wall item).
 *
 * Both call `window.theme.buildBundleProperties()` so the contract
 * (`_bundleId`, `_tpc_frame_poster_item_relation`, `_bundleItems`,
 * `_bundleAmounts`, `_bundlePrices`, `_bundlePrice`, `_source`,
 * `_awdPasse`, `_awdFrame`) lives in exactly one place. If you change
 * the Shopify Function's expected property shape, update this file
 * (and only this file) on the theme side.
 *
 * This util deliberately does NOT build the human-readable addon label
 * — each caller has its own labelling rules (the PDP dedupes
 * "Oak Frame Oak Frame", the wall designer concatenates type + color
 * verbatim). Callers pass an already-built `label` string per addon.
 * Use `window.theme.buildAddonLabel()` if you want the deduped form.
 * ---------------------------------------------------------------------------
 */

window.theme = window.theme || {};

/**
 * Join truthy parts with spaces, dropping later parts whose lowercased
 * value already appeared. Produces "Oak Frame A4" from
 * ["Oak Frame", "Oak Frame", "A4"] (no "Oak Frame Oak Frame A4").
 *
 * @param {Array<string>} parts
 * @param {string} fallback returned when every part is empty / falsy
 * @returns {string}
 */
/**
 * Generate a unique 15-character id used to relate the expanded child
 * line items of one bundle back together at checkout.
 *
 * The cart-transformer Shopify Function expands a single bundle line
 * into its constituent variants via `lineExpand`, which REPLACES the
 * parent line - so the resulting order contains only child line items
 * and no real parent. The Function copies this id onto every child it
 * expands (`_tpc_frame_poster_item_relation`), so all lines that came
 * from the same bundle share one value. Without it, two identical
 * bundles in one cart (same poster + frame) are indistinguishable,
 * because the only other link is the non-unique `_bundle` title.
 *
 * Exactly 15 chars by downstream order-processing requirement (a full
 * UUID is 36 chars and does not fit). Drawn from a case-insensitive
 * base-36 alphabet so the id survives systems that upper/lower-case it.
 * Uses `crypto.getRandomValues` for collision-resistant randomness with
 * a `Math.random` fallback for very old browsers.
 *
 * @returns {string} 15-char [0-9a-z] id, e.g. "k3n8x1qp7d2m9af"
 */
window.theme.buildRelationId = function buildRelationId() {
  var ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
  var LENGTH = 15;
  var out = '';
  var i;
  if (window.crypto && window.crypto.getRandomValues) {
    var bytes = new Uint8Array(LENGTH);
    window.crypto.getRandomValues(bytes);
    for (i = 0; i < LENGTH; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  } else {
    for (i = 0; i < LENGTH; i++) {
      out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
    }
  }
  return out;
};

/**
 * Build the cart line items for a MULTI-LINE bundle (print + add-ons), to be
 * grouped at checkout by the `cart-transformer` Function's `merge` operation.
 *
 * Unlike `buildBundleProperties` (the legacy single-line `lineExpand`
 * contract), this returns ONE cart line PER component — the print and each
 * add-on are real, separately-priced cart lines that share a `_bundleId` and
 * carry a `_role`. The Function `merge`s the group into one bundle line whose
 * price = SUM of the components' native cart-line costs (correct currency +
 * discounts, no baked prices). The print line additionally carries
 * `_bundleImage` (the Function input exposes no product image) used as the
 * merged line's image.
 *
 * @param {Object} opts
 * @param {number|string} opts.printVariantId  Print (parent) variant id.
 * @param {number}        [opts.quantity]      Bundles to add (default 1).
 * @param {Array<Object>} [opts.addons]        [{ variantId, awdKey | role, sku }]
 *                                             awdKey: 'passe' | 'frame'.
 * @param {string}        [opts.source]        Value for `_source` (omitted if empty).
 * @param {string}        [opts.artist]        Visible `Artist` on the print line.
 * @param {string}        [opts.printSku]      Visible `SKU` on the print line.
 * @param {string}        [opts.printImageUrl] Print image URL → `_bundleImage`.
 * @returns {Array<{id:number, quantity:number, properties:Object}>}
 */
window.theme.buildBundleItems = function buildBundleItems(opts) {
  if (!opts || opts.printVariantId == null) return [];

  var quantity = Number(opts.quantity) || 1;
  var addons = Array.isArray(opts.addons) ? opts.addons : [];
  var source = opts.source || '';
  var artist = opts.artist ? String(opts.artist).trim() : '';
  var printImageUrl = opts.printImageUrl ? String(opts.printImageUrl).trim() : '';

  var bundleId =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    'b_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);

  // One id shared by every component line so the order keeps the poster and
  // its frame / passepartout relatable after the Function merges them.
  var relationId = window.theme.buildRelationId();

  // awdKey → cart `_role`. Roles drive the cart drawer's multi-line sibling
  // rendering and the `cart.bundle.<role>` translation keys.
  var ROLE = { passe: 'passepartout', passepartout: 'passepartout', frame: 'frame' };

  function makeProps(role) {
    var p = {
      _bundleId: bundleId,
      _role: role,
      _tpc_frame_poster_item_relation: relationId,
    };
    if (source) p._source = source;
    return p;
  }

  var printProps = makeProps('print');
  if (artist) printProps.Artist = artist;
  if (opts.printSku) printProps.SKU = String(opts.printSku);

  // Display payload for the CART drawer/page, baked as FLAT properties on the
  // print line (Liquid can't parse JSON). The cart-transformer `merge`s the
  // component lines into one parent line and STRIPS their per-line properties
  // + components (Liquid `bundle_components` is empty), so the theme can't
  // reconstruct the bundle card from the cart afterwards. The Function copies
  // these onto the merged line; cart-line-items.liquid renders the card from
  // them (image, addon swatch+label per role). DISPLAY ONLY — pricing stays
  // native (merge sums the real lines). The image is browser-rendered (an
  // <img src>), so a relative/preview URL is fine (unlike the merge `image`).
  if (printImageUrl) printProps._bundleImage = printImageUrl;
  addons.forEach(function (addon) {
    if (!addon || addon.variantId == null) return;
    var role = ROLE[addon.awdKey] || addon.role || 'addon';
    // Format `label|hex|priceCents`. The /cart page reads the price segment for
    // merged lines, which carry no `_bundlePrices`.
    var cents = addon.priceCents != null ? String(Number(addon.priceCents) || 0) : '';
    var val = (addon.label || '') + '|' + (addon.hex || '') + '|' + cents;
    if (role === 'passepartout') printProps._awdPasse = val;
    else if (role === 'frame') printProps._awdFrame = val;
  });

  var items = [
    { id: Number(opts.printVariantId), quantity: quantity, properties: printProps },
  ];

  addons.forEach(function (addon) {
    if (!addon || addon.variantId == null) return;
    var role = ROLE[addon.awdKey] || addon.role || 'addon';
    var props = makeProps(role);
    if (addon.sku) props.SKU = String(addon.sku);
    items.push({
      id: Number(addon.variantId),
      quantity: quantity,
      properties: props,
    });
  });

  return items;
};

window.theme.buildAddonLabel = function buildAddonLabel(parts, fallback) {
  var seen = Object.create(null);
  var out = [];
  (parts || []).forEach(function (p) {
    if (!p) return;
    var key = String(p).trim().toLowerCase();
    if (!key || seen[key]) return;
    seen[key] = true;
    out.push(p);
  });
  return out.join(' ') || fallback;
};

/**
 * Build the line-item `properties` payload for a single-line bundle.
 *
 * The cart-transformer Shopify Function reads `_bundleId` plus the
 * JSON `_bundleItems` / `_bundleAmounts` / `_bundlePrices` triplet
 * from the parent cart line and expands the cart at checkout into the
 * constituent variants with fixed per-unit prices. `_bundlePrice`
 * (total cents) is the authoritative price the cart drawer displays
 * for the parent line.
 *
 * `_awdPasse` / `_awdFrame` carry `label|hex|priceCents` strings for the cart
 * drawer's swatch + label block. They are underscore-prefixed so
 * Shopify's checkout hides them automatically. The bundle's expanded
 * child line items already make the Passepartout / Frame selections
 * visible at checkout, so we deliberately do NOT add visible
 * `Passepartout` / `Frame` line attributes (which would otherwise
 * show as duplicate "Passepartout: …" / "Frame: …" text on the
 * parent line).
 *
 * Price format: `_bundlePrices` entries are decimal strings produced
 * via `(cents / 100).toFixed(2)`. The Function applies these as
 * `fixedPricePerUnit` on each expanded child so the bundle's checkout
 * total = sum(printPrice + addons), not the parent variant's price.
 *
 * @param {Object} opts
 * @param {number|string} opts.printVariantId    Parent variant id.
 * @param {number}        opts.printPriceCents   Print's price in cart-currency cents.
 * @param {Array<Object>} [opts.addons]          [{ variantId, priceCents, awdKey, label, hex }]
 *                                               awdKey: 'passe' | 'frame'
 * @param {string}        [opts.source]          Value for `_source` (omitted if empty).
 * @returns {{ properties: Object, totalCents: number }}
 */
window.theme.buildBundleProperties = function buildBundleProperties(opts) {
  var printVariantId = opts.printVariantId;
  var printPriceCents = Number(opts.printPriceCents) || 0;
  var addons = Array.isArray(opts.addons) ? opts.addons : [];
  var source = opts.source || '';

  var bundleId =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    'b_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);

  var bundleItems = [String(printVariantId)];
  var bundleAmounts = [1];
  var bundlePrices = [(printPriceCents / 100).toFixed(2)];
  var totalCents = printPriceCents;

  var awd = {};
  addons.forEach(function (addon) {
    if (!addon || addon.variantId == null) return;
    var cents = Number(addon.priceCents) || 0;
    bundleItems.push(String(addon.variantId));
    bundleAmounts.push(1);
    bundlePrices.push((cents / 100).toFixed(2));
    totalCents += cents;
    if (addon.awdKey) {
      // Format: `label|hex|priceCents`. The per-addon price travels on
      // the addon's own display property so the /cart page can show it
      // without depending on the `_bundlePrices` array (which the bundle
      // line doesn't always carry reliably — only `_bundlePrice`, the
      // subtotal, is guaranteed). `cents` is the same value pushed into
      // `_bundlePrices` above, sourced from the real addon variant.
      awd[addon.awdKey] = (addon.label || '') + '|' + (addon.hex || '') + '|' + cents;
    }
  });

  var properties = {
    _bundleId: bundleId,
    // Unique 15-char id shared by every child line the cart-transformer
    // Function expands from this bundle, so the poster and its frame /
    // passepartout stay relatable in the order even though `lineExpand`
    // leaves no real parent line behind. The Function reads this off the
    // parent line and copies it onto each expanded child.
    _tpc_frame_poster_item_relation: window.theme.buildRelationId(),
    _bundleItems: JSON.stringify(bundleItems),
    _bundleAmounts: JSON.stringify(bundleAmounts),
    _bundlePrices: JSON.stringify(bundlePrices),
    _bundlePrice: String(totalCents),
  };
  if (source) properties._source = source;
  if (awd.passe) properties._awdPasse = awd.passe;
  if (awd.frame) properties._awdFrame = awd.frame;

  return { properties: properties, totalCents: totalCents };
};
    const classes$h = {
      animated: 'is-animated',
      active: 'is-active',
      added: 'is-added',
      disabled: 'is-disabled',
      empty: 'is-empty',
      error: 'has-error',
      headerStuck: 'js__header__stuck',
      hidden: 'is-hidden',
      hiding: 'is-hiding',
      loading: 'is-loading',
      open: 'is-open',
      removed: 'is-removed',
      success: 'is-success',
      visible: 'is-visible',
      expanded: 'is-expanded',
      updated: 'is-updated',
      variantSoldOut: 'variant--soldout',
      variantUnavailable: 'variant--unavailable',
    };

    const selectors$k = {
      apiContent: '[data-api-content]',
      apiLineItems: '[data-api-line-items]',
      apiUpsellItems: '[data-api-upsell-items]',
      apiBundleItems: '[data-api-bundle-items]',
      apiCartPrice: '[data-api-cart-price]',
      animation: '[data-animation]',
      buttonSkipUpsellProduct: '[data-skip-upsell-product]',
      cartBarAdd: '[data-cart-bar-add-to-cart]',
      cartClear: '[data-cart-clear]',
      cartCloseError: '[data-cart-error-close]',
      cartDrawer: 'cart-drawer',
      cartDrawerClose: '[data-cart-drawer-close]',
      cartEmpty: '[data-cart-empty]',
      cartErrors: '[data-cart-errors]',
      cartItemRemove: '[data-item-remove]',
      cartPage: '[data-cart-page]',
      cartForm: '[data-cart-form]',
      cartTermsCheckbox: '[data-cart-acceptance-checkbox]',
      cartCheckoutButtonWrapper: '[data-cart-checkout-buttons]',
      cartCheckoutButton: '[data-cart-checkout-button]',
      cartTotal: '[data-cart-total]',
      checkoutButtons: '[data-checkout-buttons]',
      errorMessage: '[data-error-message]',
      formCloseError: '[data-close-error]',
      formErrorsContainer: '[data-cart-errors-container]',
      formWrapper: '[data-form-wrapper]',
      freeShipping: '[data-free-shipping]',
      freeShippingGraph: '[data-progress-graph]',
      freeShippingProgress: '[data-progress-bar]',
      headerWrapper: '[data-header-wrapper]',
      item: '[data-item]',
      itemsHolder: '[data-items-holder]',
      leftToSpend: '[data-left-to-spend]',
      navDrawer: '[data-drawer]',
      outerSection: '[data-section-id]',
      priceHolder: '[data-cart-price-holder]',
      quickAddHolder: '[data-quick-add-holder]',
      quickAddModal: '[data-quick-add-modal]',
      qtyInput: 'input[name="updates[]"]',
      upsellProductsHolder: '[data-upsell-products]',
      bundleProductsHolder: '[data-bundle-products]',
      upsellWidget: '[data-upsell-widget]',
      bundleWidget: '[data-bundle-widget]',
      termsErrorMessage: '[data-terms-error-message]',
      collapsibleBody: '[data-collapsible-body]',
      discountInput: '[data-discount-input]',
      discountField: '[data-discount-field]',
      discountButton: '[data-apply-discount]',
      discountBody: '[data-discount-body]',
      discountCode: '[data-discount-code]',
      discountErrorMessage: '[data-discount-error-message]',
      removeDiscount: '[data-remove-discount]',
    };

    const attributes$d = {
      cartTotal: 'data-cart-total',
      disabled: 'disabled',
      freeShipping: 'data-free-shipping',
      freeShippingLimit: 'data-free-shipping-limit',
      item: 'data-item',
      itemIndex: 'data-item-index',
      itemTitle: 'data-item-title',
      open: 'open',
      quickAddHolder: 'data-quick-add-holder',
      quickAddVariant: 'data-quick-add-variant',
      scrollLocked: 'data-scroll-locked',
      upsellAutoOpen: 'data-upsell-auto-open',
      name: 'name',
      maxInventoryReached: 'data-max-inventory-reached',
      errorMessagePosition: 'data-error-message-position',
      discountButton: 'data-cart-discount-button',
    };

    class CartItems extends HTMLElement {
      constructor() {
        super();

        this.a11y = window.theme.a11y;
      }

      connectedCallback() {
        // DOM Elements
        this.cartPage = document.querySelector(selectors$k.cartPage);
        this.cartForm = document.querySelector(selectors$k.cartForm);
        this.cartDrawer = document.querySelector(selectors$k.cartDrawer);

        // On the /cart page the cart drawer is ALSO present in the
        // DOM (rendered by the header group). Both contain their own
        // `[data-cart-price-holder]`, `[data-items-holder]`,
        // `[data-cart-total]`, etc. A naive `document.querySelector`
        // returns the first match in document order — usually the
        // drawer's — so the cart-page instance of `<cart-items>` ends
        // up rewriting the drawer's summary instead of its own when
        // it AJAX-rebuilds after a discount is applied. Scope the
        // lookups to whichever container actually holds *this*
        // instance so each cart-items operates on its own subtree.
        const containingDrawer = this.closest(selectors$k.cartDrawer);
        const containingPage = this.closest(selectors$k.cartPage);
        const scope = containingDrawer || containingPage || document;

        this.cartEmpty = scope.querySelector(selectors$k.cartEmpty) || document.querySelector(selectors$k.cartEmpty);
        this.cartTermsCheckbox = document.querySelector(selectors$k.cartTermsCheckbox);
        this.cartCheckoutButtonWrapper = document.querySelector(selectors$k.cartCheckoutButtonWrapper);
        this.cartCheckoutButton = document.querySelector(selectors$k.cartCheckoutButton);
        this.checkoutButtons = document.querySelector(selectors$k.checkoutButtons);
        this.itemsHolder = this.matches(selectors$k.itemsHolder) ? this : scope.querySelector(selectors$k.itemsHolder);
        this.priceHolder = scope.querySelector(selectors$k.priceHolder) || document.querySelector(selectors$k.priceHolder);
        this.items = scope.querySelectorAll(selectors$k.item);
        this.cartTotal = scope.querySelector(selectors$k.cartTotal) || document.querySelector(selectors$k.cartTotal);
        this.freeShipping = document.querySelectorAll(selectors$k.freeShipping);
        this.cartErrorHolder = document.querySelector(selectors$k.cartErrors);
        this.cartCloseErrorMessage = document.querySelector(selectors$k.cartCloseError);
        this.headerWrapper = document.querySelector(selectors$k.headerWrapper);
        this.navDrawer = document.querySelector(selectors$k.navDrawer);
        this.upsellProductsHolder = document.querySelector(selectors$k.upsellProductsHolder);
        this.bundleProductsHolder = document.querySelector(selectors$k.bundleProductsHolder);
        this.subtotal = window.theme.subtotal;
        // The discount form (`snippets/cart-block-discount.liquid`) can
        // be rendered in both the cart drawer AND the /cart page at the
        // same time. Using `querySelector` would bind only the first
        // input/button pair in the DOM (usually the drawer's, since the
        // header-group renders before the main content), leaving the
        // /cart page's "Apply" button inert. Track every instance and
        // wire each one independently.
        this.discountInputs = document.querySelectorAll(selectors$k.discountInput);
        this.discountFields = document.querySelectorAll(selectors$k.discountField);
        this.discountButtons = document.querySelectorAll(selectors$k.discountButton);
        this.discountErrorMessages = document.querySelectorAll(selectors$k.discountErrorMessage);
        this.hasDiscountBlock = this.discountButtons.length > 0;
        this.existingDiscountCodes = [];
        this.discounts = document.querySelectorAll(selectors$k.discountBody);

        // Define Cart object depending on if we have cart drawer or cart page
        this.cart = this.cartDrawer || this.cartPage;

        // Discounts
        if (this.hasDiscountBlock) {
          this.discountButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();

              // Resolve the input belonging to the same discount widget
              // as the clicked button so the value comes from the form
              // the customer actually interacted with.
              const widget = button.closest('.cart__widget--discount') || button.parentElement;
              const input = widget?.querySelector(selectors$k.discountInput) || this.discountInputs[0];
              if (!input) return;

              const newDiscountCode = input.value.trim();
              input.value = '';

              if (newDiscountCode) {
                this.applyDiscount(newDiscountCode);
              }
            });
          });

          // Fill existing discount codes and bind event listeners
          this.bindDiscountEventListeners();
        }

        // Cart events
        this.animateItems = this.animateItems.bind(this);
        this.addToCart = this.addToCart.bind(this);
        this.cartAddEvent = this.cartAddEvent.bind(this);
        this.updateProgress = this.updateProgress.bind(this);
        this.onCartDrawerClose = this.onCartDrawerClose.bind(this);

        // Set global event listeners for "Add to cart" and Announcement bar wheel progress
        document.addEventListener('theme:cart:add', this.cartAddEvent);
        document.addEventListener('theme:announcement:init', this.updateProgress);

        if (theme.settings.cartType == 'drawer') {
          document.addEventListener('theme:cart-drawer:open', this.animateItems);
          document.addEventListener('theme:cart-drawer:close', this.onCartDrawerClose);
        }

        // Upsell or bundle products
        this.skipUpsellProductsArray = [];
        this.skipBundleProductsArray = [];
        this.skipUpsellOrBundleProductEvent();
        this.checkSkippedUpsellOrBundleProductsFromStorage();
        this.toggleCartUpsellOrBundleWidgetVisibility();

        // Free Shipping values
        this.circumference = 28 * Math.PI; // radius - stroke * 4 * PI
        this.freeShippingLimit = this.freeShipping.length ? Number(this.freeShipping[0].getAttribute(attributes$d.freeShippingLimit)) * 100 * window.Shopify.currency.rate : 0;

        this.freeShippingMessageHandle(this.subtotal);
        this.updateProgress();

        this.build = this.build.bind(this);
        this.updateCart = this.updateCart.bind(this);
        this.productAddCallback = this.productAddCallback.bind(this);
        this.formSubmitHandler = window.theme.throttle(this.formSubmitHandler.bind(this), 50);

        if (this.cartPage) {
          this.animateItems();
        }

        if (this.cart) {
          // Checking
          this.hasItemsInCart = this.hasItemsInCart.bind(this);
          this.cartCount = this.getCartItemCount();
        }

        // Set classes
        this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

        // Flags
        this.totalItems = this.items.length;
        this.showCannotAddMoreInCart = false;
        this.cartUpdateFailed = false;
        this.discountError = false;

        // Cart Events
        this.cartEvents();
        this.cartRemoveEvents();
        this.cartUpdateEvents();
        this.cartClearEvents(containingDrawer || containingPage);

        document.addEventListener('theme:product:add', this.productAddCallback);
        document.addEventListener('theme:product:add-error', this.productAddCallback);
        document.addEventListener('theme:cart:refresh', this.getCart.bind(this));
      }

      disconnectedCallback() {
        document.removeEventListener('theme:cart:add', this.cartAddEvent);
        document.removeEventListener('theme:cart:refresh', this.cartAddEvent);
        document.removeEventListener('theme:announcement:init', this.updateProgress);
        document.removeEventListener('theme:product:add', this.productAddCallback);
        document.removeEventListener('theme:product:add-error', this.productAddCallback);

        if (document.documentElement.hasAttribute(attributes$d.scrollLocked)) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }
      }

      onCartDrawerClose() {
        this.resetAnimatedItems();

        if (this.cartDrawer?.classList.contains(classes$h.open)) {
          this.cart.classList.remove(classes$h.updated);
        }

        this.cartEmpty.classList.remove(classes$h.updated);
        this.cartErrorHolder.classList.remove(classes$h.expanded);
        this.cart.querySelectorAll(selectors$k.animation).forEach((item) => {
          const removeHidingClass = () => {
            item.classList.remove(classes$h.hiding);
            item.removeEventListener('animationend', removeHidingClass);
          };

          item.classList.add(classes$h.hiding);
          item.addEventListener('animationend', removeHidingClass);
        });

        if (this.hasDiscountBlock) {
          this.discountErrorMessages?.forEach((msg) => msg.classList.add('hidden'));
        }
      }

      /**
       * Cart update event hook
       *
       * @return  {Void}
       */

      cartUpdateEvents() {
        this.items = document.querySelectorAll(selectors$k.item);

        this.items.forEach((item) => {
          item.addEventListener('theme:cart:update', (event) => {
            const quantity = parseInt(event.detail.quantity);

            if (Number.isNaN(quantity) || quantity < 1) {
              this.resetLineItem(item);
              return;
            }

            this.updateCart(
              {
                id: event.detail.id,
                quantity: event.detail.quantity,
              },
              item
            );
          });
        });
      }

      /**
       * Cart events
       *
       * @return  {Void}
       */

      cartRemoveEvents() {
        const cartItemRemove = this.querySelectorAll(selectors$k.cartItemRemove);

        cartItemRemove.forEach((button) => {
          const item = button.closest(selectors$k.item);
          button.addEventListener('click', (event) => {
            event.preventDefault();

            if (button.classList.contains(classes$h.disabled)) return;

            this.updateCart(
              {
                id: button.dataset.id,
                quantity: 0,
              },
              item
            );
          });
        });

        if (this.cartCloseErrorMessage) {
          this.cartCloseErrorMessage.addEventListener('click', (event) => {
            event.preventDefault();

            this.cartErrorHolder.classList.remove(classes$h.expanded);
          });
        }
      }

      /**
       * Clear the whole cart from the clear button inside the given container
       *
       * @param   {DOM Element}  container
       *
       * @return  {Void}
       */

      cartClearEvents(container) {
        if (!container) return;

        container.addEventListener('click', (event) => {
          const button = event.target.closest(selectors$k.cartClear);
          if (!button || button.disabled) return;

          event.preventDefault();
          this.clearCart(button);
        });
      }

      clearCart(button) {
        this.cart.classList.add(classes$h.loading);
        this.disableCartButtons();
        button.disabled = true;

        fetch(`${theme.routes.cart_url}/clear.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
          })
          .catch((error) => {
            console.log(error);
            this.cartUpdateFailed = true;
            this.updateErrorText(button.textContent.trim());
            this.toggleErrorMessage();
            this.enableCartButtons();
          })
          .finally(() => {
            button.disabled = false;
          });
      }

      /**
       * Cart event add product to cart
       *
       * @return  {Void}
       */

      cartAddEvent(event) {
        let formData = '';
        let button = event.detail.button;

        if (button.hasAttribute('disabled')) return;
        const form = button.form || button.closest('form');
        // Validate form

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        formData = new FormData(form);

        if (form !== null && form.querySelector('[type="file"]')) {
          return;
        }
        if (theme.settings.cartType === 'drawer' && this.cartDrawer) {
          event.preventDefault();
        }

        const maxInventoryReached = form.getAttribute(attributes$d.maxInventoryReached);
        const errorMessagePosition = form.getAttribute(attributes$d.errorMessagePosition);
        this.showCannotAddMoreInCart = false;
        if (maxInventoryReached === 'true' && errorMessagePosition === 'cart') {
          this.showCannotAddMoreInCart = true;
        }

        this.addToCart(formData, button);
      }


      // =========================================================================
      // DISCOUNT METHODS
      // Handles discount code application, removal, and cart discount updates
      // =========================================================================

      /**
       * Bind event listeners for discount elements
       *
       * @return  {Void}
       */
      bindDiscountEventListeners() {
        if (!this.hasDiscountBlock) return;

        this.discounts = document.querySelectorAll(selectors$k.discountBody);

        this.discounts.forEach((discount) => {
          const discountCode = discount.dataset.discountCode;

          if (!this.existingDiscountCodes.includes(discountCode)) {
            this.existingDiscountCodes.push(discountCode);
          }

          // Add event listener to remove discount
          const removeButton = discount.querySelector(selectors$k.removeDiscount);
          if (removeButton) {
            // Remove existing listener to prevent duplicates
            removeButton.removeEventListener('click', this.handleRemoveDiscount);

            // Add new listener
            removeButton.addEventListener('click', (event) => {
              event.preventDefault();
              this.removeDiscount(discountCode);
            });
          }
        });
      }

      applyDiscount(discountCode) {
        if (this.existingDiscountCodes.includes(discountCode)) {
          this.discountErrorMessages.forEach((msg) => {
            msg.textContent = window.theme.strings.discount_already_applied;
            msg.classList.remove('hidden');
          });
          return;
        }

        this.existingDiscountCodes.push(discountCode);
        this.updateCartDiscounts(this.existingDiscountCodes.join(','));
      }

      removeDiscount(discountCode) {
        if (!this.existingDiscountCodes.includes(discountCode)) return;

        this.existingDiscountCodes = this.existingDiscountCodes.filter((code) => code !== discountCode);
        this.updateCartDiscounts(this.existingDiscountCodes.join(','));
      }

      updateCartDiscounts(discountString) {
        const lastAttemptedDiscount = discountString
          .split(',')
          .filter((c) => c)
          .pop()
          ?.trim();

        this.disableCartButtons();
        this.discountErrorMessages.forEach((msg) => msg.classList.add('hidden'));

        fetch(window.theme.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            discount: discountString,
          }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((text) => {
            let data;
            try {
              data = JSON.parse(text);
            } catch (e) {
              console.error('Failed to parse cart update response:', text);
              throw new Error('Invalid JSON response from server.');
            }

            if (lastAttemptedDiscount) {
              const wasApplied = data.discount_codes && Array.isArray(data.discount_codes) && data.discount_codes.some((d) => d.code === lastAttemptedDiscount && d.applicable);

              if (!wasApplied) {
                this.discountError = true;
                this.existingDiscountCodes = this.existingDiscountCodes.filter((code) => code !== lastAttemptedDiscount);
              } else {
                this.discountError = false;
              }
            } else {
              this.discountError = false;
            }

            this.getCart();
          })
          .catch((error) => {
            console.log(error);
          });
      }

      /**
       * Cart events
       *
       * @return  {Void}
       */

      // =========================================================================
      // CART EVENTS & FORM HANDLING
      // =========================================================================


      cartEvents() {
        if (this.cartTermsCheckbox) {
          this.cartTermsCheckbox.removeEventListener('change', this.formSubmitHandler);
          this.cartCheckoutButtonWrapper.removeEventListener('click', this.formSubmitHandler);
          this.cartForm.removeEventListener('submit', this.formSubmitHandler);

          this.cartTermsCheckbox.addEventListener('change', this.formSubmitHandler);
          this.cartCheckoutButtonWrapper.addEventListener('click', this.formSubmitHandler);
          this.cartForm.addEventListener('submit', this.formSubmitHandler);
        }
      }

      formSubmitHandler() {
        const termsAccepted = document.querySelector(selectors$k.cartTermsCheckbox).checked;
        const termsError = document.querySelector(selectors$k.termsErrorMessage);

        // Disable form submit if terms and conditions are not accepted
        if (!termsAccepted) {
          if (document.querySelector(selectors$k.termsErrorMessage).length > 0) {
            return;
          }

          termsError.innerText = theme.strings.cartAcceptanceError;
          this.cartCheckoutButton.setAttribute(attributes$d.disabled, true);
          termsError.classList.add(classes$h.expanded);
        } else {
          termsError.classList.remove(classes$h.expanded);
          this.cartCheckoutButton.removeAttribute(attributes$d.disabled);
        }
      }

      /**
       * Cart event remove out of stock error
       *
       * @return  {Void}
       */

      formErrorsEvents(errorContainer) {
        const buttonErrorClose = errorContainer.querySelector(selectors$k.formCloseError);
        buttonErrorClose?.addEventListener('click', (e) => {
          e.preventDefault();

          if (errorContainer) {
            errorContainer.classList.remove(classes$h.visible);
          }
        });
      }

      /**
       * Get response from the cart
       *
       * @return  {Void}
       */

      getCart() {
        // The /cart page and the cart drawer need different line-item
        // markup. `api-cart-items` renders the drawer snippet; the
        // dedicated `api-cart-page-items` endpoint renders
        // `cart-page-line-items` plus the totals (including the
        // Discount row). When the `<cart-items>` element is the cart
        // page, fetch the page-specific endpoint so the AJAX refresh
        // (e.g. after applying a discount code) keeps the layout
        // intact and surfaces the new discount in the summary.
        const sectionId = this.cartPage && this.cartPage.contains(this) ? 'api-cart-page-items' : 'api-cart-items';

        fetch(`${theme.routes.cart_url}?section_id=${sectionId}`)
          .then(this.cartErrorsHandler)
          .then((response) => response.text())
          .then((response) => {
            const element = document.createElement('div');
            element.innerHTML = response;

            const cleanResponse = element.querySelector(selectors$k.apiContent);
            this.build(cleanResponse);
          })
          .catch((error) => console.log(error));
      }

      /**
       * Add item(s) to the cart and show the added item(s)
       *
       * @param   {String}  formData
       * @param   {DOM Element}  button
       *
       * @return  {Void}
       */

      addToCart(formData, button) {
        if (this.cart) {
          this.cart.classList.add(classes$h.loading);
        }

        const quickAddHolder = button?.closest(selectors$k.quickAddHolder);

        if (button) {
          button.classList.add(classes$h.loading);
          button.disabled = true;
        }

        if (quickAddHolder) {
          quickAddHolder.classList.add(classes$h.visible);
        }

        fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        })
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              this.addToCartError(response, button);

              if (button) {
                button.classList.remove(classes$h.loading);
                button.disabled = false;
              }

              if (!this.showCannotAddMoreInCart) return;
            }

            if (this.cart) {
              if (button) {
                button.classList.remove(classes$h.loading);
                button.classList.add(classes$h.added);

                button.dispatchEvent(
                  new CustomEvent('theme:product:add', {
                    detail: {
                      response: response,
                      button: button,
                    },
                    bubbles: true,
                  })
                );
              }
              if (theme.settings.cartType === 'page') {
                window.location = theme.routes.cart_url;
              }
              this.getCart();
            } else {
              // Redirect to cart page if "Add to cart" is successful
              window.location = theme.routes.cart_url;
            }

            publish(theme.PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          })
          .catch((error) => {
            this.addToCartError(error, button);
            this.enableCartButtons();
          });
      }

      /**
       * Update cart
       *
       * @param   {Object}  updateData
       *
       * @return  {Void}
       */

      updateCart(updateData = {}, currentItem = null) {
        this.cart.classList.add(classes$h.loading);

        let updatedQuantity = updateData.quantity;
        if (currentItem !== null) {
          if (updatedQuantity) {
            currentItem.classList.add(classes$h.loading);
          } else {
            currentItem.classList.add(classes$h.removed);
          }
        }
        this.disableCartButtons();

        const newItem = this.cart.querySelector(`[${attributes$d.item}="${updateData.id}"]`) || currentItem;
        const lineIndex = newItem?.hasAttribute(attributes$d.itemIndex) ? parseInt(newItem.getAttribute(attributes$d.itemIndex)) : 0;
        const itemTitle = newItem?.hasAttribute(attributes$d.itemTitle) ? newItem.getAttribute(attributes$d.itemTitle) : null;

        if (lineIndex === 0) return;

        // -----------------------------------------------------------------
        // Bundle fan-out: when changing qty/removing a bundle parent line,
        // apply the same change to every sibling line sharing _bundleId.
        // -----------------------------------------------------------------
        const bundleId = newItem?.getAttribute('data-bundle-id');
        if (bundleId) {
          const updates = {};
          // Parent
          updates[updateData.id] = updatedQuantity;
          // Children (rendered as data-bundle-child-key inside the parent's wrapper)
          newItem.querySelectorAll('[data-bundle-child-key]').forEach((childEl) => {
            const childKey = childEl.getAttribute('data-bundle-child-key');
            if (childKey) updates[childKey] = updatedQuantity;
          });

          fetch(theme.routes.cart_update_url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({updates}),
          })
            .then((response) => response.text())
            .then((state) => {
              const parsedState = JSON.parse(state);
              if (parsedState.errors) {
                this.cartUpdateFailed = true;
                this.updateErrorText(itemTitle);
                this.toggleErrorMessage();
                this.resetLineItem(currentItem);
                this.enableCartButtons();
                this.bindDiscountEventListeners();
                return;
              }
              this.getCart();
            })
            .catch((error) => {
              console.log(error);
              this.enableCartButtons();
            });
          return;
        }

        const data = {
          line: lineIndex,
          quantity: updatedQuantity,
        };

        fetch(theme.routes.cart_change_url, {
          method: 'post',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify(data),
        })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);

            if (parsedState.errors) {
              this.cartUpdateFailed = true;
              this.updateErrorText(itemTitle);
              this.toggleErrorMessage();
              this.resetLineItem(currentItem);
              this.enableCartButtons();
              this.bindDiscountEventListeners();

              return;
            }

            this.getCart();
          })
          .catch((error) => {
            console.log(error);
            this.enableCartButtons();
          });
      }

      /**
       * Reset line item initial state
       *
       * @return  {Void}
       */
      resetLineItem(item) {
        const qtyInput = item.querySelector(selectors$k.qtyInput);
        const qty = qtyInput.getAttribute('value');
        qtyInput.value = qty;
        qtyInput.closest('quantity-input')?.validateQtyRules();
        item.classList.remove(classes$h.loading);
      }

      /**
       * Disable cart buttons and inputs
       *
       * @return  {Void}
       */
      disableCartButtons() {
        const inputs = this.cart.querySelectorAll('input');
        const buttons = this.cart.querySelectorAll(`button, ${selectors$k.cartItemRemove}`);

        if (inputs.length) {
          inputs.forEach((item) => {
            item.classList.add(classes$h.disabled);
            item.blur();
            item.disabled = true;
          });
        }

        if (buttons.length) {
          buttons.forEach((item) => {
            item.setAttribute(attributes$d.disabled, true);
          });
        }
      }

      /**
       * Enable cart buttons and inputs
       *
       * @return  {Void}
       */
      enableCartButtons() {
        const inputs = this.cart.querySelectorAll('input');
        const buttons = this.cart.querySelectorAll(`button, ${selectors$k.cartItemRemove}`);

        if (inputs.length) {
          inputs.forEach((item) => {
            item.classList.remove(classes$h.disabled);
            item.disabled = false;
          });
        }

        if (buttons.length) {
          buttons.forEach((item) => {
            item.removeAttribute(attributes$d.disabled);
          });
        }

        this.cart.classList.remove(classes$h.loading);
      }

      /**
       * Update error text
       *
       * @param   {String}  itemTitle
       *
       * @return  {Void}
       */

      updateErrorText(itemTitle) {
        this.cartErrorHolder.querySelector(selectors$k.errorMessage).innerText = itemTitle;
      }

      /**
       * Toggle error message
       *
       * @return  {Void}
       */

      toggleErrorMessage() {
        if (!this.cartErrorHolder) return;

        this.cartErrorHolder.classList.toggle(classes$h.expanded, this.cartUpdateFailed || this.showCannotAddMoreInCart);

        // Reset cart error events flag
        this.showCannotAddMoreInCart = false;
        this.cartUpdateFailed = false;
      }

      /**
       * Handle errors
       *
       * @param   {Object}  response
       *
       * @return  {Object}
       */

      cartErrorsHandler(response) {
        if (!response.ok) {
          return response.json().then(function (json) {
            const e = new FetchError({
              status: response.statusText,
              headers: response.headers,
              json: json,
            });
            throw e;
          });
        }
        return response;
      }

      /**
       * Add to cart error handle
       *
       * @param   {Object}  data
       * @param   {DOM Element/Null} button
       *
       * @return  {Void}
       */

      /**
       * Hide error message container as soon as an item is successfully added to the cart
       */
      hideAddToCartErrorMessage() {
        const holder = this.button.closest(selectors$k.upsellHolder) ? this.button.closest(selectors$k.upsellHolder) : this.button.closest(selectors$k.productForm);
        const errorContainer = holder?.querySelector(selectors$k.formErrorsContainer);

        errorContainer?.classList.remove(classes$h.visible);
      }

      addToCartError(data, button) {
        if (this.showCannotAddMoreInCart) return; // Show error in cart drawer instead of product form

        if (button !== null) {
          const outerContainer = button.closest(selectors$k.outerSection) || button.closest(selectors$k.quickAddHolder) || button.closest(selectors$k.quickAddModal);
          let errorContainer = outerContainer?.querySelector(selectors$k.formErrorsContainer);
          const buttonUpsellHolder = button.closest(selectors$k.quickAddHolder);

          if (buttonUpsellHolder && buttonUpsellHolder.querySelector(selectors$k.formErrorsContainer)) {
            errorContainer = buttonUpsellHolder.querySelector(selectors$k.formErrorsContainer);
          }

          if (errorContainer) {
            let errorMessage = `${data.message}: ${data.description}`;

            if (data.message == data.description) {
              errorMessage = data.message;
            }

            errorContainer.innerHTML = `<div class="errors">${errorMessage}<button type="button" class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" width="24px" height="24px" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel"><path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path></svg></button></div>`;
            errorContainer.classList.add(classes$h.visible);
            this.formErrorsEvents(errorContainer);
          }

          button.dispatchEvent(
            new CustomEvent('theme:product:add-error', {
              detail: {
                response: data,
                button: button,
              },
              bubbles: true,
            })
          );
        }

        const quickAddHolder = button?.closest(selectors$k.quickAddHolder);

        if (quickAddHolder) {
          quickAddHolder.dispatchEvent(
            new CustomEvent('theme:cart:error', {
              bubbles: true,
              detail: {
                message: data.message,
                description: data.description,
                holder: quickAddHolder,
              },
            })
          );
        }

        this.cart?.classList.remove(classes$h.loading);
      }

      /**
       * Add product to cart events
       *
       * @return  {Void}
       */
      productAddCallback(event) {
        let buttons = [];
        let quickAddHolder = null;
        const hasError = event.type == 'theme:product:add-error';
        const buttonATC = event.detail.button;
        const cartBarButtonATC = document.querySelector(selectors$k.cartBarAdd);

        buttons.push(buttonATC);
        quickAddHolder = buttonATC.closest(selectors$k.quickAddHolder);

        if (cartBarButtonATC) {
          buttons.push(cartBarButtonATC);
        }

        buttons.forEach((button) => {
          button.classList.remove(classes$h.loading);
          if (!hasError) {
            button.classList.add(classes$h.added);
          }
        });

        setTimeout(() => {
          buttons.forEach((button) => {
            button.classList.remove(classes$h.added);
            const isVariantUnavailable =
              button.closest(selectors$k.formWrapper)?.classList.contains(classes$h.variantSoldOut) || button.closest(selectors$k.formWrapper)?.classList.contains(classes$h.variantUnavailable);

            if (!isVariantUnavailable) {
              button.disabled = false;
            }
          });

          quickAddHolder?.classList.remove(classes$h.visible);
        }, 1000);
      }

      /**
       * Toggle classes on different containers and messages
       *
       * @return  {Void}
       */

      toggleClassesOnContainers() {
        const hasItemsInCart = this.hasItemsInCart();

        this.cart.classList.toggle(classes$h.empty, !hasItemsInCart);
        this.closest(selectors$k.cartPage)?.classList.toggle(classes$h.empty, !hasItemsInCart);

        if (!hasItemsInCart && this.cartDrawer) {
          setTimeout(() => {
            this.a11y.trapFocus(this.cartDrawer, {
              elementToFocus: this.cartDrawer.querySelector(selectors$k.cartDrawerClose),
            });
          }, 100);
        }
      }

      /**
       * Build cart depends on results
       *
       * @param   {Object}  data
       *
       * @return  {Void}
       */

      build(data) {
        const cartItemsData = data.querySelector(selectors$k.apiLineItems);
        const upsellItemsData = data.querySelector(selectors$k.apiUpsellItems);
        const bundleItemsData = data.querySelector(selectors$k.apiBundleItems);

        const cartEmptyData = Boolean(cartItemsData === null && upsellItemsData === null && bundleItemsData === null);
        const priceData = data.querySelector(selectors$k.apiCartPrice);
        const cartTotal = data.querySelector(selectors$k.cartTotal);

        if (this.priceHolder && priceData) {
          this.priceHolder.innerHTML = priceData.innerHTML;
        }

        if (cartEmptyData) {
          this.itemsHolder.innerHTML = data.innerHTML;

          if (this.upsellProductsHolder) {
            this.upsellProductsHolder.innerHTML = '';
          }

          if (this.bundleProductsHolder) {
            this.bundleProductsHolder.innerHTML = '';
          }
        } else {
          this.itemsHolder.innerHTML = cartItemsData.innerHTML;

          if (this.upsellProductsHolder) {
            this.upsellProductsHolder.innerHTML = upsellItemsData.innerHTML;
          }

          if (this.bundleProductsHolder) {
            this.bundleProductsHolder.innerHTML = bundleItemsData.innerHTML;
          }

          this.skipUpsellOrBundleProductEvent();
          this.checkSkippedUpsellOrBundleProductsFromStorage();
          this.toggleCartUpsellOrBundleWidgetVisibility();
        }

        this.newTotalItems = cartItemsData && cartItemsData.querySelectorAll(selectors$k.item).length ? cartItemsData.querySelectorAll(selectors$k.item).length : 0;
        this.subtotal = cartTotal && cartTotal.hasAttribute(attributes$d.cartTotal) ? parseInt(cartTotal.getAttribute(attributes$d.cartTotal)) : 0;
        this.cartCount = this.getCartItemCount();

        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            bubbles: true,
            detail: {
              cartCount: this.cartCount,
            },
          })
        );

        // Update cart total price. Use the theme-wide moneyFormat,
        // which already honors `settings.currency_code_enable` (see
        // `snippets/head.liquid`). Using `moneyWithCurrencyFormat`
        // here forced the "EUR" suffix even when the merchant
        // disabled the currency-code setting.
        this.cartTotal.innerHTML = this.subtotal === 0 ? window.theme.strings.free : window.theme.formatMoney(this.subtotal, theme.moneyFormat);

        if (this.totalItems !== this.newTotalItems) {
          this.totalItems = this.newTotalItems;

          this.toggleClassesOnContainers();
        }

        // Add class "is-updated" line items holder to reduce cart items animation delay via CSS variables
        if (this.cartDrawer?.classList.contains(classes$h.open)) {
          this.cart.classList.add(classes$h.updated);
        }

        // Remove cart loading class
        this.cart.classList.remove(classes$h.loading);

        // Prepare empty cart buttons for animation
        if (!this.hasItemsInCart()) {
          this.cartEmpty.querySelectorAll(selectors$k.animation).forEach((item) => {
            item.classList.remove(classes$h.animated);
          });
        }

        if (this.hasDiscountBlock) {
          // Re-query in case the cart was rebuilt and the form lives
          // inside the swapped-in markup.
          this.discountFields = document.querySelectorAll(selectors$k.discountField);
          this.discountErrorMessages = document.querySelectorAll(selectors$k.discountErrorMessage);

          const codes = this.existingDiscountCodes.join(',');
          this.discountFields.forEach((field) => {
            field.value = codes;
          });

          if (this.discountError) {
            this.discountErrorMessages.forEach((msg) => {
              msg.textContent = window.theme.strings.discount_not_applicable;
              msg.classList.remove('hidden');
            });
          } else {
            this.discountErrorMessages.forEach((msg) => msg.classList.add('hidden'));
          }
        }

        this.freeShippingMessageHandle(this.subtotal);
        this.cartRemoveEvents();
        this.cartUpdateEvents();
        this.toggleErrorMessage();
        this.enableCartButtons();
        this.updateProgress();
        this.animateItems();
        this.bindDiscountEventListeners();

        document.dispatchEvent(
          new CustomEvent('theme:product:added', {
            bubbles: true,
          })
        );
      }

      /**
       * Get cart item count
       *
       * @return  {Void}
       */

      getCartItemCount() {
        // Count every constituent product, including single-line bundle add-ons
        // (passepartout / frame). Those collapse into one cart line whose line
        // element carries `data-bundle-count` (print + add-ons); multiply the
        // line quantity by it so the bag badge matches the server-rendered count
        // from snippets/cart-product-count.liquid.
        return Array.from(this.cart.querySelectorAll(selectors$k.qtyInput)).reduce((total, quantityInput) => {
          const qty = parseInt(quantityInput.value) || 0;
          const line = quantityInput.closest(selectors$k.item);
          const perUnit = line ? parseInt(line.getAttribute('data-bundle-count'), 10) || 1 : 1;
          return total + qty * perUnit;
        }, 0);
      }

      /**
       * Check for items in the cart
       *
       * @return  {Void}
       */

      hasItemsInCart() {
        return this.totalItems > 0;
      }

      /**
       * Show/hide free shipping message
       *
       * @param   {Number}  total
       *
       * @return  {Void}
       */


      // =========================================================================
      // FREE SHIPPING METHODS
      // Handles free shipping threshold display and progress bar
      // =========================================================================

      freeShippingMessageHandle(total) {
        if (!this.freeShipping.length) return;

        this.freeShipping.forEach((message) => {
          const hasQualifiedShippingMessage = message.hasAttribute(attributes$d.freeShipping) && message.getAttribute(attributes$d.freeShipping) === 'true' && total >= 0;
          message.classList.toggle(classes$h.success, hasQualifiedShippingMessage && total >= this.freeShippingLimit);
        });
      }

      /**
       * Update progress when update cart
       *
       * @return  {Void}
       */

      updateProgress() {
        this.freeShipping = document.querySelectorAll(selectors$k.freeShipping);

        if (!this.freeShipping.length) return;

        const percentValue = isNaN(this.subtotal / this.freeShippingLimit) ? 100 : this.subtotal / this.freeShippingLimit;
        const percent = Math.min(percentValue * 100, 100);
        const dashoffset = this.circumference - ((percent / 100) * this.circumference) / 2;
        const leftToSpend = window.theme.formatMoney(this.freeShippingLimit - this.subtotal, theme.moneyFormat);

        this.freeShipping.forEach((item) => {
          const progressBar = item.querySelector(selectors$k.freeShippingProgress);
          const progressGraph = item.querySelector(selectors$k.freeShippingGraph);
          const leftToSpendMessage = item.querySelector(selectors$k.leftToSpend);

          if (leftToSpendMessage) {
            leftToSpendMessage.innerHTML = leftToSpend.replace('.00', '');
          }

          // Set progress bar value
          if (progressBar) {
            progressBar.value = percent;
          }

          // Set circle progress
          if (progressGraph) {
            progressGraph.style.setProperty('--stroke-dashoffset', `${dashoffset}`);
          }
        });
      }

      /**
       * Skip upsell or bundle product
       */

      // =========================================================================
      // UPSELL & BUNDLE METHODS
      // Handles upsell/bundle product skipping, storage, and widget visibility
      // =========================================================================

      skipUpsellOrBundleProductEvent() {
        if (this.upsellProductsHolder === null && this.bundleProductsHolder === null) {
          return;
        }

        const upsellSkipButtons = this.upsellProductsHolder?.querySelectorAll(selectors$k.buttonSkipUpsellProduct) || [];
        const bundleSkipButtons = this.bundleProductsHolder?.querySelectorAll(selectors$k.buttonSkipUpsellProduct) || [];
        const allSkipButtons = [...upsellSkipButtons, ...bundleSkipButtons];

        if (allSkipButtons.length) {
          allSkipButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();

              const productID = button.closest(selectors$k.quickAddHolder).getAttribute(attributes$d.quickAddHolder);
              const isUpsell = !!button.closest(selectors$k.upsellWidget);
              const isBundle = !!button.closest(selectors$k.bundleWidget);

              if (isUpsell && !this.skipUpsellProductsArray.includes(productID)) {
                this.skipUpsellProductsArray.push(productID);
                window.sessionStorage.setItem('skip_upsell_products', this.skipUpsellProductsArray);
                this.removeUpsellOrBundleProduct(productID, 'upsell');
              }

              if (isBundle && !this.skipBundleProductsArray.includes(productID)) {
                this.skipBundleProductsArray.push(productID);
                window.sessionStorage.setItem('skip_bundle_products', this.skipBundleProductsArray);
                this.removeUpsellOrBundleProduct(productID, 'bundle');
              }

              this.toggleCartUpsellOrBundleWidgetVisibility();
            });
          });
        }
      }

      /**
       * Check for skipped upsell or bundle product added to session storage
       */
      checkSkippedUpsellOrBundleProductsFromStorage() {
        const types = [
          {key: 'upsell', storageKey: 'skip_upsell_products', array: this.skipUpsellProductsArray},
          {key: 'bundle', storageKey: 'skip_bundle_products', array: this.skipBundleProductsArray},
        ];

        types.forEach(({key, storageKey, array}) => {
          const skippedItems = window.sessionStorage.getItem(storageKey);
          if (skippedItems) {
            skippedItems.split(',').forEach((productID) => {
              if (!array.includes(productID)) {
                array.push(productID);
              }
              this.removeUpsellOrBundleProduct(productID, key);
            });
          }
        });
      }

      removeUpsellOrBundleProduct(productID, type = 'upsell') {
        const holders = {
          upsell: this.upsellProductsHolder,
          bundle: this.bundleProductsHolder,
        };
        const holder = holders[type];
        if (!holder) return;

        const product = holder.querySelector(`[${attributes$d.quickAddHolder}="${productID}"]`);
        if (product && product.parentNode) {
          product.parentNode.remove();
        }
      }

      /**
       * Show or hide cart upsell or bundle products widget visibility
       */
      toggleCartUpsellOrBundleWidgetVisibility() {
        if (!this.upsellProductsHolder && !this.bundleProductsHolder) return;

        const upsellItems = this.upsellProductsHolder?.querySelectorAll(selectors$k.quickAddHolder);
        const bundleItems = this.bundleProductsHolder?.querySelectorAll(selectors$k.quickAddHolder);
        const upsellWidget = this.upsellProductsHolder?.closest(selectors$k.upsellWidget);
        const bundleWidget = this.bundleProductsHolder?.closest(selectors$k.bundleWidget);

        if (!upsellWidget && !bundleWidget) return;

        // Helper to toggle and auto-open widget
        const toggleWidget = (widget, items, autoOpenAttr) => {
          if (!widget) return;
          widget.classList.toggle(classes$h.hidden, !items.length);
          if (items.length && !widget.hasAttribute(attributes$d.open) && widget.hasAttribute(autoOpenAttr)) {
            widget.setAttribute(attributes$d.open, true);
            const widgetBody = widget.querySelector(selectors$k.collapsibleBody);
            if (widgetBody) {
              widgetBody.style.height = 'auto';
            }
          }
        };

        toggleWidget(upsellWidget, upsellItems, attributes$d.upsellAutoOpen);
        toggleWidget(bundleWidget, bundleItems, attributes$d.upsellAutoOpen);
      }

      /**
       * Remove initially added AOS classes to allow animation on cart drawer open
       *
       * @return  {Void}
       */

      // =========================================================================
      // ANIMATION METHODS
      // =========================================================================

      resetAnimatedItems() {
        this.cart.querySelectorAll(selectors$k.animation).forEach((item) => {
          item.classList.remove(classes$h.animated);
          item.classList.remove(classes$h.hiding);
        });
      }

      /**
       * Cart elements opening animation
       *
       * @return  {Void}
       */
      animateItems(e) {
        requestAnimationFrame(() => {
          let cart = this.cart;

          if (e && e.detail && e.detail.target) {
            cart = e.detail.target;
          }

          cart?.querySelectorAll(selectors$k.animation).forEach((item) => {
            item.classList.add(classes$h.animated);
          });
        });
      }
    }

    if (!customElements.get('cart-items')) {
      customElements.define('cart-items', CartItems);
    }

    const attributes$c = {
      count: 'data-cart-count',
      limit: 'data-limit',
    };

    class CartCount extends HTMLElement {
      constructor() {
        super();

        this.cartCount = null;
        this.limit = this.getAttribute(attributes$c.limit);
        this.onCartChangeCallback = this.onCartChange.bind(this);
      }

      connectedCallback() {
        document.addEventListener('theme:cart:change', this.onCartChangeCallback);
      }

      disconnectedCallback() {
        document.addEventListener('theme:cart:change', this.onCartChangeCallback);
      }

      onCartChange(event) {
        this.cartCount = event.detail.cartCount;
        this.update();
      }

      update() {
        if (this.cartCount !== null) {
          this.setAttribute(attributes$c.count, this.cartCount);
          let countValue = this.cartCount;

          if (this.limit && this.cartCount >= this.limit) {
            countValue = '9+';
          }

          this.innerText = countValue;
        }
      }
    }

    if (!customElements.get('cart-count')) {
      customElements.define('cart-count', CartCount);
    }

    const classes$g = {
      open: 'is-open',
      closing: 'is-closing',
      duplicate: 'drawer--duplicate',
      drawerEditorError: 'drawer-editor-error',
    };

    const selectors$j = {
      additionalCheckoutButtons: '.additional-checkout-buttons',
      cartDrawer: 'cart-drawer',
      cartDrawerClose: '[data-cart-drawer-close]',
      cartDrawerInner: '[data-cart-drawer-inner]',
      shopifySection: '.shopify-section',
    };

    const attributes$b = {
      drawerUnderlay: 'data-drawer-underlay',
    };

    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.cartDrawerIsOpen = false;

        this.cartDrawerClose = this.querySelector(selectors$j.cartDrawerClose);
        this.cartDrawerInner = this.querySelector(selectors$j.cartDrawerInner);
        this.openCartDrawer = this.openCartDrawer.bind(this);
        this.closeCartDrawer = this.closeCartDrawer.bind(this);
        this.toggleCartDrawer = this.toggleCartDrawer.bind(this);
        this.openCartDrawerOnProductAdded = this.openCartDrawerOnProductAdded.bind(this);
        this.openCartDrawerOnSelect = this.openCartDrawerOnSelect.bind(this);
        this.closeCartDrawerOnDeselect = this.closeCartDrawerOnDeselect.bind(this);
        this.resetDrawerClosed = this.resetDrawerClosed.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.cartDrawerSection = this.closest(selectors$j.shopifySection);
        this.a11y = window.theme.a11y;

        this.closeCartEvents();
      }

      connectedCallback() {
        const drawerSection = this.closest(selectors$j.shopifySection);

        /* Prevent duplicated cart drawers */
        if (window.theme.hasCartDrawer) {
          if (!window.Shopify.designMode) {
            drawerSection.remove();
            return;
          } else {
            const errorMessage = document.createElement('div');
            errorMessage.classList.add(classes$g.drawerEditorError);
            errorMessage.innerText = 'Cart drawer section already exists.';

            if (!this.querySelector(`.${classes$g.drawerEditorError}`)) {
              this.querySelector(selectors$j.cartDrawerInner).append(errorMessage);
            }

            this.classList.add(classes$g.duplicate);
          }
        }

        window.theme.hasCartDrawer = true;

        this.addEventListener('theme:cart-drawer:show', this.openCartDrawer);
        document.addEventListener('theme:cart:toggle', this.toggleCartDrawer);
        document.addEventListener('theme:quick-add:open', this.closeCartDrawer);
        document.addEventListener('theme:product:added', this.openCartDrawerOnProductAdded);
        document.addEventListener('shopify:block:select', this.openCartDrawerOnSelect);
        document.addEventListener('shopify:section:select', this.openCartDrawerOnSelect);
        document.addEventListener('shopify:section:deselect', this.closeCartDrawerOnDeselect);

        // Keep the drawer from being restored open across back/forward-cache
        // navigations. When the drawer is open and the visitor leaves the page
        // (e.g. tapping "View bag" → cart page), the page is cached with the
        // drawer open; pressing "Continue shopping" (history.back) then brings
        // it back open. `pagehide` resets before the page is cached; `pageshow`
        // with `persisted` is a fallback after a bfcache restore.
        window.addEventListener('pagehide', this.resetDrawerClosed);
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        document.removeEventListener('theme:product:added', this.openCartDrawerOnProductAdded);
        document.removeEventListener('theme:cart:toggle', this.toggleCartDrawer);
        document.removeEventListener('theme:quick-add:open', this.closeCartDrawer);
        document.removeEventListener('shopify:block:select', this.openCartDrawerOnSelect);
        document.removeEventListener('shopify:section:select', this.openCartDrawerOnSelect);
        document.removeEventListener('shopify:section:deselect', this.closeCartDrawerOnDeselect);
        window.removeEventListener('pagehide', this.resetDrawerClosed);
        window.removeEventListener('pageshow', this.onPageShow);

        if (document.querySelectorAll(selectors$j.cartDrawer).length <= 1) {
          window.theme.hasCartDrawer = false;
        }

        appendCartItems();
      }

      /**
       * Open cart drawer when product is added to cart
       *
       * @return  {Void}
       */
      openCartDrawerOnProductAdded() {
        if (!this.cartDrawerIsOpen) {
          this.openCartDrawer();
        }
      }

      /**
       * Open cart drawer on block or section select
       *
       * @return  {Void}
       */
      openCartDrawerOnSelect(e) {
        const cartDrawerSection = e.target.querySelector(selectors$j.shopifySection) || e.target.closest(selectors$j.shopifySection) || e.target;

        if (cartDrawerSection === this.cartDrawerSection) {
          this.openCartDrawer(true);
        }
      }

      /**
       * Close cart drawer on section deselect
       *
       * @return  {Void}
       */
      closeCartDrawerOnDeselect() {
        if (this.cartDrawerIsOpen) {
          this.closeCartDrawer();
        }
      }

      /**
       * Open cart drawer and add class on body
       *
       * @return  {Void}
       */

      openCartDrawer(forceOpen = false) {
        if (!forceOpen && this.classList.contains(classes$g.duplicate)) return;

        this.cartDrawerIsOpen = true;
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        document.body.addEventListener('click', this.onBodyClickEvent);

        document.dispatchEvent(
          new CustomEvent('theme:cart-drawer:open', {
            detail: {
              target: this,
            },
            bubbles: true,
          })
        );
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

        this.classList.add(classes$g.open);

        // Observe Additional Checkout Buttons
        this.observeAdditionalCheckoutButtons();

        window.theme.waitForAnimationEnd(this.cartDrawerInner).then(() => {
          this.a11y.trapFocus(this, {
            elementToFocus: this.querySelector(selectors$j.cartDrawerClose),
          });
        });
      }

      /**
       * Close cart drawer and remove class on body
       *
       * @return  {Void}
       */

      closeCartDrawer() {
        if (!this.classList.contains(classes$g.open)) return;

        this.classList.add(classes$g.closing);
        this.classList.remove(classes$g.open);

        this.cartDrawerIsOpen = false;

        document.dispatchEvent(
          new CustomEvent('theme:cart-drawer:close', {
            bubbles: true,
          })
        );

        this.a11y.removeTrapFocus();
        this.a11y.autoFocusLastElement();

        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

        window.theme.waitForAnimationEnd(this.cartDrawerInner).then(() => {
          this.classList.remove(classes$g.closing);
        });
      }

      /**
       * Only reset on a back/forward-cache restore (a fresh load already
       * renders the drawer closed).
       *
       * @return  {Void}
       */
      onPageShow(event) {
        if (event && event.persisted) {
          this.resetDrawerClosed();
        }
      }

      /**
       * Instantly force the drawer to a closed state (no close animation).
       * Used across bfcache navigations so the drawer is never restored open.
       *
       * @return  {Void}
       */
      resetDrawerClosed() {
        if (!this.classList.contains(classes$g.open) && !this.classList.contains(classes$g.closing)) {
          return;
        }

        this.classList.remove(classes$g.open, classes$g.closing);
        this.cartDrawerIsOpen = false;

        if (this.onBodyClickEvent) {
          document.body.removeEventListener('click', this.onBodyClickEvent);
        }

        if (this.a11y && typeof this.a11y.removeTrapFocus === 'function') {
          this.a11y.removeTrapFocus();
        }

        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }

      /**
       * Toggle cart drawer
       *
       * @return  {Void}
       */

      toggleCartDrawer() {
        if (!this.cartDrawerIsOpen) {
          this.openCartDrawer();
        } else {
          this.closeCartDrawer();
        }
      }

      /**
       * Event click to element to close cart drawer
       *
       * @return  {Void}
       */

      closeCartEvents() {
        this.cartDrawerClose.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeCartDrawer();
        });

        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') {
            this.closeCartDrawer();
          }
        });
      }

      onBodyClick(e) {
        if (e.target.hasAttribute(attributes$b.drawerUnderlay)) this.closeCartDrawer();
      }

      observeAdditionalCheckoutButtons() {
        // identify an element to observe
        const additionalCheckoutButtons = this.querySelector(selectors$j.additionalCheckoutButtons);
        if (additionalCheckoutButtons) {
          // create a new instance of `MutationObserver` named `observer`,
          // passing it a callback function
          const observer = new MutationObserver(() => {
            this.a11y.trapFocus(this, {
              elementToFocus: this.querySelector(selectors$j.cartDrawerClose),
            });
            observer.disconnect();
          });

          // call `observe()` on that MutationObserver instance,
          // passing it the element to observe, and the options object
          observer.observe(additionalCheckoutButtons, {subtree: true, childList: true});
        }
      }
    }

    if (!customElements.get('cart-drawer')) {
      customElements.define('cart-drawer', CartDrawer);
    }

/*
 * <cart-slider>
 *
 * The "Combine with" panel rendered by snippets/cart-slider.liquid.
 *
 * Two variants share this element:
 *   - 'side-panel' (desktop): lives inside <cart-drawer>, sibling of
 *     .drawer__inner. Its open/close animation is driven entirely by the
 *     parent `cart-drawer.is-open` / `is-closing` classes via CSS — no JS
 *     timing here. The close button just triggers `theme:cart:toggle` so
 *     the cart drawer closes (which in turn closes this panel).
 *
 *   - 'inline' (mobile): a horizontal card carousel inside drawer__body.
 *     The prev/next nav buttons scroll the list; their disabled state is
 *     synced to scroll position.
 */

class CartSlider extends HTMLElement {
  constructor() {
    super();
    this.variant = this.getAttribute('data-variant') || 'side-panel';
  }

  connectedCallback() {
    if (this.variant === 'side-panel') {
      this._wireSidePanel();
    } else if (this.variant === 'inline') {
      this._wireInlineCarousel();
    }
  }

  // ----- side-panel -------------------------------------------------------
  _wireSidePanel() {
    const closeBtn = this.querySelector('[data-cart-slider-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Closes the cart drawer; the side panel is a child of it and
        // animates out automatically via the parent class change.
        document.dispatchEvent(new CustomEvent('theme:cart:toggle', {bubbles: true}));
      });
    }
  }

  // ----- inline carousel --------------------------------------------------
  _wireInlineCarousel() {
    // Optional collapsible/accordion mode (merchant setting). Wired first so
    // it works regardless of whether the carousel below has a scrollable
    // track. In this mode the prev/next arrows aren't rendered — the chevron
    // toggles open/closed and the carousel is browsed by swipe.
    this._wireCollapsible();

    const track = this.querySelector('[data-cart-slider-track]');
    const prevBtn = this.querySelector('[data-cart-slider-prev]');
    const nextBtn = this.querySelector('[data-cart-slider-next]');

    if (!track) return;

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const scrollByCard = (direction) => {
      const item = track.querySelector('[data-cart-slider-item]');
      const step = item ? item.getBoundingClientRect().width + 8 /* CSS gap */ : track.clientWidth * 0.8;
      const max = getMaxScroll();
      // Clamp to scrollable range so we never scroll past the last card and
      // expose blank background.
      const next = Math.max(0, Math.min(max, track.scrollLeft + direction * step));
      track.scrollTo({left: next, behavior: 'smooth'});
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (prevBtn.disabled) return;
        scrollByCard(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (nextBtn.disabled) return;
        scrollByCard(1);
      });
    }

    const updateNav = () => {
      const max = getMaxScroll();
      const x = track.scrollLeft;
      const isScrollable = max > 1;
      if (prevBtn) prevBtn.disabled = !isScrollable || x <= 1;
      if (nextBtn) nextBtn.disabled = !isScrollable || x >= max - 1;
    };
    track.addEventListener('scroll', updateNav, {passive: true});
    window.addEventListener('resize', updateNav);
    // Wait a tick for layout, plus a fallback after image load since
    // the track width can grow once thumbnails decode.
    requestAnimationFrame(updateNav);
    setTimeout(updateNav, 250);
  }

  // ----- collapsible accordion (inline, optional) -------------------------
  _wireCollapsible() {
    const toggle = this.querySelector('[data-cart-slider-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
      // `is-collapsed` is the source of truth (set by Liquid for the default
      // closed state); CSS hides the body and rotates the chevron from it.
      const collapsed = this.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', String(!collapsed));
    });
  }
}

if (!customElements.get('cart-slider')) {
  customElements.define('cart-slider', CartSlider);
}
    class CollapsibleElements extends HTMLElement {
      constructor() {
        super();

        this.single = this.hasAttribute('single');
        this.toggle = this.toggle.bind(this);

        // Bind event handlers once and store references
        this.onCollapsibleClickEvent = this.onCollapsibleClick.bind(this);
        this.onTransitionEndEvent = this.onTransitionEnd.bind(this);
      }

      connectedCallback() {
        // Query fresh DOM elements first
        this.collapsibles = this.querySelectorAll('[data-collapsible]');

        this.toggle();
        document.addEventListener('theme:resize:width', this.toggle);

        this.collapsibles.forEach((collapsible) => {
          const trigger = collapsible.querySelector('[data-collapsible-trigger]');
          const body = collapsible.querySelector('[data-collapsible-body]');

          trigger?.addEventListener('click', this.onCollapsibleClickEvent);

          body?.addEventListener('transitionend', this.onTransitionEndEvent);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize:width', this.toggle);

        // Only proceed if collapsibles exist
        if (this.collapsibles) {
          this.collapsibles.forEach((collapsible) => {
            const trigger = collapsible.querySelector('[data-collapsible-trigger]');
            const body = collapsible.querySelector('[data-collapsible-body]');

            trigger?.removeEventListener('click', this.onCollapsibleClickEvent);
            body?.removeEventListener('transitionend', this.onTransitionEndEvent);
          });
        }

        // Clear the reference
        this.collapsibles = null;
      }

      toggle() {
        const isDesktopView = !theme.isMobile;

        this.collapsibles.forEach((collapsible) => {
          if (!collapsible.hasAttribute('desktop') && !collapsible.hasAttribute('mobile')) return;

          const enableDesktop = collapsible.hasAttribute('desktop') ? collapsible.getAttribute('desktop') : 'true';
          const enableMobile = collapsible.hasAttribute('mobile') ? collapsible.getAttribute('mobile') : 'true';
          const isEligible = (isDesktopView && enableDesktop == 'true') || (!isDesktopView && enableMobile == 'true');
          const body = collapsible.querySelector('[data-collapsible-body]');

          if (isEligible) {
            collapsible.removeAttribute('disabled');
            collapsible.querySelector('[data-collapsible-trigger]').removeAttribute('tabindex');
            collapsible.removeAttribute('open');

            this.setBodyHeight(body, '');
          } else {
            collapsible.setAttribute('disabled', '');
            collapsible.setAttribute('open', true);
            collapsible.querySelector('[data-collapsible-trigger]').setAttribute('tabindex', -1);
          }
        });
      }

      open(collapsible) {
        if (collapsible.getAttribute('open') == 'true') return;

        const body = collapsible.querySelector('[data-collapsible-body]');
        const content = collapsible.querySelector('[data-collapsible-content]');

        collapsible.setAttribute('open', true);

        this.setBodyHeight(body, content.offsetHeight);
      }

      close(collapsible) {
        if (collapsible.getAttribute('open') !== 'true') return;

        const body = collapsible.querySelector('[data-collapsible-body]');
        const content = collapsible.querySelector('[data-collapsible-content]');

        // Set explicit px height (body may be 'auto' after open animation).
        // Reading body.offsetHeight immediately after forces a synchronous
        // reflow so the browser commits this as the transition start point.
        body.style.height = content.offsetHeight + 'px';
        // eslint-disable-next-line no-unused-expressions
        body.offsetHeight; // force reflow

        collapsible.setAttribute('open', false);

        // Now animate to 0 — the reflow above ensures the transition fires.
        this.setBodyHeight(body, 0);
      }

      setBodyHeight(body, contentHeight) {
        body.style.height = contentHeight !== 'auto' && contentHeight !== '' ? `${contentHeight}px` : contentHeight;
      }

      onTransitionEnd(event) {
        const target = event.target;
        const collapsible = target.closest('[data-collapsible]');
        const body = collapsible.querySelector('[data-collapsible-body]');

        if (target !== body || !collapsible) return;

        if (collapsible.getAttribute('open') == 'true') {
          this.setBodyHeight(body, 'auto');
        }

        if (collapsible.getAttribute('open') == 'false') {
          collapsible.removeAttribute('open');
          this.setBodyHeight(body, '');
        }
      }

      onCollapsibleClick(event) {
        event.preventDefault();

        const trigger = event.target;
        const collapsible = trigger.closest('[data-collapsible]');

        // When we want only one item expanded at the same time
        if (this.single) {
          this.collapsibles.forEach((otherCollapsible) => {
            // if otherCollapsible has attribute open and it's not the one we clicked on, remove the open attribute
            if (otherCollapsible.hasAttribute('open') && otherCollapsible != collapsible) {
              requestAnimationFrame(() => {
                this.close(otherCollapsible);
              });
            }
          });
        }

        if (collapsible.getAttribute('open') === 'true') {
          this.close(collapsible);
        } else {
          this.open(collapsible);
        }

        collapsible.dispatchEvent(
          new CustomEvent('theme:form:sticky', {
            bubbles: true,
            detail: {
              element: 'accordion',
            },
          })
        );
        collapsible.dispatchEvent(
          new CustomEvent('theme:collapsible:toggle', {
            bubbles: true,
          })
        );
      }
    }

    if (!customElements.get('collapsible-elements')) {
      customElements.define('collapsible-elements', CollapsibleElements);
    }

    class DeferredMedia extends HTMLElement {
      constructor() {
        super();

        const poster = this.querySelector('[data-deferred-media-button]');
        poster?.addEventListener('click', this.loadContent.bind(this));
      }

      loadContent(focus = true) {
        window.theme.pauseAllMedia();

        if (!this.getAttribute('loaded')) {
          const content = document.createElement('div');
          content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
          this.setAttribute('loaded', true);

          const mediaElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
          if (focus) mediaElement.focus();
          if (mediaElement.nodeName == 'VIDEO' && mediaElement.getAttribute('autoplay')) {
            // Force autoplay on Safari browsers
            mediaElement.play();
          }
        }
      }
    }

    if (!customElements.get('deferred-media')) {
      customElements.define('deferred-media', DeferredMedia);
    }

    window.theme.DeferredMedia = window.theme.DeferredMedia || DeferredMedia;

    /*
      Observe whether or not elements are visible in their container.
      Used for sections with horizontal sliders built by native scrolling
    */

    const classes$f = {
      visible: 'is-visible',
    };

    class IsInView {
      constructor(container, itemSelector) {
        if (!container || !itemSelector) return;

        this.observer = null;
        this.container = container;
        this.itemSelector = itemSelector;

        this.init();
      }

      init() {
        const options = {
          root: this.container,
          threshold: [0.01, 0.5, 0.75, 0.99],
        };

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.99) {
              entry.target.classList.add(classes$f.visible);
            } else {
              entry.target.classList.remove(classes$f.visible);
            }
          });
        }, options);

        this.container.querySelectorAll(this.itemSelector)?.forEach((item) => {
          this.observer.observe(item);
        });
      }

      destroy() {
        this.observer.disconnect();
      }
    }

    const classes$e = {
      dragging: 'is-dragging',
      enabled: 'is-enabled',
      scrolling: 'is-scrolling',
      visible: 'is-visible',
    };

    const selectors$i = {
      image: 'img, svg',
      productImage: '[data-product-image]',
      slide: '[data-grid-item]',
      slider: '[data-grid-slider]',
    };

    class DraggableSlider {
      constructor(sliderElement) {
        this.slider = sliderElement;
        this.isDown = false;
        this.startX = 0;
        this.scrollLeft = 0;
        this.velX = 0;
        this.scrollAnimation = null;
        this.isScrolling = false;
        this.duration = 800; // Change this value if you want to increase or decrease the velocity

        this.scrollStep = this.scrollStep.bind(this);
        this.scrollToSlide = this.scrollToSlide.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseWheel = this.handleMouseWheel.bind(this);

        this.slider.addEventListener('mousedown', this.handleMouseDown);
        this.slider.addEventListener('mouseleave', this.handleMouseLeave);
        this.slider.addEventListener('mouseup', this.handleMouseUp);
        this.slider.addEventListener('mousemove', this.handleMouseMove);
        this.slider.addEventListener('wheel', this.handleMouseWheel, {passive: true});

        this.slider.classList.add(classes$e.enabled);
      }

      handleMouseDown(e) {
        e.preventDefault();
        this.isDown = true;
        this.startX = e.pageX - this.slider.offsetLeft;
        this.scrollLeft = this.slider.scrollLeft;
        this.cancelMomentumTracking();
      }

      handleMouseLeave() {
        if (!this.isDown) return;
        this.isDown = false;
        this.beginMomentumTracking();
      }

      handleMouseUp() {
        this.isDown = false;
        this.beginMomentumTracking();
      }

      handleMouseMove(e) {
        if (!this.isDown) return;
        e.preventDefault();

        const x = e.pageX - this.slider.offsetLeft;
        const ratio = 1; // Increase the number to make it scroll-fast
        const walk = (x - this.startX) * ratio;
        const prevScrollLeft = this.slider.scrollLeft;
        const direction = walk > 0 ? 1 : -1;

        this.slider.classList.add(classes$e.dragging, classes$e.scrolling);
        this.slider.scrollLeft = this.scrollLeft - walk;

        if (this.slider.scrollLeft !== prevScrollLeft) {
          this.velX = this.slider.scrollLeft - prevScrollLeft || direction;
        }
      }

      handleMouseWheel() {
        this.cancelMomentumTracking();
        this.slider.classList.remove(classes$e.scrolling);
      }

      beginMomentumTracking() {
        this.isScrolling = false;
        this.slider.classList.remove(classes$e.dragging);
        this.cancelMomentumTracking();
        this.scrollToSlide();
      }

      cancelMomentumTracking() {
        cancelAnimationFrame(this.scrollAnimation);
      }

      scrollToSlide() {
        if (!this.velX && !this.isScrolling) return;

        const slide = this.slider.querySelector(`${selectors$i.slide}.${classes$e.visible}`);
        if (!slide) return;

        const gap = parseInt(window.getComputedStyle(slide).marginRight) || 0;
        const slideWidth = slide.offsetWidth + gap;
        const targetPosition = slide.offsetLeft;
        const direction = this.velX > 0 ? 1 : -1;
        const slidesToScroll = Math.floor(Math.abs(this.velX) / 100) || 1;

        this.startPosition = this.slider.scrollLeft;
        this.distance = targetPosition - this.startPosition;
        this.startTime = performance.now();
        this.isScrolling = true;

        // Make sure it will move to the next slide if you don't drag far enough
        if (direction < 0 && this.velX < slideWidth) {
          this.distance -= slideWidth * slidesToScroll;
        }

        // Make sure it will move to the previous slide if you don't drag far enough
        if (direction > 0 && this.velX < slideWidth) {
          this.distance += slideWidth * slidesToScroll;
        }

        // Run scroll animation
        this.scrollAnimation = requestAnimationFrame(this.scrollStep);
      }

      scrollStep() {
        const currentTime = performance.now() - this.startTime;
        const scrollPosition = parseFloat(this.easeOutCubic(Math.min(currentTime, this.duration))).toFixed(1);

        this.slider.scrollLeft = scrollPosition;

        if (currentTime < this.duration) {
          this.scrollAnimation = requestAnimationFrame(this.scrollStep);
        } else {
          this.slider.classList.remove(classes$e.scrolling);

          // Reset velocity
          this.velX = 0;
          this.isScrolling = false;
        }
      }

      easeOutCubic(t) {
        t /= this.duration;
        t--;
        return this.distance * (t * t * t + 1) + this.startPosition;
      }

      destroy() {
        this.slider.classList.remove(classes$e.enabled);
        this.slider.removeEventListener('mousedown', this.handleMouseDown);
        this.slider.removeEventListener('mouseleave', this.handleMouseLeave);
        this.slider.removeEventListener('mouseup', this.handleMouseUp);
        this.slider.removeEventListener('mousemove', this.handleMouseMove);
        this.slider.removeEventListener('wheel', this.handleMouseWheel);
      }
    }

    if (!customElements.get('grid-slider')) {
      customElements.define(
        'grid-slider',

        class GridSlider extends HTMLElement {
          constructor() {
            super();

            this.isInitialized = false;
            this.draggableSlider = null;
            this.positionArrows = this.positionArrows.bind(this);
            this.onButtonArrowClick = (e) => this.buttonArrowClickEvent(e);
            this.slidesObserver = null;
            this.firstLastSlidesObserver = null;
            this.isDragging = false;
            this.toggleSlider = this.toggleSlider.bind(this);
          }

          connectedCallback() {
            this.init();
            this.addEventListener('theme:grid-slider:init', this.init);
          }

          init() {
            this.slider = this.querySelector('[data-grid-slider]');
            this.slides = this.querySelectorAll('[data-grid-item]');
            this.buttons = this.querySelectorAll('[data-button-arrow]');
            this.slider.classList.add('scroll-snap-disabled');
            this.toggleSlider();
            document.addEventListener('theme:resize:width', this.toggleSlider);

            window.theme
              .waitForAllAnimationsEnd(this)
              .then(() => {
                this.slider.classList.remove('scroll-snap-disabled');
              })
              .catch(() => {
                this.slider.classList.remove('scroll-snap-disabled');
              });
          }

          toggleSlider() {
            const sliderWidth = this.slider.clientWidth;
            const slidesWidth = this.getSlidesWidth();
            const isEnabled = sliderWidth < slidesWidth;

            if (isEnabled && (!theme.isMobile || !window.theme.touch)) {
              if (this.isInitialized) return;

              this.slidesObserver = new IsInView(this.slider, '[data-grid-item]');

              this.initArrows();
              this.isInitialized = true;

              // Create an instance of DraggableSlider
              this.draggableSlider = new DraggableSlider(this.slider);
            } else {
              this.destroy();
            }
          }

          initArrows() {
            // Create arrow buttons if don't exist
            if (!this.buttons.length) {
              const buttonsWrap = document.createElement('div');
              buttonsWrap.classList.add('slider__arrows');
              buttonsWrap.innerHTML = theme.sliderArrows.prev + theme.sliderArrows.next;

              // Append buttons outside the slider element
              this.append(buttonsWrap);
              this.buttons = this.querySelectorAll('[data-button-arrow]');
              this.buttonPrev = this.querySelector('[data-button-prev]');
              this.buttonNext = this.querySelector('[data-button-next]');
            }

            // Support pre-placed arrow buttons (e.g. in a section header row)
            if (!this.buttonPrev) this.buttonPrev = this.querySelector('[data-button-prev]');
            if (!this.buttonNext) this.buttonNext = this.querySelector('[data-button-next]');

            this.toggleArrowsObserver();

            if (this.hasAttribute('align-arrows')) {
              this.positionArrows();
              this.arrowsResizeObserver();
            }

            this.buttons.forEach((buttonArrow) => {
              buttonArrow.addEventListener('click', this.onButtonArrowClick);
            });
          }

          buttonArrowClickEvent(e) {
            e.preventDefault();

            const firstVisibleSlide = this.slider.querySelector(`[data-grid-item].is-visible`);
            let slide = null;

            if (e.currentTarget.hasAttribute('data-button-prev')) {
              slide = firstVisibleSlide?.previousElementSibling;
            }

            if (e.currentTarget.hasAttribute('data-button-next')) {
              slide = firstVisibleSlide?.nextElementSibling;
            }

            this.goToSlide(slide);
          }

          removeArrows() {
            this.querySelector('.slider__arrows')?.remove();
          }

          // Go to prev/next slide on arrow click
          goToSlide(slide) {
            if (!slide) return;

            this.slider.scrollTo({
              top: 0,
              left: slide.offsetLeft,
              behavior: 'smooth',
            });
          }

          getSlidesWidth() {
            return this.slider.querySelector('[data-grid-item]')?.clientWidth * this.slider.querySelectorAll('[data-grid-item]').length;
          }

          toggleArrowsObserver() {
            // Add disable class/attribute on prev/next button

            if (this.buttonPrev && this.buttonNext) {
              const slidesCount = this.slides.length;
              const firstSlide = this.slides[0];
              const lastSlide = this.slides[slidesCount - 1];

              const config = {
                attributes: true,
                childList: false,
                subtree: false,
              };

              const callback = (mutationList) => {
                for (const mutation of mutationList) {
                  if (mutation.type === 'attributes') {
                    const slide = mutation.target;
                    const isDisabled = Boolean(slide.classList.contains('is-visible'));

                    if (slide == firstSlide) {
                      this.buttonPrev.disabled = isDisabled;
                    }

                    if (slide == lastSlide) {
                      this.buttonNext.disabled = isDisabled;
                    }
                  }
                }
              };

              if (firstSlide && lastSlide) {
                this.firstLastSlidesObserver = new MutationObserver(callback);
                this.firstLastSlidesObserver.observe(firstSlide, config);
                this.firstLastSlidesObserver.observe(lastSlide, config);
              }
            }
          }

          positionArrows() {
            if (this.hasAttribute('images-widths-different')) {
              const figureElements = this.slider.querySelectorAll('figure');

              const biggestHeight = Math.max(0, ...Array.from(figureElements).map((figure) => figure.clientHeight));

              this.style.setProperty('--button-position', `${biggestHeight / 2}px`);
              return;
            }

            const targetElement =
              this.slider.querySelector('[data-social-video-item]') ||
              this.slider.querySelector('[data-product-image]') ||
              this.slider.querySelector('[data-collection-image]') ||
              this.slider.querySelector('[data-column-image]') ||
              this.slider;

            if (!targetElement) return;

            this.style.setProperty('--button-position', `${targetElement.clientHeight / 2}px`);
          }

          arrowsResizeObserver() {
            document.addEventListener('theme:resize:width', this.positionArrows);
          }

          disconnectedCallback() {
            this.destroy();
            document.removeEventListener('theme:resize:width', this.toggleSlider);
          }

          destroy() {
            this.isInitialized = false;
            this.draggableSlider?.destroy();
            this.draggableSlider = null;
            this.slidesObserver?.destroy();
            this.slidesObserver = null;
            this.removeArrows();

            document.removeEventListener('theme:resize:width', this.positionArrows);
          }
        }
      );
    }

    /*
      Observe whether or not there are open modals that require scroll lock
    */

    window.theme.hasOpenModals = function () {
      const openModals = Boolean(document.querySelectorAll('dialog[open][data-scroll-lock-required]').length);
      const openDrawers = Boolean(document.querySelectorAll('.drawer.is-open').length);

      return openModals || openDrawers;
    };

    if (!customElements.get('header-component')) {
      customElements.define(
        'header-component',
        class HeaderComponent extends HTMLElement {
          constructor() {
            super();

            this.style = this.dataset.style;
            this.desktop = this.querySelector('[data-header-desktop]');
            this.deadLinks = document.querySelectorAll('.navlink[href="#"]');
            this.resizeObserver = null;
            this.checkWidth = this.checkWidth.bind(this);
            this.isSticky = this.hasAttribute('data-header-sticky');

            document.body.classList.toggle('has-header-sticky', this.isSticky);
          }

          connectedCallback() {
            this.killDeadLinks();
            this.drawerToggleEvent();
            this.cartToggleEvent();
            this.initSticky();

            if (this.style !== 'drawer' && this.desktop) {
              this.minWidth = this.getMinWidth();
              this.listenWidth();
            }
          }

          listenWidth() {
            if ('ResizeObserver' in window) {
              this.resizeObserver = new ResizeObserver(this.checkWidth);
              this.resizeObserver.observe(this);
            } else {
              document.addEventListener('theme:resize', this.checkWidth);
            }
          }

          drawerToggleEvent() {
            const button = this.querySelector('[data-drawer-toggle]');

            button.addEventListener('click', () => {
              const drawer = document.querySelector('[data-drawer]');

              drawer.dispatchEvent(
                new CustomEvent('theme:drawer:toggle', {
                  bubbles: false,
                  detail: {
                    button: button,
                  },
                })
              );
            });
          }

          killDeadLinks() {
            this.deadLinks.forEach((el) => {
              el.onclick = (e) => {
                e.preventDefault();
              };
            });
          }

          checkWidth() {
            if (document.body.clientWidth < this.minWidth) {
              this.classList.add('js__show__mobile');

              // Update --header-height CSS variable when switching to a mobile nav
              const {headerHeight} = window.theme.readHeights();
              document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
            } else {
              this.classList.remove('js__show__mobile');
            }
          }

          getMinWidth() {
            const comparitor = document.createElement('div');
            comparitor.classList.add('js__header__clone', 'header-wrapper');
            comparitor.appendChild(this.querySelector('header').cloneNode(true));
            document.body.appendChild(comparitor);
            const widthWrappers = comparitor.querySelectorAll('[data-takes-space-wrapper]');
            let minWidth = 0;
            let spaced = 0;

            widthWrappers.forEach((context) => {
              const wideElements = context.querySelectorAll('[data-child-takes-space]');
              let thisWidth = 0;
              if (wideElements.length === 3) {
                thisWidth = this._sumSplitWidths(wideElements);
              } else {
                thisWidth = this._sumWidths(wideElements);
              }
              if (thisWidth > minWidth) {
                minWidth = thisWidth;
                spaced = wideElements.length * 20;
              }
            });

            document.body.removeChild(comparitor);
            return minWidth + spaced;
          }

          cartToggleEvent() {
            if (theme.settings.cartType !== 'drawer') return;

            this.querySelectorAll('[data-cart-toggle]')?.forEach((button) => {
              button.addEventListener('click', (e) => {
                const cartDrawer = document.querySelector('cart-drawer');

                if (cartDrawer) {
                  e.preventDefault();
                  cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
                  window.a11y.lastElement = button;
                }
              });
            });
          }

          initSticky() {
            if (!this.isSticky) return;

            this.isStuck = false;
            this.cls = this.classList;
            this.headerOffset = document.querySelector('.page-header')?.offsetTop;
            this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
            this.scrollEvent = (e) => this.onScroll(e);

            this.listen();
            this.stickOnLoad();
          }

          listen() {
            document.addEventListener('theme:scroll', this.scrollEvent);
            document.addEventListener('shopify:section:load', this.updateHeaderOffset);
            document.addEventListener('shopify:section:unload', this.updateHeaderOffset);
          }

          onScroll(e) {
            if (e.detail.down) {
              if (!this.isStuck && e.detail.position > this.headerOffset) {
                this.stickSimple();
              }
            } else if (e.detail.position <= this.headerOffset) {
              this.unstickSimple();
            }
          }

          updateHeaderOffset(event) {
            if (!event.target.classList.contains('shopify-section-group-header-group')) return;

            // Update header offset after any "Header group" section has been changed
            setTimeout(() => {
              this.headerOffset = document.querySelector('.page-header')?.offsetTop;
            });
          }

          stickOnLoad() {
            if (window.scrollY > this.headerOffset) {
              this.stickSimple();
            }
          }

          stickSimple() {
            this.cls.add('js__header__stuck');
            this.isStuck = true;
          }

          unstickSimple() {
            if (!document.documentElement.hasAttribute('data-scroll-locked')) {
              // check for scroll lock
              this.cls.remove('js__header__stuck');
              this.isStuck = false;
            }
          }

          _sumSplitWidths(nodes) {
            let arr = [];
            nodes.forEach((el) => {
              if (el.firstElementChild) {
                arr.push(el.firstElementChild.clientWidth);
              }
            });
            if (arr[0] > arr[2]) {
              arr[2] = arr[0];
            } else {
              arr[0] = arr[2];
            }
            const width = arr.reduce((a, b) => a + b);
            return width;
          }

          _sumWidths(nodes) {
            let width = 0;
            nodes.forEach((el) => {
              width += el.clientWidth;
            });
            return width;
          }

          disconnectedCallback() {
            if ('ResizeObserver' in window) {
              this.resizeObserver?.unobserve(this);
            } else {
              document.removeEventListener('theme:resize', this.checkWidth);
            }

            if (this.isSticky) {
              document.removeEventListener('theme:scroll', this.scrollEvent);
              document.removeEventListener('shopify:section:load', this.updateHeaderOffset);
              document.removeEventListener('shopify:section:unload', this.updateHeaderOffset);
            }
          }
        }
      );
    }

    const selectors$h = {
      link: '[data-top-link]',
      wrapper: '[data-header-wrapper]',
      stagger: '[data-stagger]',
      staggerPair: '[data-stagger-first]',
      staggerAfter: '[data-stagger-second]',
      focusable: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    };

    const classes$d = {
      isVisible: 'is-visible',
      meganavVisible: 'meganav--visible',
      meganavIsTransitioning: 'meganav--is-transitioning',
    };

    if (!customElements.get('hover-disclosure')) {
      customElements.define(
        'hover-disclosure',

        class HoverDisclosure extends HTMLElement {
          constructor() {
            super();

            this.wrapper = this.closest(selectors$h.wrapper);
            this.key = this.getAttribute('aria-controls');
            this.link = this.querySelector(selectors$h.link);
            this.grandparent = this.classList.contains('grandparent');
            this.disclosure = document.getElementById(this.key);
            this.transitionTimeout = 0;
            this.hideTimeout = 0;
          }

          connectedCallback() {
            this.setAttribute('aria-haspopup', true);
            this.setAttribute('aria-expanded', false);
            this.setAttribute('aria-controls', this.key);

            this.connectHoverToggle();
            this.handleTablets();
            this.staggerChildAnimations();

            this.addEventListener('theme:disclosure:show', (evt) => {
              this.showDisclosure(evt);
            });
            this.addEventListener('theme:disclosure:hide', (evt) => {
              this.hideDisclosure(evt);
            });
          }

          showDisclosure(e) {
            if (this.hideTimeout) {
              clearTimeout(this.hideTimeout);
              this.hideTimeout = 0;
            }

            if (e && e.type && e.type === 'mouseenter') {
              this.wrapper.classList.add(classes$d.meganavIsTransitioning);
            }

            if (this.grandparent) {
              this.wrapper.classList.add(classes$d.meganavVisible);
            } else {
              this.wrapper.classList.remove(classes$d.meganavVisible);
            }
            this.setAttribute('aria-expanded', true);
            this.classList.add(classes$d.isVisible);
            this.disclosure.classList.add(classes$d.isVisible);

            if (this.transitionTimeout) {
              clearTimeout(this.transitionTimeout);
            }

            this.transitionTimeout = setTimeout(() => {
              this.wrapper.classList.remove(classes$d.meganavIsTransitioning);
            }, 200);
          }

          hideDisclosure() {
            this.classList.remove(classes$d.isVisible);
            this.disclosure.classList.remove(classes$d.isVisible);
            this.setAttribute('aria-expanded', false);

            // Only release the header's solid (meganav--visible) state once no
            // other grandparent mega menu is still open. When moving between
            // top-level items the outgoing item's delayed hide fires ~100ms
            // after the next item has already opened; without this guard it
            // would strip meganav--visible and the transparent header would
            // flash back while a mega menu is still on screen.
            const anotherMegaOpen = this.wrapper.querySelector('hover-disclosure.grandparent.is-visible');
            if (!anotherMegaOpen) {
              this.wrapper.classList.remove(classes$d.meganavVisible);
            }
            this.wrapper.classList.remove(classes$d.meganavIsTransitioning);
          }

          staggerChildAnimations() {
            const simple = this.querySelectorAll(selectors$h.stagger);
            let step = 50;
            simple.forEach((el, index) => {
              el.style.transitionDelay = `${index * step + 10}ms`;
              step *= 0.95;
            });

            const pairs = this.querySelectorAll(selectors$h.staggerPair);
            pairs.forEach((child, i) => {
              const d1 = i * 100;
              child.style.transitionDelay = `${d1}ms`;
              child.parentElement.querySelectorAll(selectors$h.staggerAfter).forEach((grandchild, i2) => {
                const di1 = i2 + 1;
                const d2 = di1 * 20;
                grandchild.style.transitionDelay = `${d1 + d2}ms`;
              });
            });
          }

          handleTablets() {
            // first click opens the popup, second click opens the link
            this.addEventListener(
              'touchstart',
              function (e) {
                const isOpen = this.classList.contains(classes$d.isVisible);
                if (!isOpen) {
                  e.preventDefault();
                  this.showDisclosure(e);
                }
              }.bind(this),
              {passive: true}
            );
          }

          connectHoverToggle() {
            this.addEventListener('mouseenter', (e) => this.showDisclosure(e));
            this.link.addEventListener('focus', (e) => this.showDisclosure(e));

            this.addEventListener('mouseleave', () => {
              this.hideTimeout = setTimeout(() => this.hideDisclosure(), 100);
            });
            this.addEventListener('focusout', (e) => {
              const inMenu = this.contains(e.relatedTarget);
              if (!inMenu) {
                this.hideDisclosure();
              }
            });
            this.addEventListener('keyup', (evt) => {
              if (evt.code !== 'Escape') {
                return;
              }
              this.hideDisclosure();
            });
          }
        }
      );
    }

    if (!customElements.get('megamenu-two-panel')) {
      customElements.define(
        'megamenu-two-panel',

        class MegamenuTwoPanel extends HTMLElement {
          constructor() {
            super();
            this.triggers = this.querySelectorAll('[data-megamenu-trigger]');
            this.sublinkPanels = this.querySelectorAll('[data-megamenu-panel]');
            this.imagePanels = this.querySelectorAll('[data-megamenu-image]');
            this.panelTimeout = 0;
            this.mouseX = 0;
            this.prevMouseX = 0;
          }

          connectedCallback() {
            this.addEventListener('mousemove', (e) => {
              this.prevMouseX = this.mouseX;
              this.mouseX = e.clientX;
            });

            this.triggers.forEach((trigger) => {
              // Switch panels only when the actual label text is hovered, not
              // the whole row. The row container and its block-level <a> both
              // span the full nav column, so hovering the empty space beside a
              // short label used to switch the right-hand results — which made
              // it hard to travel diagonally toward the panel without changing
              // it. The inline `.navtext` span hugs the text, so use that as
              // the hover hotspot. (Activation still targets the row for its
              // is-active state; keyboard focus still uses the row too.)
              const hotspot = trigger.querySelector('.navtext') || trigger.querySelector('.navlink') || trigger;
              hotspot.addEventListener('mouseenter', () => {
                if (this.panelTimeout) clearTimeout(this.panelTimeout);
                // If mouse is moving rightward (towards content panel), use longer delay
                // so diagonal movement doesn't accidentally switch the active panel
                const movingRight = this.mouseX > this.prevMouseX;
                const delay = movingRight ? 250 : 80;
                this.panelTimeout = setTimeout(() => this.activatePanel(trigger), delay);
              });
              trigger.addEventListener('focusin', () => this.activatePanel(trigger));
            });

            // Once the cursor leaves the column of labels (heading toward the
            // results), drop any queued switch — otherwise a label you merely
            // passed through on the way out could change the panel behind you.
            const navColumn = this.querySelector('[data-megamenu-nav]');
            if (navColumn) {
              navColumn.addEventListener('mouseleave', () => {
                if (this.panelTimeout) clearTimeout(this.panelTimeout);
              });
            }
          }

          activatePanel(trigger) {
            // Already showing this panel — nothing to do (avoids redundant
            // class churn when hover and focus both fire for the same item).
            if (trigger.classList.contains('is-active')) return;

            const index = trigger.getAttribute('data-megamenu-trigger');

            this.triggers.forEach((t) => t.classList.remove('is-active'));
            this.sublinkPanels.forEach((p) => p.classList.remove('is-active'));
            this.imagePanels.forEach((p) => p.classList.remove('is-active'));

            trigger.classList.add('is-active');

            const sublinkPanel = this.querySelector(`[data-megamenu-panel="${index}"]`);
            if (sublinkPanel) {
              sublinkPanel.classList.add('is-active');
            }

            const imagePanel = this.querySelector(`[data-megamenu-image="${index}"]`);
            if (imagePanel) {
              imagePanel.classList.add('is-active');
            }
          }
        }
      );
    }
    const selectors$g = {
      drawerInner: '[data-drawer-inner]',
      drawerClose: '[data-drawer-close]',
      underlay: '[data-drawer-underlay]',
      stagger: '[data-stagger-animation]',
      wrapper: '[data-header-wrapper]',
      focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
    };

    const classes$c = {
      animated: 'drawer--animated',
      open: 'is-open',
      closing: 'is-closing',
      isFocused: 'is-focused',
      headerStuck: 'js__header__stuck',
    };

    if (!customElements.get('header-drawer')) {
      customElements.define(
        'header-drawer',
        class HeaderDrawer extends HTMLElement {
          constructor() {
            super();

            this.a11y = window.theme.a11y;
            this.isAnimating = false;
            this.drawer = this;
            this.drawerInner = this.querySelector(selectors$g.drawerInner);
            this.underlay = this.querySelector(selectors$g.underlay);
            this.triggerButton = null;

            this.staggers = this.querySelectorAll(selectors$g.stagger);
            this.showDrawer = this.showDrawer.bind(this);
            this.hideDrawer = this.hideDrawer.bind(this);

            this.connectDrawer();
            this.closers();
          }

          connectDrawer() {
            this.addEventListener('theme:drawer:toggle', (e) => {
              this.triggerButton = e.detail?.button;

              if (this.classList.contains(classes$c.open)) {
                this.dispatchEvent(
                  new CustomEvent('theme:drawer:close', {
                    bubbles: true,
                  })
                );
              } else {
                this.dispatchEvent(
                  new CustomEvent('theme:drawer:open', {
                    bubbles: true,
                  })
                );
              }
            });

            this.addEventListener('theme:drawer:close', this.hideDrawer);
            this.addEventListener('theme:drawer:open', this.showDrawer);

            document.addEventListener('theme:cart-drawer:open', this.hideDrawer);
          }

          closers() {
            this.querySelectorAll(selectors$g.drawerClose)?.forEach((button) => {
              button.addEventListener('click', () => {
                this.hideDrawer();
              });
            });

            document.addEventListener('keyup', (event) => {
              if (event.code !== 'Escape') {
                return;
              }

              this.hideDrawer();
            });

            this.underlay.addEventListener('click', () => {
              this.hideDrawer();
            });
          }

          showDrawer() {
            if (this.isAnimating) return;

            this.isAnimating = true;

            // Calculate and set dropdown position from actual header
            const headerEl = document.querySelector('[data-header-height]');
            if (headerEl) {
              const headerRect = headerEl.getBoundingClientRect();
              const menuTop = headerRect.bottom;
              if (this.drawerInner) {
                this.drawerInner.style.setProperty('--menu-top', `${menuTop}px`);
                this.drawerInner.style.top = `${menuTop}px`;
                this.drawerInner.style.height = `calc(100% - ${menuTop}px)`;
              }
            }

            document.body.classList.add('mobile-menu-open');

            this.triggerButton?.setAttribute('aria-expanded', true);
            this.classList.add(classes$c.open, classes$c.animated);

            document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

            if (this.drawerInner) {
              this.a11y.removeTrapFocus();

              window.theme.waitForAnimationEnd(this.drawerInner).then(() => {
                this.isAnimating = false;

                this.a11y.trapFocus(this.drawerInner, {
                  elementToFocus: this.querySelector(selectors$g.focusable),
                });
              });
            }
          }

          hideDrawer() {
            if (this.isAnimating || !this.classList.contains(classes$c.open)) return;

            this.isAnimating = true;

            document.body.classList.remove('mobile-menu-open');

            this.classList.add(classes$c.closing);
            this.classList.remove(classes$c.open);

            this.a11y.removeTrapFocus();

            if (this.triggerButton) {
              this.triggerButton.setAttribute('aria-expanded', false);

              if (document.body.classList.contains(classes$c.isFocused)) {
                this.triggerButton.focus();
              }
            }

            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

            window.theme.waitForAnimationEnd(this.drawerInner).then(() => {
              this.classList.remove(classes$c.closing, classes$c.animated);

              this.isAnimating = false;

              // Reset menu items state after drawer hiding animation completes
              document.dispatchEvent(new CustomEvent('theme:sliderule:close', {bubbles: false}));
            });
          }

          disconnectedCallback() {
            document.removeEventListener('theme:cart-drawer:open', this.hideDrawer);
          }
        }
      );
    }

    if (!customElements.get('mobile-sliderule')) {
      customElements.define(
        'mobile-sliderule',

        class HeaderMobileSliderule extends HTMLElement {
          constructor() {
            super();

            this.key = this.id;
            this.menuBlock = this.closest('[data-mobile-menu-block]');
            this.sliderule = this.querySelector('[data-sliderule]');
            const btnSelector = `[data-sliderule-open='${this.key}']`;
            this.exitSelector = `[data-sliderule-close='${this.key}']`;
            this.trigger = this.querySelector(btnSelector);
            this.exit = document.querySelectorAll(this.exitSelector);
            this.pane = this.trigger.closest('[data-sliderule-pane]');
            this.childrenElements = this.querySelectorAll(`:scope > [data-animates],
             :scope > * > [data-animates],
             :scope > * > * > [data-animates],
             :scope > * > .sliderule-grid  > *`);
            this.drawerContent = this.closest('[data-drawer-content]');
            this.cachedButton = null;
            this.a11y = window.theme.a11y;

            this.trigger.setAttribute('aria-haspopup', true);
            this.trigger.setAttribute('aria-expanded', false);
            this.trigger.setAttribute('aria-controls', this.key);
            this.closeSliderule = this.closeSliderule.bind(this);

            this.clickEvents();
            this.keyboardEvents();

            document.addEventListener('theme:sliderule:close', this.closeSliderule);
          }

          connectedCallback() {
            // Set initial height for menu block (required for CSS transition)
            requestAnimationFrame(() => {
              if (this.menuBlock && !this.menuBlock.style.getPropertyValue('--block-height')) {
                this.menuBlock.style.setProperty('--block-height', `${this.menuBlock.scrollHeight}px`);
              }
            });
          }

          clickEvents() {
            this.trigger.addEventListener('click', () => {
              this.cachedButton = this.trigger;
              this.showSliderule();
            });
            this.exit.forEach((element) => {
              element.addEventListener('click', () => {
                this.hideSliderule();
              });
            });
          }

          keyboardEvents() {
            this.addEventListener('keyup', (evt) => {
              evt.stopPropagation();
              if (evt.code !== 'Escape') {
                return;
              }

              this.hideSliderule();
            });
          }

          adjustMenuHeight() {
            if (!this.menuBlock) return;

            const visiblePanel = this.menuBlock.querySelector('[data-sliderule="2"].is-visible') || this.menuBlock.querySelector('[data-sliderule="1"].is-visible') || this.menuBlock;
            const linksContainer = visiblePanel.querySelector('[data-links]');

            this.menuBlock.style.setProperty('--block-height', `${linksContainer.scrollHeight}px`);
          }

          trapFocusSliderule(showSliderule = true) {
            const trapFocusButton = showSliderule ? this.querySelector(this.exitSelector) : this.cachedButton;

            this.a11y.removeTrapFocus();

            if (trapFocusButton && this.drawerContent) {
              this.a11y.trapFocus(this.drawerContent, {
                elementToFocus: document.body.classList.contains('is-focused') ? trapFocusButton : null,
              });
            }
          }

          hideSliderule(close = false) {
            const newPosition = parseInt(this.pane.dataset.sliderulePane, 10) - 1;
            this.pane.setAttribute('data-sliderule-pane', newPosition);
            this.pane.classList.add('is-hiding');
            this.sliderule.classList.add('is-hiding');
            const hiddenSelector = close ? `[data-animates].is-hidden` : `[data-animates="${newPosition}"]`;
            const hiddenItems = this.pane.querySelectorAll(hiddenSelector);
            hiddenItems?.forEach((element) => {
              element.classList.remove('is-hidden');
            });

            const children = close ? this.pane.querySelectorAll('.is-visible, .is-hiding') : this.childrenElements;
            const childrenLength = children.length;
            children.forEach((element, index) => {
              const lastElement = index === childrenLength - 1;
              element.classList.remove('is-visible');
              if (close) {
                element.classList.remove('is-hiding');
                this.pane.classList.remove('is-hiding');
              }
              const removeHidingClass = () => {
                if (parseInt(this.pane.getAttribute('data-sliderule-pane')) === newPosition) {
                  this.sliderule.classList.remove('is-visible');
                }
                this.sliderule.classList.remove('is-hiding');
                this.pane.classList.remove('is-hiding');

                if (lastElement) {
                  this.a11y.removeTrapFocus();
                  if (!close) {
                    this.trapFocusSliderule(false);
                  }
                  requestAnimationFrame(() => {
                    this.adjustMenuHeight();
                  });
                }

                element.removeEventListener('animationend', removeHidingClass);
              };

              if (window.theme.settings.enableAnimations) {
                element.addEventListener('animationend', removeHidingClass);
              } else {
                removeHidingClass();
              }
            });
          }

          showSliderule() {
            let lastScrollableFrame = null;
            const parent = this.closest('.is-visible');
            const lastScrollableElement = parent || this.pane;

            lastScrollableElement.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });

            lastScrollableElement.classList.add('is-scrolling');

            const lastScrollableIsScrolling = () => {
              if (lastScrollableElement.scrollTop <= 0) {
                lastScrollableElement.classList.remove('is-scrolling');
                if (lastScrollableFrame) {
                  cancelAnimationFrame(lastScrollableFrame);
                }
              } else {
                lastScrollableFrame = requestAnimationFrame(lastScrollableIsScrolling);
              }
            };

            lastScrollableFrame = requestAnimationFrame(lastScrollableIsScrolling);

            const oldPosition = parseInt(this.pane.dataset.sliderulePane, 10);
            const newPosition = oldPosition + 1;
            this.sliderule.classList.add('is-visible');
            this.pane.setAttribute('data-sliderule-pane', newPosition);

            requestAnimationFrame(() => {
              this.adjustMenuHeight();
            });

            const hiddenItems = this.pane.querySelectorAll(`[data-animates="${oldPosition}"]`);
            const hiddenItemsLength = hiddenItems.length;
            hiddenItems?.forEach((element, index) => {
              const lastElement = index === hiddenItemsLength - 1;
              element.classList.add('is-hiding');
              const removeHidingClass = () => {
                element.classList.remove('is-hiding');
                if (parseInt(this.pane.getAttribute('data-sliderule-pane')) !== oldPosition) {
                  element.classList.add('is-hidden');
                }

                if (lastElement) {
                  this.trapFocusSliderule();
                }
                element.removeEventListener('animationend', removeHidingClass);
              };

              if (window.theme.settings.enableAnimations) {
                element.addEventListener('animationend', removeHidingClass);
              } else {
                removeHidingClass();
              }
            });
          }

          closeSliderule() {
            if (this.pane && this.pane.hasAttribute('data-sliderule-pane') && parseInt(this.pane.getAttribute('data-sliderule-pane')) > 0) {
              this.hideSliderule(true);
              if (parseInt(this.pane.getAttribute('data-sliderule-pane')) > 0) {
                this.pane.setAttribute('data-sliderule-pane', 0);
              }
              requestAnimationFrame(() => {
                this.adjustMenuHeight();
              });
            }
          }

          disconnectedCallback() {
            document.removeEventListener('theme:sliderule:close', this.closeSliderule);
          }
        }
      );
    }

    const selectors$f = {
      details: 'details',
      popdown: '[data-popdown]',
      popdownClose: '[data-popdown-close]',
      input: 'input:not([type="hidden"])',
      mobileMenu: 'mobile-menu',
    };

    const attributes$a = {
      popdownUnderlay: 'data-popdown-underlay',
      scrollLocked: 'data-scroll-locked',
    };

    const classes$b = {
      open: 'is-open',
    };
    class SearchPopdown extends HTMLElement {
      constructor() {
        super();
        this.popdown = this.querySelector(selectors$f.popdown);
        this.popdownContainer = this.querySelector(selectors$f.details);
        this.popdownClose = this.querySelector(selectors$f.popdownClose);
        this.popdownTransitionCallback = this.popdownTransitionCallback.bind(this);
        this.detailsToggleCallback = this.detailsToggleCallback.bind(this);
        this.mobileMenu = this.closest(selectors$f.mobileMenu);
        this.a11y = window.theme.a11y;
      }

      connectedCallback() {
        this.popdown.addEventListener('transitionend', this.popdownTransitionCallback);
        this.popdownContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
        this.popdownContainer.addEventListener('toggle', this.detailsToggleCallback);
        this.popdownClose.addEventListener('click', this.close.bind(this));
      }

      detailsToggleCallback(event) {
        if (event.target.hasAttribute('open')) {
          this.open();
        }
      }

      popdownTransitionCallback(event) {
        if (event.target !== this.popdown || event.propertyName !== 'opacity') return;

        if (!this.classList.contains('is-open')) {
          this.popdownContainer.removeAttribute('open');
          this.a11y.removeTrapFocus();
        } else {
          // Wait for the 'transform' transition to complete in order to prevent jumping content issues because of the trapFocus
          this.a11y.trapFocus(this.popdown, {
            elementToFocus: this.popdown.querySelector('input:not([type="hidden"])'),
          });
        }
      }

      onBodyClick(event) {
        const insideHost = this.contains(event.target);
        const insidePopdown = this.popdown && this.popdown.contains(event.target);
        if ((!insideHost && !insidePopdown) || event.target.hasAttribute(attributes$a.popdownUnderlay)) this.close();
      }

      open() {
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

        document.body.addEventListener('click', this.onBodyClickEvent);

        if (!document.documentElement.hasAttribute(attributes$a.scrollLocked)) {
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
        }

        // Teleport the popdown panel out of the (transformed) header into <body>
        // so position:fixed works against the viewport, not against the header.
        if (this.popdown && this.popdown.parentElement !== document.body) {
          this._popdownOrigParent = this.popdown.parentElement;
          this._popdownOrigNext = this.popdown.nextSibling;
          document.body.appendChild(this.popdown);
        }

        requestAnimationFrame(() => {
          this.classList.add(classes$b.open);
          if (this.popdown) this.popdown.classList.add(classes$b.open);
        });
      }

      _restorePopdown() {
        if (this.popdown && this._popdownOrigParent && this.popdown.parentElement === document.body) {
          this._popdownOrigParent.insertBefore(this.popdown, this._popdownOrigNext || null);
        }
      }

      close() {
        this.classList.remove(classes$b.open);
        if (this.popdown) this.popdown.classList.remove(classes$b.open);

        document.body.removeEventListener('click', this.onBodyClickEvent);

        if (!this.mobileMenu) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }
      }
    }

    if (!customElements.get('header-search-popdown')) {
      customElements.define('header-search-popdown', SearchPopdown);
    }

/* assets/js/components/search-explore.js */
//
// <search-explore> — drives the redesigned header search popdown.
//
// Responsibilities:
//  1. Tab switching for the empty-state Explore module
//     (Bestsellers / New Arrivals / Collections / Artists).
//  2. Mode switching: tabs ⇄ suggestions sidebar based on input value.
//  3. Re-homing the predictive-search results so that the Suggestions
//     list lives inside the sidebar and the products grid lives inside
//     the panels area.
//  4. Client-side artist name filtering (since Shopify predictive search
//     does not index metaobjects natively).
//
// Lives inside the same IIFE as the rest of theme.dev.js (no `import`).
//

if (!customElements.get('search-explore')) {
  customElements.define(
    'search-explore',

    class SearchExplore extends HTMLElement {
      constructor() {
        super();
        this.unique = this.dataset.unique || 'desktop';
        this.defaultTab = this.dataset.defaultTab || 'bestsellers';
        this.popdown = this.closest('.search-popdown') || this.closest('header-search-popdown');
        this.input =
          this.popdown && this.popdown.querySelector(`#SearchInput--${this.unique}`);
        this.predictiveResults =
          this.popdown && this.popdown.querySelector('[data-predictive-search-results]');
        this.predictiveSearch =
          this.popdown && this.popdown.querySelector('predictive-search');

        // Sidebar regions
        this.exploreMode = this.querySelector('[data-sidebar-mode="explore"]');
        this.suggestionsMode = this.querySelector('[data-sidebar-mode="suggestions"]');
        this.suggestionsList = this.querySelector('[data-search-suggestions]');
        this.suggestionsEmpty = this.querySelector('[data-suggestions-empty]');

        // Panels
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
        this.tabs = Array.from(this.querySelectorAll('[data-tab]'));

        // Artist filter index
        const artistIndexEl = this.querySelector('[data-artist-index]');
        try {
          this.artistIndex = artistIndexEl ? JSON.parse(artistIndexEl.textContent) : [];
        } catch (e) {
          this.artistIndex = [];
        }

        // Container that hosts the live products grid when mode=active.
        this.productsHost = this.querySelector('[data-search-products-host]') || this._createProductsHost();
      }

      connectedCallback() {
        // Reveal (the snippet ships with `hidden` to avoid flash before JS).
        this.removeAttribute('hidden');

        // Wire tabs (click + hover both activate, matching Figma behavior).
        this.tabs.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.activateTab(btn.dataset.tab);
          });
          btn.addEventListener('mouseenter', () => {
            this.activateTab(btn.dataset.tab);
          });
          btn.addEventListener('focus', () => {
            this.activateTab(btn.dataset.tab);
          });
        });

        // Default tab.
        this.activateTab(this.defaultTab);

        // Wire input watcher for mode switching.
        if (this.input) {
          this.input.addEventListener('input', this._onInput.bind(this));
          // If the popdown opens with a pre-filled value, sync immediately.
          if (this.input.value.trim().length > 0) this._setMode('active');
        }

        // Watch the predictive-search results region so we can pluck out the
        // suggestions list and the products grid after every AJAX render.
        if (this.predictiveResults) {
          this._observer = new MutationObserver(this._onResultsMutate.bind(this));
          this._observer.observe(this.predictiveResults, { childList: true, subtree: true });
        }

        // Reset button — clear input and return to explore mode.
        const resetBtn = this.popdown && this.popdown.querySelector('.search-popdown__reset');
        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            // Defer so the form's reset has flushed.
            setTimeout(() => this._setMode('explore'), 0);
          });
        }
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
      }

      // ------------------------------------------------------------------
      // Tabs
      // ------------------------------------------------------------------

      activateTab(name) {
        this.tabs.forEach((btn) => {
          const active = btn.dataset.tab === name;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.panels.forEach((panel) => {
          const active = panel.dataset.tabPanel === name;
          panel.classList.toggle('is-active', active);
          if (active) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        });
        this.dataset.activeTab = name;
      }

      // ------------------------------------------------------------------
      // Mode switching (explore / active)
      // ------------------------------------------------------------------

      _onInput() {
        const value = this.input.value.trim();
        if (!value) {
          this._setMode('explore');
          this._clearSuggestions();
          if (this.productsHost) this.productsHost.innerHTML = '';
          return;
        }
        this._setMode('active');
        // Render suggestions (artists-only) immediately so the sidebar
        // is responsive while the AJAX request for products is in flight.
        // Cards in the unified products grid are rebuilt by
        // `_onResultsMutate` once the predictive-search response lands.
        this._renderSuggestions({ artistsOnly: true });
      }

      _setMode(mode) {
        if (mode === 'active') {
          this.dataset.mode = 'active';
          if (this.exploreMode) this.exploreMode.hidden = true;
          if (this.suggestionsMode) this.suggestionsMode.hidden = false;
        } else {
          delete this.dataset.mode;
          if (this.exploreMode) this.exploreMode.hidden = false;
          if (this.suggestionsMode) this.suggestionsMode.hidden = true;
        }
      }

      // ------------------------------------------------------------------
      // Predictive-search results integration
      // ------------------------------------------------------------------

      _onResultsMutate() {
        if (this.dataset.mode !== 'active') return;
        // Move the products grid wrapper into productsHost (only once per
        // AJAX response — the parentElement guard prevents re-entrant
        // mutations from disrupting subsequent observer ticks). Then
        // inject artist cards INTO the inner Shopify `.grid` element so
        // both card types are siblings inside the same grid container,
        // sharing column layout + gap.
        const products = this.predictiveResults.querySelector('[data-search-results]');
        if (products && products.parentElement !== this.productsHost) {
          this.productsHost.innerHTML = '';
          this.productsHost.appendChild(products);
        }

        if (products) {
          const grid = products.querySelector('.grid') || products;
          // Strip previously-injected artist cards before re-inserting,
          // since the input value (and matching set) may have changed.
          grid.querySelectorAll('[data-search-artist-card]').forEach((el) => el.remove());
          this._prependArtistCardsTo(grid);
        }

        // Move the "Search for X" CTA out of the absolute-positioned
        // .predictive-search container into the panels area so it lands
        // naturally at the bottom of all results. Always strip any
        // previously-moved actions first — predictive-search creates a
        // fresh actions node on every AJAX response, so identity-based
        // checks aren't reliable.
        const panels = this.querySelector('.search-explore__panels');
        const actions = this.predictiveResults.querySelector('.predictive-search__actions');
        if (panels) {
          panels.querySelectorAll('.predictive-search__actions').forEach((el) => {
            if (el !== actions) el.remove();
          });
          if (actions && actions.parentElement !== panels) {
            panels.appendChild(actions);
          }
        }
        // Render suggestions (server queries + collections + artists).
        this._renderSuggestions({ artistsOnly: false });
      }

      _prependArtistCardsTo(grid) {
        const value = (this.input && this.input.value.trim()) || '';
        if (!value) return;
        const needle = value.toLowerCase();
        const matches = this.artistIndex
          .filter((a) => a.name && a.name.toLowerCase().includes(needle))
          .slice(0, 5);
        if (matches.length === 0) return;

        // Build cards then prepend in reverse so first match ends up first.
        matches
          .slice()
          .reverse()
          .forEach((a) => {
            const link = document.createElement('a');
            link.href = a.url || '/pages/artists';
            link.className = 'search-explore__card search-explore__card--artist predictive-search__grid-item';
            link.dataset.searchArtistCard = '';
            link.tabIndex = -1;

            const media = document.createElement('div');
            media.className = 'search-explore__card-media search-explore__card-media--cover';
            if (a.image) {
              const img = document.createElement('img');
              img.src = a.image;
              img.alt = a.name || '';
              img.loading = 'lazy';
              img.decoding = 'async';
              img.className = 'search-explore__card-image search-explore__card-image--cover';
              media.appendChild(img);
            }
            link.appendChild(media);

            const info = document.createElement('div');
            info.className = 'search-explore__card-info';
            const title = document.createElement('p');
            title.className = 'search-explore__card-title search-explore__card-title--artist';
            title.textContent = a.name || '';
            info.appendChild(title);
            link.appendChild(info);

            grid.insertBefore(link, grid.firstChild);
          });
      }

      _renderSuggestions(opts) {
        if (!this.suggestionsList) return;
        const value = (this.input && this.input.value.trim()) || '';
        if (!value) {
          this._clearSuggestions();
          return;
        }
        const items = [];

        // Server suggestions (queries + collection titles + page/article titles).
        // Pulled from `.predictive-search__item` nodes rendered by
        // sections/predictive-search.liquid — each contains a
        // `.predictive-search__link` anchor with the suggestion text/url.
        if (!opts || !opts.artistsOnly) {
          if (this.predictiveResults) {
            const seen = new Set();
            const serverItems = this.predictiveResults.querySelectorAll(
              '.predictive-search__item .predictive-search__link'
            );
            Array.from(serverItems)
              .slice(0, 8)
              .forEach((a) => {
                const text = (a.textContent || '').trim();
                if (!text) return;
                const key = text.toLowerCase();
                if (seen.has(key)) return;
                seen.add(key);

                const li = document.createElement('li');
                li.className = 'predictive-suggestions-list__item';
                li.dataset.suggestionKind = 'server';
                li.setAttribute('role', 'option');
                li.setAttribute('aria-selected', 'false');

                const link = document.createElement('a');
                link.className = 'predictive-suggestions-list__link';
                link.href = a.getAttribute('href') || '#';
                link.tabIndex = -1;
                link.dataset.suggestionText = text;
                link.textContent = text;
                li.appendChild(link);
                items.push(li);
              });
          }
        }

        // Client-side artist filter.
        const needle = value.toLowerCase();
        const matchingArtists = this.artistIndex
          .filter((a) => a.name && a.name.toLowerCase().includes(needle))
          .slice(0, 3);
        matchingArtists.forEach((a) => {
          const li = document.createElement('li');
          li.className = 'predictive-suggestions-list__item';
          li.dataset.suggestionKind = 'artist';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');

          const link = document.createElement('a');
          link.className = 'predictive-suggestions-list__link';
          link.href = a.url;
          link.tabIndex = -1;
          link.dataset.suggestionText = a.name;
          link.textContent = a.name;
          li.appendChild(link);
          items.push(li);
        });

        // Replace list contents.
        this.suggestionsList.innerHTML = '';
        items.forEach((li) => this.suggestionsList.appendChild(li));
        // Hide the entire Suggestions sidebar mode when there's nothing
        // to show — avoids the dangling "Keep typing..." placeholder.
        if (this.suggestionsMode) {
          this.suggestionsMode.hidden = items.length === 0;
        }
      }

      _clearSuggestions() {
        if (!this.suggestionsList) return;
        this.suggestionsList.innerHTML = '';
      }

      // ------------------------------------------------------------------
      // Helpers
      // ------------------------------------------------------------------

      _createProductsHost() {
        const host = document.createElement('div');
        host.className = 'search-explore__results-host';
        host.dataset.searchProductsHost = '';
        const panels = this.querySelector('.search-explore__panels');
        if (panels) panels.appendChild(host);
        return host;
      }
    }
  );
}
    const selectors$e = {
      inputSearch: 'input[type="search"]',
      focusedElements: '[aria-selected="true"] a',
      resetButton: 'button[type="reset"]',
    };

    const classes$a = {
      hidden: 'hidden',
    };

    class HeaderSearchForm extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector(selectors$e.inputSearch);
        this.resetButton = this.querySelector(selectors$e.resetButton);

        if (this.input) {
          this.input.form.addEventListener('reset', this.onFormReset.bind(this));
          this.input.addEventListener(
            'input',
            window.theme
              .debounce((event) => {
                this.onChange(event);
              }, 300)
              .bind(this)
          );
        }
      }

      toggleResetButton() {
        const resetIsHidden = this.resetButton.classList.contains(classes$a.hidden);
        if (this.input.value.length > 0 && resetIsHidden) {
          this.resetButton.classList.remove(classes$a.hidden);
        } else if (this.input.value.length === 0 && !resetIsHidden) {
          this.resetButton.classList.add(classes$a.hidden);
        }
      }

      onChange() {
        this.toggleResetButton();
      }

      shouldResetForm() {
        return !document.querySelector(selectors$e.focusedElements);
      }

      onFormReset(event) {
        // Prevent default so the form reset doesn't set the value gotten from the url on page load
        event.preventDefault();
        // Don't reset if the user has selected an element on the predictive search dropdown
        if (this.shouldResetForm()) {
          this.input.value = '';
          this.toggleResetButton();
          event.target.querySelector(selectors$e.inputSearch).focus();
        }
      }
    }

    customElements.define('header-search-form', HeaderSearchForm);

    const selectors$d = {
      inputSearch: 'input[type="search"]',
    };

    class MainSearch extends HeaderSearchForm {
      constructor() {
        super();

        this.allSearchInputs = document.querySelectorAll(selectors$d.inputSearch);
        this.setupEventListeners();
      }

      setupEventListeners() {
        let allSearchForms = [];
        this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
        this.input.addEventListener('focus', this.onInputFocus.bind(this));
        if (allSearchForms.length < 2) return;
        allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
        this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
      }

      onFormReset(event) {
        super.onFormReset(event);
        if (super.shouldResetForm()) {
          this.keepInSync('', this.input);
        }
      }

      onInput(event) {
        const target = event.target;
        this.keepInSync(target.value, target);
      }

      onInputFocus() {
        if (theme.isMobile) {
          this.scrollIntoView({behavior: 'smooth'});
        }
      }

      keepInSync(value, target) {
        this.allSearchInputs.forEach((input) => {
          if (input !== target) {
            input.value = value;
          }
        });
      }
    }

    if (!customElements.get('main-search')) {
      customElements.define('main-search', MainSearch);
    }

    const selectors$c = {
      scrollbar: '[data-scrollbar]',
      scrollbarArrowPrev: '[data-scrollbar-arrow-prev]',
      scrollbarArrowNext: '[data-scrollbar-arrow-next]',
    };

    const classes$9 = {
      hidden: 'is-hidden',
    };

    const attributes$9 = {
      scrollbarSlider: 'data-scrollbar-slider',
      scrollbarSlideFullWidth: 'data-scrollbar-slide-fullwidth',
    };

    if (!customElements.get('native-scrollbar')) {
      customElements.define(
        'native-scrollbar',
        class NativeScrollbar extends HTMLElement {
          constructor() {
            super();

            this.scrollbar = this.querySelector(selectors$c.scrollbar);
            this.arrowNext = this.querySelector(selectors$c.scrollbarArrowNext);
            this.arrowPrev = this.querySelector(selectors$c.scrollbarArrowPrev);
            this.toggleNextArrow = this.toggleNextArrow.bind(this);
          }

          connectedCallback() {
            document.addEventListener('theme:resize', this.toggleNextArrow);

            if (this.scrollbar.hasAttribute(attributes$9.scrollbarSlider)) {
              this.scrollToVisibleElement();
            }

            if (this.arrowNext && this.arrowPrev) {
              this.events();
              this.toggleNextArrow(); // Show arrow next on page load if there are items to scroll to
            }
          }

          disconnectedCallback() {
            document.removeEventListener('theme:resize', this.toggleNextArrow);
          }

          events() {
            this.arrowNext.addEventListener('click', (event) => {
              event.preventDefault();

              this.goToNext();
            });

            this.arrowPrev.addEventListener('click', (event) => {
              event.preventDefault();

              this.goToPrev();
            });

            this.scrollbar.addEventListener('scroll', () => {
              this.togglePrevArrow();
              this.toggleNextArrow();
            });
          }

          goToNext() {
            const moveWith = this.scrollbar.hasAttribute(attributes$9.scrollbarSlideFullWidth) ? this.scrollbar.getBoundingClientRect().width : this.scrollbar.getBoundingClientRect().width / 2;
            const position = moveWith + this.scrollbar.scrollLeft;

            this.move(position);

            this.arrowPrev.classList.remove(classes$9.hidden);

            this.toggleNextArrow();
          }

          goToPrev() {
            const moveWith = this.scrollbar.hasAttribute(attributes$9.scrollbarSlideFullWidth) ? this.scrollbar.getBoundingClientRect().width : this.scrollbar.getBoundingClientRect().width / 2;
            const position = this.scrollbar.scrollLeft - moveWith;

            this.move(position);

            this.arrowNext.classList.remove(classes$9.hidden);

            this.togglePrevArrow();
          }

          toggleNextArrow() {
            requestAnimationFrame(() => {
              this.arrowNext?.classList.toggle(classes$9.hidden, Math.round(this.scrollbar.scrollLeft + this.scrollbar.getBoundingClientRect().width + 1) >= this.scrollbar.scrollWidth);
            });
          }

          togglePrevArrow() {
            requestAnimationFrame(() => {
              this.arrowPrev.classList.toggle(classes$9.hidden, this.scrollbar.scrollLeft <= 0);
            });
          }

          scrollToVisibleElement() {
            [].forEach.call(this.scrollbar.children, (element) => {
              element.addEventListener('click', (event) => {
                event.preventDefault();

                this.move(element.offsetLeft - element.clientWidth);
              });
            });
          }

          move(offsetLeft, behavior = 'smooth') {
            this.scrollbar.scrollTo({
              top: 0,
              left: offsetLeft,
              behavior: behavior,
            });
          }
        }
      );
    }

    if (!customElements.get('popout-select')) {
      customElements.define(
        'popout-select',
        class Popout extends HTMLElement {
          constructor() {
            super();
          }

          connectedCallback() {
            this.popoutList = this.querySelector('[data-popout-list]');
            this.popoutToggle = this.querySelector('[data-popout-toggle]');
            this.popoutToggleText = this.querySelector('[data-popout-toggle-text]');
            this.popoutInput = this.querySelector('[data-popout-input]') || this.parentNode.querySelector('[data-popout-input]') || this.parentNode.parentNode.querySelector('[data-quantity-input]');

            this.popoutOptions = this.querySelectorAll('[data-popout-option]');
            this.productGridItem = this.popoutList.closest('[data-grid-item]');
            this.fireSubmitEvent = this.hasAttribute('submit');
            // Drawer variant (e.g. gift-card denominations): always opens
            // downward on desktop and presents as a bottom sheet on mobile.
            this.isDrawer = this.classList.contains('select-popout--drawer');

            this.popupToggleFocusoutEvent = (evt) => this.onPopupToggleFocusout(evt);
            this.popupListFocusoutEvent = (evt) => this.onPopupListFocusout(evt);
            this.popupToggleClickEvent = (evt) => this.onPopupToggleClick(evt);
            this.keyUpEvent = (evt) => this.onKeyUp(evt);
            this.bodyClickEvent = (evt) => this.onBodyClick(evt);

            this._connectOptions();
            this._connectToggle();
            this._onFocusOut();
          }

          onPopupToggleClick(evt) {
            const button = evt.currentTarget;
            const ariaExpanded = button.getAttribute('aria-expanded') === 'true';

            if (this.productGridItem) {
              const productGridItemImage = this.productGridItem.querySelector('[data-product-image]');

              if (productGridItemImage) {
                productGridItemImage.classList.toggle('is-visible', !ariaExpanded);
              }

              this.popoutList.style.maxHeight = `${Math.abs(this.popoutToggle.getBoundingClientRect().bottom - this.productGridItem.getBoundingClientRect().bottom)}px`;
            }

            this.popoutList.classList.toggle('popout-list--visible');
            button.setAttribute('aria-expanded', !ariaExpanded);

            if (!ariaExpanded) {
              this.toggleListPosition();
              this.popupListSetDimensions();
              if (this.isDrawer) {
                this._openDrawer();
              }
            } else {
              this._hideList();
            }

            document.body.addEventListener('click', this.bodyClickEvent);
          }

          onPopupToggleFocusout(evt) {
            const popoutLostFocus = this.contains(evt.relatedTarget);

            if (!popoutLostFocus) {
              this._hideList();
            }
          }

          onPopupListFocusout(evt) {
            const childInFocus = evt.currentTarget.contains(evt.relatedTarget);
            const isVisible = this.popoutList.classList.contains('popout-list--visible');

            if (isVisible && !childInFocus) {
              this._hideList();
            }
          }

          toggleListPosition() {
            // Drawer variant always opens downward (mobile uses a bottom sheet),
            // so never add the upward-opening modifier.
            if (this.isDrawer) {
              return;
            }

            const popoutToggleTop = this.popoutToggle.getBoundingClientRect().top;

            if (theme.windowHeight / 2 < popoutToggleTop) {
              this.popoutList.classList.add('popout-list--top');
            }
          }

          popupListSetDimensions() {
            // The mobile bottom sheet is sized in CSS (fixed, max-height: dvh),
            // so the viewport-fit measurements below don't apply to it.
            if (this.isDrawer && this._isMobile()) {
              return;
            }

            this.popoutList.style.setProperty('--max-width', '100vw');
            this.popoutList.style.setProperty('--max-height', '100vh');

            requestAnimationFrame(() => {
              const listRect = this.popoutList.getBoundingClientRect();
              const toggleRect = this.popoutToggle.getBoundingClientRect();

              const distanceToRight = theme.windowWidth - listRect.left;
              const distanceToBottom = theme.windowHeight - toggleRect.bottom;
              const distanceToTop = toggleRect.top;

              const opensUp = this.popoutList.classList.contains('popout-list--top');
              const maxHeight = opensUp ? distanceToTop : distanceToBottom;

              this.popoutList.style.setProperty('--max-width', `${Math.floor(distanceToRight)}px`);
              this.popoutList.style.setProperty('--max-height', `${Math.floor(maxHeight)}px`);
            });
          }

          popupOptionsClick(evt) {
            const link = evt.target.closest('[data-popout-option]');

            if (link.attributes.href.value === '#') {
              evt.preventDefault();

              const attrValue = evt.currentTarget.hasAttribute('data-value') ? evt.currentTarget.getAttribute('data-value') : '';

              // No-op guard for submit-style popouts (the localization country /
              // language selector). Re-selecting the value that's already active
              // would POST to /localization and reload the page for nothing — and
              // since the cart is market-bound in Shopify, it invites needless
              // doubt about cart loss when a shopper re-picks the country they're
              // already on. Skip the submit and just close the list.
              if (this.fireSubmitEvent && this.popoutInput.value === attrValue) {
                this._hideList();
                return;
              }

              this.popoutInput.value = attrValue;

              // Sync option metadata onto the hidden input so downstream logic can read it
              const listItem = evt.currentTarget.closest('li');
              if (listItem) {
                const optionValueId = listItem.getAttribute('data-option-value-id');
                const productUrl = listItem.getAttribute('data-product-url');
                if (optionValueId) this.popoutInput.setAttribute('data-option-value-id', optionValueId);
                if (productUrl) this.popoutInput.setAttribute('data-product-url', productUrl);
              }

              if (this.popoutInput.disabled) {
                this.popoutInput.removeAttribute('disabled');
              }

              if (this.fireSubmitEvent) {
                this._submitForm(attrValue);
              } else {
                const currentTarget = evt.currentTarget.parentElement;
                const listTargetElement = this.popoutList.querySelector('.is-active');
                const targetAttribute = this.popoutList.querySelector('[aria-current]');

                // Fire a bubbling change event so parent controllers can react
                this.popoutInput.dispatchEvent(new Event('change', {bubbles: true}));

                if (listTargetElement) {
                  listTargetElement.classList.remove('is-active');
                  currentTarget.classList.add('is-active');
                }

                if (this.popoutInput.name == 'quantity' && !currentTarget.nextSibling) {
                  this.classList.add('is-active');
                }

                if (targetAttribute && targetAttribute.hasAttribute('aria-current')) {
                  targetAttribute.removeAttribute('aria-current');
                  evt.currentTarget.setAttribute('aria-current', 'true');
                }

                if (attrValue !== '') {
                  this.popoutToggleText.innerHTML = attrValue;

                  if (this.popoutToggleText.hasAttribute('data-popout-toggle-text') && this.popoutToggleText.getAttribute('data-popout-toggle-text') !== '') {
                    this.popoutToggleText.setAttribute('data-popout-toggle-text', attrValue);
                  }
                }
                this.onPopupToggleFocusout(evt);
                this.onPopupListFocusout(evt);
              }
            }
          }

          onKeyUp(evt) {
            if (evt.code !== 'Escape') {
              return;
            }
            this._hideList();
            this.popoutToggle.focus();
          }

          onBodyClick(evt) {
            const isOption = this.contains(evt.target);
            const isVisible = this.popoutList.classList.contains('popout-list--visible');

            if (isVisible && !isOption) {
              this._hideList();
            }
          }

          _connectToggle() {
            this.popoutToggle.addEventListener('click', this.popupToggleClickEvent);
          }

          _connectOptions() {
            if (this.popoutOptions.length) {
              this.popoutOptions.forEach((element) => {
                element.addEventListener('click', (evt) => this.popupOptionsClick(evt));
              });
            }
          }

          _onFocusOut() {
            this.addEventListener('keyup', this.keyUpEvent);
            this.popoutToggle.addEventListener('focusout', this.popupToggleFocusoutEvent);
            this.popoutList.addEventListener('focusout', this.popupListFocusoutEvent);
          }

          _submitForm() {
            const form = this.closest('form');
            if (form) {
              form.submit();
            }
          }

          _hideList() {
            this.popoutList.classList.remove('popout-list--visible', 'popout-list--top');
            this.popoutToggle.setAttribute('aria-expanded', false);
            document.body.removeEventListener('click', this.bodyClickEvent);

            if (this.isDrawer) {
              this._hideBackdrop();
              document.body.style.overflow = '';
            }
          }

          _isMobile() {
            return window.innerWidth < 750;
          }

          // Mobile-only: show the backdrop, lock body scroll and make sure the
          // sticky drawer header (title + close) exists inside the sheet.
          _openDrawer() {
            if (!this._isMobile()) {
              return;
            }
            this._ensureDrawerChrome();
            this._showBackdrop();
            document.body.style.overflow = 'hidden';
          }

          _ensureDrawerChrome() {
            if (!this._backdrop) {
              this._backdrop = document.createElement('div');
              this._backdrop.className = 'select-popout__backdrop';
              this._backdrop.addEventListener('click', () => {
                this._hideList();
                this.popoutToggle.focus();
              });
              document.body.appendChild(this._backdrop);
            }

            if (!this._drawerHead) {
              const head = document.createElement('li');
              head.className = 'select-popout__drawer-head';
              head.setAttribute('role', 'presentation');

              const title = document.createElement('span');
              title.className = 'select-popout__drawer-title';
              const labelEl =
                this.closest('.select__fieldset')?.querySelector('.radio__legend__option-name') ||
                this.closest('.selector-wrapper')?.querySelector('.radio__legend__option-name');
              title.textContent = labelEl ? labelEl.textContent.trim() : '';

              const close = document.createElement('button');
              close.type = 'button';
              close.className = 'select-popout__drawer-close';
              close.setAttribute('aria-label', 'Close');
              close.innerHTML =
                '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
              close.addEventListener('click', (evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                this._hideList();
                this.popoutToggle.focus();
              });

              head.appendChild(title);
              head.appendChild(close);
              this.popoutList.insertBefore(head, this.popoutList.firstChild);
              this._drawerHead = head;
            }
          }

          _showBackdrop() {
            if (this._backdrop) {
              this._backdrop.classList.add('is-visible');
            }
          }

          _hideBackdrop() {
            if (this._backdrop) {
              this._backdrop.classList.remove('is-visible');
            }
          }
        }
      );
    }

    class PopupCookie {
      constructor(name, value, daysToExpire = 7) {
        const today = new Date();
        const expiresDate = new Date();
        expiresDate.setTime(today.getTime() + 3600000 * 24 * daysToExpire);

        this.config = {
          expires: expiresDate.toGMTString(), // session cookie
          path: '/',
          domain: window.location.hostname,
          sameSite: 'none',
          secure: true,
        };
        this.name = name;
        this.value = value;
      }

      write() {
        const hasCookie = document.cookie.indexOf('; ') !== -1 && !document.cookie.split('; ').find((row) => row.startsWith(this.name));

        if (hasCookie || document.cookie.indexOf('; ') === -1) {
          document.cookie = `${this.name}=${this.value}; expires=${this.config.expires}; path=${this.config.path}; domain=${this.config.domain}; sameSite=${this.config.sameSite}; secure=${this.config.secure}`;
        }
      }

      read() {
        if (document.cookie.indexOf('; ') !== -1 && document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
          const returnCookie = document.cookie
            .split('; ')
            .find((row) => row.startsWith(this.name))
            .split('=')[1];

          return returnCookie;
        } else {
          return false;
        }
      }

      destroy() {
        if (document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
          document.cookie = `${this.name}=null; expires=${this.config.expires}; path=${this.config.path}; domain=${this.config.domain}`;
        }
      }
    }

    const selectors$b = {
      open: '[data-popup-open]',
      close: '[data-popup-close]',
      dialog: 'dialog',
      focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      newsletterForm: '[data-newsletter-form]',
      newsletterHeading: '[data-newsletter-heading]',
      newsletterField: '[data-newsletter-field]',
    };

    const attributes$8 = {
      closing: 'closing',
      delay: 'data-popup-delay',
      scrollLock: 'data-scroll-lock-required',
      cookieName: 'data-cookie-name',
      cookieValue: 'data-cookie-value',
      preventTopLayer: 'data-prevent-top-layer',
    };

    const classes$8 = {
      hidden: 'hidden',
      hasValue: 'has-value',
      cartBarVisible: 'cart-bar-visible',
      isVisible: 'is-visible',
      success: 'has-success',
      mobile: 'mobile',
      desktop: 'desktop',
      bottom: 'bottom',
    };

    class PopupComponent extends HTMLElement {
      constructor() {
        super();
        this.popup = this.querySelector(selectors$b.dialog);
        this.preventTopLayer = this.popup.hasAttribute(attributes$8.preventTopLayer);
        this.enableScrollLock = this.popup.hasAttribute(attributes$8.scrollLock);
        this.buttonPopupOpen = this.querySelector(selectors$b.open);
        this.a11y = window.theme.a11y;
        this.isAnimating = false;
        this.cookie = new PopupCookie(this.popup.getAttribute(attributes$8.cookieName), this.popup.getAttribute(attributes$8.cookieValue));

        this.checkTargetReferrer();
        this.checkCookie();
        this.bindListeners();
      }

      checkTargetReferrer() {
        if (!this.popup.hasAttribute(attributes$8.referrer)) return;

        if (location.href.indexOf(this.popup.getAttribute(attributes$8.referrer)) === -1 && !window.Shopify.designMode) {
          this.popup.parentNode.removeChild(this.popup);
        }
      }

      checkCookie() {
        const cookieExists = this.cookie && this.cookie.read() !== false;

        if (!cookieExists) {
          this.showPopupEvents();

          this.popup.addEventListener('theme:popup:onclose', () => this.cookie.write());
        }
      }

      bindListeners() {
        // Open button click event
        this.buttonPopupOpen?.addEventListener('click', (e) => {
          e.preventDefault();
          this.popupOpen();
          window.theme.a11y.lastElement = this.buttonPopupOpen;
        });

        // Close button click event
        this.popup.querySelectorAll(selectors$b.close)?.forEach((closeButton) => {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.popupClose();
          });
        });

        // Close dialog on click outside content
        this.popup.addEventListener('click', (event) => {
          if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
            this.popupClose();
          }
        });

        // Close dialog on click ESC key pressed
        this.popup.addEventListener('keydown', (event) => {
          if (event.code === 'Escape') {
            event.preventDefault();
            this.popupClose();
          }
        });

        this.popup.addEventListener('close', () => this.popupCloseActions());
      }

      // True when the popup opens as a native top-layer modal (showModal).
      // In that mode the background is already inert, so we must NOT engage the
      // touchmove-based JS scroll-lock — on real iOS Safari it would block
      // scrolling inside the drawer body too.
      _usesNativeModal() {
        return typeof this.popup.showModal === 'function' && !this.preventTopLayer;
      }

      popupOpen() {
        this.isAnimating = true;

        // Check if browser supports Dialog tags
        if (typeof this.popup.showModal === 'function' && !this.preventTopLayer) {
          this.popup.showModal();
        } else if (typeof this.popup.show === 'function') {
          this.popup.show();
        } else {
          this.popup.setAttribute('open', '');
        }

        this.popup.removeAttribute('inert');
        this.popup.setAttribute('aria-hidden', false);
        this.popup.focus(); // Focus <dialog> tag element to prevent immediate closing on Escape keypress

        // Native modal dialogs already trap the background (showModal makes the
        // rest of the page inert), and the touchmove-based scroll-lock library
        // blocks scrolling INSIDE the drawer body on real iOS Safari — it works
        // with a mouse/trackpad in desktop emulation but not with touch. So only
        // engage the JS lock for the non-native fallback (.show()/[open]).
        if (this.enableScrollLock && !this._usesNativeModal()) {
          const scrollTarget = this.popup.querySelector('[data-scroll-lock-scrollable]') || this.popup;
          document.dispatchEvent(
            new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollTarget})
          );
        }

        window.theme.waitForAnimationEnd(this.popup).then(() => {
          this.isAnimating = false;

          if (this.enableScrollLock) {
            this.a11y.trapFocus(this.popup);
          }

          const focusTarget = this.popup.querySelector('[autofocus]') || this.popup.querySelector(selectors$b.focusable);
          focusTarget?.focus();
        });
      }

      popupClose() {
        if (this.isAnimating || this.popup.hasAttribute('inert')) {
          return;
        }

        if (!this.popup.hasAttribute(attributes$8.closing)) {
          this.popup.setAttribute(attributes$8.closing, '');
          this.isAnimating = true;

          // Force a synchronous reflow so the browser computes the [closing]
          // CSS (animation: fadeOut ...) before waitForAnimationEnd reads
          // animationDuration. Without this, getComputedStyle returns the
          // pre-[closing] value (0s) and the dialog closes instantly,
          // making the scrollbar reappear before the fade-out finishes.
          // eslint-disable-next-line no-unused-expressions
          this.popup.offsetHeight;

          window.theme.waitForAnimationEnd(this.popup).then(() => {
            this.isAnimating = false;
            this.popupClose();
          });

          return;
        }

        // Check if browser supports Dialog tags
        if (typeof this.popup.close === 'function') {
          this.popup.close();
        } else {
          this.popup.removeAttribute('open');
          this.popup.setAttribute('aria-hidden', true);
        }

        this.popupCloseActions();
      }

      popupCloseActions() {
        if (this.popup.hasAttribute('inert')) return;

        this.popup.setAttribute('inert', '');
        this.popup.setAttribute('aria-hidden', true);
        this.popup.removeAttribute(attributes$8.closing);

        // Unlock scroll if no other popups & modals are open
        if (!window.theme.hasOpenModals() && this.enableScrollLock && !this._usesNativeModal()) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }

        this.popup.dispatchEvent(new CustomEvent('theme:popup:onclose', {bubbles: false}));

        if (this.enableScrollLock) {
          this.a11y.removeTrapFocus();
          this.a11y.autoFocusLastElement();
        }
      }

      showPopupEvents() {
        // Auto show popup if it has open attribute
        if (this.popup.hasAttribute('open') && this.popup.getAttribute('open') == true) {
          this.popupOpen();
        }

        this.delay = this.popup.hasAttribute(attributes$8.delay) ? this.popup.getAttribute(attributes$8.delay) : null;
        this.isSubmitted = window.location.href.indexOf('accepts_marketing') !== -1 || window.location.href.indexOf('customer_posted=true') !== -1;

        if (this.showOnScrollEvent) {
          document.removeEventListener('theme:scroll', this.showOnScrollEvent);
        }

        this.showOnScrollEvent = window.theme.throttle(this.showOnScroll.bind(this), 200);

        if (this.delay === 'always' || this.isSubmitted) {
          this.popupOpen();
        }

        if (this.delay && this.delay.includes('delayed') && !this.isSubmitted) {
          this.showDelayed();
        }

        if (this.delay === 'bottom' && !this.isSubmitted) {
          this.showOnBottomReached();
        }

        if (this.delay === 'idle' && !this.isSubmitted) {
          this.showOnIdle();
        }
      }

      showDelayed() {
        const seconds = this.delay.includes('_') ? parseInt(this.delay.split('_')[1]) : 10;

        // Show popup after specific seconds
        setTimeout(() => {
          this.popupOpen();
        }, seconds * 1000);
      }

      showOnIdle() {
        let timer = 0;
        let idleTime = 60000;
        const documentEvents = ['mousemove', 'mousedown', 'click', 'touchmove', 'touchstart', 'touchend', 'keydown', 'keypress'];
        const windowEvents = ['load', 'resize', 'scroll'];

        const startTimer = () => {
          timer = setTimeout(() => {
            timer = 0;
            this.popupOpen();
          }, idleTime);

          documentEvents.forEach((eventType) => {
            document.addEventListener(eventType, resetTimer);
          });

          windowEvents.forEach((eventType) => {
            window.addEventListener(eventType, resetTimer);
          });
        };

        const resetTimer = () => {
          if (timer) {
            clearTimeout(timer);
          }

          documentEvents.forEach((eventType) => {
            document.removeEventListener(eventType, resetTimer);
          });

          windowEvents.forEach((eventType) => {
            window.removeEventListener(eventType, resetTimer);
          });

          startTimer();
        };

        startTimer();
      }

      showOnBottomReached() {
        document.addEventListener('theme:scroll', this.showOnScrollEvent);
      }

      showOnScroll() {
        if (window.scrollY + window.innerHeight >= document.body.clientHeight) {
          this.popupOpen();
          document.removeEventListener('theme:scroll', this.showOnScrollEvent);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('theme:scroll', this.showOnScrollEvent);
      }
    }

    class PopupNewsletter extends PopupComponent {
      constructor() {
        super();
        this.form = this.popup.querySelector(selectors$b.newsletterForm);
        this.heading = this.popup.querySelector(selectors$b.newsletterHeading);
        this.newsletterField = this.popup.querySelector(selectors$b.newsletterField);
      }

      connectedCallback() {
        const cookieExists = this.cookie?.read() !== false;
        const submissionSuccess = window.location.search.indexOf('?customer_posted=true') !== -1;
        const classesString = [...this.classList].toString();
        const isPositionBottom = classesString.includes(classes$8.bottom);
        const targetMobile = this.popup.classList.contains(classes$8.mobile);
        const targetDesktop = this.popup.classList.contains(classes$8.desktop);
        const isMobileView = theme.isMobile;

        let targetMatches = true;

        if ((targetMobile && !isMobileView) || (targetDesktop && isMobileView)) {
          targetMatches = false;
        }

        if (!targetMatches) {
          super.a11y.removeTrapFocus();
          if (!this._usesNativeModal()) {
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
          }
          return;
        }

        if (!cookieExists || window.Shopify.designMode) {
          if (!window.Shopify.designMode && !window.location.pathname.endsWith('/challenge')) {
            super.showPopupEvents();
          }

          if (this.form && this.form.classList.contains(classes$8.success)) {
            super.popupOpen();
            this.cookie.write();
          }

          this.popup.addEventListener('theme:popup:onclose', () => this.cookie.write());
        }

        if (submissionSuccess) {
          this.delay = 0;
        }

        if (!cookieExists || window.Shopify.designMode) {
          this.show();

          if (this.form.classList.contains(classes$8.success)) {
            this.popupOpen();
            this.cookie.write();
          }
        }

        if (isPositionBottom) {
          this.observeCartBar();
        }
      }

      show() {
        if (!window.location.pathname.endsWith('/challenge')) {
          if (!window.Shopify.designMode) {
            super.showPopupEvents();
          } else {
            super.popupOpen();
          }
        }

        this.showForm();
        this.inputField();

        this.popup.addEventListener('theme:popup:onclose', () => this.cookie.write());
      }

      observeCartBar() {
        this.cartBar = document.getElementById(selectors$b.cartBar);

        if (!this.cartBar) return;

        const config = {attributes: true, childList: false, subtree: false};
        let isVisible = this.cartBar.classList.contains(classes$8.isVisible);
        document.body.classList.toggle(classes$8.cartBarVisible, isVisible);

        // Callback function to execute when mutations are observed
        const callback = (mutationList) => {
          for (const mutation of mutationList) {
            if (mutation.type === 'attributes') {
              isVisible = mutation.target.classList.contains(classes$8.isVisible);
              document.body.classList.toggle(classes$8.cartBarVisible, isVisible);
            }
          }
        };

        this.observer = new MutationObserver(callback);
        this.observer.observe(this.cartBar, config);
      }

      showForm() {
        this.heading?.addEventListener('click', (event) => {
          event.preventDefault();

          this.heading.classList.add(classes$8.hidden);
          this.form.classList.remove(classes$8.hidden);
          this.newsletterField.focus();
        });

        this.heading?.addEventListener('keyup', (event) => {
          if (event.code === 'Enter') {
            this.heading.dispatchEvent(new Event('click'));
          }
        });
      }

      inputField() {
        const setClass = () => {
          // Reset timer if exists and is active
          if (this.resetClassTimer) {
            clearTimeout(this.resetClassTimer);
          }

          if (this.newsletterField.value !== '') {
            this.popup.classList.add(classes$8.hasValue);
          }
        };

        const unsetClass = () => {
          // Reset timer if exists and is active
          if (this.resetClassTimer) {
            clearTimeout(this.resetClassTimer);
          }

          // Reset class
          this.resetClassTimer = setTimeout(() => {
            this.popup.classList.remove(classes$8.hasValue);
          }, 2000);
        };

        this.newsletterField.addEventListener('input', setClass);
        this.newsletterField.addEventListener('focus', setClass);
        this.newsletterField.addEventListener('focusout', unsetClass);
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
      }
    }

    if (!customElements.get('popup-component')) {
      customElements.define('popup-component', PopupComponent);
    }

    if (!customElements.get('popup-newsletter')) {
      customElements.define('popup-newsletter', PopupNewsletter);
    }

    const selectors$a = {
      allVisibleElements: '[role="option"]',
      ariaSelected: '[aria-selected="true"]',
      popularSearches: '[data-popular-searches]',
      predictiveSearch: 'predictive-search',
      predictiveSearchResults: '[data-predictive-search-results]',
      predictiveSearchStatus: '[data-predictive-search-status]',
      searchInput: 'input[type="search"]',
      searchPopdown: '[data-popdown]',
      searchResultsLiveRegion: '[data-predictive-search-live-region-count-value]',
      searchResultsGroupsWrapper: '[data-search-results-groups-wrapper]',
      searchForText: '[data-predictive-search-search-for-text]',
      sectionPredictiveSearch: '#shopify-section-predictive-search',
      selectedLink: '[aria-selected="true"] a',
      selectedOption: '[aria-selected="true"] a, button[aria-selected="true"]',
    };

    if (!customElements.get('predictive-search')) {
      customElements.define(
        'predictive-search',

        class PredictiveSearch extends HeaderSearchForm {
          constructor() {
            super();

            this.a11y = window.theme.a11y;
            this.abortController = new AbortController();
            this.allPredictiveSearchInstances = document.querySelectorAll(selectors$a.predictiveSearch);
            this.cachedResults = {};
            this.input = this.querySelector(selectors$a.searchInput);
            this.isOpen = false;
            this.predictiveSearchResults = this.querySelector(selectors$a.predictiveSearchResults);
            this.searchPopdown = this.closest(selectors$a.searchPopdown);
            this.popularSearches = this.searchPopdown?.querySelector(selectors$a.popularSearches);
            this.searchTerm = '';
          }

          connectedCallback() {
            this.input.addEventListener('focus', this.onFocus.bind(this));
            this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

            this.addEventListener('focusout', this.onFocusOut.bind(this));
            this.addEventListener('keyup', this.onKeyup.bind(this));
            this.addEventListener('keydown', this.onKeydown.bind(this));
          }

          getQuery() {
            return this.input.value.trim();
          }

          onChange() {
            super.onChange();
            const newSearchTerm = this.getQuery();

            if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
              // Remove the results when they are no longer relevant for the new search term
              // so they don't show up when the dropdown opens again
              this.querySelector(selectors$a.searchResultsGroupsWrapper)?.remove();
            }

            // Update the term asap, don't wait for the predictive search query to finish loading
            this.updateSearchForTerm(this.searchTerm, newSearchTerm);

            this.searchTerm = newSearchTerm;

            if (!this.searchTerm.length) {
              this.reset();
              return;
            }

            this.getSearchResults(this.searchTerm);
          }

          onFormSubmit(event) {
            if (!this.getQuery().length || this.querySelector(selectors$a.selectedLink)) event.preventDefault();
          }

          onFormReset(event) {
            super.onFormReset(event);
            if (super.shouldResetForm()) {
              this.searchTerm = '';
              this.abortController.abort();
              this.abortController = new AbortController();
              this.closeResults(true);
            }
          }

          shouldResetForm() {
            return !document.querySelector(selectors$a.selectedLink);
          }

          onFocus() {
            const currentSearchTerm = this.getQuery();

            if (!currentSearchTerm.length) return;

            if (this.searchTerm !== currentSearchTerm) {
              // Search term was changed from other search input, treat it as a user change
              this.onChange();
            } else if (this.getAttribute('results') === 'true') {
              this.open();
            } else {
              this.getSearchResults(this.searchTerm);
            }
          }

          onFocusOut() {
            setTimeout(() => {
              if (!this.searchPopdown.contains(document.activeElement)) this.close();
            });
          }

          onKeyup(event) {
            if (!this.getQuery().length) this.close(true);
            event.preventDefault();

            switch (event.code) {
              case 'ArrowUp':
                this.switchOption('up');
                break;
              case 'ArrowDown':
                this.switchOption('down');
                break;
              case 'Enter':
                this.selectOption();
                break;
            }
          }

          onKeydown(event) {
            // Prevent the cursor from moving in the input when using the up and down arrow keys
            if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
              event.preventDefault();
            }
          }

          updateSearchForTerm(previousTerm, newTerm) {
            const searchForTextElement = this.querySelector(selectors$a.searchForText);
            const currentButtonText = searchForTextElement?.innerText;

            if (currentButtonText) {
              if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
                // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
                return;
              }
              const newButtonText = currentButtonText.replace(previousTerm, newTerm);
              searchForTextElement.innerText = newButtonText;
            }
          }

          switchOption(direction) {
            if (!this.getAttribute('open')) return;

            const moveUp = direction === 'up';
            const selectedElement = this.querySelector(selectors$a.ariaSelected);

            // Filter out hidden elements (duplicated page and article resources) thanks
            // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
            const allVisibleElements = Array.from(this.querySelectorAll(selectors$a.allVisibleElements)).filter((element) => element.offsetParent !== null);

            let activeElementIndex = 0;

            if (moveUp && !selectedElement) return;

            let selectedElementIndex = -1;
            let i = 0;

            while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
              if (allVisibleElements[i] === selectedElement) {
                selectedElementIndex = i;
              }
              i++;
            }

            this.statusElement.textContent = '';

            if (!moveUp && selectedElement) {
              activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
            } else if (moveUp) {
              activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
            }

            if (activeElementIndex === selectedElementIndex) return;

            const activeElement = allVisibleElements[activeElementIndex];

            activeElement.setAttribute('aria-selected', true);
            if (selectedElement) selectedElement.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', activeElement.id);
          }

          selectOption() {
            const selectedOption = this.querySelector(selectors$a.selectedOption);

            if (selectedOption) selectedOption.click();
          }

          getSearchResults(searchTerm) {
            const queryKey = searchTerm.replace(' ', '-').toLowerCase();
            this.setLiveRegionLoadingState();

            if (this.cachedResults[queryKey]) {
              this.renderSearchResults(this.cachedResults[queryKey]);
              return;
            }

            fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,query,collection`, {signal: this.abortController.signal})
              .then((response) => {
                if (!response.ok) {
                  var error = new Error(response.status);
                  this.close();
                  throw error;
                }

                return response.text();
              })
              .then((text) => {
                const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector(selectors$a.sectionPredictiveSearch).innerHTML;
                // Save bandwidth keeping the cache in all instances synced
                this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
                  predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
                });
                this.renderSearchResults(resultsMarkup);
              })
              .catch((error) => {
                if (error?.code === 20) {
                  // Code 20 means the call was aborted
                  return;
                }
                this.close();
                throw error;
              });
          }

          setLiveRegionLoadingState() {
            this.statusElement = this.statusElement || this.querySelector(selectors$a.predictiveSearchStatus);
            this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

            this.setLiveRegionText(this.loadingText);
            this.setAttribute('loading', true);
          }

          setLiveRegionText(statusText) {
            this.statusElement.setAttribute('aria-hidden', 'false');
            this.statusElement.textContent = statusText;

            setTimeout(() => {
              this.statusElement.setAttribute('aria-hidden', 'true');
            }, 1000);
          }

          renderSearchResults(resultsMarkup) {
            this.predictiveSearchResults.innerHTML = resultsMarkup;

            this.setAttribute('results', true);

            this.setLiveRegionResults();
            this.open();
          }

          setLiveRegionResults() {
            this.removeAttribute('loading');
            this.setLiveRegionText(this.querySelector(selectors$a.searchResultsLiveRegion).textContent);
          }

          open() {
            this.setAttribute('open', true);
            this.input.setAttribute('aria-expanded', true);
            this.isOpen = true;
            this.predictiveSearchResults.style.setProperty('--results-height', `${window.visualViewport.height - this.predictiveSearchResults.getBoundingClientRect().top}px`);
          }

          close(clearSearchTerm = false) {
            this.closeResults(clearSearchTerm);
            this.isOpen = false;
            this.predictiveSearchResults.style.removeProperty('--results-height');
          }

          closeResults(clearSearchTerm = false) {
            if (clearSearchTerm) {
              this.input.value = '';
              this.removeAttribute('results');
            }
            const selected = this.querySelector(selectors$a.ariaSelected);

            if (selected) selected.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', '');
            this.removeAttribute('loading');
            this.removeAttribute('open');
            this.input.setAttribute('aria-expanded', false);
            this.predictiveSearchResults?.removeAttribute('style');
          }

          reset() {
            this.predictiveSearchResults.innerHTML = '';

            this.input.val = '';
            this.a11y.removeTrapFocus();

            if (this.popularSearches) {
              this.input.dispatchEvent(new Event('blur', {bubbles: false}));
              this.a11y.trapFocus(this.searchPopdown, {
                elementToFocus: this.input,
              });
            }
          }
        }
      );
    }

    if (!customElements.get('product-form')) {
      customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
          constructor() {
            super();

            this.form = this.querySelector('form');
            this.variantIdInput.disabled = false;
            this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
            this.cart = document.querySelector('cart-drawer');
            this.submitButton = this.querySelector('[type="submit"]');
            this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-text]');

            if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

            this.hideErrors = this.dataset.hideErrors === 'true';
          }

          onSubmitHandler(evt) {
            evt.preventDefault();

            document.dispatchEvent(
              new CustomEvent('theme:cart:add', {
                detail: {
                  button: this.submitButton,
                },
                bubbles: false,
              })
            );

            const quickAddModal = this.closest('quick-add-modal');
            if (!quickAddModal) {
              window.a11y.lastElement = this.submitButton;
            }
          }

          toggleSubmitButton(disable = true, text = window.theme.strings.addToCart) {
            this.submitButton.toggleAttribute('disabled', disable);
            // Preserve existing price markup inside the add-to-cart text
            const existingPriceElement = this.submitButtonText.querySelector('[data-product-price]');
            if (existingPriceElement) {
              const priceHTML = existingPriceElement.outerHTML;
              this.submitButtonText.textContent = text;
              this.submitButtonText.insertAdjacentHTML('beforeend', ` ${priceHTML}`);
            } else {
              this.submitButtonText.textContent = text;
            }
          }

          get variantIdInput() {
            return this.form.querySelector('[name=id]');
          }
        }
      );
    }

if (!customElements.get('print-configurator')) {
  customElements.define(
    'print-configurator',
    class PrintConfigurator extends HTMLElement {
      static normalize(s) {
        return (s || '')
          .toString()
          .toLowerCase()
          .replace(/\u00d7/g, 'x')
          .replace(/,/g, '.')
          .trim();
      }

      connectedCallback() {
        this.formId = this.dataset.productFormId;
        this.printPriceCents = Number(this.dataset.printPrice);
        this.printPriceFormatted = this.dataset.printPriceFormatted;
        this.printImageUrl = this.dataset.printImage || '';
        this.size = this.dataset.currentSize || '';
        this.normSize = PrintConfigurator.normalize(this.size);
        this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex || 0);
        this.selection = { passepartout: null, frame: null, packaging: null };

        // B2B-only gate. Every packaging branch below hangs off this flag.
        this.b2b = Boolean(window.theme && window.theme.settings && window.theme.settings.b2bEnabled);

        // Canvas products: the frame is mandatory and free. No passepartout
        // row is rendered; the first fitting frame is selected by default
        // (see _selectDefaultCanvasFrame), so Add to Cart is enabled on load.
        // _refreshCanvasGate re-disables it only if a frame is ever cleared.
        // The frame contributes 0 to the total/bundle because its option
        // carries data-variant-price="0".
        this.isCanvas = this.dataset.pcCanvas === 'true';
        this.frameRequired = this.dataset.pcFrameRequired === 'true';

        this.sizeWrap = this.querySelector('[data-pc-size]');
        this.passeWrap = this.querySelector('[data-pc-addon="passepartout"]');
        this.frameWrap = this.querySelector('[data-pc-addon="frame"]');
        this.packagingWrap = this.b2b ? this.querySelector('[data-pc-addon="packaging"]') : null;
        this.packagingSection = this.packagingWrap
          ? this.packagingWrap.closest('[data-pc-packaging-section]')
          : null;
        this.packagingInput = this.packagingWrap
          ? this.packagingWrap.querySelector('[data-pc-packaging-input]')
          : null;

        this._backdrop = document.createElement('div');
        this._backdrop.className = 'pc-backdrop';
        this._backdrop.addEventListener('click', () => this.closeAllLists());
        document.body.appendChild(this._backdrop);

        this.bindToggles();
        this.bindSize();
        if (this.passeWrap) this.bindAddon('passepartout', this.passeWrap);
        if (this.frameWrap) this.bindAddon('frame', this.frameWrap);
        if (this.b2b && this.packagingWrap) this.bindAddon('packaging', this.packagingWrap);
        this.bindDrawerCloseButtons();

        this.applySizeFilter();
        this.bindFormSubmit();
        // Render initial TOTAL on load (= base variant price, no add-ons).
        this.dispatchTotal();

        // Cache elements used for framing badge relocation.
        this._addonsHead = this.querySelector('[data-pc-addons] .pc-section__head');
        this._sizeHead = this.querySelector('.pc-section--size .pc-section__head');
        this._framingBadge = this._addonsHead?.querySelector('popup-component');

        // Set initial add-ons visibility based on the initially-selected size.
        const initialSelectedOpt = this.querySelector('[data-pc-option][aria-selected="true"]');
        if (initialSelectedOpt && initialSelectedOpt.dataset.unavailable === 'true') {
          const addonsSection = this.querySelector('[data-pc-addons]');
          if (addonsSection) addonsSection.hidden = true;
          if (this.packagingSection) this.packagingSection.hidden = true;
          const totalRow = this.querySelector('[data-pc-total-row]');
          if (totalRow) totalRow.hidden = true;
          this._syncFramingBadge(true);
          this._updateBadge(initialSelectedOpt, true);
          // Also sync the ATC + cart-bar buttons so the sticky cart-bar
          // doesn't show "Add to cart" for a sold-out initial variant.
          this.updateAtcButtonState(true);
        }

        this._onDocClick = this._onDocClick.bind(this);
        document.addEventListener('click', this._onDocClick);

        if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
          this._unsubVariant = window.subscribe(
            window.theme.PUB_SUB_EVENTS.variantChange,
            () => this.dispatchTotal()
          );
        }

        // Auto-apply any selections passed via URL query params
        // (?variant=<id>&passepartout=<id>&frame=<id>) so brand-shared
        // deep links restore the configurator state on load.
        this._applyUrlParams();

        // Canvas: a frame is mandatory, so default to the first fitting frame
        // (a deep-linked ?frame= applied above takes precedence). The
        // _canvasReady flag also lets applySizeFilter re-pick a default after a
        // size change clears one that no longer fits — see below.
        this._canvasReady = true;
        this._selectDefaultCanvasFrame();

        // Canvas: gate Add to Cart on a frame being selected.
        this._refreshCanvasGate();
      }

      // Canvas products require a frame. Pre-select a default fitting frame so
      // the product is immediately addable; an existing selection (e.g. a
      // deep-linked ?frame=) takes precedence and is left untouched.
      // Preference: the (light/natural) Oak frame whenever it fits the current
      // size, otherwise the first fitting frame. The oak frame is identified by
      // "oak" in its title/colour (e.g. "Canvas Oak Wood Frame" / var(--oak)),
      // excluding any darker oak variant.
      _selectDefaultCanvasFrame() {
        if (!this.isCanvas || !this.frameWrap || this.selection.frame) return;
        const fitting = [
          ...this.frameWrap.querySelectorAll('[data-pc-option]'),
        ].filter((o) => !o.hasAttribute('data-pc-none') && !o.hidden);
        if (!fitting.length) return;
        const isOak = (o) => {
          const label = `${o.dataset.variantProductTitle || ''} ${
            o.dataset.variantColor || ''
          } ${o.dataset.variantColorHex || ''}`.toLowerCase();
          return label.includes('oak') && !label.includes('dark');
        };
        const preferred = fitting.find(isOak) || fitting[0];
        preferred.click();
      }

      // Canvas products require a frame. Keep Add to Cart (and the sticky
      // cart-bar) disabled with a prompt until one is chosen; re-enable once
      // selected. A sold-out size is handled by updateAtcButtonState and takes
      // precedence, so we no-op here in that case to avoid fighting over the
      // button label.
      _refreshCanvasGate() {
        if (!this.isCanvas || !this.frameRequired) return;
        const sizeOpt = this.sizeWrap?.querySelector(
          '[data-pc-option][aria-selected="true"]'
        );
        if (sizeOpt && sizeOpt.dataset.unavailable === 'true') return;

        const needFrame = !this.selection.frame;
        const productInfo = this.closest('product-info');
        const productForm =
          productInfo?.querySelector('product-form') || this.closest('product-form');
        const promptLabel =
          window.theme?.strings?.selectFrame || 'Select a frame';
        const addLabel = window.theme?.strings?.addToCart || 'Add to cart';
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(needFrame, needFrame ? promptLabel : addLabel);
        }

        const cartBarBtn = productInfo?.querySelector(
          'cart-bar button.product__submit__add'
        );
        const cartBarBtnText = productInfo?.querySelector(
          'cart-bar [data-cart-bar-button-text]'
        );
        if (cartBarBtn && cartBarBtnText) {
          cartBarBtnText.textContent = needFrame ? promptLabel : addLabel;
          if (needFrame) {
            cartBarBtn.setAttribute('disabled', 'disabled');
            cartBarBtn.removeAttribute('data-cart-bar-scroll');
          } else {
            cartBarBtn.removeAttribute('disabled');
            cartBarBtn.setAttribute('data-cart-bar-scroll', '');
          }
        }
      }

      // Update URL search params for the configurator state. Values that
      // are `null` or `''` are removed. Uses history.replaceState so the
      // back button is not polluted with intermediate selections.
      _updateUrl(updates) {
        try {
          const url = new URL(window.location.href);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              url.searchParams.delete(key);
            } else {
              url.searchParams.set(key, val);
            }
          }
          window.history.replaceState({}, '', url.toString());
        } catch (_) {
          /* no-op: invalid URL or unsupported env */
        }
      }

      _applyUrlParams() {
        const params = new URLSearchParams(window.location.search);
        const variant = params.get('variant');
        const passe = params.get('passepartout');
        const frame = params.get('frame');

        // Apply size first: it may filter/hide addon options.
        if (variant && this.sizeWrap) {
          const sizeOpt = this.sizeWrap.querySelector(
            `[data-pc-option][data-variant-id="${variant}"]`
          );
          // Skip if it's already the active selection (e.g. server-rendered).
          if (sizeOpt && sizeOpt.getAttribute('aria-selected') !== 'true') {
            sizeOpt.click();
          }
        }

        const applyAddon = (kind, id, wrap) => {
          if (!id || !wrap) return;
          const opt = wrap.querySelector(
            `[data-pc-option][data-variant-id="${id}"]`
          );
          if (opt) opt.click();
        };
        applyAddon('passepartout', passe, this.passeWrap);
        applyAddon('frame', frame, this.frameWrap);
        // B2B: packaging is for a loose print, so a link that also carries an
        // add-on leaves it unset.
        if (this.b2b && !this.hasAnyAddon()) {
          applyAddon('packaging', params.get('packaging'), this.packagingWrap);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocClick);
        if (typeof this._unsubVariant === 'function') this._unsubVariant();
        if (this._backdrop) this._backdrop.remove();
        this._scrollUnlock();
      }

      _onDocClick(e) {
        if (this.contains(e.target)) return;
        // On mobile the backdrop handles outside-clicks; on desktop keep normal behaviour.
        if (window.innerWidth >= 750) this.closeAllLists();
      }

      _isMobile() {
        return window.innerWidth < 750;
      }

      _scrollLock() {
        if (this._isMobile()) document.body.style.overflow = 'hidden';
      }

      _scrollUnlock() {
        document.body.style.overflow = '';
      }

      _showBackdrop() {
        if (this._backdrop) this._backdrop.classList.add('is-visible');
      }

      _hideBackdrop() {
        if (this._backdrop) this._backdrop.classList.remove('is-visible');
      }

      bindDrawerCloseButtons() {
        this.querySelectorAll('[data-pc-drawer-close]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.closeAllLists();
          });
        });
      }

      closeAllLists(except) {
        this.querySelectorAll('[data-pc-list]').forEach((list) => {
          if (list === except) return;
          list.hidden = true;
          const toggle = list.parentElement.querySelector('[data-pc-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          list.parentElement.classList.remove('is-open');
        });
        this._hideBackdrop();
        this._scrollUnlock();
      }

      bindToggles() {
        this.querySelectorAll('[data-pc-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            const row = toggle.parentElement;
            // Disabled add-on row: do not open
            if (row.classList.contains('is-disabled')) return;
            const list = row.querySelector('[data-pc-list]');
            if (!list) return;
            const willOpen = list.hidden;
            this.closeAllLists(willOpen ? list : null);
            list.hidden = !willOpen;
            toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
            row.classList.toggle('is-open', willOpen);
            if (willOpen) {
              this._updateDrawerPeek(row);
              this._showBackdrop();
              this._scrollLock();
            }
          });
        });
      }

      _updateDrawerPeek(row) {
        const list = row.querySelector('[data-pc-list]');
        if (!list) return;
        const peek = list.querySelector('[data-pc-drawer-peek]');
        const head = list.querySelector('.pc-row__drawer-head');
        if (!peek) return;

        let hasSel = false;

        if (row.hasAttribute('data-pc-size')) {
          // Size row — use the current label and price from the toggle
          const currentLabel = row.querySelector('[data-pc-current-label]');
          const sizePrice = row.querySelector('[data-pc-size-price]');
          const currentSize = (currentLabel && currentLabel.textContent.trim()) || this.size;
          if (currentSize) {
            const labelEl = peek.querySelector('[data-pc-peek-label]');
            const priceEl = peek.querySelector('[data-pc-peek-price]');
            if (labelEl) labelEl.textContent = currentSize;
            if (priceEl && sizePrice) priceEl.innerHTML = sizePrice.innerHTML;
            hasSel = true;
          }
        } else if (row.dataset.pcAddon) {
          const kind = row.dataset.pcAddon;
          const sel = this.selection[kind];
          if (sel) {
            const swatchEl = peek.querySelector('[data-pc-peek-swatch]');
            const labelEl = peek.querySelector('[data-pc-peek-label]');
            const metaEl = peek.querySelector('[data-pc-peek-meta]');
            const priceEl = peek.querySelector('[data-pc-peek-price]');
            if (swatchEl) {
              if (sel.swatchImage) {
                swatchEl.style.backgroundImage = `url('${sel.swatchImage}')`;
                swatchEl.style.backgroundSize = 'cover';
                swatchEl.style.backgroundColor = '';
              } else {
                swatchEl.style.setProperty('background', sel.colorHex || '');
                swatchEl.style.setProperty('background-size', sel.colorHex ? 'cover' : '');
              }
            }
            if (labelEl) labelEl.textContent = (sel.color || '').toUpperCase();
            const peekFitsLabel = row.dataset.pcFitsLabel || '';
            if (metaEl) metaEl.textContent = sel.fitsText ? `${sel.size || ''} | ${peekFitsLabel} ${sel.fitsText}` : (sel.size || '');
            if (priceEl) priceEl.textContent = sel.priceFormatted || '';
            hasSel = true;
          }
        }

        // Peek row is not used — always show the drawer header (title + X);
        // the current selection is highlighted inline in the list instead.
        peek.hidden = true;
        if (head) head.classList.remove('pc-row__drawer-head--hidden');
      }

      bindSize() {
        if (!this.sizeWrap) return;
        const list = this.sizeWrap.querySelector('[data-pc-list]');
        list.addEventListener('click', (e) => {
          const opt = e.target.closest('[data-pc-option]');
          if (!opt) return;

          const isUnavailable = opt.dataset.unavailable === 'true';

          list.querySelectorAll('[data-pc-option]').forEach((o) => {
            o.classList.remove('is-selected');
            o.setAttribute('aria-selected', 'false');
          });
          opt.classList.add('is-selected');
          opt.setAttribute('aria-selected', 'true');

          this.size = opt.dataset.size || '';
          this.normSize = PrintConfigurator.normalize(this.size);
          this.printPriceCents = Number(opt.dataset.variantPrice);
          this.printPriceFormatted = opt.dataset.variantPriceFormatted;

          // Update toggle label + price (with optional compare-at strikethrough).
          // Passepartouts carry a display label with the "| FITS: <size>" suffix;
          // this.size stays the pure size for add-on matching.
          const label = this.sizeWrap.querySelector('[data-pc-current-label]');
          if (label) label.textContent = opt.dataset.displayLabel || this.size;

          const priceWrap = this.sizeWrap.querySelector('[data-pc-size-price]');
          if (priceWrap) {
            const onSale = opt.dataset.variantOnSale === 'true';
            const compareFmt = opt.dataset.variantCompareFormatted || '';
            priceWrap.innerHTML =
              (onSale ? `<s class="pc-price-compare">${compareFmt}</s> ` : '') +
              `<span class="pc-price-current">${this.printPriceFormatted}</span>`;
          }

          // Toggle sold-out styling on the row when a sold-out variant is selected
          this.sizeWrap.classList.toggle('is-sold-out', isUnavailable);

          // Hide the add-ons section and total row when the selected size is unavailable
          const addonsSection = this.querySelector('[data-pc-addons]');
          if (addonsSection) addonsSection.hidden = isUnavailable;
          if (this.packagingSection) this.packagingSection.hidden = isUnavailable;
          const totalRow = this.querySelector('[data-pc-total-row]');
          if (totalRow) totalRow.hidden = isUnavailable;
          this._syncFramingBadge(isUnavailable);

          // Update the desktop title badge and mobile image badge immediately.
          // For print-configurator products, variantChange pub/sub is not fired,
          // so we update the badges directly from available data-attributes on the option.
          this._updateBadge(opt, isUnavailable);
          this._updateSku(opt);

          this.syncVariantInput(opt.dataset.variantId);

          // Reflect the selected size in the URL as ?variant=<id> so the
          // standard Shopify share/deep-link behaviour is preserved.
          this._updateUrl({ variant: opt.dataset.variantId || null });

          // Directly update the ATC button text & disabled state to match availability.
          // product-info.js updates the button via a full section fetch; we update it
          // immediately here so the UI responds before the network round-trip.
          this.updateAtcButtonState(isUnavailable);
          // Canvas: re-assert the frame-required gate after the availability
          // update (which would otherwise re-enable ATC on an available size).
          this._refreshCanvasGate();

          list.hidden = true;
          this.sizeWrap.querySelector('[data-pc-toggle]').setAttribute('aria-expanded', 'false');
          this.sizeWrap.classList.remove('is-open');
          this._hideBackdrop();
          this._scrollUnlock();

          this.applySizeFilter();
          this.dispatchVariantPrice(opt);

          // Like picking a passepartout / frame, jump the gallery back to the
          // product (preview) slide so a shopper who scrolled to another image
          // sees the print at the chosen size. Guard on `e.isTrusted` so the
          // programmatic size `.click()`s (URL deep-link / addon re-fitting)
          // never move the gallery — only a real user selection does.
          if (e.isTrusted) this.dispatchFocusPreview();
        });
      }

      syncVariantInput(variantId) {
        if (!variantId) return;
        const form = this.getForm();
        if (!form) return;
        const input = form.querySelector('[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      bindAddon(kind, wrap) {
        const list = wrap.querySelector('[data-pc-list]');
        const toggle = wrap.querySelector('[data-pc-toggle]');
        const fromPriceEl = wrap.querySelector('[data-pc-from-price]');
        const fromPriceSrc = wrap.querySelector('[data-pc-from-price-template]');

        // Render "FROM €X" into the empty-state toggle
        if (fromPriceEl && fromPriceSrc) {
          const tpl = fromPriceSrc.dataset.pcFromPriceTemplate || '';
          const formatted = fromPriceSrc.dataset.pcFromPriceFormatted || '';
          fromPriceEl.innerHTML = tpl.replace('__PRICE__', formatted);
        }

        list.addEventListener('click', (e) => {
          const opt = e.target.closest('[data-pc-option]');
          if (!opt) return;

          list.querySelectorAll('[data-pc-option]').forEach((o) => {
            o.classList.remove('is-selected');
            o.setAttribute('aria-selected', 'false');
          });
          opt.classList.add('is-selected');
          opt.setAttribute('aria-selected', 'true');

          if (opt.hasAttribute('data-pc-none') || !opt.dataset.variantId) {
            this.selection[kind] = null;
            this.renderAddonToggle(kind, wrap, null);
          } else {
            this.selection[kind] = {
              id: Number(opt.dataset.variantId),
              price: Number(opt.dataset.variantPrice),
              // Canvas frames are free; hide the €0 price in the toggle/peek
              // (the cents `price` above stays 0 for the bundle total).
              priceFormatted: this.isCanvas ? '' : opt.dataset.variantPriceFormatted,
              productTitle: opt.dataset.variantProductTitle,
              color: opt.dataset.variantColor,
              colorHex: opt.dataset.variantColorHex,
              swatchImage: opt.dataset.variantSwatchImage || '',
              fitsText: opt.dataset.variantFits || '',
              sku: opt.dataset.variantSku || '',
              size: opt.dataset.variantSize,
              fits: opt.dataset.fitsSize,
              // B2B packaging: a one-size product (the tube) fits every print.
              fitsAny: opt.hasAttribute('data-pc-fits-any'),
              pdpImages: {
                portrait: opt.dataset.variantPdpPortrait || '',
                landscape: opt.dataset.variantPdpLandscape || '',
                square: opt.dataset.variantPdpSquare || '',
              },
              // 40x50-only prints: alternative overlays with the smaller
              // opening, used by <print-preview> only when no mat is selected.
              pdpImages40x50: {
                portrait: opt.dataset.variantPdpPortrait40x50 || '',
                landscape: opt.dataset.variantPdpLandscape40x50 || '',
              },
            };
            this.renderAddonToggle(kind, wrap, this.selection[kind]);
          }

          list.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          wrap.classList.remove('is-open');
          this._hideBackdrop();
          this._scrollUnlock();

          // Reflect the selection in the URL as ?passepartout=<id> /
          // ?frame=<id>; clear the param when the user picks "None".
          this._updateUrl({
            [kind]: this.selection[kind] ? this.selection[kind].id : null,
          });

          // `e.isTrusted` is true only for real user clicks; programmatic
          // `.click()` calls from size re-fitting pass false, so the gallery
          // doesn't jump to the preview on a size swap.
          this.dispatchAddonChange(kind, e.isTrusted);
          this.dispatchTotal();

          // Mobile: the gallery/preview sits above the configurator, so the
          // composed preview update isn't visible while the shopper is down at
          // the add-on rows. The first add-on they pick, scroll the preview
          // back into view (desktop already refocuses the preview slide in
          // place). Real clicks only, once per page. Packaging has no
          // preview layer, so it never moves the page.
          if (e.isTrusted && kind !== 'packaging') this._maybeScrollPreviewIntoViewMobile();

          // Selecting or clearing a passe-partout changes the size a
          // frame must fit (the matt's outer size vs. the print size),
          // so re-filter the frame options and re-pick or clear the
          // current frame to match. (Frame clicks don't re-enter this,
          // so there's no recursion.)
          if (kind === 'passepartout') {
            this.applySizeFilter();
          }
          // Canvas: enable/disable ATC based on whether a frame is now picked.
          this._refreshCanvasGate();
          // Picking a frame doesn't re-filter, so lock packaging from here too.
          this.applyPackagingLock();
        });
      }

      // Swap between empty-state, selected-state, and unavailable-state markup
      renderAddonToggle(kind, wrap, sel) {
        const empty = wrap.querySelector('[data-pc-toggle-empty]');
        const filled = wrap.querySelector('[data-pc-toggle-filled]');
        const unavailable = wrap.querySelector('[data-pc-toggle-unavailable]');
        const fitsLabel = wrap.dataset.pcFitsLabel || '';

        const isDisabled = wrap.classList.contains('is-disabled');

        if (isDisabled) {
          if (empty) empty.hidden = true;
          if (filled) filled.hidden = true;
          if (unavailable) unavailable.hidden = false;
          wrap.classList.remove('has-selection');
          return;
        }

        if (unavailable) unavailable.hidden = true;

        if (sel) {
          if (empty) empty.hidden = true;
          if (filled) {
            filled.hidden = false;
            const swatch = filled.querySelector('[data-pc-toggle-swatch]');
            if (swatch) {
              if (sel.swatchImage) {
                swatch.style.backgroundImage = `url('${sel.swatchImage}')`;
                swatch.style.backgroundSize = 'cover';
                swatch.style.backgroundColor = '';
              } else if (sel.colorHex) {
                swatch.style.setProperty('background', sel.colorHex);
                swatch.style.setProperty('background-size', 'cover');
              } else {
                swatch.style.setProperty('background', '');
                swatch.style.backgroundImage = '';
                swatch.style.backgroundSize = '';
              }
            }
            const labelEl = filled.querySelector('[data-pc-toggle-label]');
            if (labelEl) labelEl.textContent = (sel.color || '').toUpperCase();
            const metaEl = filled.querySelector('[data-pc-toggle-meta]');
            if (metaEl) {
              metaEl.textContent = sel.fitsText ? `${sel.size || ''} | ${fitsLabel} ${sel.fitsText}` : (sel.size || '');
            }
            const priceEl = filled.querySelector('[data-pc-toggle-price]');
            if (priceEl) priceEl.textContent = sel.priceFormatted || '';
          }
          wrap.classList.add('has-selection');
        } else {
          if (filled) filled.hidden = true;
          if (empty) empty.hidden = false;
          wrap.classList.remove('has-selection');
        }
      }

      applySizeFilter() {
        const wraps = [this.passeWrap, this.frameWrap, this.b2b && this.packagingWrap].filter(
          Boolean
        );
        wraps.forEach((wrap) => {
          const kind = wrap.dataset.pcAddon;
          // A frame fits the OUTER edge of whatever it surrounds: the
          // matt's outer size when a passe-partout is selected, otherwise
          // the print's own size. Passe-partouts always filter by the
          // print size (matched via their `size_fit`).
          let filterSize = this.normSize;
          if (kind === 'frame' && this.selection.passepartout && this.selection.passepartout.size) {
            filterSize = PrintConfigurator.normalize(this.selection.passepartout.size);
          }
          let visibleCount = 0;
          wrap.querySelectorAll('[data-pc-option]').forEach((row) => {
            if (row.hasAttribute('data-pc-none')) {
              row.hidden = false;
              return;
            }
            // The tube fits every size, so it opts out of matching.
            if (this.b2b && row.hasAttribute('data-pc-fits-any')) {
              row.hidden = false;
              visibleCount += 1;
              return;
            }
            const fitsNorm = row.dataset.fitsNormalized || '';
            const show = Boolean(filterSize) && fitsNorm === filterSize;
            row.hidden = !show;
            if (show) visibleCount += 1;
          });

          const list = wrap.querySelector('[data-pc-list]');
          const toggle = wrap.querySelector('[data-pc-toggle]');
          const noFitting = visibleCount === 0;

          if (noFitting) {
            list.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
            wrap.classList.remove('is-open');
            wrap.classList.add('is-disabled');
            toggle.setAttribute('disabled', 'disabled');
            // Reset any previously-selected addon so the preview clears
            // the corresponding layer (passepartout / frame), the URL
            // param is dropped, and the toggle UI returns to its empty
            // state. resetAddonSelection() dispatches
            // `print-configurator:change` which <print-preview> listens
            // for; without it the preview would keep showing the old
            // passepartout/frame even though no addon is available for
            // the new print size.
            if (this.selection[kind]) {
              this.resetAddonSelection(kind, wrap);
            } else {
              this.renderAddonToggle(kind, wrap, null);
            }
          } else {
            wrap.classList.remove('is-disabled');
            toggle.removeAttribute('disabled');

            // If the currently selected addon doesn't fit the new size,
            // first try to find the same addon (same product + color) in a
            // variant that fits the new size — that lets the user keep
            // their choice without having to re-pick after every size swap.
            const sel = this.selection[kind];
            if (sel && !sel.fitsAny && PrintConfigurator.normalize(sel.fits) !== filterSize) {
              const currentOpt = wrap.querySelector(
                `[data-pc-option][data-variant-id="${sel.id}"]`
              );
              const productTitle = currentOpt?.dataset.variantProductTitle || '';
              const color = currentOpt?.dataset.variantColor || sel.color || '';
              const replacement = [...wrap.querySelectorAll('[data-pc-option]')].find(
                (o) =>
                  !o.hasAttribute('data-pc-none') &&
                  o.dataset.variantProductTitle === productTitle &&
                  o.dataset.variantColor === color &&
                  o.dataset.fitsNormalized === filterSize
              );
              if (replacement) {
                replacement.click();
              } else {
                this.resetAddonSelection(kind, wrap);
              }
            } else {
              // Re-render in correct state (toggle classes may have changed)
              this.renderAddonToggle(kind, wrap, this.selection[kind]);
            }
          }
        });
        // Canvas: keep a frame selected after a size change — filtering above
        // may have cleared one that no longer fits the new size. Guarded by
        // _canvasReady so it doesn't fire during the initial filter, before
        // _applyUrlParams has had a chance to honour a deep-linked frame.
        if (this._canvasReady) this._selectDefaultCanvasFrame();
        this.applyPackagingLock();
        this.dispatchTotal();
      }

      // B2B: packaging is for a loose print. Once a passepartout or frame is
      // added TPC pack the framed piece, so the row locks, reads "Delivered
      // framed" and any choice drops.
      applyPackagingLock() {
        if (!this.b2b || !this.packagingWrap) return;
        const wrap = this.packagingWrap;
        const toggle = wrap.querySelector('[data-pc-toggle]');
        const list = wrap.querySelector('[data-pc-list]');
        const framed = Boolean(this.selection.passepartout || this.selection.frame);

        // The disabled row has two meanings: framed, or nothing fits the size.
        const unavailable = wrap.querySelector('[data-pc-toggle-unavailable]');
        if (unavailable) {
          unavailable.textContent =
            (framed ? wrap.dataset.pcFramedLabel : wrap.dataset.pcNoLabel) || '';
        }

        if (framed) {
          if (list) list.hidden = true;
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('disabled', 'disabled');
          }
          wrap.classList.remove('is-open');
          wrap.classList.add('is-disabled');
          this._setPackagingError(false);
          if (this.selection.packaging) this.resetAddonSelection('packaging', wrap);
          else this.renderAddonToggle('packaging', wrap, null);
          return;
        }

        // Unlocked again. Only re-enable if a size actually fits, so this
        // never undoes the size filter above.
        const fits = [...wrap.querySelectorAll('[data-pc-option]')].some(
          (row) => !row.hidden && !row.hasAttribute('data-pc-none')
        );
        if (!fits) return;
        wrap.classList.remove('is-disabled');
        if (toggle) toggle.removeAttribute('disabled');
        this.renderAddonToggle('packaging', wrap, this.selection.packaging || null);
      }

      // B2B: the packaging choice rides on the product form as a visible
      // `Packaging` line item property, so the normal add to cart carries it
      // and the cart shows it next to the print. Disabled while empty so a
      // blank value is never submitted.
      syncPackagingInput() {
        if (!this.b2b || !this.packagingInput) return;
        const sel = this.selection.packaging;
        const label = sel ? (sel.productTitle || sel.color || '').trim() : '';
        this.packagingInput.value = label;
        this.packagingInput.disabled = !label;
        if (label) this._setPackagingError(false);
      }

      // B2B: "Packaging is required" beside the section heading, and a red row.
      _setPackagingError(show) {
        if (!this.b2b) return;
        const el = this.querySelector('[data-pc-packaging-error]');
        if (el) el.hidden = !show;
        if (this.packagingWrap) this.packagingWrap.classList.toggle('is-error', Boolean(show));
      }

      // True when packaging is offered and nothing is chosen. False when a
      // frame or passepartout locks the row, or when no option fits the size.
      _packagingMissing() {
        if (!this.b2b || !this.packagingWrap) return false;
        if (this.packagingWrap.classList.contains('is-disabled')) return false;
        if (this.selection.packaging) return false;
        return [...this.packagingWrap.querySelectorAll('[data-pc-option]')].some(
          (row) => !row.hidden && !row.hasAttribute('data-pc-none')
        );
      }

      resetAddonSelection(kind, wrap) {
        this.selection[kind] = null;
        const list = wrap.querySelector('[data-pc-list]');
        list.querySelectorAll('[data-pc-option]').forEach((o) => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-selected', 'false');
        });
        const noneRow = list.querySelector('[data-pc-none]');
        if (noneRow) {
          noneRow.classList.add('is-selected');
          noneRow.setAttribute('aria-selected', 'true');
        }
        this.renderAddonToggle(kind, wrap, null);
        this.dispatchAddonChange(kind);
        // Selection was cleared because the new size doesn't fit it; drop
        // the matching URL param so the deep link stays consistent.
        this._updateUrl({ [kind]: null });
      }

      dispatchAddonChange(kind, userInitiated = false) {
        // Packaging is not a preview layer (<print-preview> recomposes from the
        // event below); its only consumer is the form input.
        if (this.b2b && kind === 'packaging') {
          this.syncPackagingInput();
          return;
        }
        this.dispatchEvent(
          new CustomEvent('print-configurator:change', {
            detail: { kind, selection: this.selection[kind] || null, userInitiated },
            bubbles: true,
          })
        );
      }

      // Ask <print-preview> to bring the product/preview slide into view without
      // recomposing the addon layers (a size change doesn't alter the
      // passepartout/frame composition). Dispatched only for real user clicks.
      dispatchFocusPreview() {
        this.dispatchEvent(
          new CustomEvent('print-configurator:focus-preview', { bubbles: true })
        );

        // Products without a composed preview (e.g. wall objects) have no
        // <print-preview> to handle the event above, so scroll the gallery
        // back to its lead slide directly — the equivalent of the print's
        // composed slide — so a shopper who scrolled to a lifestyle shot
        // returns to the main product image when they pick a size.
        const root = this.closest('product-info') || document;
        if (root.querySelector('print-preview')) return;
        const gallery = root.querySelector('product-images');
        if (!gallery || typeof gallery.selectMedia !== 'function') return;
        const firstSlide = gallery.querySelector('[data-image-id][data-media-id]');
        const mediaId = firstSlide?.getAttribute('data-media-id');
        if (mediaId) gallery.selectMedia(mediaId);
      }

      // Mobile-only, once per page: scroll the product gallery/preview back
      // into view the first time a shopper picks an add-on. On desktop the
      // preview sits beside the configurator and is refocused in place, so no
      // page scroll is needed there.
      _maybeScrollPreviewIntoViewMobile() {
        if (this._didScrollPreviewMobile) return;
        if (!this._isMobile()) return;
        this._didScrollPreviewMobile = true;

        const root = this.closest('product-info') || document;
        const target =
          root.querySelector('product-images') || root.querySelector('print-preview');
        if (!target) return;

        // Defer one frame so the add-on drawer has closed and body scroll is
        // unlocked before we move the page.
        requestAnimationFrame(() => {
          const headerHeight =
            window.theme && typeof window.theme.readHeights === 'function'
              ? window.theme.readHeights().headerHeight || 0
              : 0;
          const top = Math.max(
            0,
            target.getBoundingClientRect().top + window.scrollY - headerHeight - 8
          );
          window.scrollTo({ top, behavior: 'smooth' });
        });
      }

      // Update the ATC button disabled state and text immediately when a variant
      // is selected. This avoids waiting for the product-info network fetch.
      // Update desktop title badge and mobile image badge based on selected size.
      _updateBadge(opt, isUnavailable) {
        const sectionId = this.dataset.sectionId;
        const badge = sectionId ? document.getElementById(`TitleBadge-${sectionId}`) : null;
        const mobileBadge = sectionId ? document.getElementById(`TitleBadgeMobile-${sectionId}`) : null;

        let desktopText = '';
        let mobileText = '';

        if (isUnavailable) {
          const soldOutStr = this.dataset.stringSoldOut || (window.theme?.strings?.soldOut) || 'Sold out';
          desktopText = soldOutStr;
          mobileText = soldOutStr;
        } else {
          const onSale = opt.dataset.variantOnSale === 'true';
          if (onSale) {
            const comparePrice = Number(opt.dataset.variantComparePrice || 0);
            const price = Number(opt.dataset.variantPrice || 0);
            const badgeType = badge?.getAttribute('data-sale-badge-type') || 'percentage';
            if (badgeType === 'percentage' && comparePrice > 0) {
              const pct = Math.round((comparePrice - price) / comparePrice * 100);
              desktopText = `- ${pct}%`;
              mobileText = `- ${pct}%`;
            } else {
              const fmt = window.theme?.formatMoney;
              const fmtStr = fmt ? fmt(comparePrice - price, window.theme.moneyFormat) : '';
              desktopText = fmtStr;
              mobileText = fmtStr;
            }
          }
        }

        if (badge) {
          badge.textContent = desktopText;
          desktopText ? badge.removeAttribute('hidden') : badge.setAttribute('hidden', '');
        }
        if (mobileBadge) {
          mobileBadge.textContent = mobileText;
          mobileText ? mobileBadge.removeAttribute('hidden') : mobileBadge.setAttribute('hidden', '');
        }
      }

      // B2B: keep the SKU in the title block and the hidden `properties[SKU]`
      // input in step with the selected size. Like the badges above, nothing
      // else refreshes them for configurator products.
      _updateSku(opt) {
        if (!this.b2b) return;
        const sku = opt.dataset.variantSku || '';
        const skuInput = this.getForm()?.querySelector('[name="properties[SKU]"]');
        if (skuInput) {
          skuInput.value = sku;
          skuInput.disabled = !sku;
        }
        const sectionId = this.dataset.sectionId;
        const el = sectionId ? document.getElementById(`Sku-${sectionId}`) : null;
        const valueEl = el ? el.querySelector('[data-sku-value]') : null;
        if (!valueEl) return;
        valueEl.textContent = sku;
        el.classList.toggle('hidden', !sku);
      }

      // Move the Free Framing Service badge to/from the size section head
      // depending on whether add-ons are visible.
      _syncFramingBadge(isUnavailable) {
        if (!this._framingBadge) return;
        if (isUnavailable) {
          this._sizeHead?.appendChild(this._framingBadge);
        } else {
          this._addonsHead?.appendChild(this._framingBadge);
        }
      }

      updateAtcButtonState(isUnavailable) {
        const productInfo = this.closest('product-info');
        const productForm = productInfo?.querySelector('product-form') || this.closest('product-form');
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          if (isUnavailable) {
            productForm.toggleSubmitButton(true, window.theme?.strings?.soldOut || 'Sold out');
          } else {
            productForm.toggleSubmitButton(false, window.theme?.strings?.addToCart || 'Add to cart');
          }
        }
        // Show/hide the "Notify me when available" button
        if (productInfo && typeof productInfo.showSoldoutNotificationButton === 'function') {
          productInfo.showSoldoutNotificationButton(isUnavailable);
        }

        // Sync the sticky cart-bar button — print-configurator products
        // don't fire the variantChange pub/sub, so the cart-bar's own
        // subscriber never runs. Update it directly here.
        const cartBarBtn = productInfo?.querySelector('cart-bar button.product__submit__add');
        const cartBarBtnText = productInfo?.querySelector('cart-bar [data-cart-bar-button-text]');
        if (cartBarBtn && cartBarBtnText) {
          const labelAdd =
            window.theme?.strings?.addToCart || cartBarBtn.dataset.labelAdd || 'Add to cart';
          const labelSoldOut =
            window.theme?.strings?.soldOut || cartBarBtn.dataset.labelSoldOut || 'Sold out';
          cartBarBtnText.textContent = isUnavailable ? labelSoldOut : labelAdd;
          if (isUnavailable) {
            cartBarBtn.setAttribute('disabled', 'disabled');
            cartBarBtn.removeAttribute('data-cart-bar-scroll');
          } else {
            cartBarBtn.removeAttribute('disabled');
            cartBarBtn.setAttribute('data-cart-bar-scroll', '');
          }
        }
      }

      // Update the main PDP price element with the selected variant's price
      // (without add-ons). The TOTAL row, which includes add-ons, is owned
      // by dispatchTotal(). Called whenever the size selection changes.
      dispatchVariantPrice(opt) {        const onSale = opt.dataset.variantOnSale === 'true';
        const priceFmt = opt.dataset.variantPriceFormatted || '';
        const compareFmt = opt.dataset.variantCompareFormatted || '';

        const root = this.closest('product-info') || document;
        const pdpPriceContainer = root.querySelector('[id^="Price-"]');
        if (pdpPriceContainer) {
          // Current (sale) price
          pdpPriceContainer
            .querySelectorAll('.price__sale .price-item--sale.price-item--last, .price__sale .price-item--last')
            .forEach((el) => { el.innerHTML = priceFmt; });
          // Regular (non-sale) price
          pdpPriceContainer
            .querySelectorAll('.price__regular .price-item--regular')
            .forEach((el) => { el.innerHTML = priceFmt; });

          // Compare-at price (strikethrough <s>)
          pdpPriceContainer
            .querySelectorAll('.price__sale s.price-item--regular')
            .forEach((el) => { el.innerHTML = onSale ? compareFmt : ''; });

          // Toggle on-sale state
          const priceWrap = pdpPriceContainer.querySelector('.price') || pdpPriceContainer;
          if (priceWrap && priceWrap.classList) {
            priceWrap.classList.toggle('price--on-sale', onSale);
          }
        }

        // Add-to-cart button price (in product-form submit label, if used).
        const form = this.getForm();
        const atcPriceEl = form?.querySelector('[data-product-price]');
        if (atcPriceEl) atcPriceEl.innerHTML = priceFmt;
      }

      dispatchTotal() {
        const selectedOpt = this.querySelector('[data-pc-option][aria-selected="true"]');
        const isUnavailable = selectedOpt?.dataset.unavailable === 'true';

        // When sold out, the add-ons section is hidden and not purchasable, so
        // exclude their prices from the total broadcast to listeners (like cart-bar).
        const passe = isUnavailable ? 0 : (this.selection.passepartout?.price ?? 0);
        const frame = isUnavailable ? 0 : (this.selection.frame?.price ?? 0);
        const total = this.printPriceCents + passe + frame;

        const fmt = window.theme?.formatMoney;
        const moneyFormat = window.theme?.moneyFormat;
        const formatted = fmt ? fmt(total, moneyFormat) : (total / 100).toFixed(2);

        // Write total to dedicated TOTAL row(s) only — never to the main
        // product price element (that one reflects the selected variant price
        // and is owned by <variant-selects> / <product-info>).
        const root = this.closest('product-info') || this;
        root.querySelectorAll('[data-pc-total]').forEach((el) => {
          el.innerHTML = formatted;
        });

        this.dispatchEvent(
          new CustomEvent('print-configurator:total', {
            detail: { cents: total, isUnavailable },
            bubbles: true,
          })
        );
      }

      hasAnyAddon() {
        return Boolean(this.selection.passepartout || this.selection.frame);
      }

      // The quantity field, or 1 when no quantity selector is shown. The theme
      // still renders a hidden `quantity` input with value 1 in that case.
      readQuantity() {
        const form = this.getForm();
        const input = form && form.querySelector('[name="quantity"]');
        const n = input ? parseInt(input.value, 10) : 1;
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      getForm() {
        if (this.formId) {
          const f = document.getElementById(this.formId);
          if (f) return f;
        }
        const productForm = this.closest('product-info')?.querySelector('form[data-product-form]');
        return productForm || document.querySelector('form[data-product-form]');
      }

      bindFormSubmit() {
        const form = this.getForm();
        if (!form) return;
        form.addEventListener(
          'submit',
          (e) => {
            // Canvas: a frame is mandatory. Never let a bare canvas product
            // (no frame) reach the cart, even if the disabled button is
            // bypassed — block the submit outright.
            if (this.isCanvas && this.frameRequired && !this.selection.frame) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this._refreshCanvasGate();
              return;
            }
            // B2B: a loose print can't go in the bag without packaging.
            if (this._packagingMissing()) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this._setPackagingError(true);
              this.packagingWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
            if (!this.hasAnyAddon()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            const button = form.querySelector('[type="submit"]');
            this.submitBundle(form, button);
          },
          true
        );
      }

      async submitBundle(form, button) {
        const variantInput = form.querySelector('[name="id"]');
        const printVariantId = Number(variantInput?.value);
        if (!printVariantId) return;

        // Carry the product's artist into the bundle line, matching the
        // plain PDP add-to-cart. The value is the hidden "Artist" line
        // item property rendered into the product form by
        // `snippets/artist-property-input.liquid`, so the source of truth
        // stays in one place.
        const artistInput = form.querySelector('[name="properties[Artist]"]');
        const artistName = artistInput ? artistInput.value.trim() : '';
        const skuInput = form.querySelector('[name="properties[SKU]"]');

        // Build the bundle as SEPARATE cart lines (print + each add-on) via
        // the shared util in `assets/js/utils/bundle-properties.js`. The
        // cart-transformer Function `merge`s the lines (sharing `_bundleId` /
        // `_role`) into one bundle line at checkout, priced as the SUM of each
        // real line's native cost — no frontend-baked prices. (Canvas frames
        // are real 0-priced variants, so they merge in for free.)
        const items = window.theme.buildBundleItems({
          printVariantId: printVariantId,
          quantity: this.readQuantity(),
          addons: this._buildBundleAddons(),
          source: this.isCanvas ? 'canvas-configurator' : 'print-configurator',
          artist: artistName,
          printSku: skuInput ? skuInput.value : '',
          // Used for the CART card's `_bundleDisplay` (browser-rendered <img>),
          // NOT the merge `image` field — so a relative/preview URL is fine and
          // it can't break the merge.
          printImageUrl: this.printImageUrl,
        });

        const cartDrawer = document.querySelector('cart-drawer');
        const cartItems = document.querySelector('cart-items');

        this._setSubmitLoading(button, cartDrawer, true);
        try {
          await this._postCartAdd(items);
          if (cartItems && typeof cartItems.getCart === 'function') {
            cartItems.getCart();
          }
          if (window.theme?.settings?.cartType === 'drawer') {
            document.dispatchEvent(new CustomEvent('theme:cart:toggle'));
          } else {
            window.location = window.theme?.routes?.cart_url || '/cart';
          }
        } catch (err) {
          this._showCartError(form, err);
          // eslint-disable-next-line no-console
          console.error('[print-configurator] add to cart failed', err);
        } finally {
          this._setSubmitLoading(button, cartDrawer, false);
        }
      }

      /**
       * Normalize the current passepartout / frame selection into the
       * `addons` array shape expected by `buildBundleProperties`.
       *
       * `print-configurator-addon.liquid` populates both
       * `data-variant-product-title` and `data-variant-color` from
       * `p.title` (the addon product's title is also its colour-swatch
       * label), so we dedupe parts via `buildAddonLabel` to avoid
       * "Oak Frame Oak Frame A4 …" style repetition.
       */
      _buildBundleAddons() {
        const passe = this.selection.passepartout;
        const frame = this.selection.frame;
        const buildLabel = window.theme.buildAddonLabel;
        const addons = [];

        if (passe) {
          addons.push({
            variantId: passe.id,
            priceCents: passe.price,
            awdKey: 'passe',
            label: buildLabel(
              [passe.color, passe.productTitle, passe.size],
              'Passepartout'
            ),
            hex: passe.colorHex || '',
            sku: passe.sku,
          });
        }
        if (frame) {
          addons.push({
            variantId: frame.id,
            priceCents: frame.price,
            awdKey: 'frame',
            label: buildLabel(
              [frame.productTitle, frame.color, frame.size],
              'Frame'
            ),
            hex: frame.colorHex || '',
            sku: frame.sku,
          });
        }
        return addons;
      }

      _setSubmitLoading(button, cartDrawer, isLoading) {
        if (button) {
          button.classList.toggle('is-loading', isLoading);
          button.disabled = isLoading;
        }
        if (cartDrawer) cartDrawer.classList.toggle('is-loading', isLoading);
      }

      async _postCartAdd(items) {
        const cartAddUrl = (window.theme?.routes?.cart_add_url || '/cart/add') + '.js';
        const res = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });
        let data = {};
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('json') || contentType.includes('javascript')) {
          const text = await res.text();
          try {
            data = text ? JSON.parse(text) : {};
          } catch (_) {
            // Non-JSON body (e.g. HTML error page) — treat as error
            if (!res.ok) throw new Error(`Cart add failed (${res.status})`);
          }
        }
        if (data && data.status && data.status >= 400) {
          throw data;
        }
        return data;
      }

      _showCartError(form, err) {
        const errorContainer = form
          .closest('product-form')
          ?.querySelector('[data-cart-errors-container]');
        const errorMessage = errorContainer?.querySelector('[data-cart-error-message]');
        if (errorMessage) {
          errorMessage.textContent =
            (err && (err.description || err.message)) || 'Could not add bundle to cart.';
          errorContainer.classList.add('is-visible');
        }
      }
    }
  );
}
/* components/location-popup.js
 * ---------------------------------------------------------------------------
 * <location-popup> — initial-visit geo prompt.
 *
 * 1. Reads Klaviyo's anonymous geo-IP endpoint (no auth required) to detect
 *    the visitor's country code.
 * 2. If the detected country differs from `localization.country.iso_code`
 *    AND it's one of `localization.available_countries`, shows a modal
 *    offering to switch markets.
 * 3. "Confirm" submits a hidden POST to `/localization` — Shopify's
 *    canonical mechanism for switching country / currency / domain.
 * 4. "Select another location" swaps the modal to a dropdown view.
 * 5. Dismissing (X / backdrop / ESC) sets a session-scoped flag so the
 *    popup doesn't keep nagging in the same browser session. It will
 *    re-evaluate on the next visit/tab. No long-lived cookies.
 *
 * Dev overrides (query string):
 *   ?geo=NO       — bypass Klaviyo, treat country as NO (etc.)
 *   ?geo-reset    — clear the dismissal flag so the popup re-appears
 *   ?geo-show=1   — force the popup open ignoring every show condition
 *                   (uses ?geo override if present, else current country)
 * ---------------------------------------------------------------------------
 */
if (!customElements.get('location-popup')) {
  customElements.define(
    'location-popup',
    class LocationPopup extends HTMLElement {
      connectedCallback() {
        this.SESSION_KEY = 'tpc_geo_dismissed';
        this.GEO_URL = 'https://a.klaviyo.com/forms/api/v3/geo-ip';
        this.OVERRIDE_PARAM = 'geo';
        this.RESET_PARAM = 'geo-reset';
        this.FORCE_PARAM = 'geo-show';

        this.dialog = this.querySelector('.location-popup__dialog');
        this.backdrop = this.querySelector('.location-popup__backdrop');
        this.form = this.querySelector('#LocationPopupForm');
        this.countryInput = this.querySelector('[data-country-input]');
        // Picker view now uses <popout-select>; the chosen ISO lives on the
        // hidden input that popout-select writes to on option click.
        this.countryPickerInput = this.querySelector('[data-country-picker-input]');
        this.countryToggleText = this.querySelector(
          '.location-popup__popout [data-popout-toggle-text]'
        );
        // Client-side search over the picker list.
        this.countrySearch = this.querySelector('[data-country-search]');
        this.popoutToggle = this.querySelector('.location-popup__popout [data-popout-toggle]');
        this.popoutList = this.querySelector('.location-popup__popout-list');
        this.countryListItems = this.querySelectorAll(
          '.location-popup__popout-list .popout-list__item'
        );
        this.views = {
          detected: this.querySelector('[data-view="detected"]'),
          picker: this.querySelector('[data-view="picker"]'),
        };
        this.detectedHeading = this.views.detected?.querySelector('.location-popup__heading');
        this.headingTemplate = this.detectedHeading?.dataset.headingTemplate || '';

        this.currentCountry = (this.dataset.currentCountry || '').toUpperCase();

        // The modal now always renders so it can serve as the country/market
        // switcher (footer / mobile menu / header). `data-auto-prompt` gates
        // ONLY the automatic geo prompt — the manual switcher works regardless.
        this.autoPrompt = this.dataset.autoPrompt !== 'false';

        const countriesScript = this.querySelector('[data-countries]');
        try {
          this.countries = countriesScript ? JSON.parse(countriesScript.textContent) : [];
        } catch (_) {
          this.countries = [];
        }

        this._handleResetParam();
        this._bindEvents();
        this._maybeShow();
      }

      _handleResetParam() {
        const params = new URLSearchParams(window.location.search);
        if (params.has(this.RESET_PARAM)) {
          try {
            sessionStorage.removeItem(this.SESSION_KEY);
          } catch (_) {}
        }
      }

      async _maybeShow() {
        const params = new URLSearchParams(window.location.search);
        const forced = params.has(this.FORCE_PARAM);

        // Auto prompt disabled by the merchant — only the manual switcher and
        // the ?geo-show=1 preview override may open the modal.
        if (!this.autoPrompt && !forced) return;

        if (!forced) {
          try {
            if (sessionStorage.getItem(this.SESSION_KEY) === '1') return;
          } catch (_) {}
        }

        const detected = await this._detectCountry();
        if (!detected) return;
        if (!forced && detected === this.currentCountry) return;

        const match = this.countries.find((c) => c.iso === detected);
        if (!match && !forced) return;

        const displayName = match ? match.name : detected;
        const displayCurrency = match ? match.currency : '';
        if (this.detectedHeading && this.headingTemplate) {
          // Fill the merchant's placeholders. Square-bracket tokens (unlike
          // the old <country> tag) survive theme-editor serialization
          // untouched, so no stray-tag cleanup is needed.
          this.detectedHeading.textContent = this.headingTemplate
            .split('[location]').join(displayName)
            .split('[currency]').join(displayCurrency);
        }

        // Pre-select the detected country in the picker dropdown so that
        // "Select another location" already has the right default.
        if (match) {
          this._setPickerSelection(match.iso, match.name, match.currency);
        }

        this.detectedIso = detected;
        this._showView('detected');
        this._open();
      }

      async _detectCountry() {
        const params = new URLSearchParams(window.location.search);
        const override = (params.get(this.OVERRIDE_PARAM) || '').toUpperCase();
        if (override) return override;

        try {
          const res = await fetch(this.GEO_URL, { credentials: 'omit' });
          if (!res.ok) return null;
          const data = await res.json();
          return ((data && data.country_code) || '').toUpperCase() || null;
        } catch (_) {
          return null;
        }
      }

      _open() {
        this.hidden = false;
        if (typeof this.dialog.showModal === 'function') {
          try {
            this.dialog.showModal();
          } catch (_) {
            this.dialog.setAttribute('open', '');
          }
        } else {
          this.dialog.setAttribute('open', '');
        }
        if (this.backdrop) this.backdrop.hidden = false;
        document.documentElement.classList.add('location-popup-open');
      }

      _close({ dismiss } = { dismiss: true }) {
        if (this.dialog.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
        if (this.backdrop) this.backdrop.hidden = true;
        document.documentElement.classList.remove('location-popup-open');
        if (dismiss) {
          try {
            sessionStorage.setItem(this.SESSION_KEY, '1');
          } catch (_) {}
        }
      }

      _setPickerSelection(iso, name, currency) {
        if (this.countryPickerInput) {
          this.countryPickerInput.value = iso;
          this.countryPickerInput.setAttribute('value', iso);
        }
        if (this.countryToggleText) {
          const label =
            String(name || iso).toUpperCase() +
            (currency ? ' (' + currency + ')' : '');
          this.countryToggleText.textContent = label;
          // popout-select reads/writes this attribute as its remembered
          // "current" label — keep them in sync so a later click on the
          // same option doesn't fall back to a stale value.
          this.countryToggleText.setAttribute('data-popout-toggle-text', label);
        }
        // Mirror is-active / aria-current onto the option list so the
        // dropdown highlights the chosen row.
        const items = this.querySelectorAll(
          '.location-popup__popout .popout-list__item'
        );
        items.forEach((li) => {
          const opt = li.querySelector('[data-popout-option]');
          const isMatch = opt && opt.getAttribute('data-value') === iso;
          li.classList.toggle('is-active', !!isMatch);
          if (opt) {
            if (isMatch) opt.setAttribute('aria-current', 'true');
            else opt.removeAttribute('aria-current');
          }
        });
      }

      _showView(name) {
        Object.keys(this.views).forEach((key) => {
          const el = this.views[key];
          if (el) el.hidden = key !== name;
        });
      }

      // Public entry point for the footer / mobile-menu / header country
      // switcher. Skips the geo "detected" prompt and opens straight to the
      // manual picker, which is already pre-selected to the current market.
      openPicker() {
        if (this.countrySearch) {
          this.countrySearch.value = '';
          this._filterCountries();
        }
        this._showView('picker');
        this._open();
      }

      // Hide list rows whose country name / ISO doesn't match the query.
      _filterCountries() {
        if (!this.countryListItems) return;
        const q = (this.countrySearch?.value || '').trim().toLowerCase();
        this.countryListItems.forEach((li) => {
          const opt = li.querySelector('[data-popout-option]');
          const name = (opt?.getAttribute('data-country-name') || '').toLowerCase();
          const iso = (opt?.getAttribute('data-value') || '').toLowerCase();
          li.hidden = q !== '' && !name.includes(q) && !iso.includes(q);
        });
      }

      _confirm(iso) {
        if (!iso || !this.form || !this.countryInput) return;
        this.countryInput.value = iso;
        // Suppress the prompt for the rest of this session. The user has made
        // an explicit choice, so we must not nag again — and we can't rely on
        // the "detected === current" check, because picking a market other
        // than the geo-detected one (e.g. staying on Sweden while geo-IP
        // reports Denmark) leaves that mismatch in place on the next load.
        try {
          sessionStorage.setItem(this.SESSION_KEY, '1');
        } catch (_) {}
        this.form.submit();
      }

      _bindEvents() {
        // External triggers — the inline country dropdowns in the footer,
        // mobile menu, header and toolbar were replaced by buttons carrying
        // `data-location-popup-open`. Delegated from the document so every
        // trigger on the page shares this single modal instance.
        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-location-popup-open]');
          if (!trigger) return;
          e.preventDefault();
          this.openPicker();
        });

        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-location-popup-close]')) {
            this._close({ dismiss: true });
            return;
          }
          const actionEl = e.target.closest('[data-action]');
          const action = actionEl && actionEl.dataset.action;
          if (!action) return;

          if (action === 'confirm-detected') {
            this._confirm(this.detectedIso);
          } else if (action === 'show-picker') {
            this._showView('picker');
          } else if (action === 'confirm-picked') {
            const picked =
              (this.countryPickerInput && this.countryPickerInput.value) ||
              this.detectedIso;
            this._confirm(picked);
          }
        });

        // popout-select sets the hidden input value AND rewrites the toggle
        // text to the bare ISO code (e.g. "NO"). We want the friendlier
        // "NORWAY (NOK)" label, so intercept the change event and rewrite.
        if (this.countryPickerInput) {
          this.countryPickerInput.addEventListener('change', () => {
            const iso = this.countryPickerInput.value;
            const match = this.countries.find((c) => c.iso === iso);
            if (!match) return;
            // popout-select fires this `change` event and THEN — still in the
            // same synchronous task — overwrites the toggle text with the bare
            // `data-value` (its generic non-submit behaviour). Defer our
            // friendly-label rewrite to a microtask so it lands after that
            // clobber; otherwise the toggle is left reading "AF" instead of
            // "AFGHANISTAN (EUR)".
            queueMicrotask(() =>
              this._setPickerSelection(match.iso, match.name, match.currency)
            );
          });
        }

        // ESC key on a native <dialog> fires the 'cancel' event.
        if (this.dialog) {
          this.dialog.addEventListener('cancel', (e) => {
            e.preventDefault();
            this._close({ dismiss: true });
          });

          // Click the overlay to close (desktop + mobile). With showModal()
          // the visible dim is the dialog's ::backdrop, and a click on it
          // targets the dialog element itself — selecting a country or
          // clicking inside the box targets a descendant, so it stays open.
          // (The fallback .show()/[open] path is covered by the separate
          // `.location-popup__backdrop` element's data-location-popup-close.)
          this.dialog.addEventListener('click', (e) => {
            if (e.target === this.dialog) this._close({ dismiss: true });
          });
        }

        // Live country search inside the picker dropdown.
        if (this.countrySearch) {
          this.countrySearch.addEventListener('input', () => this._filterCountries());

          // Enter in the search field confirms the choice — shoppers expect it
          // to behave like the Confirm button. The field sits outside the hidden
          // localization <form>, so the browser submits nothing on its own.
          // If a query has been typed, pick the first matching country first so
          // "type + Enter" jumps straight to it; an empty field just confirms
          // whatever is already selected (e.g. an option clicked by mouse).
          this.countrySearch.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const q = (this.countrySearch.value || '').trim().toLowerCase();
            if (q) {
              const firstVisible = Array.from(this.countryListItems || []).find(
                (li) => !li.hidden
              );
              const opt =
                firstVisible && firstVisible.querySelector('[data-popout-option]');
              const iso = opt && opt.getAttribute('data-value');
              const match = iso && this.countries.find((c) => c.iso === iso);
              // A query that matches nothing: don't confirm a stale choice.
              if (!match) return;
              this._setPickerSelection(match.iso, match.name, match.currency);
            }
            const picked =
              (this.countryPickerInput && this.countryPickerInput.value) ||
              this.detectedIso;
            this._confirm(picked);
          });
        }

        // Focus the search field when the dropdown opens so the visitor can
        // type straight away. popout-select toggles its own open state on this
        // same click, so defer the focus until after it has revealed the list.
        if (this.popoutToggle && this.countrySearch) {
          this.popoutToggle.addEventListener('click', () => {
            requestAnimationFrame(() => {
              if (this.popoutList?.classList.contains('popout-list--visible')) {
                this.countrySearch.focus();
              }
            });
          });
        }
      }
    }
  );
}
/*
 * <print-preview> — geometry-driven PDP composer.
 *
 * The composed slide stacks four layers inside a "stage" box:
 *   bg → art print → passepartout PNG → frame PNG
 *
 * Nothing about the overlay PNGs is assumed. Each overlay's canvas (any size —
 * 800×1100, 2048×2048, …) is MEASURED when it loads:
 *   - aspect: naturalWidth / naturalHeight → the stage takes the frame's
 *     canvas ratio (largest box that fits the slide, centred; see CSS), so the
 *     frame renders 1:1 with no letterbox mismatch.
 *   - outer: bounding box of opaque pixels (the graphic inside the canvas).
 *   - hole: the transparent opening in the middle (frame rebate / mat window),
 *     found by scanning outwards from the centre of `outer`.
 * All rects are fractions of the canvas, so the whole composition is ratio math
 * (no pixel sizes, no ResizeObserver): frame fills the stage → the mat's outer
 * bbox is fitted into the frame hole → the art is fitted into the mat window
 * (or the frame hole, or floats at FLOAT_SCALE when nothing is selected).
 *
 * Pixel data is read through an offscreen canvas (Shopify CDN serves CORS
 * headers). If reading fails (tainted canvas, decode error) we still know the
 * aspect from the <img>, and fall back to "opening = whole canvas".
 */
(() => {
  const ALPHA_THRESHOLD = 128; // ≥ this is "solid"; skips drop/inner shadows
  const MEASURE_SIZE = 400; // px, longest side of the analysis bitmap
  const FLOAT_SCALE = 0.75; // unframed print size (legacy behaviour)
  const COVER_TOLERANCE = 0.12; // art vs opening ratio mismatch allowed for cover
  // How far a covered layer is tucked UNDER the layer above it (print under the
  // frame rebate / mat window, mat under the frame), as a fraction of the stage
  // width. Hides the ~1px measurement + pixel-rounding seam that otherwise can
  // show as a hairline of backdrop along an edge. 0.6% ≈ 2px at 375px, 5px at
  // 800px — well inside any frame lip.
  const BLEED = 0.006;
  const FULL = { x: 0, y: 0, w: 1, h: 1 };
  const geometryCache = new Map();

  const measureUrl = (url) =>
    /([?&])width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, `$1width=${MEASURE_SIZE}`) : url;

  function measure(url) {
    if (!url) return Promise.resolve(null);
    if (geometryCache.has(url)) return geometryCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(analyze(img));
      img.onerror = () => {
        // CORS refused: retry without crossOrigin so we at least get the aspect.
        const plain = new Image();
        plain.onload = () => resolve({ aspect: plain.naturalWidth / plain.naturalHeight, outer: FULL, hole: null });
        plain.onerror = () => resolve(null);
        plain.src = measureUrl(url);
      };
      img.src = measureUrl(url);
    });
    geometryCache.set(url, p);
    return p;
  }

  function analyze(img) {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return null;
    const geo = { aspect: w / h, outer: FULL, hole: null };
    try {
      const scale = Math.min(1, MEASURE_SIZE / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, cw, ch);
      const a = ctx.getImageData(0, 0, cw, ch).data;
      const opaque = (x, y) => a[(y * cw + x) * 4 + 3] >= ALPHA_THRESHOLD;

      let minX = cw, minY = ch, maxX = -1, maxY = -1;
      for (let y = 0; y < ch; y++) {
        for (let x = 0; x < cw; x++) {
          if (!opaque(x, y)) continue;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < 0) return geo; // fully transparent — nothing to align to
      geo.outer = { x: minX / cw, y: minY / ch, w: (maxX - minX + 1) / cw, h: (maxY - minY + 1) / ch };

      const cx = Math.round((minX + maxX) / 2);
      const cy = Math.round((minY + maxY) / 2);
      if (!opaque(cx, cy)) {
        let l = cx, r = cx, t = cy, b = cy;
        while (l > minX && !opaque(l - 1, cy)) l--;
        while (r < maxX && !opaque(r + 1, cy)) r++;
        while (t > minY && !opaque(cx, t - 1)) t--;
        while (b < maxY && !opaque(cx, b + 1)) b++;
        // A hole must be bounded by solid pixels on all four sides.
        if (l > minX && r < maxX && t > minY && b < maxY) {
          geo.hole = { x: l / cw, y: t / ch, w: (r - l + 1) / cw, h: (b - t + 1) / ch };
        }
      }
    } catch (e) {
      /* tainted canvas etc. — keep aspect, opening = whole canvas */
    }
    return geo;
  }

  // Rect `frac` expressed inside `rect` (both as fractions of the same box).
  const within = (rect, frac) => ({
    x: rect.x + frac.x * rect.w,
    y: rect.y + frac.y * rect.h,
    w: frac.w * rect.w,
    h: frac.h * rect.h,
  });

  const centred = (scale) => ({ x: (1 - scale) / 2, y: (1 - scale) / 2, w: scale, h: scale });

  // Grow `rect` by BLEED on every side, uniform in pixels (y is scaled by the
  // stage aspect because stage fractions are per-axis).
  const bleed = (rect, stageAspect) => ({
    x: rect.x - BLEED,
    y: rect.y - BLEED * stageAspect,
    w: rect.w + 2 * BLEED,
    h: rect.h + 2 * BLEED * stageAspect,
  });

  // Place a whole overlay canvas (aspect `geo.aspect`, graphic at `geo.outer`)
  // so its graphic fits — contained & centred — inside `target` (a rect in
  // stage fractions). Returns the canvas rect in stage fractions.
  function fitCanvasInto(geo, target, stageAspect) {
    const W = stageAspect, H = 1; // stage in "real" units
    const tx = target.x * W, tw = target.w * W;
    const ty = target.y * H, th = target.h * H;
    const o = geo.outer;
    const k = Math.min(tw / (o.w * geo.aspect), th / o.h); // canvas height in real units
    const cw = k * geo.aspect, ch = k;
    const ow = o.w * cw, oh = o.h * ch;
    const canvasLeft = tx + (tw - ow) / 2 - o.x * cw;
    const canvasTop = ty + (th - oh) / 2 - o.y * ch;
    return { x: canvasLeft / W, y: canvasTop / H, w: cw / W, h: ch / H };
  }

  // Accepts "0.7273", "800 / 1100" or "800/1100".
  function parseAspect(value) {
    if (!value) return 0;
    const m = String(value).match(/^\s*([\d.]+)\s*(?:\/\s*([\d.]+))?\s*$/);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    const d = m[2] ? parseFloat(m[2]) : 1;
    return n > 0 && d > 0 ? n / d : 0;
  }

  // Resolve once the DISPLAY copy of an overlay is decoded, so showing it
  // never lags behind the art moving into place.
  const preloadCache = new Map();
  function preload(url) {
    if (preloadCache.has(url)) return preloadCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => (img.decode ? img.decode().catch(() => {}).then(resolve) : resolve());
      img.onerror = () => resolve();
      img.src = url;
    });
    preloadCache.set(url, p);
    return p;
  }

  function setRect(el, r) {
    if (!el) return;
    el.style.left = `${r.x * 100}%`;
    el.style.top = `${r.y * 100}%`;
    el.style.width = `${r.w * 100}%`;
    el.style.height = `${r.h * 100}%`;
  }

  if (customElements.get('print-preview')) return;
  customElements.define(
    'print-preview',
    class PrintPreview extends HTMLElement {
      connectedCallback() {
        this.orientation = (this.dataset.orientation || 'portrait').toLowerCase();
        this.inner = this.querySelector('[data-pdp-print-preview]');
        this.artEl = this.querySelector('[data-pdp-print-art]');
        this.passepartoutEl = this.querySelector('[data-pdp-print-passepartout]');
        this.frameEl = this.querySelector('[data-pdp-print-frame]');

        this._defaultAspect = parseAspect(this.inner?.dataset.defaultAspect) || 800 / 1100;

        this._state = { passepartout: null, frame: null }; // { url, geo }
        this._selections = { passepartout: null, frame: null }; // last raw selections
        this._onChange = this._onChange.bind(this);
        // Size changes only need to refocus the product/preview slide — no
        // addon recomposition — so they use a dedicated, lighter event.
        this._onFocusPreview = () => this._focusPreviewSlide();

        const root = this.closest('product-info') || document;
        this._root = root;
        root.addEventListener('print-configurator:change', this._onChange);
        root.addEventListener('print-configurator:focus-preview', this._onFocusPreview);

        // Re-fit once the art has loaded (its ratio decides cover vs contain).
        if (this.artEl && !this.artEl.complete) {
          this.artEl.addEventListener('load', () => this._composeWhenReady(), { once: true });
        }

        // Restore the configurator's current selection. Both add-ons are
        // registered before anything composes, so the first paint already
        // shows the complete framed result (no unframed → framed hop). With no
        // selection the CSS default (floating art) is already correct.
        const config = root.querySelector('print-configurator');
        const sel = config && config.selection;
        if (sel && (sel.passepartout || sel.frame)) {
          this._apply('passepartout', sel.passepartout);
          this._apply('frame', sel.frame);
        } else if (this.inner) {
          // Nothing to restore — the CSS default is the first paint; arm the
          // animation for the shopper's first pick.
          this._painted = true;
          requestAnimationFrame(() => requestAnimationFrame(() => this.inner.setAttribute('data-animate', 'true')));
        }
      }

      disconnectedCallback() {
        if (this._root) {
          this._root.removeEventListener('print-configurator:change', this._onChange);
          this._root.removeEventListener('print-configurator:focus-preview', this._onFocusPreview);
        }
      }

      _onChange(e) {
        const { kind, selection, userInitiated } = e.detail || {};
        if (!kind) return;
        this._apply(kind, selection);

        // When the shopper actively picks or clears a frame / passepartout,
        // bring the composed preview slide into view so they see the result —
        // even if they'd scrolled down to a lifestyle shot. Programmatic
        // changes (e.g. a size swap re-fitting the addon) pass
        // userInitiated=false and leave the gallery where it is.
        if (userInitiated) this._focusPreviewSlide();
      }

      _focusPreviewSlide() {
        const gallery = (this._root || document).querySelector('product-images');
        const mediaId = this.getAttribute('data-media-id');
        if (gallery && mediaId && typeof gallery.selectMedia === 'function') {
          gallery.selectMedia(mediaId);
        }
      }

      _apply(kind, selection) {
        this._selections[kind] = selection || null;
        // The frame image depends on whether a mat is present (40x50-only
        // prints switch between the 40x50 and the standard frame opening).
        // Re-pick the frame FIRST so, if it changes, it is pending before the
        // mat update composes — one paint, not two.
        if (kind === 'passepartout' && this._selections.frame) {
          this._applyUrl('frame', this._pickImage('frame', this._selections.frame));
        }
        this._applyUrl(kind, this._pickImage(kind, selection));
      }

      _applyUrl(kind, url) {
        const el = kind === 'passepartout' ? this.passepartoutEl : this.frameEl;
        if (!el) return;
        const selection = this._selections[kind];
        const current = this._state[kind];
        if ((current?.url || '') === (url || '')) return; // unchanged

        if (this.inner) {
          const attr = kind === 'passepartout' ? 'data-has-passepartout' : 'data-has-frame';
          this.inner.setAttribute(attr, url ? 'true' : 'false');
        }

        if (!url) {
          this._state[kind] = null;
          el.removeAttribute('src');
          el.alt = '';
          el.setAttribute('hidden', '');
          this._composeWhenReady();
          return;
        }

        // Nothing moves until BOTH the geometry and the display image are
        // ready — then stage, mat, art and overlay update in one paint. Until
        // then the previous composition stays exactly as it is (no
        // intermediate "aspect-only" layout, no late-arriving frame).
        const entry = { url, geo: null, alt: selection?.color || '', ready: false };
        this._state[kind] = entry;
        Promise.all([measure(url), preload(url)]).then(([geo]) => {
          if (this._state[kind] !== entry) return; // superseded
          entry.geo = geo;
          entry.ready = true;
          this._composeWhenReady();
        });
      }

      _composeWhenReady() {
        const pending = Object.values(this._state).some((e) => e && !e.ready);
        if (!pending) this._compose();
      }

      _compose() {
        if (!this.inner) return;
        const frame = this._state.frame;
        const passe = this._state.passepartout;

        const stageAspect = frame?.geo?.aspect || passe?.geo?.aspect || this._defaultAspect;
        this.inner.style.setProperty('--stage-aspect', stageAspect);

        // Frame: its canvas IS the stage.
        this._show(this.frameEl, frame, FULL);
        const frameHole = frame ? frame.geo?.hole || frame.geo?.outer || FULL : null;

        // Anything that sits inside an opening is tucked slightly under the
        // layer that covers it (see BLEED).
        const openingUnderFrame = frameHole ? bleed(frameHole, stageAspect) : null;

        const art = this.artEl;
        let artRect;
        if (passe) {
          let matRect;
          if (frame && passe.geo && frame.geo) {
            matRect = fitCanvasInto(passe.geo, openingUnderFrame, stageAspect);
          } else if (frame) {
            matRect = openingUnderFrame;
          } else {
            matRect = FULL; // mat canvas is the stage
          }
          this._show(this.passepartoutEl, passe, matRect);
          // The whole print file — its white paper border is part of the
          // product and shows inside the mat, exactly like the packshots.
          artRect = bleed(within(matRect, passe.geo?.hole || passe.geo?.outer || FULL), stageAspect);
        } else {
          this._show(this.passepartoutEl, null, FULL);
          artRect = frame ? openingUnderFrame : centred(FLOAT_SCALE);
        }
        setRect(art, artRect);

        // Cover the opening when the print's ratio (near-)matches it — like a
        // real print tucked behind a rebate / mat — otherwise contain so a
        // mismatched combination (wrong-orientation add-on) is not cropped.
        let artFit = 'contain';
        if (art && art.naturalWidth && art.naturalHeight && (frame || passe)) {
          const artAspect = art.naturalWidth / art.naturalHeight;
          const rectAspect = (artRect.w * stageAspect) / artRect.h;
          artFit = Math.abs(artAspect / rectAspect - 1) <= COVER_TOLERANCE ? 'cover' : 'contain';
        }
        if (art) {
          art.style.objectFit = artFit;
          art.style.clipPath = '';
          art.removeAttribute('data-clip');
        }

        // Let other composers (e.g. assets/blankwall.js "View in your room")
        // mirror the exact same geometry instead of re-deriving it.
        this.dispatchEvent(new CustomEvent('print-preview:composed', { bubbles: true }));

        // First composition snaps; subsequent ones animate (see CSS).
        if (!this._painted) {
          this._painted = true;
          requestAnimationFrame(() => requestAnimationFrame(() => this.inner.setAttribute('data-animate', 'true')));
        }
      }

      // Show/hide an overlay <img> together with its rect (single paint).
      _show(el, entry, rect) {
        if (!el) return;
        if (entry) {
          setRect(el, rect);
          if (el.getAttribute('src') !== entry.url) el.src = entry.url;
          el.alt = entry.alt || '';
          el.removeAttribute('hidden');
        } else if (!el.hasAttribute('hidden')) {
          el.removeAttribute('src');
          el.alt = '';
          el.setAttribute('hidden', '');
        }
      }

      _pickImage(kind, selection) {
        if (!selection || !selection.pdpImages) return '';
        const pick = (imgs) =>
          (imgs && (imgs[this.orientation] || imgs.portrait || imgs.landscape || imgs.square)) || '';
        // 40x50-only prints: the frame with the 40x50 opening is only right
        // when the print sits directly in it. With a passepartout the shopper
        // buys the larger frame that fits the mat, so use the standard image.
        const matSelected = kind === 'frame' && !!this._selections.passepartout;
        if (!matSelected) {
          const small = pick(selection.pdpImages40x50);
          if (small) return small;
        }
        return pick(selection.pdpImages);
      }
    }
  );
})();
if (!customElements.get('product-usp-row')) {
  customElements.define(
    'product-usp-row',
    class ProductUspRow extends HTMLElement {
      connectedCallback() {
        if (this.dataset.autoRotate !== 'true') return;
        this.items = Array.from(this.querySelectorAll('[data-usp-item]'));
        if (this.items.length < 2) return;
        this.interval = Number(this.dataset.interval) || 4000;
        this.index = 0;

        // Only rotate while in mobile breakpoint and respect reduced motion
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        this.mq = window.matchMedia('(max-width: 749px)');
        this._onMQ = () => this.evaluate();
        if (this.mq.addEventListener) this.mq.addEventListener('change', this._onMQ);
        else this.mq.addListener(this._onMQ);
        this.evaluate();
      }

      disconnectedCallback() {
        this.stop();
        if (this.mq) {
          if (this.mq.removeEventListener) this.mq.removeEventListener('change', this._onMQ);
          else this.mq.removeListener(this._onMQ);
        }
      }

      evaluate() {
        if (this.mq && this.mq.matches) this.start();
        else this.stop();
      }

      start() {
        if (this.timer) return;
        this.timer = window.setInterval(() => this.next(), this.interval);
      }

      stop() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }

      next() {
        const prev = this.items[this.index];
        this.index = (this.index + 1) % this.items.length;
        const curr = this.items[this.index];
        if (prev) prev.removeAttribute('data-active');
        if (curr) curr.setAttribute('data-active', '');
      }
    }
  );
}
/* Drawer accordion tabs — when a product accordion uses style "drawer-tabs",
   the open dialog renders a tab bar listing every sibling drawer-tabs accordion.
   Clicking a tab swaps the body content in-place without closing/reopening the
   dialog, avoiding the buggy close+open animation.
   Content is cached from the original Liquid render so switching back
   always shows the correct tab's content. */
(function () {
  if (window.__drawerTabsBound) return;
  window.__drawerTabsBound = true;

  // Cache each popup's original body innerHTML on first click so repeated
  // tab switches always read from the original server-rendered content.
  function cacheOriginalContent() {
    document.querySelectorAll('popup-component[data-drawer-tab-id]').forEach((popup) => {
      if (popup.__drawerTabContent === undefined) {
        const body = popup.querySelector('.drawer__body');
        if (body) popup.__drawerTabContent = body.innerHTML;
      }
    });
  }

  // Mark `targetId`'s tab active in a tab bar; reset the rest.
  function setActiveTab(tabList, targetId) {
    if (!tabList) return;
    tabList.querySelectorAll('[data-drawer-tab-target]').forEach((btn) => {
      const isNow = btn.getAttribute('data-drawer-tab-target') === targetId;
      btn.classList.toggle('is-active', isNow);
      btn.setAttribute('aria-selected', isNow ? 'true' : 'false');
      if (isNow) {
        btn.setAttribute('aria-current', 'page');
      } else {
        btn.removeAttribute('aria-current');
      }
    });
  }

  // The in-place tab swap mutates the OPEN popup's own DOM (body content +
  // tab-bar active state). Each accordion is a separate popup-component that
  // re-shows whatever DOM it currently holds, so without this a popup that was
  // used to view a sibling tab would re-open showing that sibling. Restore the
  // popup to its own canonical state when its dialog closes.
  function resetPopupToSelf(popup) {
    const ownId = popup.getAttribute('data-drawer-tab-id');
    const dialog = popup.querySelector('dialog');
    if (!dialog) return;

    cacheOriginalContent();

    const body = dialog.querySelector('.drawer__body');
    if (body && popup.__drawerTabContent !== undefined) {
      body.innerHTML = popup.__drawerTabContent;
      body.scrollTop = 0;
    }

    setActiveTab(dialog.querySelector('[role="tablist"]'), ownId);
  }

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-drawer-tab-target]');
    if (!tab) return;
    e.preventDefault();

    // Active tab? do nothing.
    if (tab.classList.contains('is-active')) return;

    const targetId = tab.getAttribute('data-drawer-tab-target');
    if (!targetId) return;

    const currentPopup = tab.closest('popup-component');
    if (!currentPopup) return;

    const targetPopup = document.querySelector(
      `popup-component[data-drawer-tab-id="${CSS.escape(targetId)}"]`
    );
    if (!targetPopup) return;

    // Ensure all original content is cached before we modify anything
    cacheOriginalContent();

    // Swap body content in-place using cached original content
    const currentDialog = currentPopup.querySelector('dialog');
    const liveBody = currentDialog && currentDialog.querySelector('.drawer__body');

    if (liveBody && targetPopup.__drawerTabContent !== undefined) {
      liveBody.innerHTML = targetPopup.__drawerTabContent;
      liveBody.scrollTop = 0;
    }

    // Update active tab state in the open tab bar
    setActiveTab(currentDialog && currentDialog.querySelector('[role="tablist"]'), targetId);
  });

  // ---------------------------------------------------------------------------
  // Bind each drawer-tabs popup's dialog so closing restores its own content.
  const closeBound = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function bindResetOnClose() {
    document.querySelectorAll('popup-component[data-drawer-tab-id]').forEach((popup) => {
      if (closeBound && closeBound.has(popup)) return;
      const dialog = popup.querySelector('dialog');
      if (!dialog) return;
      if (closeBound) closeBound.add(popup);
      dialog.addEventListener('close', () => resetPopupToSelf(popup));
    });
  }

  // ---------------------------------------------------------------------------
  // Edge-fade overflow hint. The tab bar scrolls horizontally with a hidden
  // scrollbar, so toggle is-overflow-start / is-overflow-end on each tablist
  // (CSS turns these into a fading mask) to signal there are more tabs past the
  // cut. Best-effort: a missing ResizeObserver just means no live updates.
  function updateFade(el) {
    const max = el.scrollWidth - el.clientWidth;
    const x = el.scrollLeft;
    const EPS = 2; // ignore sub-pixel rounding
    el.classList.toggle('is-overflow-start', x > EPS);
    el.classList.toggle('is-overflow-end', max > EPS && x < max - EPS);
  }

  const fadeBound = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function setupTabFades() {
    document.querySelectorAll('.drawer__tabs[role="tablist"]').forEach((el) => {
      if (fadeBound && fadeBound.has(el)) return;
      if (fadeBound) fadeBound.add(el);

      updateFade(el);
      el.addEventListener('scroll', () => updateFade(el), { passive: true });

      // Fires when the dialog opens and the bar finally has a width, plus any
      // later resize / orientation change.
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(() => updateFade(el)).observe(el);
      }
    });
  }

  setupTabFades();
  bindResetOnClose();
})();
if (!customElements.get('cart-slider-qa-price')) {
  customElements.define(
    'cart-slider-qa-price',
    class CartSliderQaPrice extends HTMLElement {
      connectedCallback() {
        this.info =
          this.closest('product-info') ||
          (this.getRootNode && this.getRootNode().querySelector
            ? this.getRootNode().querySelector('product-info')
            : null);
        if (!this.info) return;

        // Mirror the merchant's configured slider heading onto the modal so the
        // CSS-injected header matches the title shown on the cart-slider panel
        // (e.g. "Pair With"). The api-cart-slider-quick-add section can't read
        // the cart-drawer block setting, but the slider has already rendered it
        // in `.cart-slider__heading`, so copy it across as a CSS variable. Falls
        // back to the CSS default ("Combine with") when not found.
        const headingEl = document.querySelector('.cart-slider__heading');
        const modal = this.closest('.product-quick-add--cart-slider');
        if (headingEl && modal) {
          const headingText = headingEl.textContent.trim();
          if (headingText) {
            modal.style.setProperty('--cart-slider-qa-heading', JSON.stringify(headingText));
          }
        }

        this._lastSelected = null; // { label, price }

        this._onClick = this._onClick.bind(this);
        this._onVariantChange = this._onVariantChange.bind(this);
        this.info.addEventListener('click', this._onClick);

        if (
          window.subscribe &&
          window.theme &&
          window.theme.PUB_SUB_EVENTS &&
          window.theme.PUB_SUB_EVENTS.variantChange
        ) {
          this._unsub = window.subscribe(
            window.theme.PUB_SUB_EVENTS.variantChange,
            this._onVariantChange
          );
        }
      }

      disconnectedCallback() {
        if (this.info && this._onClick) {
          this.info.removeEventListener('click', this._onClick);
        }
        if (typeof this._unsub === 'function') this._unsub();
      }

      _onClick(e) {
        const li = e.target.closest('li[data-variant-price-formatted]');
        if (!li || !this.info.contains(li)) return;

        const formatted = li.getAttribute('data-variant-price-formatted');
        const sizeAttr = li.getAttribute('value') || '';

        if (!formatted) return;

        this._lastSelected = {
          label: sizeAttr,
          price: formatted,
          variantId: li.getAttribute('data-variant-id') || '',
          available: li.getAttribute('data-variant-available') === 'true',
        };
        this._applySelection(this._lastSelected);
        this._applyAvailability(this._lastSelected);
      }

      _onVariantChange(payload) {
        if (
          !payload ||
          !payload.data ||
          payload.data.sectionId !== this.info.dataset.sectionId
        ) {
          return;
        }
        // The section has been re-rendered and the variant-selects subtree
        // replaced — re-apply the last user-picked label/price so the UI
        // doesn't flash back to "Select size" / "From €X".
        if (!this._lastSelected) return;
        // Defer so DOM updates from product-info have settled.
        setTimeout(() => {
          this._applySelection(this._lastSelected);
          this._applyAvailability(this._lastSelected);
        }, 0);
      }

      _applySelection(sel) {
        if (!sel) return;

        // Update both [data-product-price] elements (card + toggle)
        this.info.querySelectorAll('[data-product-price]').forEach((el) => {
          el.innerHTML = sel.price;
        });

        // Update the toggle label
        const label = this.info.querySelector('[data-popout-toggle-text]');
        if (label && sel.label) {
          label.innerHTML = sel.label;
        }
      }

      // Mirror the PDP's sold-out behaviour when a size is picked. Reuses the
      // exact same state classes/markup as the product page
      // (snippets/product.liquid + product-buttons.liquid), so the shared CSS
      // produces an identical result: sold-out variants disable + swap the
      // add-to-cart button to "Sold out" (hidden by the
      // `.show-product-notification.variant--soldout` rule) and reveal the
      // notify-me button; in-stock variants restore "Add to cart" and hide it.
      _applyAvailability(sel) {
        if (!sel) return;

        const soldOut = sel.available === false;

        // `variant--soldout` on the form wrapper drives notify-me visibility
        // and the sold-out button hide rule (assets/product.css + theme.css).
        const wrapper = this.info.querySelector('[data-form-wrapper]');
        if (wrapper) {
          wrapper.classList.toggle('variant--soldout', soldOut);
        }

        const productForm = this.info.querySelector('product-form');
        if (!productForm) return;

        // Point the add-to-cart form at the chosen variant.
        const idInput = productForm.querySelector('[name="id"]');
        if (idInput && sel.variantId) {
          idInput.value = sel.variantId;
          idInput.disabled = soldOut;
        }

        // Swap the submit button via the standard <product-form> API (keeps
        // any price markup inside the label intact).
        if (typeof productForm.toggleSubmitButton === 'function') {
          const strings = (window.theme && window.theme.strings) || {};
          productForm.toggleSubmitButton(
            soldOut,
            soldOut ? strings.soldOut || 'Sold out' : strings.addToCart || 'Add to cart'
          );
        }
      }
    }
  );
}
    function fetchProduct(handle) {
      const requestRoute = `${window.theme.routes.root}products/${handle}.js`;

      return window
        .fetch(requestRoute)
        .then((response) => {
          return response.json();
        })
        .catch((e) => {
          console.error(e);
        });
    }

    const selectors$9 = {
      gridSwatchFieldset: '[data-grid-swatch-fieldset]',
      productItem: '[data-grid-item]',
      productInfo: '[data-product-information]',
      productImage: '[data-product-image]',
      swatchButton: '[data-swatch-button]',
    };

    const classes$7 = {
      visible: 'is-visible',
      stopEvents: 'no-events',
    };

    const attributes$7 = {
      handle: 'data-swatch-handle',
      label: 'data-swatch-label',
      swatchCount: 'data-swatch-count',
      variantName: 'data-swatch-variant-name',
      variantTitle: 'data-variant-title',
    };

    class GridSwatch extends HTMLElement {
      constructor() {
        super();

        this.productItemMouseLeaveEvent = () => this.hideVariantImages();
        this.showVariantImageEvent = (swatchButton) => this.showVariantImage(swatchButton);
        this.resizeEvent = () => this.init();
      }

      connectedCallback() {
        this.handle = this.getAttribute(attributes$7.handle);
        this.productItem = this.closest(selectors$9.productItem);
        this.productInfo = this.closest(selectors$9.productInfo);
        this.productImage = this.productItem.querySelector(selectors$9.productImage);
        this.swatchesStyle = theme.settings.collectionSwatchStyle;
        document.addEventListener('theme:resize:width', this.resizeEvent);

        const label = this.getAttribute(attributes$7.label).trim().toLowerCase();

        fetchProduct(this.handle).then((product) => {
          this.product = product;
          this.colorOption = product.options.find(function (element) {
            return element.name.toLowerCase() === label || null;
          });

          if (this.colorOption) {
            this.init();
          }
        });
      }

      init() {
        this.swatchCount = this.productInfo.querySelector(`[${attributes$7.swatchCount}]`);
        this.swatchFieldset = this.productInfo.querySelector(selectors$9.gridSwatchFieldset);
        this.hideSwatchesTimer = 0;

        if (this.swatchCount.hasAttribute(attributes$7.swatchCount)) {
          if (this.swatchesStyle == 'text-slider') {
            this.swatchCount.addEventListener('mouseenter', () => {
              if (this.hideSwatchesTimer) clearTimeout(this.hideSwatchesTimer);

              this.productInfo.classList.add(classes$7.stopEvents);
              this.swatchFieldset.classList.add(classes$7.visible);
            });

            // Prevent color swatches blinking on mouse move
            this.productInfo.addEventListener('mouseleave', () => {
              this.hideSwatchesTimer = setTimeout(() => {
                this.productInfo.classList.remove(classes$7.stopEvents);
                this.swatchFieldset.classList.remove(classes$7.visible);
              }, 100);
            });
          }

          if (this.swatchesStyle == 'slider' || this.swatchesStyle == 'grid' || this.swatchesStyle == 'limited') {
            this.swatchFieldset.classList.add(classes$7.visible);
          }
        }

        this.bindSwatchButtonEvents();
      }

      bindSwatchButtonEvents() {
        this.querySelectorAll(selectors$9.swatchButton)?.forEach((swatchButton) => {
          // Show variant image when hover on color swatch
          swatchButton.addEventListener('mouseenter', this.showVariantImageEvent);
        });

        this.productItem.addEventListener('mouseleave', this.productItemMouseLeaveEvent);
      }

      showVariantImage(event) {
        const swatchButton = event.target;
        const variantName = swatchButton.getAttribute(attributes$7.variantName)?.replaceAll('"', "'");
        const variantImages = this.productImage.querySelectorAll(`[${attributes$7.variantTitle}]`);
        const variantImageSelected = this.productImage.querySelector(`[${attributes$7.variantTitle}="${variantName}"]`);

        // Hide all variant images
        variantImages?.forEach((image) => {
          image.classList.remove(classes$7.visible);
        });

        // Show selected variant image
        variantImageSelected?.classList.add(classes$7.visible);
      }

      hideVariantImages() {
        // Hide all variant images
        this.productImage.querySelectorAll(`[${attributes$7.variantTitle}].${classes$7.visible}`)?.forEach((image) => {
          image.classList.remove(classes$7.visible);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize:width', this.resizeEvent);
      }
    }


    if (!customElements.get('grid-swatch')) {
      customElements.define('grid-swatch', GridSwatch);
    }
    const selectors$8 = {
      flickityButton: '.flickity-prev-next-button',
      productLink: '[data-product-link]',
      slide: '[data-hover-slide]',
      slideTouch: '[data-hover-slide-touch]',
      slider: '[data-hover-slider]',
      recentlyViewed: 'recently-viewed',
      video: 'video',
      vimeo: '[data-host="vimeo"]',
      youtube: '[data-host="youtube"]',
    };

    class HoverImages extends HTMLElement {
      constructor() {
        super();

        this.flkty = null;
        this.slider = this.querySelector(selectors$8.slider);
        this.handleScroll = this.handleScroll.bind(this);
        this.recentlyViewed = this.closest(selectors$8.recentlyViewed);
        this.hovered = false;

        this.mouseEnterEvent = () => this.mouseEnterActions();
        this.mouseLeaveEvent = () => this.mouseLeaveActions();

        this.addEventListener('mouseenter', this.mouseEnterEvent);
        this.addEventListener('mouseleave', this.mouseLeaveEvent);
      }

      connectedCallback() {
        this.addArrowClickHandler();

        if (this.recentlyViewed) {
          if (!this.recentlyViewed.classList.contains('hidden')) {
            this.initBasedOnDevice();
          } else {
            this.onRecentlyViewedLoaded = () => this.initBasedOnDevice();
            this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', this.onRecentlyViewedLoaded);
          }
        } else {
          this.initBasedOnDevice();
        }
      }

      disconnectedCallback() {
        if (this.flkty) {
          this.flkty.options.watchCSS = false;
          this.flkty.destroy();
          this.flkty = null;
        }

        if (this.recentlyViewed && this.onRecentlyViewedLoaded) {
          this.recentlyViewed.removeEventListener('theme:recently-viewed:loaded', this.onRecentlyViewedLoaded);
        }

        this.removeEventListener('mouseenter', this.mouseEnterEvent);
        this.removeEventListener('mouseleave', this.mouseLeaveEvent);
      }

      initBasedOnDevice() {
        if (window.theme.touch) {
          this.initTouch();
        } else {
          this.initFlickity();
        }
      }

      addArrowClickHandler() {
        const productLink = this.closest(selectors$8.productLink);
        if (productLink) {
          productLink.addEventListener('click', (e) => {
            if (e.target.matches(selectors$8.flickityButton)) {
              e.preventDefault();
            }
          });
        }
      }

      initTouch() {
        this.style.setProperty('--slides-count', this.querySelectorAll(selectors$8.slideTouch).length);
        this.slider.addEventListener('scroll', this.handleScroll);
      }

      handleScroll() {
        const slideIndex = this.slider.scrollLeft / this.slider.clientWidth;
        this.style.setProperty('--slider-index', slideIndex);
      }

      initFlickity() {
        if (this.flkty || !this.slider || this.slider.classList.contains('flickity-enabled') || this.querySelectorAll(selectors$8.slide).length < 2) return;

        this.flkty = new window.theme.Flickity(this.slider, {
          cellSelector: selectors$8.slide,
          contain: true,
          wrapAround: true,
          watchCSS: true,
          autoPlay: false,
          draggable: false,
          pageDots: false,
          prevNextButtons: true,
        });

        this.flkty.pausePlayer();

        this.addEventListener('mouseenter', () => {
          this.flkty.unpausePlayer();
        });

        this.addEventListener('mouseleave', () => {
          this.flkty.pausePlayer();
        });
      }

      mouseEnterActions() {
        this.hovered = true;

        this.videoActions();
      }

      mouseLeaveActions() {
        this.hovered = false;

        this.videoActions();
      }

      videoActions() {
        const youtube = this.querySelector(selectors$8.youtube);
        const vimeo = this.querySelector(selectors$8.vimeo);
        const mediaExternal = youtube || vimeo;
        const mediaNative = this.querySelector(selectors$8.video);

        if (mediaExternal) {
          let action = this.hovered ? 'playVideo' : 'pauseVideo';
          let string = `{"event":"command","func":"${action}","args":""}`;

          if (vimeo) {
            action = this.hovered ? 'play' : 'pause';
            string = `{"method":"${action}"}`;
          }

          mediaExternal.contentWindow.postMessage(string, '*');

          mediaExternal.addEventListener('load', (e) => {
            // Call videoActions() again when iframe is loaded to prevent autoplay being triggered if it loads after the "mouseleave" event
            this.videoActions();
          });
        } else if (mediaNative) {
          if (this.hovered) {
            mediaNative.play();
          } else {
            mediaNative.pause();
          }
        }
      }
    }


    if (!customElements.get('hover-images')) {
      customElements.define('hover-images', HoverImages);
    }
    const classes$6 = {
      added: 'is-added',
      animated: 'is-animated',
      disabled: 'is-disabled',
      error: 'has-error',
      loading: 'is-loading',
      open: 'is-open',
      overlayText: 'product-item--overlay-text',
      visible: 'is-visible',
      siblingLinkCurrent: 'sibling__link--current',
    };

    const selectors$7 = {
      animation: '[data-animation]',
      apiContent: '[data-api-content]',
      buttonQuickAdd: '[data-quick-add-btn]',
      buttonAddToCart: '[data-add-to-cart]',
      cartDrawer: 'cart-drawer',
      cartPage: '[data-cart-page]',
      cartLineItems: '[data-line-items]',
      dialog: 'dialog',
      focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      messageError: '[data-message-error]',
      modalButton: '[data-quick-add-modal-handle]',
      modalContent: '[data-product-upsell-ajax]',
      modalClose: '[data-quick-add-modal-close]',
      productGridItem: 'data-grid-item',
      productInformationHolder: '[data-product-information]',
      quickAddHolder: '[data-quick-add-holder]',
      quickAddModal: '[data-quick-add-modal]',
      quickAddModalTemplate: '[data-quick-add-modal-template]',
      tooltip: '[data-tooltip]',
    };

    const attributes$6 = {
      closing: 'closing',
      productId: 'data-product-id',
      modalHandle: 'data-quick-add-modal-handle',
      siblingSwapper: 'data-sibling-swapper',
      quickAddHolder: 'data-quick-add-holder',
    };

    class QuickAddProduct extends HTMLElement {
      constructor() {
        super();

        this.quickAddHolder = this.querySelector(selectors$7.quickAddHolder);

        if (this.quickAddHolder) {
          this.modal = null;
          this.currentModal = null;
          this.productId = this.quickAddHolder.getAttribute(attributes$6.quickAddHolder);
          this.modalButton = this.quickAddHolder.querySelector(selectors$7.modalButton);
          this.handle = this.modalButton?.getAttribute(attributes$6.modalHandle);
          this.buttonQuickAdd = this.quickAddHolder.querySelector(selectors$7.buttonQuickAdd);
          this.buttonATC = this.quickAddHolder.querySelector(selectors$7.buttonAddToCart);
          this.button = this.modalButton || this.buttonATC;
          this.modalClose = this.modalClose.bind(this);
          this.modalCloseOnProductAdded = this.modalCloseOnProductAdded.bind(this);
          this.a11y = window.theme.a11y;
          this.isAnimating = false;

          this.modalButtonClickEvent = this.modalButtonClickEvent.bind(this);
          this.quickAddLoadingToggle = this.quickAddLoadingToggle.bind(this);
        }
      }

      connectedCallback() {
        /**
         * Modal button works for multiple variants products
         */
        if (this.modalButton) {
          this.modalButton.addEventListener('click', this.modalButtonClickEvent);
        }

        /**
         * Quick add button works for single variant products
         */
        if (this.buttonATC) {
          this.buttonATC.addEventListener('click', (e) => {
            e.preventDefault();

            window.a11y.lastElement = this.buttonATC;

            document.dispatchEvent(
              new CustomEvent('theme:cart:add', {
                detail: {
                  button: this.buttonATC,
                },
              })
            );
          });
        }

        if (this.quickAddHolder) {
          this.quickAddHolder.addEventListener('animationend', this.quickAddLoadingToggle);
          this.errorHandler();
        }
      }

      modalButtonClickEvent(e) {
        e.preventDefault();

        const isSiblingSwapper = this.modalButton.hasAttribute(attributes$6.siblingSwapper);
        const isSiblingLinkCurrent = this.modalButton.classList.contains(classes$6.siblingLinkCurrent);

        if (isSiblingLinkCurrent) return;

        this.modalButton.classList.add(classes$6.loading);
        this.modalButton.disabled = true;

        // Siblings product modal swapper
        if (isSiblingSwapper && !isSiblingLinkCurrent) {
          this.currentModal = e.target.closest(selectors$7.quickAddModal);
          this.currentModal.classList.add(classes$6.loading);
        }

        this.renderModal();
      }

      modalCreate(response) {
        const modalTemplate = this.quickAddHolder.querySelector(selectors$7.quickAddModalTemplate);
        if (!modalTemplate) return;

        const htmlObject = document.createElement('div');
        htmlObject.innerHTML = modalTemplate.innerHTML;

        // Add dialog to the body
        document.body.appendChild(htmlObject.querySelector(selectors$7.quickAddModal));

        this.modal = document.querySelector(`${selectors$7.quickAddModal}[${attributes$6.productId}="${this.productId}"]`);
        this.modal.querySelector(selectors$7.modalContent).innerHTML = new DOMParser().parseFromString(response, 'text/html').querySelector(selectors$7.apiContent).innerHTML;

        this.modalCreatedCallback();
      }

      modalOpen() {
        if (this.currentModal) {
          this.currentModal.dispatchEvent(new CustomEvent('theme:modal:close', {bubbles: false}));
        }

        // Check if browser supports Dialog tags
        if (typeof this.modal.show === 'function') {
          this.modal.show();
        }

        this.modal.setAttribute('open', true);
        this.modal.removeAttribute('inert');

        this.quickAddHolder.classList.add(classes$6.disabled);

        if (this.modalButton) {
          this.modalButton.classList.remove(classes$6.loading);
          this.modalButton.disabled = false;
          window.a11y.lastElement = this.modalButton;
        }

        // Animate items
        requestAnimationFrame(() => {
          this.modal.querySelectorAll(selectors$7.animation).forEach((item) => {
            item.classList.add(classes$6.animated);
          });
        });

        document.dispatchEvent(new CustomEvent('theme:quick-add:open', {bubbles: true}));
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
        document.addEventListener('theme:product:added', this.modalCloseOnProductAdded, {once: true});
      }

      modalClose() {
        if (this.isAnimating) {
          return;
        }

        if (!this.modal.hasAttribute(attributes$6.closing)) {
          this.modal.setAttribute(attributes$6.closing, '');
          this.isAnimating = true;
          return;
        }

        // Check if browser supports Dialog tags
        if (typeof this.modal.close === 'function') {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
        }

        this.modal.removeAttribute(attributes$6.closing);
        this.modal.setAttribute('inert', '');
        this.modal.classList.remove(classes$6.loading);

        if (this.modalButton) {
          this.modalButton.disabled = false;
        }

        if (this.quickAddHolder && this.quickAddHolder.classList.contains(classes$6.disabled)) {
          this.quickAddHolder.classList.remove(classes$6.disabled);
        }

        this.resetAnimatedItems();

        // Dispatch event to close all tooltips when modal closes
        document.dispatchEvent(
          new CustomEvent('theme:tooltip:close', {
            bubbles: true,
            detail: {
              hideTransition: true,
              immediate: true,
            },
          })
        );

        // Unlock scroll if no other drawers & modals are open
        if (!window.theme.hasOpenModals()) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }

        document.removeEventListener('theme:product:added', this.modalCloseOnProductAdded);

        this.a11y.removeTrapFocus();
        this.a11y.autoFocusLastElement();
        this.modalRemove();
      }

      modalRemove() {
        window.theme
          .waitForAllAnimationsEnd(this.modal)
          .then(() => {
            this.modal?.remove();
          })
          .catch(() => {
            this.modal?.remove();
          });
      }

      modalEvents() {
        // Close button click event
        this.modal.querySelector(selectors$7.modalClose)?.addEventListener('click', (e) => {
          e.preventDefault();
          this.modalClose();
        });

        // Close dialog on click outside content
        this.modal.addEventListener('click', (event) => {
          if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
            this.modalClose();
          }
        });

        // Close dialog on click ESC key pressed
        this.modal.addEventListener('keydown', (event) => {
          if (event.code == 'Escape') {
            event.preventDefault();
            this.modalClose();
          }
        });

        this.modal.addEventListener('theme:modal:close', () => {
          this.modalClose();
        });

        // Close dialog after animation completes
        this.modal.addEventListener('animationend', (event) => {
          if (event.target !== this.modal) return;
          this.isAnimating = false;

          if (this.modal.hasAttribute(attributes$6.closing)) {
            this.modalClose();
          } else {
            setTimeout(() => {
              this.a11y.trapFocus(this.modal);
              const focusTarget = this.modal.querySelector('[autofocus]') || this.modal.querySelector(selectors$7.focusable);
              focusTarget?.focus();
            }, 50);
          }
        });
      }

      modalCloseOnProductAdded() {
        this.resetQuickAddButtons();
        if (this.modal && this.modal.hasAttribute('open')) {
          this.modalClose();
        }
      }

      quickAddLoadingToggle(e) {
        if (e.target != this.quickAddHolder) return;

        this.quickAddHolder.classList.remove(classes$6.disabled);
      }

      /**
       * Handle error cart response
       */
      errorHandler() {
        this.quickAddHolder.addEventListener('theme:cart:error', (event) => {
          const holder = event.detail.holder;
          const parentProduct = holder.closest(`[${selectors$7.productGridItem}]`);
          if (!parentProduct) return;

          const errorMessageHolder = holder.querySelector(selectors$7.messageError);
          const hasOverlayText = parentProduct.classList.contains(classes$6.overlayText);
          const productInfo = parentProduct.querySelector(selectors$7.productInformationHolder);
          const button = holder.querySelector(selectors$7.buttonAddToCart);

          if (button) {
            button.classList.remove(classes$6.added, classes$6.loading);
            holder.classList.add(classes$6.error);

            const removeErrorClass = () => {
              this.resetQuickAddButtons();

              if (hasOverlayText) {
                productInfo.classList.remove(classes$6.hidden);
              }

              holder.removeEventListener('animationend', removeErrorClass);
            };

            holder.addEventListener('animationend', removeErrorClass);
          }

          if (errorMessageHolder) {
            errorMessageHolder.innerText = event.detail.description;
          }

          if (hasOverlayText) {
            productInfo.classList.add(classes$6.hidden);
          }
        });
      }

      /**
       * Reset buttons to default states
       */
      resetQuickAddButtons() {
        if (this.quickAddHolder) {
          this.quickAddHolder.classList.remove(classes$6.visible, classes$6.error);
        }

        if (this.buttonQuickAdd) {
          this.buttonQuickAdd.classList.remove(classes$6.added);
          this.buttonQuickAdd.disabled = false;
        }
      }

      renderModal() {
        // Allow the trigger button to override the section that gets
        // fetched for the modal content (e.g. the cart-slider's
        // compact `api-cart-slider-quick-add` instead of the standard
        // full-product `api-product-upsell`). Defaults to the
        // existing upsell section when no override is set.
        const sectionId =
          this.modalButton?.getAttribute('data-quick-add-section') ||
          'api-product-upsell';

        window
          .fetch(`${window.theme.routes.root}products/${this.handle}?section_id=${sectionId}`)
          .then(this.upsellErrorsHandler)
          .then((response) => {
            return response.text();
          })
          .then((response) => {
            this.modalCreate(response);
          });
      }

      modalCreatedCallback() {
        this.modalEvents();
        this.modalOpen();

        wrapElements(this.modal);
      }

      upsellErrorsHandler(response) {
        if (!response.ok) {
          return response.json().then(function (json) {
            const e = new FetchError({
              status: response.statusText,
              headers: response.headers,
              json: json,
            });
            throw e;
          });
        }
        return response;
      }

      resetAnimatedItems() {
        this.modal?.querySelectorAll(selectors$7.animation).forEach((item) => {
          item.classList.remove(classes$6.animated);
        });
      }
    }


    if (!customElements.get('quick-add-product')) {
      customElements.define('quick-add-product', QuickAddProduct);
    }
// Klaviyo back-in-stock form id comes from the theme setting; Klaviyo reads the variant from the URL on submit
const notifyMeFallbackDelay = 10000;

function getNotifyMeFormId() {
  return (window.theme && window.theme.settings && window.theme.settings.klaviyoBisFormId) || '';
}

let notifyMeSavedUrl = null;
let notifyMeTargetUrl = null;
let notifyMeFallbackTimeout = null;
let notifyMeIsOpen = false;

function restoreNotifyMeUrl() {
  if (notifyMeFallbackTimeout) {
    clearTimeout(notifyMeFallbackTimeout);
    notifyMeFallbackTimeout = null;
  }

  if (notifyMeSavedUrl) {
    history.replaceState(history.state, '', notifyMeSavedUrl);
    notifyMeSavedUrl = null;
  }
}

window.addEventListener('klaviyoForms', function (event) {
  const notifyMeFormId = getNotifyMeFormId();
  if (!notifyMeFormId || !event.detail || event.detail.formId !== notifyMeFormId) return;

  if (event.detail.type === 'open') {
    notifyMeIsOpen = true;

    if (notifyMeFallbackTimeout) {
      clearTimeout(notifyMeFallbackTimeout);
      notifyMeFallbackTimeout = null;
    }

    // Re-assert the product URL in case the fallback already restored it
    if (notifyMeTargetUrl) {
      if (!notifyMeSavedUrl) notifyMeSavedUrl = location.pathname + location.search + location.hash;
      history.replaceState(history.state, '', notifyMeTargetUrl);
    }
  }

  if (event.detail.type === 'close') {
    notifyMeIsOpen = false;
    restoreNotifyMeUrl();
    notifyMeTargetUrl = null;
  }
});

window.addEventListener('pagehide', restoreNotifyMeUrl);

document.addEventListener('click', function (event) {
  const trigger = event.target.closest('[data-notify-me]');
  if (!trigger) return;

  if (!window.klaviyo || typeof window.klaviyo.openForm !== 'function') return;

  const handle = trigger.dataset.productHandle;
  const variantId = trigger.dataset.variantId;
  if (!handle || !variantId) return;

  const formId = getNotifyMeFormId();
  if (!formId) return;

  if (notifyMeIsOpen) {
    event.preventDefault();
    return;
  }

  event.preventDefault();

  if (!notifyMeSavedUrl) {
    notifyMeSavedUrl = location.pathname + location.search + location.hash;
  }

  const routesRoot = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
  notifyMeTargetUrl = routesRoot + 'products/' + handle + '?variant=' + variantId;
  history.replaceState(history.state, '', notifyMeTargetUrl);

  // Restore if the open event never arrives (e.g. form fails to load)
  if (notifyMeFallbackTimeout) clearTimeout(notifyMeFallbackTimeout);
  notifyMeFallbackTimeout = setTimeout(restoreNotifyMeUrl, notifyMeFallbackDelay);

  window.klaviyo.openForm(formId);
});
    const selectors$6 = {
      buttonArrow: '[data-button-arrow]',
      deferredMediaButton: '[data-deferred-media-button]',
      focusedElement: 'model-viewer, video, iframe, button, [href], input, [tabindex]',
      productMedia: '[data-image-id]',
      productMediaList: '[data-product-media-list]',
      section: '[data-section-id]',
    };

    const classes$5 = {
      arrows: 'slider__arrows',
      dragging: 'is-dragging',
      hidden: 'hidden',
      isFocused: 'is-focused',
      mediaActive: 'media--active',
      mediaHidden: 'media--hidden',
      mediaHiding: 'media--hiding',
      productQuickAddImages: 'product-quick-add__images',
    };

    const attributes$5 = {
      activeMedia: 'data-active-media',
      buttonPrev: 'data-button-prev',
      buttonNext: 'data-button-next',
      imageId: 'data-image-id',
      mediaId: 'data-media-id',
      type: 'data-type',
      faderDesktop: 'data-fader-desktop',
      faderMobile: 'data-fader-mobile',
    };

    if (!customElements.get('product-images')) {
      customElements.define(
        'product-images',
        class ProductImages extends HTMLElement {
          constructor() {
            super();

            this.initialized = false;
            this.buttons = false;
            this.isDown = false;
            this.startX = 0;
            this.startY = 0;
            this.scrollLeft = 0;
            this.onButtonArrowClick = (e) => this.buttonArrowClickEvent(e);
            this.container = this.closest(selectors$6.section);
            this.handleMouseDown = this.handleMouseDown.bind(this);
            this.handleMouseLeave = this.handleMouseLeave.bind(this);
            this.handleMouseUp = this.handleMouseUp.bind(this);
            this.handleMouseMove = this.handleMouseMove.bind(this);
            this.handleKeyUp = this.handleKeyUp.bind(this);
            this.productMediaItems = this.querySelectorAll(selectors$6.productMedia);
            this.productMediaList = this.querySelector(selectors$6.productMediaList);
            this.setHeight = this.setHeight.bind(this);
            this.toggleEvents = this.toggleEvents.bind(this);
            this.selectMediaEvent = (e) => this.showMediaOnVariantSelect(e);
          }

          connectedCallback() {
            if (Object.keys(this.productMediaItems).length < 2) return;

            // Initialise the filmstrip track offset to the currently active
            // slide (so the initial render is correct without a click).
            // Suppress the slide-transition during this first paint so the
            // strip starts AT the variant/featured media instead of animating
            // to it from index 0 (which on products where the variant
            // featured_media sits far down the list looks like the page is
            // scrolling on load).
            const initialActive = this.querySelector(`${selectors$6.productMedia}.${classes$5.mediaActive}`);
            if (initialActive) {
              const idx = Array.from(this.productMediaItems).indexOf(initialActive);
              if (idx !== -1) {
                if (idx > 0) this.classList.add('is-initializing');
                this.style.setProperty('--active-index', idx);
                if (idx > 0) {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => this.classList.remove('is-initializing'));
                  });
                }
              }
            }

            this.productMediaObserver();
            this.toggleEvents();
            this.listen();
            this.setHeight();
          }

          disconnectedCallback() {
            this.unlisten();
          }

          listen() {
            document.addEventListener('theme:resize:width', this.toggleEvents);
            document.addEventListener('theme:resize:width', this.setHeight);
            this.addEventListener('theme:media:select', this.selectMediaEvent);

            // Mobile prev/next arrows for thumbnails-right layout (desktop
            // uses native free-scroll inside the wrapper, no wheel hijack).
            if (this.closest('.product__wrapper--thumbnails-right')) {
              const mobileSlides = this.querySelector('[data-product-media-list]');
              const mobilePrev = this.querySelector('[data-mobile-prev]');
              const mobileNext = this.querySelector('[data-mobile-next]');

              const updateMobileArrows = () => {
                if (!mobileSlides || !mobilePrev || !mobileNext) return;
                const atStart = mobileSlides.scrollLeft <= 1;
                const atEnd = mobileSlides.scrollLeft >= mobileSlides.scrollWidth - mobileSlides.clientWidth - 1;
                mobilePrev.classList.toggle('product__mobile-arrow--disabled', atStart);
                mobileNext.classList.toggle('product__mobile-arrow--disabled', atEnd);
              };

              if (mobilePrev) {
                mobilePrev.addEventListener('click', () => {
                  if (mobileSlides) mobileSlides.scrollBy({left: -mobileSlides.clientWidth, behavior: 'smooth'});
                  setTimeout(updateMobileArrows, 400);
                });
              }
              if (mobileNext) {
                mobileNext.addEventListener('click', () => {
                  if (mobileSlides) mobileSlides.scrollBy({left: mobileSlides.clientWidth, behavior: 'smooth'});
                  setTimeout(updateMobileArrows, 400);
                });
              }
              if (mobileSlides) {
                mobileSlides.addEventListener('scroll', updateMobileArrows, {passive: true});
                updateMobileArrows(); // set initial state
              }

              // Desktop free-scroll inside `.product__slides-wrapper`: detect
              // which slide is most visible and sync the active state (so
              // the matching thumbnail highlights as the user scrolls). Covers
              // both the plain thumbnails-right layout and the print-preview
              // gallery (whose wrapper is the fixed-height vertical scroller).
              const desktopWrapper = this.querySelector(':scope > .product__slides-wrapper');
              if (desktopWrapper && this.classList.contains('product__images--thumbs')) {
                let scrollRaf = null;
                const onScroll = () => {
                  // Suppress while a click-triggered smooth scroll is in
                  // progress — otherwise this handler would yank the active
                  // state back to the slide currently passing under the
                  // threshold and re-toggle `data-active-media`, fighting
                  // the click intent.
                  if (this._programmaticScroll) return;
                  if (scrollRaf) return;
                  scrollRaf = requestAnimationFrame(() => {
                    scrollRaf = null;
                    const slides = this.querySelectorAll(selectors$6.productMedia);
                    if (!slides.length) return;
                    const threshold = desktopWrapper.scrollTop + desktopWrapper.clientHeight / 3;
                    let candidate = slides[0];
                    for (const slide of slides) {
                      if (slide.offsetTop - desktopWrapper.offsetTop <= threshold) {
                        candidate = slide;
                      } else {
                        break;
                      }
                    }
                    const id = candidate?.getAttribute(attributes$5.mediaId);
                    if (id && this.getAttribute(attributes$5.activeMedia) !== id) {
                      this._fromScrollListener = true;
                      this.setActiveMedia(id);
                      this._fromScrollListener = false;
                    }
                  });
                };
                desktopWrapper.addEventListener('scroll', onScroll, {passive: true});
              }
            }
          }
          unlisten() {
            document.removeEventListener('theme:resize:width', this.toggleEvents);
            document.removeEventListener('theme:resize:width', this.setHeight);
            this.removeEventListener('theme:media:select', this.selectMediaEvent);
          }

          toggleEvents() {
            const isMobileView = window.innerWidth < 1024;

            if ((isMobileView && this.hasAttribute(attributes$5.faderMobile)) || (!isMobileView && this.hasAttribute(attributes$5.faderDesktop))) {
              this.bindEventListeners();
            } else {
              this.unbindEventListeners();
            }
          }

          bindEventListeners() {
            if (this.initialized) return;

            this.productMediaList.addEventListener('mousedown', this.handleMouseDown);
            this.productMediaList.addEventListener('mouseleave', this.handleMouseLeave);
            this.productMediaList.addEventListener('mouseup', this.handleMouseUp);
            this.productMediaList.addEventListener('mousemove', this.handleMouseMove);
            this.productMediaList.addEventListener('touchstart', this.handleMouseDown, {passive: true});
            this.productMediaList.addEventListener('touchend', this.handleMouseUp, {passive: true});
            this.productMediaList.addEventListener('touchmove', this.handleMouseMove, {passive: true});
            this.productMediaList.addEventListener('keyup', this.handleKeyUp);
            this.initArrows();
            this.resetScrollPosition();

            this.initialized = true;
          }

          unbindEventListeners() {
            if (!this.initialized) return;

            this.productMediaList.removeEventListener('mousedown', this.handleMouseDown);
            this.productMediaList.removeEventListener('mouseleave', this.handleMouseLeave);
            this.productMediaList.removeEventListener('mouseup', this.handleMouseUp);
            this.productMediaList.removeEventListener('mousemove', this.handleMouseMove);
            this.productMediaList.removeEventListener('touchstart', this.handleMouseDown);
            this.productMediaList.removeEventListener('touchend', this.handleMouseUp);
            this.productMediaList.removeEventListener('touchmove', this.handleMouseMove);
            this.productMediaList.removeEventListener('keyup', this.handleKeyUp);
            this.removeArrows();

            this.initialized = false;
          }

          handleMouseDown(e) {
            this.isDown = true;
            this.startX = (e.pageX || e.changedTouches[0].screenX) - this.offsetLeft;
            this.startY = (e.pageY || e.changedTouches[0].screenY) - this.offsetTop;
          }

          handleMouseLeave() {
            if (!this.isDown) return;
            this.isDown = false;
          }

          handleMouseUp(e) {
            const x = (e.pageX || e.changedTouches[0].screenX) - this.offsetLeft;
            const y = (e.pageY || e.changedTouches[0].screenY) - this.offsetTop;
            const distanceX = x - this.startX;
            const distanceY = y - this.startY;
            const direction = distanceX > 0 ? 1 : -1;
            const isImage = this.getCurrentMedia().hasAttribute(attributes$5.type) && this.getCurrentMedia().getAttribute(attributes$5.type) === 'image';

            if (Math.abs(distanceX) > 10 && Math.abs(distanceX) > Math.abs(distanceY) && isImage) {
              direction < 0 ? this.showNextImage() : this.showPreviousImage();
            }

            this.isDown = false;

            requestAnimationFrame(() => {
              this.classList.remove(classes$5.dragging);
            });
          }

          handleMouseMove() {
            if (!this.isDown) return;

            this.classList.add(classes$5.dragging);
          }

          handleKeyUp(e) {
            if (e.code === 'ArrowLeft') {
              this.showPreviousImage();
            }

            if (e.code === 'ArrowRight') {
              this.showNextImage();
            }
          }

          handleArrowsClickEvent() {
            this.querySelectorAll(selectors$6.buttonArrow)?.forEach((button) => {
              button.addEventListener('click', (e) => {
                e.preventDefault();

                if (e.target.hasAttribute(attributes$5.buttonPrev)) {
                  this.showPreviousImage();
                }

                if (e.target.hasAttribute(attributes$5.buttonNext)) {
                  this.showNextImage();
                }
              });
            });
          }

          // When changing from Mobile do Desktop view
          resetScrollPosition() {
            if (this.productMediaList.scrollLeft !== 0) {
              this.productMediaList.scrollLeft = 0;
            }
          }

          initArrows() {
            // Create arrow buttons if don't exist
            if (!this.buttons.length) {
              const buttonsWrap = document.createElement('div');
              buttonsWrap.classList.add(classes$5.arrows);
              buttonsWrap.innerHTML = theme.sliderArrows.prev + theme.sliderArrows.next;

              // Append buttons outside the slider element
              this.productMediaList.append(buttonsWrap);
              this.buttons = this.querySelectorAll(selectors$6.buttonArrow);
              this.buttonPrev = this.querySelector(`[${attributes$5.buttonPrev}]`);
              this.buttonNext = this.querySelector(`[${attributes$5.buttonNext}]`);
            }

            this.handleArrowsClickEvent();
            this.preloadImageOnArrowHover();
          }

          removeArrows() {
            this.querySelector(`.${classes$5.arrows}`)?.remove();
          }

          preloadImageOnArrowHover() {
            this.buttonPrev?.addEventListener('mouseover', () => {
              const id = this.getPreviousMediaId();
              this.preloadImage(id);
            });

            this.buttonNext?.addEventListener('mouseover', () => {
              const id = this.getNextMediaId();
              this.preloadImage(id);
            });
          }

          preloadImage(id) {
            this.querySelector(`[${attributes$5.mediaId}="${id}"] img`)?.setAttribute('loading', 'eager');
          }

          showMediaOnVariantSelect(e) {
            const id = e.detail.id;
            this.setActiveMedia(id);
          }

          getCurrentMedia() {
            return this.querySelector(`${selectors$6.productMedia}.${classes$5.mediaActive}`);
          }

          getNextMediaId() {
            const currentMedia = this.getCurrentMedia();
            const nextMedia = currentMedia?.nextElementSibling.hasAttribute(attributes$5.imageId) ? currentMedia?.nextElementSibling : this.querySelector(selectors$6.productMedia);
            return nextMedia?.getAttribute(attributes$5.mediaId);
          }

          getPreviousMediaId() {
            const currentMedia = this.getCurrentMedia();
            const lastIndex = this.productMediaItems.length - 1;
            const previousMedia = currentMedia?.previousElementSibling || this.productMediaItems[lastIndex];

            return previousMedia?.getAttribute(attributes$5.mediaId);
          }

          showNextImage() {
            const id = this.getNextMediaId();
            this.selectMedia(id);
          }

          showPreviousImage() {
            const id = this.getPreviousMediaId();
            this.selectMedia(id);
          }

          selectMedia(id) {
            this.dispatchEvent(
              new CustomEvent('theme:media:select', {
                detail: {
                  id: id,
                },
              })
            );
          }

          setActiveMedia(id) {
            if (!id) return;

            this.setAttribute(attributes$5.activeMedia, id);

            const activeImage = this.querySelector(`${selectors$6.productMedia}.${classes$5.mediaActive}`);
            const selectedImage = this.querySelector(`[${attributes$5.mediaId}="${id}"]`);
            const selectedImageFocus = selectedImage?.querySelector(selectors$6.focusedElement);
            const deferredMedia = selectedImage.querySelector('deferred-media');

            // Drive the desktop "filmstrip" track translation. The
            // thumbnails-right layout stacks every slide vertically inside one
            // `.product__slides` track and translates the whole track by
            // `calc(var(--active-index) * -100%)`. This guarantees every image
            // moves together as a single strip rather than each slide animating
            // independently. Direction is implicit in the index change.
            if (selectedImage) {
              const items = Array.from(this.productMediaItems);
              const selectedIndex = items.indexOf(selectedImage);
              if (selectedIndex !== -1) {
                this.style.setProperty('--active-index', selectedIndex);
              }
            }

            activeImage?.classList.add(classes$5.mediaHiding);
            activeImage?.classList.remove(classes$5.mediaActive);

            selectedImage?.classList.remove(classes$5.mediaHiding, classes$5.mediaHidden);
            selectedImage?.classList.add(classes$5.mediaActive);

            // Print-preview gallery: scroll the selected slide into view
            // inside the fixed-height scrollable wrapper. Skip when the change
            // came from the scroll listener (the user is already scrolling),
            // and suppress that listener while the click-driven smooth scroll
            // runs so it doesn't yank the active state back to whatever slide
            // is passing the visibility threshold mid-scroll.
            if (
              !this._fromScrollListener &&
              selectedImage &&
              this.classList.contains('product__images--print-preview')
            ) {
              const wrapper = this.querySelector('.product__slides-wrapper');
              if (wrapper && window.matchMedia('(min-width: 990px)').matches) {
                const top = selectedImage.offsetTop - wrapper.offsetTop;
                this._programmaticScroll = true;
                if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
                this._programmaticScrollTimer = setTimeout(() => {
                  this._programmaticScroll = false;
                }, 800);
                wrapper.scrollTo({top, behavior: 'smooth'});
              }
            }

            // Print-preview gallery on mobile (<990px): the slides are a
            // horizontal scroll-snap strip rather than the vertical wrapper
            // used on desktop, so bring the selected slide into view
            // horizontally (e.g. jump back to the composed preview when an
            // add-on is picked while a lifestyle shot is showing).
            if (
              !this._fromScrollListener &&
              selectedImage &&
              this.classList.contains('product__images--print-preview') &&
              !window.matchMedia('(min-width: 990px)').matches
            ) {
              const list = this.querySelector(selectors$6.productMediaList);
              if (list) {
                const slideRect = selectedImage.getBoundingClientRect();
                const listRect = list.getBoundingClientRect();
                list.scrollTo({
                  left: list.scrollLeft + (slideRect.left - listRect.left),
                  behavior: 'smooth',
                });
              }
            }

            // Desktop thumbnails-right layout: slides are stacked vertically
            // inside `.product__slides-wrapper` which scrolls natively. When
            // a thumbnail is clicked, scroll the selected slide into view.
            // Skip when the change was triggered by the scroll listener itself
            // (otherwise we'd fight the user's scroll).
            if (
              !this._fromScrollListener &&
              selectedImage &&
              this.closest('.product__wrapper--thumbnails-right') &&
              this.classList.contains('product__images--thumbs') &&
              !this.classList.contains('product__images--print-preview')
            ) {
              const wrapper = this.querySelector('.product__slides-wrapper');
              if (wrapper && window.matchMedia('(min-width: 1024px)').matches) {
                const top = selectedImage.offsetTop - wrapper.offsetTop;
                // Suppress the scroll listener while the smooth scroll runs
                // so it doesn't re-set `data-active-media` to whatever slide
                // is currently passing the visibility threshold.
                this._programmaticScroll = true;
                if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
                this._programmaticScrollTimer = setTimeout(() => {
                  this._programmaticScroll = false;
                }, 800);
                wrapper.scrollTo({top, behavior: 'smooth'});
              }
            }

            // Force media loading if slide becomes visible
            if (deferredMedia && deferredMedia.getAttribute('loaded') !== true) {
              selectedImage.querySelector(selectors$6.deferredMediaButton)?.dispatchEvent(new Event('click', {bubbles: false}));
            }

            requestAnimationFrame(() => {
              this.setHeight();

              // Move focus to the selected media
              if (document.body.classList.contains(classes$5.isFocused)) {
                selectedImageFocus?.focus();
              }
            });
          }

          // Set current product image height variable to product images container
          setHeight() {
            if (this.classList.contains(classes$5.productQuickAddImages)) return;

            const mediaHeight = this.querySelector(`${selectors$6.productMedia}.${classes$5.mediaActive}`)?.offsetHeight || this.productMediaItems[0]?.offsetHeight;
            this.style.setProperty('--height', `${mediaHeight}px`);
          }

          productMediaObserver() {
            this.productMediaItems.forEach((media) => {
              media.addEventListener('transitionend', (e) => {
                if (e.target == media && media.classList.contains(classes$5.mediaHiding)) {
                  media.classList.remove(classes$5.mediaHiding);
                  media.classList.add(classes$5.mediaHidden);
                }
              });
              media.addEventListener('transitioncancel', (e) => {
                if (e.target == media && media.classList.contains(classes$5.mediaHiding)) {
                  media.classList.remove(classes$5.mediaHiding);
                  media.classList.add(classes$5.mediaHidden);
                }
              });
            });
          }
        }
      );
    }

    const selectors$5 = {
      swapHandle: '[data-swap-handle]',
      nativeScrollbar: 'native-scrollbar',
      activeSibling: '.sibling__link--current',
    };

    const attributes$4 = {
      swapHandle: 'data-swap-handle',
      swapUrl: 'data-swap-url',
    };

    if (!customElements.get('product-item')) {
      customElements.define(
        'product-item',
        class ProductItem extends HTMLElement {
          abortController = undefined;
          pendingSwapHandle = null;
          postProcessHtmlCallbacks = [];

          handleClick = (event) => this.handleChange(event);
          handleKeyup = (event) => {
            if (event.code === theme.keyboardKeys.ENTER || event.code === theme.keyboardKeys.NUMPADENTER) {
              this.handleChange(event);
            }
          };
          onHtmlChange = (event) => this.handleHtmlChange(event);

          constructor() {
            super();
            this.swapHandles = this.querySelectorAll(selectors$5.swapHandle);
            this.activeSibling = [...this.swapHandles].find((el) => el.querySelector(selectors$5.activeSibling));
          }

          connectedCallback() {
            this.scrollIntoView();

            this.swapHandles?.forEach((element) => {
              element.addEventListener('click', this.handleClick);
              element.addEventListener('keyup', this.handleKeyup);
            });

            this.initProductSwapUtility();
          }

          disconnectedCallback() {
            // Abort any pending fetch requests
            this.abortController?.abort();

            // Remove event listeners
            this.swapHandles?.forEach((element) => {
              element.removeEventListener('click', this.handleClick);
              element.removeEventListener('keyup', this.handleKeyup);
            });
          }

          initProductSwapUtility() {
            this.postProcessHtmlCallbacks.push(() => {
              document.addEventListener('theme:html:change', this.onHtmlChange);
            });
          }

          handleChange(event) {
            if (!this.contains(event.target)) return;

            const element = event.target.closest(selectors$5.swapHandle);
            const targetUrl = element.dataset.swapUrl;
            const productUrl = `${window.Shopify.routes.root}products/${element.dataset.swapHandle}`;
            // Store the clicked element's handle to refocus it after swap
            this.pendingSwapHandle = element.dataset.swapHandle;

            const shouldSwapProduct = this.dataset.url !== targetUrl;
            if (!shouldSwapProduct) return;

            this.renderProductItem({
              // Fetch the new product's HTML with section rendering API
              requestUrl: `${productUrl}?section_id=api-product-grid-item`,
              // Returns a function that will process and swap the HTML after fetch completes
              callback: this.handleSwapProduct(),
            });
          }

          handleSwapProduct() {
            return (html) => {
              // Set up animation parameters with the new product-item element's ID
              const aosDelay = 0;
              const productItem = html.querySelector('product-item');
              const aosAnchor = productItem.id ? `#${productItem.id}` : '';

              // Get the raw HTML and replace animation placeholder strings
              let productHTML = productItem.outerHTML;
              productHTML = productHTML.includes('||itemAnimationDelay||') ? productHTML.replaceAll('||itemAnimationDelay||', aosDelay) : productHTML;
              productHTML = productHTML.includes('||itemAnimationAnchor||') ? productHTML.replaceAll('||itemAnimationAnchor||', aosAnchor) : productHTML;

              // Parse the processed HTML back into a DOM element
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = productHTML;
              const processedProductItem = tempDiv.querySelector('product-item');

              window.theme.htmlUpdate.viewTransition(
                this, // Current product-item element to be replaced
                processedProductItem, // New product-item element with updated content
                this.postProcessHtmlCallbacks // Run any post-processing after swap (focus, init components)
              );
            };
          }

          renderProductItem({requestUrl, callback}) {
            this.abortController?.abort();
            this.abortController = new AbortController();

            fetch(requestUrl, {signal: this.abortController.signal})
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                callback(html);
              })
              .catch((error) => {
                if (error.name === 'AbortError') {
                  console.log('Fetch aborted by user');
                } else {
                  console.error(error);
                }

                document.removeEventListener('theme:html:change', this.onHtmlChange);
              });
          }

          handleHtmlChange(event) {
            if (!event?.detail?.element) return;
            if (!this.pendingSwapHandle) return;

            const product = event.detail.element;
            const swatch = product.querySelector(`[${attributes$4.swapHandle}="${this.pendingSwapHandle}"]`);

            // Set focus and scroll into view of the last clicked sibling swatch element
            swatch.focus();
            this.scrollIntoView({product, swatch});

            this.pendingSwapHandle = null;
            document.removeEventListener('theme:html:change', this.onHtmlChange);
          }

          scrollIntoView(elements = false) {
            if (!this.activeSibling) return;

            const swatch = elements ? elements.swatch : this.activeSibling;
            const product = elements ? elements.product : this;

            const nativeScrollbar = product.querySelector(selectors$5.nativeScrollbar);
            if (!nativeScrollbar || typeof nativeScrollbar.move !== 'function') return;

            const computedStyle = getComputedStyle(swatch);
            const swatchOffset = swatch.offsetLeft + parseFloat(computedStyle.marginLeft) + parseFloat(computedStyle.marginRight);
            requestAnimationFrame(() => nativeScrollbar.move(swatchOffset - swatch.clientWidth, 'instant'));
          }
        }
      );
    }

    /**
     * Adds a Shopify size attribute to a URL
     *
     * @param src
     * @param size
     * @returns {*}
     */
    function getSizedImageUrl(src, size) {
      if (size === null) {
        return src;
      }

      if (size === 'master') {
        return removeProtocol(src);
      }

      const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);

      if (match) {
        const prefix = src.split(match[0]);
        const suffix = match[0];

        return removeProtocol(`${prefix[0]}_${size}${suffix}`);
      } else {
        return null;
      }
    }

    function removeProtocol(path) {
      return path.replace(/http(s)?:/, '');
    }

    const selectors$4 = {
      productCutline: '[data-product-cutline]',
      productLink: '[data-product-link]',
      productGridItem: '[data-grid-item]',
      productInfo: '[data-product-information]',
      productImage: '[data-product-image-default]',
      productImageSibling: '[data-product-image-sibling]',
      productPrice: '[data-product-price]',
      siblingsInnerHolder: '[data-sibling-inner]',
      siblingCount: '[data-sibling-count]',
      siblingFieldset: '[data-sibling-fieldset]',
      siblingLink: '[data-sibling-link]',
      siblingLinkCurrent: '.sibling__link--current',
    };

    const classes$4 = {
      visible: 'is-visible',
      fade: 'is-fade',
      stopEvents: 'no-events',
      active: 'is-active',
      isFocused: 'is-focused',
    };

    const attributes$3 = {
      siblingAddedImage: 'data-sibling-added-image',
      siblingCutline: 'data-sibling-cutline',
      siblingImage: 'data-sibling-image',
      siblingPrice: 'data-sibling-price',
      siblingCompareAtPrice: 'data-sibling-compare-at-price',
      productLink: 'data-product-link',
      siblingLink: 'data-sibling-link',
      mobileColumnsSmall: 'data-mobile-columns-small',
      anySiblingCutline: 'data-any-sibling-cutline',
    };

    class SiblingSwatches {
      constructor(swatches, product) {
        this.swatches = swatches;
        this.product = product;
        this.productLinks = this.product.querySelectorAll(selectors$4.productLink);
        this.productCutline = this.product.querySelector(selectors$4.productCutline);
        this.productPrice = this.product.querySelector(selectors$4.productPrice);
        this.productImage = this.product.querySelector(selectors$4.productImage);
        this.productImageSibling = this.product.querySelector(selectors$4.productImageSibling);
        this.siblingsInnerHolder = this.product.querySelector(selectors$4.siblingsInnerHolder);

        this.init();
      }

      init() {
        this.cacheDefaultValues();

        this.siblingsInnerHolder.addEventListener('mouseleave', () => this.resetProductValues());
        this.siblingsInnerHolder.addEventListener('focusout', () => {
          if (document.body.classList.contains(classes$4.isFocused)) this.resetProductValues();
        });

        this.swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', (event) => this.showSibling(event));
          swatch.addEventListener('focusin', (event) => {
            if (document.body.classList.contains(classes$4.isFocused)) this.showSibling(event);
          });
        });

        if (this.productLinks.length) {
          this.swatches.forEach((swatch) => {
            swatch.addEventListener('click', () => {
              this.productLinks[0].click();
            });
          });
        }
      }

      cacheDefaultValues() {
        this.activeSibling = this.siblingsInnerHolder.querySelector(selectors$4.siblingLinkCurrent)?.closest(selectors$4.siblingLink);
        if (this.activeSibling) {
          this.productImageSibling.setAttribute(attributes$3.siblingImage, this.activeSibling.dataset.siblingImage);
        }

        this.productLinkValue = this.productLinks[0].hasAttribute(attributes$3.productLink) ? this.productLinks[0].getAttribute(attributes$3.productLink) : '';
        this.productPriceValue = this.productPrice.innerHTML;

        if (this.productCutline) {
          this.productCutlineValue = this.productCutline.innerHTML;
          this.anySiblingCutline = this.productCutline.getAttribute(attributes$3.anySiblingCutline) === 'true';
        }
      }

      resetProductValues() {
        this.product.classList.remove(classes$4.active);

        if (this.productLinkValue) {
          this.productLinks.forEach((productLink) => {
            productLink.href = this.productLinkValue;
          });
        }

        if (this.productPrice) {
          this.productPrice.innerHTML = this.productPriceValue;
        }

        if (this.productCutline && this.productCutline) {
          this.productCutline.innerHTML = this.productCutlineValue;
          this.productCutline.title = this.productCutlineValue;
        }

        this.hideSiblingImage();
      }

      showSibling(event) {
        const swatch = event.target;
        const siblingLink = swatch.hasAttribute(attributes$3.siblingLink) ? swatch.getAttribute(attributes$3.siblingLink) : '';
        const siblingPrice = swatch.hasAttribute(attributes$3.siblingPrice) ? swatch.getAttribute(attributes$3.siblingPrice) : '';
        const siblingCompareAtPrice = swatch.hasAttribute(attributes$3.siblingCompareAtPrice) ? swatch.getAttribute(attributes$3.siblingCompareAtPrice) : '';
        const siblingCutline = swatch.hasAttribute(attributes$3.siblingCutline) ? swatch.getAttribute(attributes$3.siblingCutline) : '';
        const siblingImage = swatch.hasAttribute(attributes$3.siblingImage) ? swatch.getAttribute(attributes$3.siblingImage) : '';

        if (siblingLink) {
          this.productLinks.forEach((productLink) => {
            productLink.href = siblingLink;
          });
        }

        if (siblingCompareAtPrice) {
          this.productPrice.innerHTML = `<span class="price sale"><span class="new-price">${siblingPrice}</span> <span class="old-price">${siblingCompareAtPrice}</span></span>`;
        } else {
          this.productPrice.innerHTML = `<span class="price">${siblingPrice}</span>`;
        }

        if (this.productCutline) {
          if (siblingCutline) {
            this.productCutline.innerHTML = siblingCutline;
            this.productCutline.title = siblingCutline;
          } else {
            // Use &nbsp; to reserve space when any sibling has a cutline, preventing CLS
            const anySiblingCutline = this.productCutline.getAttribute(attributes$3.anySiblingCutline) === 'true';
            this.productCutline.innerHTML = anySiblingCutline ? '&nbsp;' : '';
            this.productCutline.title = '';
          }
        }

        if (siblingImage) {
          this.showSiblingImage(siblingImage);
        }
      }

      showSiblingImage(siblingImage) {
        if (!this.productImageSibling) return;

        // Add current sibling swatch image to PGI image
        const ratio = window.devicePixelRatio || 1;
        const pixels = this.productImage.offsetWidth * ratio;
        const widthRounded = Math.ceil(pixels / 180) * 180;
        const imageSrc = getSizedImageUrl(siblingImage, `${widthRounded}x`);
        const imageExists = this.productImageSibling.querySelector(`[src="${imageSrc}"]`);
        const showCurrentImage = () => {
          this.productImageSibling.classList.add(classes$4.visible);
          this.productImageSibling.querySelector(`[src="${imageSrc}"]`).classList.add(classes$4.fade);
        };
        const swapImages = () => {
          const activeSiblingImage = this.productImageSibling.getAttribute(attributes$3.siblingImage);
          const swapNewImageOnHover = siblingImage !== activeSiblingImage;
          const swapActiveSiblingImage = siblingImage === activeSiblingImage && this.productImageSibling.classList.contains(classes$4.visible);

          // Avoid changing the image when hovering over the currently active sibling link, if the featured image remains unchanged.
          const shouldSwap = Boolean(swapActiveSiblingImage || swapNewImageOnHover);

          if (!shouldSwap) return;

          this.productImageSibling.querySelectorAll('img').forEach((image) => {
            image.classList.remove(classes$4.fade);
          });
          requestAnimationFrame(showCurrentImage);
        };

        if (imageExists) {
          swapImages();
        } else {
          const imageTag = document.createElement('img');

          imageTag.src = imageSrc;

          if (this.productCutline) {
            imageTag.alt = this.productCutline.innerText;
          }

          imageTag.addEventListener('load', () => {
            this.productImageSibling.append(imageTag);

            swapImages();
          });
        }
      }

      hideSiblingImage() {
        if (!this.productImageSibling) return;

        this.productImageSibling.classList.remove(classes$4.visible);
        this.productImageSibling.querySelectorAll('img').forEach((image) => {
          image.classList.remove(classes$4.fade);
        });
      }
    }

    if (!customElements.get('product-item-siblings')) {
      customElements.define(
        'product-item-siblings',
        class ProductSiblings extends HTMLElement {
          constructor() {
            super();

            this.resizeEvent = () => this.handleResize();
          }

          connectedCallback() {
            this.product = this.closest(selectors$4.productGridItem);
            this.siblingCount = this.querySelector(selectors$4.siblingCount);
            this.siblingFieldset = this.querySelector(selectors$4.siblingFieldset);
            this.siblingLinks = this.querySelectorAll(selectors$4.siblingLink);
            this.productInfo = this.closest(selectors$4.productInfo);
            this.productLink = this.closest(selectors$4.link);
            this.hideSwatchesTimer = 0;
            this.swatchesStyle = theme.settings.collectionSwatchStyle;

            if (this.siblingFieldset && this.productInfo) {
              if (this.swatchesStyle == 'grid' || this.swatchesStyle == 'slider' || this.swatchesStyle == 'limited') {
                this.siblingFieldset.classList.add(classes$4.visible);
              }

              if (this.siblingCount) {
                this.siblingCount.addEventListener('mouseenter', () => this.showSiblings());

                // Prevent color swatches blinking on mouse move
                this.productInfo.addEventListener('mouseleave', () => this.hideSiblings());
              }
            }

            if (this.siblingLinks.length) {
              new SiblingSwatches(this.siblingLinks, this.product);
            }

            document.addEventListener('theme:resize:width', this.resizeEvent);
          }

          disconnectedCallback() {
            document.removeEventListener('theme:resize:width', this.resizeEvent);
          }

          handleResize() {
            if (this.siblingFieldset && this.productInfo) {
              if (this.swatchesStyle == 'grid' || this.swatchesStyle == 'slider' || this.swatchesStyle == 'limited') {
                this.siblingFieldset.classList.add(classes$4.visible);
                this.limitVisibleSwatches();
              }
            }
          }

          showSiblings() {
            if (this.hideSwatchesTimer) clearTimeout(this.hideSwatchesTimer);

            if (this.productLink) {
              this.productLink.classList.add(classes$4.stopEvents);
            }

            if (this.swatchesStyle == 'text') return;

            this.siblingFieldset.classList.add(classes$4.visible);
          }

          hideSiblings() {
            this.hideSwatchesTimer = setTimeout(() => {
              if (this.productLink) {
                this.productLink.classList.remove(classes$4.stopEvents);
              }

              this.siblingFieldset.classList.remove(classes$4.visible);
            }, 100);
          }

          limitVisibleSwatches() {
            const isMobile = theme.isMobile;
            const mobileColumnsElement = this.querySelector(`[${attributes$3.mobileColumnsSmall}]`);
            const isMobileSwatches = mobileColumnsElement?.getAttribute(attributes$3.mobileColumnsSmall) === 'true';

            // Early return if no swatches or mobile conditions not met
            if (!this.siblingLinks?.length || (isMobile && !isMobileSwatches)) {
              return;
            }

            // For desktop, only proceed if style is 'limited'
            if (!isMobile && this.swatchesStyle !== 'limited') {
              // Reset display for all swatches on desktop for other styles
              this.siblingLinks.forEach((swatch) => {
                swatch.style.display = '';
              });
              // Remove the limited count element if it exists
              const limitedCountElement = this.querySelector('.swatch-limited');
              if (limitedCountElement) {
                limitedCountElement.remove();
              }
              return;
            }

            const maxVisible = isMobile ? 3 : 5;
            const totalSwatches = this.siblingLinks.length;
            const visibleSwatches = Math.min(maxVisible, totalSwatches);
            const remainingSwatches = totalSwatches - visibleSwatches;

            // Update swatch visibility in a single pass
            this.siblingLinks.forEach((swatch, index) => {
              swatch.style.display = index < visibleSwatches ? '' : 'none';
            });

            // Handle remaining count display
            const limitedCountElement = this.querySelector('.swatch-limited');
            if (remainingSwatches > 0) {
              if (!limitedCountElement) {
                const newCountElement = document.createElement('div');
                newCountElement.className = 'swatch-limited';
                newCountElement.textContent = `+${remainingSwatches}`;
                mobileColumnsElement?.appendChild(newCountElement);
              } else {
                limitedCountElement.textContent = `+${remainingSwatches}`;
              }
            } else if (limitedCountElement) {
              limitedCountElement.remove();
            }
          }
        }
      );
    }

    /**
     * Module to show Recently Viewed Products
     *
     * Copyright (c) 2014 Caroline Schnapp (11heavens.com)
     * Dual licensed under the MIT and GPL licenses:
     * http://www.opensource.org/licenses/mit-license.php
     * http://www.gnu.org/licenses/gpl.html
     *
     */

    Shopify.Products = (function () {
      const config = {
        howManyToShow: 4,
        howManyToStoreInMemory: 10,
        wrapperId: 'recently-viewed-products',
        section: null,
        target: 'api-product-grid-item',
        onComplete: null,
      };

      let productHandleQueue = [];
      let wrapper = null;
      let howManyToShowItems = null;

      const today = new Date();
      const expiresDate = new Date();
      const daysToExpire = 90;
      expiresDate.setTime(today.getTime() + 3600000 * 24 * daysToExpire);

      const cookie = {
        configuration: {
          expires: expiresDate.toGMTString(),
          path: '/',
          domain: window.location.hostname,
          sameSite: 'none',
          secure: true,
        },
        name: 'shopify_recently_viewed',
        write: function (recentlyViewed) {
          const recentlyViewedString = encodeURIComponent(recentlyViewed.join(' '));
          document.cookie = `${this.name}=${recentlyViewedString}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}; sameSite=${this.configuration.sameSite}; secure=${this.configuration.secure}`;
        },
        read: function () {
          let recentlyViewed = [];
          let cookieValue = null;

          if (document.cookie.indexOf('; ') !== -1 && document.cookie.split('; ').find((row) => row.startsWith(this.name))) {
            cookieValue = document.cookie
              .split('; ')
              .find((row) => row.startsWith(this.name))
              .split('=')[1];
          }

          if (cookieValue !== null) {
            recentlyViewed = decodeURIComponent(cookieValue).split(' ');
          }

          return recentlyViewed;
        },
        destroy: function () {
          const cookieVal = null;
          document.cookie = `${this.name}=${cookieVal}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
        },
        remove: function (productHandle) {
          const recentlyViewed = this.read();
          const position = recentlyViewed.indexOf(productHandle);
          if (position !== -1) {
            recentlyViewed.splice(position, 1);
            this.write(recentlyViewed);
          }
        },
      };

      const finalize = (wrapper, section, onComplete) => {
        wrapper.classList.remove('hidden');
        const cookieItemsLength = cookie.read().length;

        if (Shopify.recentlyViewed && howManyToShowItems && cookieItemsLength && cookieItemsLength < howManyToShowItems && wrapper.children.length) {
          let allClassesArr = [];
          let addClassesArr = [];
          let objCounter = 0;
          for (const property in Shopify.recentlyViewed) {
            objCounter += 1;
            const objString = Shopify.recentlyViewed[property];
            const objArr = objString.split(' ');
            const propertyIdx = parseInt(property.split('_')[1]);
            allClassesArr = [...allClassesArr, ...objArr];

            if (cookie.read().length === propertyIdx || (objCounter === Object.keys(Shopify.recentlyViewed).length && !addClassesArr.length)) {
              addClassesArr = [...addClassesArr, ...objArr];
            }
          }

          for (let i = 0; i < wrapper.children.length; i++) {
            const element = wrapper.children[i];
            if (allClassesArr.length) {
              element.classList.remove(...allClassesArr);
            }

            if (addClassesArr.length) {
              element.classList.add(...addClassesArr);
            }
          }
        }

        // If we have a callback.
        if (onComplete) {
          try {
            onComplete(wrapper, section);
          } catch (error) {
            console.log(error);
          }
        }
      };

      const moveAlong = (shown, productHandleQueue, wrapper, section, target, howManyToShow, onComplete) => {
        if (productHandleQueue.length && shown < howManyToShow) {
          fetch(`${window.theme.routes.root}products/${productHandleQueue[0]}?section_id=${target}`)
            .then((response) => response.text())
            .then((product) => {
              const aosDelay = shown * 100;
              const aosAnchor = wrapper.id ? `#${wrapper.id}` : '';
              const fresh = document.createElement('div');
              let productReplaced = product;
              productReplaced = productReplaced.includes('||itemAnimationDelay||') ? productReplaced.replaceAll('||itemAnimationDelay||', aosDelay) : productReplaced;
              productReplaced = productReplaced.includes('||itemAnimationAnchor||') ? productReplaced.replaceAll('||itemAnimationAnchor||', aosAnchor) : productReplaced;
              fresh.innerHTML = productReplaced;

              wrapper.innerHTML += fresh.querySelector('[data-api-content]').innerHTML;

              productHandleQueue.shift();
              shown++;
              moveAlong(shown, productHandleQueue, wrapper, section, target, howManyToShow, onComplete);
            })
            .catch(() => {
              cookie.remove(productHandleQueue[0]);
              productHandleQueue.shift();
              moveAlong(shown, productHandleQueue, wrapper, section, target, howManyToShow, onComplete);
            });
        } else {
          finalize(wrapper, section, onComplete);
        }
      };

      return {
        showRecentlyViewed: function (params) {
          if (Shopify.visualPreviewMode) return;

          const paramsNew = params || {};
          const shown = 0;

          // Update defaults.
          Object.assign(config, paramsNew);

          const onComplete = config.onComplete;

          // Read cookie.
          productHandleQueue = cookie.read();

          // Element where to insert.
          wrapper = document.querySelector(`#${config.wrapperId}`);

          // How many products to show.
          howManyToShowItems = config.howManyToShow;
          config.howManyToShow = Math.min(productHandleQueue.length, config.howManyToShow);

          // If we have any to show.
          if (config.howManyToShow && wrapper) {
            // Getting each product with an Ajax call and rendering it on the page.
            moveAlong(shown, productHandleQueue, wrapper, config.section, config.target, howManyToShowItems, onComplete);
          }
        },

        getConfig: function () {
          return config;
        },

        clearList: function () {
          cookie.destroy();
        },

        recordRecentlyViewed: function (params) {
          const paramsNew = params || {};

          // Update defaults.
          Object.assign(config, paramsNew);

          // Read cookie.
          let recentlyViewed = cookie.read();

          // If we are on a product page.
          if (window.location.pathname.indexOf('/products/') !== -1) {
            // What is the product handle on this page.
            let productHandle = decodeURIComponent(window.location.pathname)
              .match(
                /\/products\/([a-z0-9\-]|[\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|[\u203B]|[\w\u0430-\u044f]|[\u0400-\u04FF]|[\u0900-\u097F]|[\u0590-\u05FF\u200f\u200e]|[\u0621-\u064A\u0660-\u0669 ])+/
              )[0]
              .split('/products/')[1];

            if (config.handle) {
              productHandle = config.handle;
            }

            // In what position is that product in memory.
            const position = recentlyViewed.indexOf(productHandle);

            // If not in memory.
            if (position === -1) {
              // Add product at the start of the list.
              recentlyViewed.unshift(productHandle);
              // Only keep what we need.
              recentlyViewed = recentlyViewed.splice(0, config.howManyToStoreInMemory);
            } else {
              // Remove the product and place it at start of list.
              recentlyViewed.splice(position, 1);
              recentlyViewed.unshift(productHandle);
            }

            // Update cookie.
            cookie.write(recentlyViewed);
          }
        },

        hasProducts: cookie.read().length > 0,
      };
    })();

    /**
     * <artists-section>
     * Wraps the Artists list section. Provides the Artist / Artwork view toggle.
     *
     * Markup contract:
     *   <artists-section data-view="artist" id="ArtistsList--{section.id}">
     *     ...
     *     <button data-artists-view="artist">Artist</button>
     *     <button data-artists-view="artwork">Artwork</button>
     *     ...
     *   </artists-section>
     */
    class ArtistsSection extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.defaultView = this.getAttribute('data-default') || 'artist';
        // The chosen view lives in the URL (?display=artist|artwork) and is the
        // single source of truth. Reading it on load means the view survives
        // pagination and back/forward navigation, and can be shared via the URL.
        this.setView(this._readUrlView() || this.defaultView, { updateUrl: false });

        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(e) {
        const btn = e.target.closest('[data-artists-view]');
        if (!btn || !this.contains(btn)) return;
        let view = btn.getAttribute('data-artists-view');
        // On mobile, the inactive button is hidden — clicking the visible
        // (active) button should cycle to the other view.
        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        if (isMobile && btn.classList.contains('is-active')) {
          view = view === 'artist' ? 'artwork' : 'artist';
        }
        this.setView(view);
      }

      setView(view, { updateUrl = true } = {}) {
        this.setAttribute('data-view', view);
        if (updateUrl) this._writeUrlView(view);
        // Keep the (server-rendered) pagination links carrying the current
        // view, so paging to another page doesn't drop the choice.
        this._syncPaginationLinks(view);
        this.querySelectorAll('[data-artists-view]').forEach((b) => {
          const isActive = b.getAttribute('data-artists-view') === view;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      _readUrlView() {
        try {
          // 'view' is reserved by Shopify (alternate-template selector), so the
          // chosen view is stored under 'display'.
          const v = new URLSearchParams(window.location.search).get('display');
          return v === 'artist' || v === 'artwork' ? v : null;
        } catch (_) {
          return null;
        }
      }

      _writeUrlView(view) {
        try {
          const url = new URL(window.location.href);
          // Keep URLs clean: the default view carries no param. replaceState
          // (not pushState) so the toggle stays a display preference rather
          // than a navigation step.
          this._applyViewParam(url, view);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (_) {}
      }

      _applyViewParam(url, view) {
        if (view === this.defaultView) {
          url.searchParams.delete('display');
        } else {
          url.searchParams.set('display', view);
        }
        return url;
      }

      _syncPaginationLinks(view) {
        this.querySelectorAll('.pagination-custom a[href], #AjaxinatePagination a[href]').forEach((a) => {
          try {
            const url = this._applyViewParam(new URL(a.getAttribute('href'), window.location.origin), view);
            a.setAttribute('href', url.pathname + url.search);
          } catch (_) {}
        });
      }
    }

    if (!customElements.get('artists-section')) {
      customElements.define('artists-section', ArtistsSection);
    }
    /**
     * <collections-section>
     * Wraps the Collections list section. No interactive behavior at this
     * time — element exists so future enhancements can hook in cleanly.
     */
    class CollectionsSection extends HTMLElement {}

    if (!customElements.get('collections-section')) {
      customElements.define('collections-section', CollectionsSection);
    }
    /**
     * <art-walls-section>
     * Wraps the Art Walls index section. No interactive behavior at this
     * time — element exists so future enhancements can hook in cleanly.
     */
    class ArtWallsSection extends HTMLElement {}

    if (!customElements.get('art-walls-section')) {
      customElements.define('art-walls-section', ArtWallsSection);
    }
    /**
     * <perfect-match-section>
     * Wraps the Perfect Match index section. No interactive behavior at
     * this time — element exists so future enhancements can hook in cleanly.
     */
    class PerfectMatchSection extends HTMLElement {}

    if (!customElements.get('perfect-match-section')) {
      customElements.define('perfect-match-section', PerfectMatchSection);
    }
    /**
     * Artist page (metaobject template) — section coordinator.
     *
     * The artist page is split into 6 independent sections so the admin
     * can reorder them in the theme editor:
     *
     *   - section-artist-breadcrumbs
     *   - section-artist-hero
     *   - section-artist-products
     *   - section-artist-about
     *   - section-artist-extras
     *   - section-artist-discover
     *
     * Because the sections are siblings (not wrapped in a single custom
     * element), this file binds behaviour by querying the document. It
     * runs once per page load and is idempotent.
     */
    (function artistPageInit() {
      const ROOT = document;
      let inited = false;

      function bindHearts() {
        ROOT.querySelectorAll('.apc__heart').forEach((btn) => {
          if (btn.dataset.apBound) return;
          btn.dataset.apBound = '1';
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const liked = btn.classList.toggle('is-liked');
            btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
          });
        });
      }

      function shuffleDiscover(grid) {
        if (!grid || grid.dataset.shuffled === 'true') return;
        const show = parseInt(grid.dataset.discoverShow, 10) || 8;
        const showMobile = parseInt(grid.dataset.discoverShowMobile, 10) || Math.min(6, show);
        const items = Array.from(grid.children);
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }
        const frag = document.createDocumentFragment();
        items.forEach((el, i) => {
          el.removeAttribute('data-discover-visible');
          el.removeAttribute('data-discover-visible-mobile-hide');
          if (i < show) {
            el.setAttribute('data-discover-visible', '');
            if (i >= showMobile) el.setAttribute('data-discover-visible-mobile-hide', '');
          }
          frag.appendChild(el);
        });
        grid.appendChild(frag);
        grid.setAttribute('data-shuffled', 'true');
      }

      function bindRow(row, prev, next, progress) {
        if (!row || (!prev && !next && !progress)) return;
        if (row.dataset.apRowBound) return;
        row.dataset.apRowBound = '1';

        const step = () => {
          const child = row.firstElementChild;
          if (!child) return row.clientWidth;
          const style = getComputedStyle(row);
          const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
          return child.getBoundingClientRect().width + gap;
        };

        const updateState = () => {
          const max = row.scrollWidth - row.clientWidth;
          if (prev) prev.toggleAttribute('disabled', row.scrollLeft <= 0);
          if (next) next.toggleAttribute('disabled', row.scrollLeft >= max - 1);
          if (progress) {
            const bar = progress.firstElementChild;
            if (bar && row.scrollWidth > 0) {
              const visibleRatio = Math.min(1, row.clientWidth / row.scrollWidth);
              const widthPct = visibleRatio * 100;
              const trackPct = 100 - widthPct;
              const xPct = max > 0 ? (row.scrollLeft / max) * trackPct : 0;
              bar.style.setProperty('--ap-progress-width', widthPct + '%');
              bar.style.setProperty('--ap-progress-x', xPct + '%');
            }
          }
        };

        if (prev) {
          prev.addEventListener('click', () => {
            row.scrollBy({ left: -step(), behavior: 'smooth' });
          });
        }
        if (next) {
          next.addEventListener('click', () => {
            row.scrollBy({ left: step(), behavior: 'smooth' });
          });
        }
        row.addEventListener('scroll', updateState, { passive: true });
        window.addEventListener('resize', updateState, { passive: true });
        updateState();
      }

      /**
       * About-section overflow handling (desktop ≥990px).
       *
       * Splits the bio into 2 CSS columns (widening the text column to 2fr)
       * whenever the bio is long enough to fill them — either because the
       * text is taller than .ap-about__images, or because the viewport is
       * wide enough that the fixed-ratio image would otherwise grow taller
       * than the text. The second about-image is promoted into the .ap-extras
       * row so the image column doesn't double up.
       */
      function bindAboutOverflow() {
        const about = ROOT.querySelector('.ap-about');
        if (!about || about.dataset.apAboutBound) return;
        about.dataset.apAboutBound = '1';

        const text = about.querySelector('.ap-about__text');
        const images = about.querySelector('.ap-about__images');
        const rte = about.querySelector('.ap-about__rte');
        const title = about.querySelector('.ap-about__title');
        if (!text || !images) return;

        const secondImage = images.children[1];
        const extrasRow = ROOT.querySelector('[data-ap-extras-row]');
        if (secondImage && extrasRow) {
          const promoted = secondImage.cloneNode(true);
          promoted.setAttribute('data-ap-about-promoted', '');
          promoted.classList.remove('ap-about__image-wrapper');
          promoted.classList.add('ap-extras__image-wrapper');
          const promotedImg = promoted.querySelector('img');
          if (promotedImg) {
            promotedImg.classList.remove('ap-about__image');
            promotedImg.classList.add('ap-extras__image');
          }
          extrasRow.insertBefore(promoted, extrasRow.firstChild);
          const resetScroll = () => { extrasRow.scrollLeft = 0; };
          resetScroll();
          requestAnimationFrame(() => {
            resetScroll();
            requestAnimationFrame(resetScroll);
          });
          const promotedImg2 = promoted.querySelector('img');
          if (promotedImg2 && !promotedImg2.complete) {
            promotedImg2.addEventListener(
              'load',
              () => { requestAnimationFrame(resetScroll); },
              { once: true }
            );
          }
        }

        const MIN_VW = 990;
        let evaluating = false;
        const evaluate = () => {
          if (evaluating) return;
          if (window.innerWidth < MIN_VW) {
            about.removeAttribute('data-overflow');
            if (rte) rte.style.removeProperty('--ap-about-fill-h');
            return;
          }
          evaluating = true;
          const had = about.hasAttribute('data-overflow');
          if (had) about.removeAttribute('data-overflow');
          // eslint-disable-next-line no-unused-expressions
          text.offsetHeight;
          const textH = text.scrollHeight;
          const imagesH = images.getBoundingClientRect().height;
          // Whether the image is already tall enough to sit beside the whole
          // bio (true on wide screens, where the fixed-ratio image grows
          // tall). We no longer bail out here: a long-enough bio should still
          // split into two columns regardless of image height — the wideTextH
          // guard below keeps trivially short bios in one column, and fillH
          // balances the columns in this case.
          const imageFitsBeside = textH <= imagesH + 8;
          const aboutW = about.getBoundingClientRect().width;
          const targetTextW = (aboutW * 2) / 3;
          const originalWidth = text.style.width;
          text.style.width = targetTextW + 'px';
          // eslint-disable-next-line no-unused-expressions
          text.offsetHeight;
          const wideTextH = text.scrollHeight;
          text.style.width = originalWidth;
          const COL2_MIN_H = 80;
          if (wideTextH < COL2_MIN_H * 2) {
            if (rte) rte.style.removeProperty('--ap-about-fill-h');
            about.removeAttribute('data-overflow');
            evaluating = false;
            return;
          }
          let balancedColH = 0;
          const probe = text.cloneNode(true);
          probe.style.position = 'absolute';
          probe.style.left = '-9999px';
          probe.style.top = '0';
          probe.style.width = targetTextW + 'px';
          probe.style.visibility = 'hidden';
          probe.style.pointerEvents = 'none';
          const probeRte = probe.querySelector('.ap-about__rte');
          if (probeRte) {
            probeRte.style.columnCount = '2';
            probeRte.style.columnGap = '50px';
            probeRte.style.columnFill = 'balance';
            probeRte.style.height = '';
          }
          document.body.appendChild(probe);
          // eslint-disable-next-line no-unused-expressions
          probe.offsetHeight;
          if (probeRte) balancedColH = probeRte.scrollHeight;
          document.body.removeChild(probe);
          let imagesAlignedRteH = imagesH;
          if (title && rte) {
            const titleRect = title.getBoundingClientRect();
            const rteRect = rte.getBoundingClientRect();
            imagesAlignedRteH = imagesH - (rteRect.top - titleRect.top);
          }
          const minFillH = balancedColH + 32;
          const maxFillH = wideTextH - COL2_MIN_H;
          let fillH;
          if (imageFitsBeside) {
            // Image is as tall as / taller than the bio (wide screens):
            // aligning column 1 to the image would leave column 2 nearly
            // empty, so split into two balanced columns instead.
            fillH = minFillH;
          } else {
            // Bio is taller than the image: fill column 1 down to the image's
            // height so the text bottom aligns with the image.
            fillH = imagesAlignedRteH;
            if (fillH > maxFillH) fillH = maxFillH;
            if (fillH < minFillH) fillH = minFillH;
          }
          if (rte) {
            rte.style.setProperty('--ap-about-fill-h', fillH + 'px');
          }
          about.setAttribute('data-overflow', 'true');
          evaluating = false;
        };

        // Re-evaluate only on genuine external triggers — NOT via a
        // ResizeObserver on these same elements. evaluate() toggles
        // [data-overflow] on `about` and resizes `text`/`images`, so a
        // ResizeObserver watching them re-fired this every frame: an infinite
        // reflow loop that flickers/lags the page (most visibly in Safari,
        // where scrollbar/sub-pixel differences stop it ever converging).
        // Viewport resize, image load and web-font swap are the only inputs
        // that actually change the measurements; an rAF coalesces bursts so we
        // touch layout at most once per frame.
        let evalRaf = 0;
        const scheduleEvaluate = () => {
          if (evalRaf) return;
          evalRaf = requestAnimationFrame(() => {
            evalRaf = 0;
            evaluate();
          });
        };
        window.addEventListener('resize', scheduleEvaluate, { passive: true });
        about.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', scheduleEvaluate, { once: true });
        });
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(scheduleEvaluate);
        }
        evaluate();
      }

      function run() {
        if (inited) return;
        if (!document.querySelector(
          '.ap-cards-row, .ap-extras__row, .ap-about, [data-discover-shuffle], .apc__heart'
        )) return;
        inited = true;

        bindRow(
          ROOT.querySelector('[data-ap-cards-row]'),
          ROOT.querySelector('[data-ap-prev]'),
          ROOT.querySelector('[data-ap-next]'),
          ROOT.querySelector('[data-ap-progress]')
        );
        bindRow(
          ROOT.querySelector('[data-ap-extras-row]'),
          ROOT.querySelector('[data-ap-extras-prev]'),
          ROOT.querySelector('[data-ap-extras-next]'),
          ROOT.querySelector('[data-ap-extras-progress]')
        );
        shuffleDiscover(ROOT.querySelector('[data-discover-shuffle]'));
        bindAboutOverflow();
        bindHearts();
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
      // Re-run after Shopify section reload (theme editor).
      document.addEventListener('shopify:section:load', () => {
        inited = false;
        run();
      });
    })();
    /**
     * <blog-page>
     * Wires the prev/next arrow buttons on the featured-products
     * slider and the heart-toggle behaviour on each product card.
     *
     * Slider/heart logic is intentionally duplicated from
     * <artist-page>; do not refactor those into shared utils as part
     * of this sprint (per blog-page sprint 02 reuse strategy).
     */
    class BlogPage extends HTMLElement {
      connectedCallback() {
        // Each product-collection slider has its own header (with prev/next
        // arrows) and slider row. Bind them all individually so multiple
        // sliders on one article each work independently.
        this.querySelectorAll('.bp-products').forEach((wrap) => {
          this.bindRow(
            wrap.querySelector('[data-bp-cards-row]'),
            wrap.querySelector('[data-bp-prev]'),
            wrap.querySelector('[data-bp-next]'),
            wrap.querySelector('[data-bp-progress]')
          );
        });
        this.bindRow(
          this.querySelector('[data-bp-lifestyle-row]'),
          this.querySelector('[data-bp-lifestyle-prev]'),
          this.querySelector('[data-bp-lifestyle-next]'),
          null
        );
        this.bindHearts();
        this.bindSequentialColumns();
        this.bindTextColumnsMobileSlider();
      }

      /**
       * Force CSS multi-column to fill column 1 fully before column 2,
       * and column 2 fully before column 3. The native algorithm
       * BALANCES heights across columns; sequential fill requires
       * `column-fill: auto` plus an explicit container height.
       *
       * To get an accurate natural-content height we clone the rich
       * text into a hidden, single-column container sized to one
       * column's width, then measure its height and divide by N.
       */
      bindSequentialColumns() {
        const rtes = this.querySelectorAll('.bp-block--text-columns .bp-block__rte');
        if (!rtes.length) return;

        const measureOneColHeight = (rte, colWidth) => {
          const clone = rte.cloneNode(true);
          clone.style.cssText =
            'position:absolute;left:-99999px;top:0;visibility:hidden;' +
            'pointer-events:none;column-count:1;column-fill:balance;' +
            'height:auto;width:' + colWidth + 'px;';
          document.body.appendChild(clone);
          const h = clone.scrollHeight;
          document.body.removeChild(clone);
          return h;
        };

        const apply = () => {
          // On mobile, the 3-column body is rendered as a 2-page
          // horizontal slider. We want column-fill: balance there so
          // the two pages are equal-height (no empty bottom on page 2);
          // sequential fill is only needed on desktop (3-col flow).
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          rtes.forEach((rte) => {
            // Reset any previous inline overrides.
            rte.style.height = '';
            rte.style.columnFill = '';

            if (isMobile) return;

            const cs = getComputedStyle(rte);
            const cols = parseInt(cs.columnCount, 10) || 1;
            if (cols < 2) return;

            // Width of a single column inside the container.
            const totalW = rte.clientWidth;
            const gap = parseFloat(cs.columnGap) || 0;
            const colWidth = Math.max(0, (totalW - gap * (cols - 1)) / cols);
            if (colWidth <= 0) return;

            const naturalH = measureOneColHeight(rte, colWidth);
            // +1px guards against subpixel rounding pushing the last
            // line into a 4th column.
            const perCol = Math.ceil(naturalH / cols) + 1;
            rte.style.height = perCol + 'px';
            rte.style.columnFill = 'auto';
          });
        };

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(apply);
        } else {
          apply();
        }
        let raf = 0;
        window.addEventListener('resize', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(apply);
        }, { passive: true });
      }

      /**
       * Wires the mobile horizontal-scroll progress bar on the 3-column
       * body. On mobile we split the rich-text into N viewport-wide
       * "pages" via CSS columns + a horizontal scroller. The page count
       * is computed from the content's natural single-column height so
       * each page is roughly one screen tall — i.e. the user always
       * gets the next chunk by swiping, never by scrolling vertically
       * inside a page. The progress bar reuses --bp-progress-x and
       * --bp-progress-width like .bp-products__progress-bar.
       */
      bindTextColumnsMobileSlider() {
        const wraps = this.querySelectorAll('.bp-block--text-columns');
        if (!wraps.length) return;

        const measureSingleColHeight = (rte, colWidth) => {
          const clone = rte.cloneNode(true);
          clone.style.cssText =
            'position:absolute;left:-99999px;top:0;visibility:hidden;' +
            'pointer-events:none;column-count:1;column-fill:balance;' +
            'height:auto;width:' + colWidth + 'px;';
          document.body.appendChild(clone);
          const h = clone.scrollHeight;
          document.body.removeChild(clone);
          return h;
        };

        const layout = () => {
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          wraps.forEach((wrap) => {
            const scroller = wrap.querySelector('[data-bp-cols-scroller]');
            const rte = wrap.querySelector('.bp-block__rte');
            const progress = wrap.querySelector('[data-bp-cols-progress]');
            if (!scroller || !rte) return;

            // Reset previous inline overrides.
            rte.style.cssText = '';

            if (!isMobile) {
              wrap.classList.remove('bp-block--single-page');
              return;
            }

            const pageWidth = scroller.clientWidth;
            if (pageWidth <= 0) return;
            // Inner padding on each "page" so the text doesn't sit flush
            // against the viewport edges (Figma spec: 24px vertical /
            // 32px horizontal per slide). The vertical padding is set
            // on the scroller in CSS; here we only handle horizontal.
            const innerPad = 32;
            const colWidth = Math.max(1, pageWidth - 2 * innerPad);
            // Gap between columns must equal 2 * innerPad so that each
            // page (pageWidth wide) shows exactly one column with
            // innerPad gutter on both sides.
            const gap = 2 * innerPad;
            // Target page height: roughly one screen minus chrome/header.
            const pageHeight = Math.max(420, Math.min(640, window.innerHeight - 240));

            const naturalH = measureSingleColHeight(rte, colWidth);
            // Decide single-page vs slider based on how the content
            // would render at the full page width (without the inline
            // padding). Otherwise, narrowing the column for padding
            // can push borderline content into the slider treatment.
            const naturalHUnpadded = measureSingleColHeight(rte, pageWidth);
            const N = Math.ceil(naturalH / pageHeight);
            const fitsSinglePage = naturalHUnpadded <= pageHeight;

            if (N <= 1 || fitsSinglePage) {
              // Content fits in a single page — don't turn into a slider.
              wrap.classList.add('bp-block--single-page');
              return;
            }
            wrap.classList.remove('bp-block--single-page');

            rte.style.columnCount = String(N);
            rte.style.columnGap = gap + 'px';
            rte.style.columnFill = 'auto';
            // Use the content-driven column height so each page is just
            // tall enough to fit its share of the body — keeps the
            // progress bar sitting close to the content instead of
            // floating far below it.
            const actualPageHeight = Math.max(1, Math.ceil(naturalH / N));
            rte.style.height = actualPageHeight + 'px';
            rte.style.width = (N * colWidth + (N - 1) * gap) + 'px';
            // The scroller has its own inline padding (--outer); subtract
            // it from innerPad so the visible gutter on the first/last
            // page matches innerPad rather than padding + innerPad.
            const scrollerStyles = getComputedStyle(scroller);
            const scrollerPadL = parseFloat(scrollerStyles.paddingLeft) || 0;
            const scrollerPadR = parseFloat(scrollerStyles.paddingRight) || 0;
            rte.style.marginLeft = Math.max(0, innerPad - scrollerPadL) + 'px';
            rte.style.marginRight = Math.max(0, innerPad - scrollerPadR) + 'px';

            // Recompute progress bar against the new scrollWidth.
            scroller.scrollLeft = 0;
            scroller.dispatchEvent(new Event('scroll'));
          });
        };

        // Wire scroll → progress bar via the shared bindRow helper.
        wraps.forEach((wrap) => {
          const scroller = wrap.querySelector('[data-bp-cols-scroller]');
          const progress = wrap.querySelector('[data-bp-cols-progress]');
          if (!scroller || !progress) return;
          this.bindRow(scroller, null, null, progress);
        });

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(layout);
        } else {
          layout();
        }
        let raf = 0;
        window.addEventListener('resize', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(layout);
        }, { passive: true });
      }

      /**
       * Wishlist heart toggle on product cards. Prevents the click
       * from navigating to the PDP and toggles a visual "liked" state.
       */
      bindHearts() {
        this.querySelectorAll('.apc__heart').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const liked = btn.classList.toggle('is-liked');
            btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
          });
        });
      }

      /**
       * Wires a horizontal scroll-snap row to prev / next buttons and
       * an optional progress bar (mobile). Keeps disabled state in
       * sync with scrollLeft.
       */
      bindRow(row, prev, next, progress) {
        if (!row || (!prev && !next && !progress)) return;

        const step = () => {
          const child = row.firstElementChild;
          if (!child) return row.clientWidth;
          const style = getComputedStyle(row);
          const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
          return child.getBoundingClientRect().width + gap;
        };

        const updateState = () => {
          const max = row.scrollWidth - row.clientWidth;
          if (prev) prev.toggleAttribute('disabled', row.scrollLeft <= 0);
          if (next) next.toggleAttribute('disabled', row.scrollLeft >= max - 1);
          if (progress) {
            const bar = progress.firstElementChild;
            if (bar && row.scrollWidth > 0) {
              const visibleRatio = Math.min(1, row.clientWidth / row.scrollWidth);
              const widthPct = visibleRatio * 100;
              const trackPct = 100 - widthPct;
              const xPct = max > 0 ? (row.scrollLeft / max) * trackPct : 0;
              bar.style.setProperty('--bp-progress-width', widthPct + '%');
              bar.style.setProperty('--bp-progress-x', xPct + '%');
            }
          }
        };

        if (prev) {
          prev.addEventListener('click', () => {
            row.scrollBy({ left: -step(), behavior: 'smooth' });
          });
        }
        if (next) {
          next.addEventListener('click', () => {
            row.scrollBy({ left: step(), behavior: 'smooth' });
          });
        }

        row.addEventListener('scroll', updateState, { passive: true });
        window.addEventListener('resize', updateState, { passive: true });
        updateState();
      }
    }

    if (!customElements.get('blog-page')) {
      customElements.define('blog-page', BlogPage);
    }
  // /assets/js/components/artist-more-products.js
  // Web component for the "More from [Artist]" section on the product page.
  //
  // Fetches products filtered by the current artist metaobject GID via the
  // Shopify Storefront Search endpoint:
  //   /search?type=product&q=&filter.p.m.custom.artist={encodedGid}&section_id=api-artist-more-products
  //
  // PREREQUISITE: The `custom.artist` product metafield must be enabled as a
  // storefront filter in Shopify Admin → Apps → Search & Discovery → Filters.
  // Without this, the search returns zero results and the section hides itself.

  if (!customElements.get('artist-more-products')) {
    customElements.define(
      'artist-more-products',
      class ArtistMoreProducts extends HTMLElement {
        constructor() {
          super();
          this.grid = null;
          this.loadingEl = null;
          this.initialized = false;
        }

        connectedCallback() {
          this.grid = this.querySelector('[data-artist-more-grid]');
          this.loadingEl = this.querySelector('[data-artist-more-loading]');

          if (!this.grid) return;

          const artistGid = this.dataset.artistGid;
          const productId = this.dataset.productId;
          const limit = parseInt(this.dataset.limit, 10) || 10;

          if (!artistGid) {
            this.classList.add('is-empty');
            return;
          }

          this._fetchArtistProducts(artistGid, productId, limit);
        }

        _fetchArtistProducts(artistGid, currentProductId, limit) {
          if (this.initialized) return;
          this.initialized = true;

          const url = [
            '/search',
            '?type=product',
            '&q=',
            '&filter.p.m.custom.artist=' + encodeURIComponent(artistGid),
            '&options[unavailable_products]=last',
            '&section_id=api-artist-more-products',
          ].join('');

          fetch(url)
            .then((res) => {
              if (!res.ok) throw new Error('Network error: ' + res.status);
              return res.text();
            })
            .then((html) => this._handleResponse(html, currentProductId, limit))
            .catch((err) => {
              console.warn('[ArtistMoreProducts] Fetch failed:', err);
              this.classList.add('is-empty');
            });
        }

        _handleResponse(html, currentProductId, limit) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const template = doc.querySelector('[data-artist-more-template]');

          if (!template) {
            this.classList.add('is-empty');
            return;
          }

          const clone = template.content.cloneNode(true);

          // Remove the current product from results so it doesn't show in its own "More from" row
          if (currentProductId) {
            const currentItemId = 'product-item--api-artist-more-products-' + currentProductId;
            const currentItem = clone.getElementById(currentItemId);
            if (currentItem) {
              currentItem.remove();
            }
          }

          // Respect the limit
          const items = Array.from(clone.children);
          items.forEach((item, i) => {
            if (i >= limit) item.remove();
          });

          // Insert into grid
          const remaining = clone.children.length;
          if (!remaining && clone.querySelector) {
            // Check childNodes count before insert
          }

          this.grid.appendChild(clone);

          // Check if anything ended up in the grid
          if (!this.grid.children.length) {
            this.classList.add('is-empty');
            return;
          }

          // Toggle slider-disabled if few items
          if (this.grid.children.length <= 4) {
            this.grid.classList.add('grid--slider-disabled');
          }

          // Hide loading text
          if (this.loadingEl) {
            this.loadingEl.remove();
          }

          // Fire grid slider init event in case it was already connected
          const slider = this.querySelector('grid-slider');
          if (slider) {
            slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
          }
        }
      }
    );
  }
    /**
     * <search-view-toggle>
     * Segmented toggle that flips the parent search results page between
     * "product" view (default product image) and "lifestyle" view (the
     * secondary / hover image, typically a room shot).
     *
     * Mirrors the pattern used by <artists-section> for its Artist/Artwork
     * toggle: writes data-view="product|lifestyle" onto the target element,
     * and CSS in sections/search.liquid swaps which image layer is visible.
     *
     * Markup contract:
     *   <search-view-toggle data-target=".search-page" data-default="product">
     *     <span class="search-view-toggle__label">View:</span>
     *     <div class="search-view-toggle__buttons" role="group" aria-label="View mode">
     *       <button type="button" data-view="lifestyle" aria-pressed="false">Lifestyle</button>
     *       <button type="button" data-view="product" class="is-active" aria-pressed="true">Product</button>
     *     </div>
     *   </search-view-toggle>
     *
     * Attributes:
     *   data-target  — CSS selector for the element that should receive the
     *                  data-view attribute. Defaults to the closest .search-page
     *                  ancestor, then falls back to the component itself.
     *   data-default — Initial view (product|lifestyle). Defaults to "product".
     */
    class SearchViewToggle extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        const targetSelector = this.getAttribute('data-target');
        if (targetSelector) {
          this.target = document.querySelector(targetSelector);
        }
        if (!this.target) {
          this.target = this.closest('.search-page') || this;
        }

        this.defaultView = this.getAttribute('data-default') || 'product';
        // The chosen view lives in the URL (?display=product|lifestyle) and is the
        // single source of truth. Reading it on load means the view survives
        // pagination, filtering and back/forward navigation, and can be shared
        // via the URL — no localStorage involved.
        this.setView(this._readUrlView() || this.defaultView, { updateUrl: false });

        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(e) {
        const btn = e.target.closest('[data-view]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        let view = btn.getAttribute('data-view');
        if (!view) return;
        // On mobile, the inactive button is hidden — clicking the visible
        // (active) button should cycle to the other view.
        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        if (isMobile && btn.classList.contains('is-active')) {
          view = view === 'product' ? 'lifestyle' : 'product';
        }
        this.setView(view);
      }

      setView(view, { updateUrl = true } = {}) {
        this.target.setAttribute('data-view', view);
        if (updateUrl) this._writeUrlView(view);
        // Keep the (server-rendered) pagination links carrying the current
        // view, so paging to another page doesn't drop the choice.
        this._syncPaginationLinks(view);
        this.querySelectorAll('[data-view]').forEach((b) => {
          const isActive = b.getAttribute('data-view') === view;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        // On touch devices, lifestyle slide images are lazy-loaded while sitting
        // in the second scroll-snap slot (off-screen). The browser's lazy-load
        // IntersectionObserver clips against the scroll container and never fires
        // for the off-screen slide, so the image never loads. Force it now.
        if (view === 'lifestyle') {
          this.target.querySelectorAll('[data-hover-slide][data-hover-slide-touch] img[loading="lazy"]').forEach((img) => {
            if (!img.complete || img.naturalWidth === 0) {
              img.setAttribute('loading', 'eager');
            }
          });
        }
      }

      _readUrlView() {
        try {
          // 'view' is reserved by Shopify (alternate-template selector), so the
          // chosen card view is stored under 'display'.
          const v = new URLSearchParams(window.location.search).get('display');
          return v === 'product' || v === 'lifestyle' ? v : null;
        } catch (_) {
          return null;
        }
      }

      _writeUrlView(view) {
        try {
          const url = new URL(window.location.href);
          // Keep URLs clean: the default view carries no param, anything else
          // is written explicitly. replaceState (not pushState) so the toggle
          // doesn't add history entries or trip the filter form's popstate
          // listener — it's a display preference, not a navigation step.
          this._applyViewParam(url, view);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (_) {}
      }

      _applyViewParam(url, view) {
        if (view === this.defaultView) {
          url.searchParams.delete('display');
        } else {
          url.searchParams.set('display', view);
        }
        return url;
      }

      _syncPaginationLinks(view) {
        if (!this.target || !this.target.querySelectorAll) return;
        this.target
          .querySelectorAll('.pagination-custom a[href], #AjaxinatePagination a[href]')
          .forEach((a) => {
            try {
              const url = this._applyViewParam(new URL(a.getAttribute('href'), window.location.origin), view);
              a.setAttribute('href', url.pathname + url.search);
            } catch (_) {}
          });
      }
    }

    if (!customElements.get('search-view-toggle')) {
      customElements.define('search-view-toggle', SearchViewToggle);
    }
    /*
     * <column-toggle>
     *   Lets the user pick a column count for a collection grid.
     *   Sets `--columns-override` (and `--COLUMNS`) on the target element so
     *   the existing grid CSS picks it up. Persists the choice in
     *   localStorage per collection handle.
     *
     *   Modes:
     *     data-mode="desktop" (default): sets --columns-override / --COLUMNS
     *     data-mode="mobile": sets --columns-mobile-override / --COLUMNS-MOBILE
     *
     *   On mobile breakpoint, clicking the active button cycles to the
     *   next available value (used by the mobile pill UI where only the
     *   active button is visible).
     *
     *   Usage:
     *     <column-toggle data-target="#Collection--abc" data-default="3">
     *       <button class="column-toggle__btn is-active" data-cols="3">3</button>
     *       <button class="column-toggle__btn" data-cols="4">4</button>
     *       <button class="column-toggle__btn" data-cols="6">6</button>
     *     </column-toggle>
     */
    class ColumnToggle extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this._onResize = this._onResize.bind(this);
      }

      connectedCallback() {
        const targetSelector = this.getAttribute('data-target');
        if (targetSelector) {
          this.target = document.querySelector(targetSelector);
        }
        if (!this.target) {
          this.target = this.closest('collection-component') || this;
        }

        this.mode = this.getAttribute('data-mode') === 'mobile' ? 'mobile' : 'desktop';
        // Scope persistence by collection (data-scope = collection handle) so the
        // choice survives pagination within a collection but resets to the
        // default on a different / not-yet-visited collection. Falls back to the
        // target id for non-collection usages.
        this.storageKey = 'tpc:cols:' + this.mode + ':' + (this.getAttribute('data-scope') || this.target.id || 'default');
        const stored = (function () {
          try { return localStorage.getItem(this.storageKey); } catch (e) { return null; }
        }).call(this);
        const initial = stored || this.getAttribute('data-default') || (this.mode === 'mobile' ? '2' : '3');
        this.setColumns(initial, false);

        this.addEventListener('click', this.onClick);
        document.addEventListener('theme:resize:width', this._onResize);

        // Web fonts can change the product info-row height after first paint;
        // recompute the breaker metrics once fonts are ready (active mode only).
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            const isMobileBp = window.matchMedia('(max-width: 989px)').matches;
            const modeMatches = (this.mode === 'mobile' && isMobileBp) || (this.mode === 'desktop' && !isMobileBp);
            if (!modeMatches) return;
            const activeBtn = this.querySelector('.column-toggle__btn.is-active');
            this._fillGrid(parseInt(activeBtn?.getAttribute('data-cols') || initial));
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('theme:resize:width', this._onResize);
      }

      _onResize() {
        const isMobileBp = window.matchMedia('(max-width: 989px)').matches;
        const modeMatches = (this.mode === 'mobile' && isMobileBp) || (this.mode === 'desktop' && !isMobileBp);
        if (!modeMatches) return;
        const activeBtn = this.querySelector('.column-toggle__btn.is-active');
        const cols = parseInt(activeBtn?.getAttribute('data-cols') || '4');
        this._fillGrid(cols);
      }

      onClick(e) {
        const btn = e.target.closest('[data-cols]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();

        // On mobile pill: clicking the active (only-visible) button cycles.
        const isMobileBp = window.matchMedia('(max-width: 989px)').matches;
        if (isMobileBp && btn.classList.contains('is-active')) {
          const all = Array.from(this.querySelectorAll('[data-cols]'));
          const i = all.indexOf(btn);
          const next = all[(i + 1) % all.length];
          if (next) this.setColumns(next.getAttribute('data-cols'), true);
          return;
        }

        const cols = btn.getAttribute('data-cols');
        if (!cols) return;
        this.setColumns(cols, true);
      }

      setColumns(cols, persist) {
        if (this.target && this.target.style) {
          if (this.mode === 'mobile') {
            this.target.style.setProperty('--columns-mobile-override', cols);
            this.target.style.setProperty('--COLUMNS-MOBILE', cols);
          } else {
            this.target.style.setProperty('--columns-override', cols);
            this.target.style.setProperty('--COLUMNS', cols);
          }
        }
        const buttons = Array.from(this.querySelectorAll('[data-cols]'));
        buttons.forEach((b) => {
          const isActive = b.getAttribute('data-cols') === String(cols);
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        // At max columns the product image becomes too small for the info
        // row, so add a marker class that lets CSS hide .product-information
        // for a dense image-only mosaic.
        //
        // NB: this used to also force the view-toggle to "product" (and
        // overwrite its saved value), which wiped a user's Lifestyle choice
        // whenever they picked the densest columns — so "columns + lifestyle"
        // never persisted. Column count and view are now independent;
        // Lifestyle is allowed at every column count.
        const max = buttons.length ? buttons[buttons.length - 1].getAttribute('data-cols') : null;
        if (this.target && this.target.classList && max) {
          const cls = 'is-max-cols-' + this.mode;
          this.target.classList.toggle(cls, String(cols) === String(max));
        }

        if (persist) {
          try { localStorage.setItem(this.storageKey, String(cols)); } catch (e) { /* ignore */ }
        }

        // Fill trailing partial row so the last row is always complete.
        // Only run for the toggle whose mode matches the current viewport.
        const isMobileBpFill = window.matchMedia('(max-width: 989px)').matches;
        const modeMatchesFill = (this.mode === 'mobile' && isMobileBpFill) || (this.mode === 'desktop' && !isMobileBpFill);
        if (modeMatchesFill) {
          requestAnimationFrame(() => this._fillGrid(parseInt(cols)));
        }
      }

      // Add invisible spacer items so the last grid row is always complete.
      _fillGrid(cols) {
        if (!this.target || !cols) return;
        const grid = this.target.querySelector('[data-products-grid] .grid');
        if (!grid) return;

        // Remove any previously added spacers.
        grid.querySelectorAll('.grid-spacer').forEach((el) => el.remove());

        // Count grid items that participate in layout on this viewport
        // (display:none breakers for the opposite viewport are excluded).
        const items = Array.from(grid.querySelectorAll('.grid-item:not(.grid-spacer)'))
          .filter((el) => getComputedStyle(el).display !== 'none').length;

        const fill = (cols - (items % cols)) % cols;
        for (let i = 0; i < fill; i++) {
          const spacer = document.createElement('div');
          spacer.className = 'grid-item grid-spacer';
          spacer.setAttribute('aria-hidden', 'true');
          grid.appendChild(spacer);
        }

        this._syncBreakerMetrics(grid);
      }

      // Measure the REAL product-card metrics so breakers line up exactly with
      // the product images at any column count — rather than reconstructing the
      // image height from container-query math, which depends on --gap being
      // exact and accumulates sub-pixel rounding error. We expose:
      //   --bk-img:  height of a single product image (one grid column)
      //   --bk-info: space below the image in a card (info row + its top gap);
      //              0 at max columns where the info row is hidden
      //   --bk-gap:  the grid's real row gap
      // breaker.css builds the height as:
      //   rows * --bk-img + (rows - 1) * (--bk-info + --bk-gap)
      // so a 2-row breaker reaches the bottom of the 2nd product image, with
      // that row's info sitting below it (same look as a 1x1 breaker).
      _syncBreakerMetrics(grid) {
        if (!this.target || !this.target.style) return;
        const card = grid.querySelector(
          '.grid-item.product-item:not(.breaker-item):not(.grid-spacer)'
        );
        const image = card && card.querySelector('[data-product-image]');
        if (!card || !image) return;

        const imgRect = image.getBoundingClientRect();
        // Space below the image: anchor to the actual image/info edges so a
        // grid row stretched by a neighbouring breaker can't inflate it.
        const info = card.querySelector('[data-product-information]');
        let infoH = 0;
        if (info && getComputedStyle(info).display !== 'none') {
          infoH = Math.max(0, info.getBoundingClientRect().bottom - imgRect.bottom);
        }
        const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;

        this.target.style.setProperty('--bk-img', imgRect.height.toFixed(2) + 'px');
        this.target.style.setProperty('--bk-info', infoH.toFixed(2) + 'px');
        this.target.style.setProperty('--bk-gap', rowGap.toFixed(2) + 'px');
      }
    }

    if (!customElements.get('column-toggle')) {
      customElements.define('column-toggle', ColumnToggle);
    }
    const selectors$3 = {
      aos: '[data-aos]',
      collectionImage: '.collection-item__image',
      columnImage: '[data-column-image]',
      flickityNextArrow: '.flickity-button.next',
      flickityPrevArrow: '.flickity-button.previous',
      link: 'a:not(.btn)',
      productItemImage: '.product-item__image',
      section: '[data-section-id]',
      slide: '[data-slide]',
      slideValue: 'data-slide',
      sliderThumb: '[data-slider-thumb]',
    };

    const attributes$2 = {
      arrowPositionMiddle: 'data-arrow-position-middle',
      slideIndex: 'data-slide-index',
      sliderOptions: 'data-options',
      slideTextColor: 'data-slide-text-color',
    };

    const classes$3 = {
      aosAnimate: 'aos-animate',
      desktop: 'desktop',
      focused: 'is-focused',
      flickityEnabled: 'flickity-enabled',
      heroContentTransparent: 'hero__content--transparent',
      hidden: 'hidden',
      initialized: 'is-initialized',
      isLoading: 'is-loading',
      isSelected: 'is-selected',
      mobile: 'mobile',
      singleSlide: 'single-slide',
    };

    if (!customElements.get('slider-component')) {
      customElements.define(
        'slider-component',
        class SliderComponent extends HTMLElement {
          constructor() {
            super();

            this.flkty = null;
            this.bindEvents();
          }

          connectedCallback() {
            this.slides = this.querySelectorAll(selectors$3.slide);
            this.thumbs = this.querySelectorAll(selectors$3.sliderThumb);
            this.section = this.closest(selectors$3.section);
            this.initSlider();
          }

          initSlider() {
            if (this.slides.length <= 1) return;

            if (this.hasAttribute(attributes$2.sliderOptions)) {
              this.customOptions = JSON.parse(decodeURIComponent(this.getAttribute(attributes$2.sliderOptions)));
            }

            this.classList.add(classes$3.isLoading);

            let slideSelector = selectors$3.slide;
            const isDesktopView = !theme.isMobile;
            const slideDesktop = `${selectors$3.slide}:not(.${classes$3.mobile})`;
            const slideMobile = `${selectors$3.slide}:not(.${classes$3.desktop})`;
            const hasDeviceSpecificSelectors = this.querySelectorAll(slideDesktop).length || this.querySelectorAll(slideMobile).length;

            if (hasDeviceSpecificSelectors) {
              if (isDesktopView) {
                slideSelector = slideDesktop;
              } else {
                slideSelector = slideMobile;
              }

              this.flkty?.destroy();
            }

            if (this.querySelectorAll(slideSelector).length <= 1) {
              this.classList.add(classes$3.singleSlide);
              this.classList.remove(classes$3.isLoading);
              return;
            }

            this.sliderOptions = {
              cellSelector: slideSelector,
              contain: true,
              wrapAround: true,
              adaptiveHeight: true,
              ...this.customOptions,
              on: {
                ready: () => {
                  requestAnimationFrame(() => {
                    this.classList.add(classes$3.initialized);
                    this.classList.remove(classes$3.isLoading);
                    this.parentNode.dispatchEvent(
                      new CustomEvent('theme:slider:loaded', {
                        bubbles: true,
                        detail: {
                          slider: this,
                        },
                      })
                    );
                  });

                  this.slideActions();

                  if (this.sliderOptions.prevNextButtons) {
                    this.positionArrows();
                  }
                },
                change: (index) => {
                  const slide = this.slides[index];
                  if (!slide || this.sliderOptions.groupCells) return;

                  const elementsToAnimate = slide.querySelectorAll(selectors$3.aos);
                  if (elementsToAnimate.length) {
                    elementsToAnimate.forEach((el) => {
                      el.classList.remove(classes$3.aosAnimate);
                      requestAnimationFrame(() => {
                        // setTimeout with `0` delay fixes functionality on Mobile and Firefox
                        setTimeout(() => {
                          el.classList.add(classes$3.aosAnimate);
                        }, 0);
                      });
                    });
                  }
                },
                resize: () => {
                  if (this.sliderOptions.prevNextButtons) {
                    this.positionArrows();
                  }
                },
              },
            };

            this.initFlickity();

            this.flkty.on('change', () => this.slideActions(true));

            this.thumbs?.forEach((thumb) => {
              thumb.addEventListener('click', (e) => {
                e.preventDefault();
                const slideIndex = [...thumb.parentElement.children].indexOf(thumb);
                this.flkty.select(slideIndex);
              });
            });

            if (!this.flkty || !this.flkty.isActive) {
              this.classList.remove(classes$3.isLoading);
            }
          }

          initFlickity() {
            if (this.sliderOptions.fade) {
              this.flkty = new window.theme.FlickityFade(this, this.sliderOptions);
            } else {
              this.flkty = new window.theme.Flickity(this, this.sliderOptions);
            }
          }

          bindEvents() {
            this.addEventListener('theme:slider:init', () => {
              this.initSlider();
            });

            this.addEventListener('theme:slider:select', (e) => {
              this.flkty.selectCell(e.detail.index);
              this.flkty.stopPlayer();
            });

            this.addEventListener('theme:slider:deselect', () => {
              if (this.flkty && this.sliderOptions.hasOwnProperty('autoPlay') && this.sliderOptions.autoPlay) {
                this.flkty.playPlayer();
              }
            });

            this.addEventListener('theme:slider:reposition', () => {
              this.flkty?.reposition();
            });

            this.addEventListener('theme:slider:destroy', () => {
              this.flkty?.destroy();
            });

            this.addEventListener('theme:slider:remove-slide', (e) => {
              if (!e.detail.slide) return;

              this.flkty?.remove(e.detail.slide);

              if (this.flkty?.cells.length === 0) {
                this.section.classList.add(classes$3.hidden);
              }
            });
          }

          slideActions(changeEvent = false) {
            const currentSlide = this.querySelector(`.${classes$3.isSelected}`);
            if (!currentSlide) return;

            const currentSlideTextColor = currentSlide.hasAttribute(attributes$2.slideTextColor) ? currentSlide.getAttribute(attributes$2.slideTextColor) : '';
            const currentSlideLink = currentSlide.querySelector(selectors$3.link);
            const buttons = this.querySelectorAll(`${selectors$3.slide} a, ${selectors$3.slide} button`);

            if (document.body.classList.contains(classes$3.focused) && currentSlideLink && this.sliderOptions.groupCells && changeEvent) {
              currentSlideLink.focus();
            }

            if (buttons.length) {
              buttons.forEach((button) => {
                const slide = button.closest(selectors$3.slide);
                if (slide) {
                  const tabIndex = slide.classList.contains(classes$3.isSelected) ? 0 : -1;
                  button.setAttribute('tabindex', tabIndex);
                }
              });
            }

            this.style.setProperty('--text', currentSlideTextColor);

            if (this.thumbs.length && this.thumbs.length === this.slides.length && currentSlide.hasAttribute(attributes$2.slideIndex)) {
              const slideIndex = parseInt(currentSlide.getAttribute(attributes$2.slideIndex));
              const currentThumb = this.querySelector(`${selectors$3.sliderThumb}.${classes$3.isSelected}`);
              if (currentThumb) {
                currentThumb.classList.remove(classes$3.isSelected);
              }
              this.thumbs[slideIndex].classList.add(classes$3.isSelected);
            }
          }

          positionArrows() {
            if (!this.hasAttribute(attributes$2.arrowPositionMiddle) || !this.sliderOptions.prevNextButtons) return;

            const itemImage = this.querySelector(selectors$3.collectionImage) || this.querySelector(selectors$3.productItemImage) || this.querySelector(selectors$3.columnImage);

            // Prevent 'clientHeight' of null error if no image
            if (!itemImage) return;

            this.querySelector(selectors$3.flickityPrevArrow).style.top = itemImage.clientHeight / 2 + 'px';
            this.querySelector(selectors$3.flickityNextArrow).style.top = itemImage.clientHeight / 2 + 'px';
          }

          disconnectedCallback() {
            if (this.flkty) {
              this.flkty.options.watchCSS = false;
              this.flkty.destroy();
            }
          }
        }
      );
    }

    const selectors$2 = {
      relatedSection: '[data-related-section]',
      aos: '[data-aos]',
      tabsLi: '[data-tab]',
      tabLink: '.tab-link',
      tabLinkRecent: '.tab-link__recent',
      tabContent: '.tab-content',
    };

    const classes$2 = {
      current: 'current',
      hidden: 'hidden',
      aosAnimate: 'aos-animate',
      aosNoTransition: 'aos-no-transition',
      focused: 'is-focused',
    };

    const attributes$1 = {
      dataTab: 'data-tab',
      dataTabIndex: 'data-tab-index',
    };

    if (!customElements.get('tabs-component')) {
      customElements.define(
        'tabs-component',
        class GlobalTabs extends HTMLElement {
          constructor() {
            super();

            this.a11y = window.a11y;
          }

          connectedCallback() {
            const tabsNavList = this.querySelectorAll(selectors$2.tabsLi);

            this.addEventListener('theme:tab:check', () => this.checkRecentTab());
            this.addEventListener('theme:tab:hide', () => this.hideRelatedTab());

            tabsNavList?.forEach((element) => {
              const tabId = parseInt(element.getAttribute(attributes$1.dataTab));
              const tab = this.querySelector(`${selectors$2.tabContent}-${tabId}`);

              element.addEventListener('click', () => {
                this.tabChange(element, tab);
              });

              element.addEventListener('keyup', (event) => {
                if ((event.code === 'Space' || event.code === 'Enter') && document.body.classList.contains(classes$2.focused)) {
                  this.tabChange(element, tab);
                }
              });
            });
          }

          tabChange(element, tab) {
            if (element.classList.contains(classes$2.current)) {
              return;
            }

            const currentTab = this.querySelector(`${selectors$2.tabsLi}.${classes$2.current}`);
            const currentTabContent = this.querySelector(`${selectors$2.tabContent}.${classes$2.current}`);

            currentTab?.classList.remove(classes$2.current);
            currentTabContent?.classList.remove(classes$2.current);

            element.classList.add(classes$2.current);
            tab.classList.add(classes$2.current);

            if (element.classList.contains(classes$2.hidden)) {
              tab.classList.add(classes$2.hidden);
            }

            this.a11y.a11y.removeTrapFocus();

            this.dispatchEvent(new CustomEvent('theme:tab:change', {bubbles: true}));

            element.dispatchEvent(
              new CustomEvent('theme:form:sticky', {
                bubbles: true,
                detail: {
                  element: 'tab',
                },
              })
            );

            this.animateItems(tab);
          }

          animateItems(tab, animated = true) {
            const animatedItems = tab.querySelectorAll(selectors$2.aos);

            if (animatedItems.length) {
              animatedItems.forEach((animatedItem) => {
                animatedItem.classList.remove(classes$2.aosAnimate);

                if (animated) {
                  animatedItem.classList.add(classes$2.aosNoTransition);

                  requestAnimationFrame(() => {
                    animatedItem.classList.remove(classes$2.aosNoTransition);
                    animatedItem.classList.add(classes$2.aosAnimate);
                  });
                }
              });
            }
          }

          checkRecentTab() {
            const tabLink = this.querySelector(selectors$2.tabLinkRecent);

            if (tabLink) {
              tabLink.classList.remove(classes$2.hidden);
              const tabLinkIdx = parseInt(tabLink.getAttribute(attributes$1.dataTab));
              const tabContent = this.querySelector(`${selectors$2.tabContent}[${attributes$1.dataTabIndex}="${tabLinkIdx}"]`);

              if (tabContent) {
                tabContent.classList.remove(classes$2.hidden);

                this.animateItems(tabContent, false);
              }
            }
          }

          hideRelatedTab() {
            const relatedSection = this.querySelector(selectors$2.relatedSection);
            if (!relatedSection) {
              return;
            }

            const parentTabContent = relatedSection.closest(`${selectors$2.tabContent}.${classes$2.current}`);
            if (!parentTabContent) {
              return;
            }
            const parentTabContentIdx = parseInt(parentTabContent.getAttribute(attributes$1.dataTabIndex));
            const tabsNavList = this.querySelectorAll(selectors$2.tabsLi);

            if (tabsNavList.length > parentTabContentIdx) {
              const nextTabsNavLink = tabsNavList[parentTabContentIdx].nextSibling;

              if (nextTabsNavLink) {
                tabsNavList[parentTabContentIdx].classList.add(classes$2.hidden);
                nextTabsNavLink.dispatchEvent(new Event('click'));
              }
            }
          }
        }
      );
    }

    const selectors$1 = {
      actions: '[data-actions]',
      content: '[data-content]',
      trigger: '[data-button]',
    };

    const attributes = {
      height: 'data-height',
    };

    const classes$1 = {
      open: 'is-open',
      enabled: 'is-enabled',
    };

    class ToggleEllipsis extends HTMLElement {
      constructor() {
        super();

        this.initialHeight = this.getAttribute(attributes.height);
        this.content = this.querySelector(selectors$1.content);
        this.trigger = this.querySelector(selectors$1.trigger);
        this.actions = this.querySelector(selectors$1.actions);
        this.toggleActions = this.toggleActions.bind(this);
      }

      connectedCallback() {
        // Make sure the data attribute height value matches the CSS value
        this.setHeight(this.initialHeight);

        this.trigger.addEventListener('click', () => {
          if (this.classList.contains(classes$1.open)) {
            // Collapse back to the initial truncated height.
            this.setHeight(this.initialHeight);
            this.classList.remove(classes$1.open);
          } else {
            this.setHeight(this.content.offsetHeight);
            this.classList.add(classes$1.open);
          }
        });

        this.setHeight(this.initialHeight);
        this.toggleActions();

        document.addEventListener('theme:resize', this.toggleActions);
        document.addEventListener('theme:collapsible:toggle', this.toggleActions);
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.toggleActions);
        document.removeEventListener('theme:collapsible:toggle', this.toggleActions);
      }

      setHeight(contentHeight) {
        this.style.setProperty('--height', `${contentHeight}px`);
      }

      toggleActions() {
        this.classList.toggle(classes$1.enabled, this.content.scrollHeight > this.initialHeight);
      }
    }

    if (!customElements.get('toggle-ellipsis')) {
      customElements.define('toggle-ellipsis', ToggleEllipsis);
    }

    const selectors = {
      sectionId: '[data-section-id]',
      tooltip: 'data-tooltip',
      tooltipStopMouseEnter: 'data-tooltip-stop-mouseenter',
    };

    const classes = {
      tooltipDefault: 'tooltip-default',
      visible: 'is-visible',
      hiding: 'is-hiding',
    };

    if (!customElements.get('tooltip-component')) {
      customElements.define(
        'tooltip-component',
        class Tooltip extends HTMLElement {
          constructor() {
            super();

            this.label = this.hasAttribute(selectors.tooltip) ? this.getAttribute(selectors.tooltip) : '';
            this.transitionSpeed = 200;
            this.hideTransitionTimeout = 0;
            this.addPinEvent = () => this.addPin();
            this.addPinMouseEvent = () => this.addPin(true);
            this.removePinEvent = (event) => window.theme.throttle(this.removePin(event), 50);
            this.removePinMouseEvent = (event) => this.removePin(event, true, true);
          }

          connectedCallback() {
            if (!document.querySelector(`.${classes.tooltipDefault}`)) {
              const tooltipTemplate = `<div class="${classes.tooltipDefault}__arrow"></div><div class="${classes.tooltipDefault}__inner"><div class="${classes.tooltipDefault}__text"></div></div>`;
              const tooltipElement = document.createElement('div');
              tooltipElement.className = classes.tooltipDefault;
              tooltipElement.innerHTML = tooltipTemplate;
              document.body.appendChild(tooltipElement);
            }

            this.addEventListener('mouseenter', this.addPinMouseEvent);
            this.addEventListener('mouseleave', this.removePinMouseEvent);
            this.addEventListener('theme:tooltip:init', this.addPinEvent);
            document.addEventListener('theme:tooltip:close', this.removePinEvent);
          }

          addPin(stopMouseEnter = false) {
            const tooltipTarget = document.querySelector(`.${classes.tooltipDefault}`);

            const section = this.closest(selectors.sectionId);
            const colorSchemeClass = Array.from(section.classList).find((cls) => cls.startsWith('color-scheme-'));

            if (colorSchemeClass) {
              tooltipTarget?.classList.add(colorSchemeClass); // add the section's color scheme class to the tooltip
            }

            if (this.label && tooltipTarget && ((stopMouseEnter && !this.hasAttribute(selectors.tooltipStopMouseEnter)) || !stopMouseEnter)) {
              const tooltipTargetArrow = tooltipTarget.querySelector(`.${classes.tooltipDefault}__arrow`);
              const tooltipTargetInner = tooltipTarget.querySelector(`.${classes.tooltipDefault}__inner`);
              const tooltipTargetText = tooltipTarget.querySelector(`.${classes.tooltipDefault}__text`);
              tooltipTargetText.innerHTML = this.label;

              const tooltipTargetWidth = tooltipTargetInner.offsetWidth;
              const tooltipRect = this.getBoundingClientRect();
              const tooltipTop = tooltipRect.top;
              const tooltipWidth = tooltipRect.width;
              const tooltipHeight = tooltipRect.height;
              const tooltipTargetPositionTop = tooltipTop + tooltipHeight + window.scrollY;
              let tooltipTargetPositionLeft = tooltipRect.left - tooltipTargetWidth / 2 + tooltipWidth / 2;
              const tooltipLeftWithWidth = tooltipTargetPositionLeft + tooltipTargetWidth;
              const sideOffset = 24;
              const tooltipTargetWindowDifference = tooltipLeftWithWidth - theme.windowWidth + sideOffset;

              if (tooltipTargetWindowDifference > 0) {
                tooltipTargetPositionLeft -= tooltipTargetWindowDifference;
              }

              if (tooltipTargetPositionLeft < 0) {
                tooltipTargetPositionLeft = 0;
              }

              tooltipTargetArrow.style.left = `${tooltipRect.left + tooltipWidth / 2}px`;
              tooltipTarget.style.setProperty('--tooltip-top', `${tooltipTargetPositionTop}px`);

              tooltipTargetInner.style.transform = `translateX(${tooltipTargetPositionLeft}px)`;
              tooltipTarget.classList.remove(classes.hiding);
              tooltipTarget.classList.add(classes.visible);

              document.addEventListener('theme:scroll', this.removePinEvent);
            }
          }

          removePin(event, stopMouseEnter = false, hideTransition = false) {
            const tooltipTarget = document.querySelector(`.${classes.tooltipDefault}`);
            const tooltipVisible = tooltipTarget.classList.contains(classes.visible);

            if (tooltipTarget && ((stopMouseEnter && !this.hasAttribute(selectors.tooltipStopMouseEnter)) || !stopMouseEnter)) {
              if (tooltipVisible && (hideTransition || event.detail.hideTransition)) {
                tooltipTarget.classList.add(classes.hiding);

                if (this.hideTransitionTimeout) {
                  clearTimeout(this.hideTransitionTimeout);
                }

                this.hideTransitionTimeout = setTimeout(() => {
                  tooltipTarget.classList.remove(classes.hiding);
                }, this.transitionSpeed);
              }

              tooltipTarget.classList.remove(classes.visible);

              document.removeEventListener('theme:scroll', this.removePinEvent);
            }
          }

          disconnectedCallback() {
            this.removeEventListener('mouseenter', this.addPinMouseEvent);
            this.removeEventListener('mouseleave', this.removePinMouseEvent);
            this.removeEventListener('theme:tooltip:init', this.addPinEvent);
            document.removeEventListener('theme:tooltip:close', this.removePinEvent);
            document.removeEventListener('theme:scroll', this.removePinEvent);
          }
        }
      );
    }

    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);

          publish(theme.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      get selectedOptionValues() {
        const selectedNativeDropdowns = Array.from(this.querySelectorAll('select option[selected]'));
        const selectedRadios = Array.from(this.querySelectorAll('fieldset input:checked'));
        const selectedPopouts = Array.from(this.querySelectorAll('[data-popout-input]'));

        return [...selectedNativeDropdowns, ...selectedRadios, ...selectedPopouts].map(({dataset}) => dataset.optionValueId).filter((id) => Boolean(id));
      }
    }

    customElements.define('variant-selects', VariantSelects);

/**
 * bundle-add-to-cart.js
 *
 * Web component for the bundle_products block on Art Wall + Perfect Match
 * product pages. Collects available variant IDs from [data-bundle-variant-id]
 * inputs and adds them all to the Shopify cart in a single request.
 *
 * After a successful add it integrates with the theme's cart system:
 *   - Dispatches `theme:cart:refresh`  → CartItems.getCart() re-fetches cart HTML
 *   - Dispatches `theme:cart-drawer:show` on the <cart-drawer> element → opens drawer
 *
 * Sold-out / unavailable items are skipped and a badge is shown in the Liquid.
 * If ALL items are unavailable the button is disabled server-side (Liquid) and
 * the component does nothing on click.
 */

class BundleAddToCart extends HTMLElement {
  connectedCallback() {
    this._btn = this.querySelector('[data-bundle-add-to-cart]');
    if (this._btn) {
      this._btn.addEventListener('click', () => this._addAllToCart());
    }

    /* ── Slider navigation ───────────────────────────────── */
    this._slider = this.querySelector('.bundle-products__slider');
    this._prevBtn = this.querySelector('.bundle-products__nav-btn--prev');
    this._nextBtn = this.querySelector('.bundle-products__nav-btn--next');

    if (this._slider && this._prevBtn && this._nextBtn) {
      this._prevBtn.addEventListener('click', () => this._slide(-1));
      this._nextBtn.addEventListener('click', () => this._slide(1));
      this._slider.addEventListener('scroll', () => this._updateNavState(), { passive: true });
      this._updateNavState();
    }
  }

  _slide(dir) {
    if (!this._slider) return;
    const slides = this._slider.querySelectorAll('.bundle-products__slide');
    if (!slides.length) return;
    const slideWidth = slides[0].offsetWidth + 5; /* gap: 5px */
    this._slider.scrollBy({ left: dir * slideWidth, behavior: 'smooth' });
  }

  _updateNavState() {
    if (!this._slider) return;
    const { scrollLeft, scrollWidth, clientWidth } = this._slider;
    if (this._prevBtn) this._prevBtn.disabled = scrollLeft <= 2;
    if (this._nextBtn) this._nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }

  /**
   * Collect cart line items for the variants that are available to buy.
   * Inputs with data-bundle-variant-available="false" are skipped.
   * Each item carries an "Artist" line item property (from
   * data-bundle-artist) so the artist reaches the cart, checkout and
   * order confirmation — matching the PDP add-to-cart behaviour.
   */
  _getAvailableItems() {
    return Array.from(this.querySelectorAll('[data-bundle-variant-id]'))
      .filter((el) => el.dataset.bundleVariantAvailable !== 'false')
      .map((el) => {
        const id = parseInt(el.dataset.bundleVariantId, 10);
        if (!id) return null;
        // Art Wall bundles carry a per-line quantity (frames repeat across prints).
        // Falls back to 1 for Perfect Match / legacy bundles with no quantity attribute.
        const quantity = Math.max(1, parseInt(el.dataset.bundleQuantity, 10) || 1);
        const item = { id, quantity };
        const artist = (el.dataset.bundleArtist || '').trim();
        const sku = el.dataset.bundleSku;
        if (artist) item.properties = { Artist: artist };
        if (sku) item.properties = { ...item.properties, SKU: sku };
        return item;
      })
      .filter(Boolean);
  }

  /**
   * When the linked metaobject has `product_groups`, the Liquid emits a
   * <script data-bundle-groups> blob: one entry per print with its frame/passe
   * add-ons. Build each print + add-ons as SEPARATE cart lines (sharing
   * `_bundleId` / `_role`) via the shared buildBundleItems() contract, so the
   * cart-transformer `merge`s each group into one bundle line at checkout
   * (parent = print variant) priced as the sum of the real lines' native
   * costs. Returns null when there are no groups (or the util isn't loaded)
   * so the caller falls back to the flat add.
   */
  _buildGroupedItems() {
    const build = window.theme && window.theme.buildBundleItems;
    if (typeof build !== 'function') return null;
    const el = this.querySelector('script[type="application/json"][data-bundle-groups]');
    if (!el) return null;
    let groups;
    try {
      groups = JSON.parse(el.textContent);
    } catch {
      return null;
    }
    if (!Array.isArray(groups) || !groups.length) return null;

    const source = this.dataset.bundleSource || '';
    const items = [];
    groups.forEach((g) => {
      if (!g || !g.print || g.print.id == null) return;
      const addons = (g.addons || [])
        .filter((a) => a && a.id != null)
        .map((a) => ({
          variantId: a.id,
          awdKey: a.role === 'frame' ? 'frame' : a.role === 'passe' ? 'passe' : undefined,
          label: a.label || '',
          hex: a.swatch || '',
          sku: a.sku,
        }));
      // No add-ons → a plain line (mirrors the Art Wall Designer); only a
      // print with a frame/passe needs the multi-line bundle treatment.
      if (!addons.length) {
        const props = source ? { _source: source } : {};
        if (g.print.sku) props.SKU = g.print.sku;
        items.push({ id: Number(g.print.id), quantity: 1, properties: props });
        return;
      }
      const lines = build({
        printVariantId: g.print.id,
        quantity: 1,
        addons,
        source,
        printImageUrl: g.print.image || '',
        printSku: g.print.sku,
      });
      lines.forEach((line) => items.push(line));
    });
    return items.length ? items : null;
  }

  async _addAllToCart() {
    // Grouped (per-print bundle) add when product_groups is authored; else flat.
    const items = this._buildGroupedItems() || this._getAvailableItems();
    if (!items.length) return;

    this._btn.classList.add('is-loading');
    this._btn.disabled = true;

    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('[BundleAddToCart] Cart API error:', errData);
        this._btn.classList.remove('is-loading');
        this._btn.disabled = false;
        return;
      }

      /* Show checkmark (same pattern as product-form add-to-cart) */
      this._btn.classList.remove('is-loading');
      this._btn.classList.add('is-added');

      /* 1. Tell CartItems to re-fetch the cart (updates count, items, subtotal) */
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

      /* 2. Open the cart drawer */
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: false }));
      } else {
        /* Fallback: toggle event (works for cart-slider / other drawer types) */
        document.dispatchEvent(new CustomEvent('theme:cart:toggle', { bubbles: true }));
      }

      setTimeout(() => {
        this._btn.classList.remove('is-added');
        this._btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('[BundleAddToCart] Fetch failed:', err);
      this._btn.classList.remove('is-loading');
      this._btn.disabled = false;
    }
  }
}

if (!customElements.get('bundle-add-to-cart')) {
  customElements.define('bundle-add-to-cart', BundleAddToCart);
}
/* components/cookie-settings.js
 * ---------------------------------------------------------------------------
 * Cookie settings link → reopen the cookie-consent preferences dialog.
 *
 * The "Cookie Settings" menu item points at href="#cmp", but #cmp is just a
 * page anchor — nothing listens for it, so the link does nothing on its own.
 *
 * This store uses Shopify's NATIVE customer-privacy banner (shopify-pc__banner)
 * — there is no CookieBot / third-party consent app installed. Shopify's banner
 * is reopened via its Customer Privacy "privacy banner" API:
 *     window.privacyBanner.showPreferences()
 *
 * We delegate the click at document level (so it works wherever the link
 * appears — footer supporting menu, etc.) and fall back to a couple of common
 * third-party CMP APIs in case the consent tool changes later. If none is
 * present a console warning is logged to make the cause obvious.
 * ---------------------------------------------------------------------------
 */
document.addEventListener('click', function (event) {
  const trigger = event.target.closest('a[href="#cmp"], a[href$="#cmp"]');
  if (!trigger) return;

  // #cmp would otherwise just jump the URL hash — swallow that.
  event.preventDefault();

  // 1. Shopify native customer-privacy banner (what this store uses).
  if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
    window.privacyBanner.showPreferences();
    return;
  }

  // 2. Fallbacks for third-party CMPs, in case the consent tool changes.
  if (window.Cookiebot && typeof window.Cookiebot.renew === 'function') {
    window.Cookiebot.renew();
    return;
  }
  if (window.UC_UI && typeof window.UC_UI.showSecondLayer === 'function') {
    window.UC_UI.showSecondLayer();
    return;
  }

  console.warn('[cookie-settings] No cookie-consent API found to open preferences for #cmp. ' +
    'Shopify’s privacy banner must be enabled (Settings → Customer privacy) or the CMP’s reopen call added here.');
});
    document.addEventListener('DOMContentLoaded', function () {
      // Scroll to top button
      const scrollTopButton = document.querySelector('[data-scroll-top-button]');
      if (scrollTopButton) {
        scrollTopButton.addEventListener('click', () => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          });
        });
        document.addEventListener('theme:scroll', () => {
          scrollTopButton.classList.toggle('is-visible', window.scrollY > window.innerHeight);
        });
      }

      if (window.self !== window.top) {
        document.querySelector('html').classList.add('iframe');
      }
    });

    // Apply a specific class to the html element for browser support of cookies.
    if (window.navigator.cookieEnabled) {
      document.documentElement.className = document.documentElement.className.replace('supports-no-cookies', 'supports-cookies');
    }

})(themeVendor.ScrollLock);
